import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import SectionErrorBoundary from "./components/ErrorBoundary";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetchJson } from "./api";
import { createPortal } from "react-dom";
import AddAccountModal from "./components/AddAccountModal";
import InlineAddProxy from "./components/InlineAddProxy";
import ProxyChecker from "./components/ProxyChecker";
import AdModal from "./components/AdModal";
import AuthPortal from "./components/AuthPortal";
import SubscriptionGate from "./components/SubscriptionGate";
import RedeemKeyForm from "./components/RedeemKeyForm";
import UserAvatar from "./components/UserAvatar";
import PaymentModal from "./components/PaymentModal";
import {
  isTelegramMiniApp,
  getTelegramInitData,
  setMiniAppSession,
  markMiniAppLoggedOut,
  wasMiniAppLoggedOut,
  isKeyboardLaunchedMiniApp,
  requestPanelButtonFromBot,
  initTelegramMiniApp,
  syncMiniAppColors
} from "./utils/telegramMiniApp";
import ThemePicker from "./components/ThemePicker";
import { buildMessageChimeWav, isDesktopDevice } from "./utils/notificationSound";
import WallpaperPicker from "./components/WallpaperPicker";
import { readChatWallpaper, saveChatWallpaper } from "./utils/chatWallpapers";
import ToastStack from "./components/ToastStack";
import AiPromptsSettings from "./components/AiPromptsSettings";
import { DEMO_OWNER_ID, DEMO_USERNAME, enterDemoMode, isDemoActive, isDemoGuest, leaveDemoMode, useDemoMode, wasDemoRequested, wasGuestDemoRequested } from "./demo/demoMode";
import StyledSelect from "./components/StyledSelect";
import { useTheme, isDarkThemeMode } from "./theme/ThemeProvider";
import { useAiPrefs, setAiPrefs, AI_PREF_KEYS } from "./utils/aiPrefs";
import { withFieldsRefreshedHint } from "./utils/publishHints";
import { buildPhotoSequence, buildPublishPhotoPayload } from "./utils/photoOrder";
import { useFontMode, setFontMode } from "./utils/fontMode";
import { useAmbientMode, setAmbientMode } from "./utils/ambientMode";
import {
  readPushState,
  enablePush,
  disablePush,
  isIosDevice,
  setAppBadgeCount,
  parseNotificationTarget,
  listenForNotificationClicks
} from "./utils/webPush";
const AmbientUmbrellas = lazyWithRetry(() => import("./components/AmbientUmbrellas"));
import { useModalPresence, useLastOpenValue, overlayMotionProps, dialogMotionProps } from "./utils/modalPresence";
import ConfirmDialogHost from "./components/ConfirmDialog";
import UpdatesBook, { LATEST_UPDATE_ID, readUpdatesSeenId, writeUpdatesSeenId } from "./components/UpdatesBook";
import SupportTicketModal from "./components/SupportTicketModal";
import AdminTicketsSection from "./components/AdminTicketsSection";
import AdminAiUsageSection from "./components/AdminAiUsageSection";
import AdminRedeemKeysSection from "./components/AdminRedeemKeysSection";
import { confirmAction } from "./utils/confirmDialog";
import { useEscapeLayer } from "./utils/escapeLayers";
import { SectionSkeleton } from "./components/Skeleton";
import AccountTile from "./components/AccountTile";
import DashboardSummary from "./components/DashboardSummary";
import {
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  ArmorLogoIcon,
  DashboardIcon,
  UserIcon,
  LinkIcon,
  MessageIcon,
  PlusIcon,
  RefreshIcon,
  EditIcon,
  FileIcon,
  CheckIcon,
  SuccessIcon,
  LoadingIcon,
  BellIcon,
  BookOpenIcon,
  AlertIcon,
  AlertTriangleIcon,
  XIcon,
  TrashIcon,
  TargetIcon,
  PackageIcon,
  ExternalLinkIcon,
  SendIcon,
  HomeIcon,
  CompassIcon,
  MonitorIcon,
  EyeIcon,
  EyeOffIcon,
  HeartIcon,
  InboxIcon,
  UploadIcon,
  ImageIcon,
  ClockIcon,
  SettingsIcon,
  ShieldCheckIcon,
  PlayCircleIcon,
  ChevronRightIcon,
  SparklesIcon,
  SearchIcon,
  SwapIcon,
  KeyIcon,
  LogOutIcon,
  Spinner
} from "./components/Icons";
import { t, tf, useLang, setLang, LANGUAGES, locale } from "./i18n";
import { CATEGORY_LANGUAGES, getCategoryLang, setCategoryLang, useCategoryLang } from "./categoryLang";
import { A, aa, S, sa, onAccent } from "./theme/palette";
import { AD_PUBLISHED_EVENT, markActiveAdsCacheStale } from "./utils/activeAdsCache";

const MessagesPage = lazyWithRetry(() => import("./pages/MessagesPage"));
const SenderPage = lazyWithRetry(() => import("./pages/SenderPage"));
const DraftsPage = lazyWithRetry(() => import("./pages/DraftsPage"));
const KlParserTab = lazyWithRetry(() => import("./components/KlParserTab"));

// Центр уведомлений: вкладки фильтра и разбор уведомления по виду. Вид
// определяется по type: сообщения и рассылки панели помечены явно, всё
// остальное — события прокси.
const NOTIFICATION_FILTERS = Object.freeze([
  { id: "all", label: "Все" },
  { id: "message", label: "Сообщения" },
  { id: "proxy", label: "Прокси" },
  { id: "system", label: "Система" }
]);
const notificationKind = (notification) => {
  const type = String(notification?.type || "").trim().toLowerCase();
  if (type === "message") return "message";
  if (type === "broadcast") return "system";
  return "proxy";
};
// Сегодня / вчера / раньше — по берлинскому календарю, как и время в строке.
const notificationDayBucket = (createdAt) => {
  const ms = Number(createdAt || 0);
  if (!Number.isFinite(ms) || ms <= 0) return "earlier";
  const dayOf = (date) => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
  const day = dayOf(new Date(ms));
  if (day === dayOf(new Date())) return "today";
  if (day === dayOf(new Date(Date.now() - 24 * 60 * 60 * 1000))) return "yesterday";
  return "earlier";
};
const ActiveAdsPage = lazyWithRetry(() => import("./pages/ActiveAdsPage"));

const PHONE_VIEW_MAX_WIDTH = 900;
const APP_VERSION_RAW = String(process.env.REACT_APP_VERSION || "").trim();
const APP_VERSION = APP_VERSION_RAW ? `v${APP_VERSION_RAW}` : "vdev";
const GLOBAL_MESSAGES_AUTO_REFRESH_MS = 60 * 1000;
const GLOBAL_PROXIES_AUTO_REFRESH_MS = 60 * 1000;
const GLOBAL_ACCOUNTS_AUTO_REFRESH_MS = 5 * 60 * 1000;
// Fire `fn` every baseMs ± ratio random jitter (recomputed each cycle) so the
// KL-hitting poll cadence isn't a fixed robotic tick. Returns a cancel fn.
const scheduleJittered = (fn, baseMs, ratio = 0.2) => {
  let timer = null;
  let cancelled = false;
  const tick = () => {
    const jitter = baseMs * ratio;
    const delay = Math.max(1000, Math.round(baseMs + (Math.random() * 2 - 1) * jitter));
    timer = window.setTimeout(() => {
      if (cancelled) return;
      try { fn(); } finally { if (!cancelled) tick(); }
    }, delay);
  };
  tick();
  return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
};
// Бот, в котором покупают подписку и входят в панель.
const SUBSCRIPTION_BOT_URL = "https://t.me/umbrellapushbot";

// Занятость окна выбора, пока отмеченные аккаунты подключаются к Umbrella.
const BUILTIN_PROXY_PICKER_KEY = "builtin-next";

// Массовые действия с аккаунтами (удаление, смена прокси, переход на Umbrella)
// — пачками: до BULK_ACCOUNT_CONCURRENCY запросов разом, освободилось место —
// сразу следующий аккаунт. Как и массовое добавление (AddAccountModal).
const BULK_ACCOUNT_CONCURRENCY = 10;
const runInBatches = async (items, worker, limit = BULK_ACCOUNT_CONCURRENCY) => {
  let cursor = 0;
  const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      await worker(item);
    }
  });
  await Promise.all(lanes);
};

const TAB_ROUTE_MAP = Object.freeze({
  dashboard: "/dashboard",
  accounts: "/accounts",
  proxies: "/proxies",
  messages: "/messages",
  sender: "/sender",
  drafts: "/drafts",
  "kl-parser": "/kl-parser",
  "active-ads": "/active-ads",
  settings: "/settings"
});
const ROUTE_TAB_MAP = Object.freeze(
  Object.entries(TAB_ROUTE_MAP).reduce((acc, [tabId, path]) => {
    acc[path] = tabId;
    return acc;
  }, {})
);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Автообновление сообщений: как часто панель проверяет новые сообщения на
// аккаунтах пользователя. Значения — те же, что принимает сервер
// (MESSAGES_REFRESH_MINUTES_OPTIONS); чаще минуты не бывает.
const MESSAGES_REFRESH_OPTIONS = Object.freeze([
  { value: 1, label: "1 мин", hint: "Новые сообщения и уведомления в Telegram — в течение минуты. Так по умолчанию." },
  { value: 2, label: "2 мин", hint: "Новые сообщения и уведомления в Telegram — в течение 2 минут." },
  { value: 5, label: "5 мин", hint: "Новые сообщения и уведомления в Telegram — в течение 5 минут." },
  { value: 10, label: "10 мин", hint: "Новые сообщения и уведомления в Telegram — в течение 10 минут." }
]);
const normalizeMessagesRefreshMinutes = (value) => {
  const minutes = Number(value);
  return MESSAGES_REFRESH_OPTIONS.some((option) => option.value === minutes) ? minutes : 1;
};

const DEFAULT_TELEGRAM_BOT_STATUS = Object.freeze({
  linked: false,
  totalChats: 0,
  username: "",
  source: "",
  linkedAt: null,
  updatedAt: null,
  chatIdMasked: ""
});

const normalizeTelegramBotStatus = (value = {}) => {
  const source = value && typeof value === "object" ? value : {};
  const totalChatsRaw = Number(source.totalChats || 0);
  return {
    linked: Boolean(source.linked),
    totalChats: Number.isFinite(totalChatsRaw) ? Math.max(0, Math.floor(totalChatsRaw)) : 0,
    username: String(source.username || "").trim(),
    source: String(source.source || "").trim(),
    linkedAt: source.linkedAt ? String(source.linkedAt).trim() : null,
    updatedAt: source.updatedAt ? String(source.updatedAt).trim() : null,
    chatIdMasked: String(source.chatIdMasked || "").trim()
  };
};

const detectPhoneView = () => {
  if (typeof window === "undefined") return false;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  return viewportWidth > 0 && viewportWidth <= PHONE_VIEW_MAX_WIDTH;
};

const normalizePathname = (pathname = "/") => {
  const normalized = `/${String(pathname || "").trim().replace(/^\/+/, "").replace(/\/+$/, "")}`;
  return normalized === "/" ? "/" : normalized;
};

const resolveTabIdFromPathname = (pathname = "/") => {
  const normalized = normalizePathname(pathname);
  if (normalized === "/") return "dashboard";
  return ROUTE_TAB_MAP[normalized] || null;
};

const toInputDateTimeValue = (isoValue) => {
  const raw = String(isoValue || "").trim();
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const PHONE_COUNTRY_OPTIONS = [
  { value: "49", label: "🇩🇪 Deutschland (+49)" },
  { value: "43", label: "🇦🇹 Österreich (+43)" },
  { value: "41", label: "🇨🇭 Schweiz (+41)" },
  { value: "31", label: "🇳🇱 Niederlande (+31)" },
  { value: "32", label: "🇧🇪 Belgien (+32)" },
  { value: "33", label: "🇫🇷 Frankreich (+33)" },
  { value: "34", label: "🇪🇸 Spanien (+34)" },
  { value: "39", label: "🇮🇹 Italien (+39)" },
  { value: "30", label: "🇬🇷 Griechenland (+30)" },
  { value: "45", label: "🇩🇰 Dänemark (+45)" },
  { value: "46", label: "🇸🇪 Schweden (+46)" },
  { value: "47", label: "🇳🇴 Norwegen (+47)" },
  { value: "48", label: "🇵🇱 Polen (+48)" },
  { value: "40", label: "🇷🇴 Rumänien (+40)" },
  { value: "36", label: "🇭🇺 Ungarn (+36)" },
  { value: "351", label: "🇵🇹 Portugal (+351)" },
  { value: "352", label: "🇱🇺 Luxemburg (+352)" },
  { value: "353", label: "🇮🇪 Irland (+353)" },
  { value: "354", label: "🇮🇸 Island (+354)" },
  { value: "356", label: "🇲🇹 Malta (+356)" },
  { value: "357", label: "🇨🇾 Zypern (+357)" },
  { value: "358", label: "🇫🇮 Finnland (+358)" },
  { value: "359", label: "🇧🇬 Bulgarien (+359)" },
  { value: "370", label: "🇱🇹 Litauen (+370)" },
  { value: "371", label: "🇱🇻 Lettland (+371)" },
  { value: "372", label: "🇪🇪 Estland (+372)" },
  { value: "385", label: "🇭🇷 Kroatien (+385)" },
  { value: "386", label: "🇸🇮 Slowenien (+386)" },
  { value: "423", label: "🇱🇮 Liechtenstein (+423)" },
  { value: "420", label: "🇨🇿 Tschechien (+420)" },
  { value: "421", label: "🇸🇰 Slowakei (+421)" }
];

// Built-in "Umbrella" panel proxies always sort to the very top of any list,
// above the user's own proxies. Stable sort keeps the rest in place.
const orderProxiesBuiltinFirst = (list) =>
  (Array.isArray(list) ? [...list] : []).sort(
    (a, b) => (b?.isBuiltin ? 1 : 0) - (a?.isBuiltin ? 1 : 0)
  );

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  // Открыта как мини-апп Telegram: окно — во весь рост, шапка Telegram — цветом
  // полотна текущей темы.
  useEffect(() => {
    initTelegramMiniApp();
  }, []);
  useEffect(() => {
    syncMiniAppColors();
  }, [themeMode]);
  const fontMode = useFontMode();
  const ambientMode = useAmbientMode();
  // Перерисовывает панель при смене языка. Компоненты не мемоизированы, поэтому
  // одного обновления корня хватает, чтобы новый язык дошёл до всех экранов.
  const lang = useLang();
  // То же для языка категорий: рубрики и параметры объявлений в окнах
  // перерисовываются вместе с корнем.
  useCategoryLang();
  // Демо-режим: панель на вымышленных данных для тех, у кого нет подписки.
  // Запросы в нём отвечает песочница (см. demo/), сервер не спрашивается.
  const demoActive = useDemoMode();
  // Настоящий владелец на время демо: панель в демо работает от имени
  // песочницы, а при выходе должна вернуться к своему.
  const demoRealOwnerRef = useRef("");
  // "Graphite" is the third theme: strict monochrome dark grey, structured
  // like the light theme (crisp surfaces, simple shadows). Fold it into
  // isDarkTheme so existing `isDarkTheme ? dark : light` ternaries keep the
  // chrome on the dark side; graphite-specific deltas (lighter greys, neutral
  // borders without the slate-blue tint) live below.
  const isDarkTheme = isDarkThemeMode(themeMode);
  const isGraphite = themeMode === "graphite";
  const appBackground = isGraphite
    // Even charcoal grey — no radial gradient, no color bloom. Strict mono.
    ? "#1c1f23"
    : isDarkTheme
      ? S.bg
      : "radial-gradient(circle at top, #ffffff 0%, #f5f6f8 52%, #eceef2 100%)";
  const shellSurface = isGraphite
    ? "linear-gradient(160deg, #2a2e34, #23262b)"
    : isDarkTheme
      ? S.surface
      : "linear-gradient(160deg, #ffffff, #f7f8fa)";
  const shellSurfaceSoft = isGraphite
    ? "rgba(255,255,255,0.04)"
    : isDarkTheme ? `${sa(0.65)}` : "rgba(14,16,21,0.05)";
  const shellCardBorder = isGraphite
    ? "rgba(255,255,255,0.08)"
    : isDarkTheme ? "rgba(141,147,163,0.15)" : "rgba(14,16,21,0.12)";
  const shellCardBorderStrong = isGraphite
    ? "rgba(255,255,255,0.14)"
    : isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.14)";
  const shellShadow = isGraphite
    ? "0 8px 22px rgba(0,0,0,0.45)"
    : isDarkTheme
      ? "0 12px 32px rgba(0,0,0,0.35)"
      : "0 12px 26px rgba(14,16,21,0.08)";
  const surfaceInputBg = isGraphite
    ? "#23262b"
    : isDarkTheme ? "rgba(8,9,12,0.6)" : "#ffffff";
  const surfaceInputBorder = isGraphite
    ? "rgba(255,255,255,0.10)"
    : isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.16)";
  const surfaceInputText = isDarkTheme ? "#e2e4e9" : S.surface;
  const surfaceTextPrimary = isDarkTheme ? "#f0f1f4" : S.surface;
  const surfaceTextSecondary = isGraphite ? "#c6c9d1" : (isDarkTheme ? "#8d93a3" : "#3f4450");
  const surfaceTextTertiary = isGraphite ? "#9ca3af" : (isDarkTheme ? "#7c8291" : "#707684");
  const headerGlassBg = isGraphite
    ? "linear-gradient(145deg, #2a2e34, #23262b)"
    : isDarkTheme
      ? S.surface
      : "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(247,248,250,0.98))";
  const headerGlassBorder = isGraphite
    ? "rgba(255,255,255,0.08)"
    : isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.12)";
  const headerGlassShadow = isGraphite
    ? "0 8px 22px rgba(0,0,0,0.4)"
    : isDarkTheme ? "0 14px 30px rgba(0,0,0,0.35)" : "0 10px 22px rgba(14,16,21,0.1)";
  // headerIconBg и headerIconBorder убраны вместе с рамкой вокруг знака: они
  // рисовали подложку и обводку, а у нового знака своя плитка. Тень осталась —
  // она отделяет его от шапки, ничего не обводя.
  const headerIconShadow = isGraphite
    ? "0 4px 12px rgba(0,0,0,0.4)"
    : isDarkTheme ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(14,16,21,0.08)";
  const popupPanelBg = isGraphite ? "#2a2e34" : (isDarkTheme ? S.surface : "#ffffff");
  const popupPanelBorder = isGraphite
    ? "rgba(255,255,255,0.12)"
    : isDarkTheme ? "rgba(141,147,163,0.3)" : "rgba(14,16,21,0.16)";
  const popupPanelShadow = isGraphite
    ? "0 20px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)"
    : isDarkTheme
      ? "0 25px 50px rgba(0,0,0,0.8), 0 0 0 1px rgba(141,147,163,0.1)"
      : "0 20px 40px rgba(14,16,21,0.16), 0 0 0 1px rgba(141,147,163,0.08)";
  const [accounts, setAccounts] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [stats, setStats] = useState(null);
  const [messagesTabUnreadCount, setMessagesTabUnreadCount] = useState(null);
  // Дашборд: последние диалоги — тот же список, по которому считаются
  // счётчики сообщений.
  const [dashboardConversations, setDashboardConversations] = useState([]);
  // «Открой этот диалог» с дашборда — его забирает раздел «Сообщения».
  const [pendingConversationOpen, setPendingConversationOpen] = useState(null);
  // Диалог из нажатого push-уведомления: панель, запущенная им, получает его в
  // адресе, уже открытая — сообщением от service worker. Открывается, когда
  // вход подтверждён.
  const [notificationTarget, setNotificationTarget] = useState(() => (
    typeof window === "undefined" ? null : parseNotificationTarget(window.location.href)
  ));
  // Push на это устройство: "checking", "on", "off", "denied", "unsupported",
  // "ios-install" (см. utils/webPush.js).
  const [pushState, setPushState] = useState("checking");
  const [pushBusy, setPushBusy] = useState(false);
  const [accountMetrics, setAccountMetrics] = useState(() => {
    try {
      const cached = localStorage.getItem("kl_account_metrics");
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [activeTab, setActiveTab] = useState(() => {
    return resolveTabIdFromPathname(location.pathname) || "dashboard";
  });
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  // Окно оплаты подписки; planId — тариф, выбранный заранее (с карточки).
  const [paymentModal, setPaymentModal] = useState({ open: false, planId: "" });
  const [showAdModal, setShowAdModal] = useState(false);
  const [proxyPickerAccount, setProxyPickerAccount] = useState(null);
  const [updatingAccountProxyId, setUpdatingAccountProxyId] = useState("");
  // Аккаунты, у которых сейчас меняется встроенный прокси («Сменить»).
  const [swappingProxyAccountIds, setSwappingProxyAccountIds] = useState(() => new Set());
  // Выбор карточек аккаунтов для массовых действий.
  const [selectedAccountIds, setSelectedAccountIds] = useState(() => new Set());
  // Непустой список означает, что окно выбора прокси открыто для нескольких
  // аккаунтов сразу, а не для одного.
  const [proxyPickerBulkIds, setProxyPickerBulkIds] = useState([]);
  const [proxyPickerQuery, setProxyPickerQuery] = useState("");
  const [tokenStatus, setTokenStatus] = useState({
    state: "unknown",
    message: "",
    expiresAt: null,
    ownerId: "",
    role: "user"
  });
  const [authIdentity, setAuthIdentity] = useState({
    username: "",
    ownerId: "",
    avatarUrl: "",
    hasPassword: true,
    subscription: null
  });
  const [authError, setAuthError] = useState("");
  const [authBootstrapDone, setAuthBootstrapDone] = useState(false);
  // С чего открывается экран входа: главная — обычно; форма регистрации —
  // гость из демо пошёл за подпиской; форма входа — в мини-аппе Telegram не
  // удался вход по Telegram (причина над формой).
  const [authPortalEntry, setAuthPortalEntry] = useState({ screen: "landing", tab: "login" });
  const [freezeDaysInput, setFreezeDaysInput] = useState("");
  const [freezeActionLoading, setFreezeActionLoading] = useState(false);
  const [freezeActionError, setFreezeActionError] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [usernameFormOpen, setUsernameFormOpen] = useState(false);
  const [redeemFormOpen, setRedeemFormOpen] = useState(false);
  const [usernameForm, setUsernameForm] = useState({ username: "", currentPassword: "" });
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInputRef = useRef(null);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  // Private mode blurs product titles and photos so the panel can be opened in
  // public without the goods on screen. Remembered, since someone who needs it
  // once usually needs it every time.
  const [privateMode, setPrivateMode] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem("kl-private-mode") === "1"; } catch (error) { return false; }
  });

  useEffect(() => {
    try { window.localStorage.setItem("kl-private-mode", privateMode ? "1" : "0"); } catch (error) { /* private browsing */ }
  }, [privateMode]);
  // Сайдбар: закреплён (всегда раскрыт) или выезжает при наведении, а в покое
  // свёрнут до значков. Переключатель — над профилем; выбор живёт в браузере.
  const [railMode, setRailMode] = useState(() => {
    try { return window.localStorage.getItem("kl-rail-mode") === "auto" ? "auto" : "fixed"; } catch (error) { return "fixed"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("kl-rail-mode", railMode); } catch (error) { /* private browsing */ }
  }, [railMode]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("kl_sound_enabled") !== "false"; } catch { return true; }
  });
  const [telegramSettings, setTelegramSettings] = useState({
    telegramNotifyNewMessages: true,
    telegramNotifyAccountOffline: true,
    telegramNotifyProxyConnectionLost: true,
  });
  const [telegramBotStatus, setTelegramBotStatus] = useState(() => ({ ...DEFAULT_TELEGRAM_BOT_STATUS }));
  const [messagesRefreshMinutes, setMessagesRefreshMinutes] = useState(1);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  // Rect of the bell button that opened the panel, so the dropdown anchors
  // directly below whichever bell was clicked instead of a fixed corner.
  const [notifAnchorRect, setNotifAnchorRect] = useState(null);
  // Фильтр центра уведомлений: все / сообщения / прокси / система.
  const [notifFilter, setNotifFilter] = useState("all");
  const [openAccountMenuId, setOpenAccountMenuId] = useState(null);
  const [copyingCookiesId, setCopyingCookiesId] = useState(null);
  // Аккаунты, у которых сейчас читается или переключается настройка писем о
  // сообщениях: пока идёт запрос, переключатель в меню помечен занятым.
  const [emailNotifyBusyIds, setEmailNotifyBusyIds] = useState(() => new Set());
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [refreshingAccountIds, setRefreshingAccountIds] = useState(new Set());
  const [profileNameModal, setProfileNameModal] = useState({
    open: false,
    account: null,
    value: "",
    saving: false
  });
  const [phoneModal, setPhoneModal] = useState({
    open: false,
    account: null,
    countryCode: "49",
    phoneNumber: "",
    code: "",
    sessionId: "",
    expiresAt: 0,
    step: "start",
    saving: false,
    resending: false,
    statusLoading: false,
    statusSource: ""
  });
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isPhoneView, setIsPhoneView] = useState(() => detectPhoneView());
  const [isMobileMessagesChatExpanded, setIsMobileMessagesChatExpanded] = useState(false);
  const [isMobileMessagesThreadOpen, setIsMobileMessagesThreadOpen] = useState(false);
  const profilePanelRef = useRef(null);
  const profilePanelPopupRef = useRef(null);
  const notificationsPanelRef = useRef(null);
  const notificationsPanelPopupRef = useRef(null);
  const extraNotifRef = useRef(null);
  const extraProfileRef = useRef(null);
  const messageStatsPollInFlight = useRef(false);
  const proxyPollInFlight = useRef(false);
  const accountsPollInFlight = useRef(false);
  // Удалённые аккаунты: id → когда удаление подтвердил сервер. Список, запрос
  // которого ушёл раньше, ещё содержит удалённый — без этой поправки аккаунт
  // «воскресал» после удаления и держался до следующего опроса (раз в пять
  // минут) или перезагрузки страницы.
  const deletedAccountsRef = useRef(new Map());
  const applyFetchedAccounts = (list, requestedAt) => {
    const deleted = deletedAccountsRef.current;
    const now = Date.now();
    deleted.forEach((deletedAt, id) => {
      // Запросы списка живут не дольше нескольких минут.
      if (now - deletedAt > 10 * 60 * 1000) deleted.delete(id);
    });
    setAccounts((Array.isArray(list) ? list : []).filter((item) => {
      const deletedAt = deleted.get(String(item?.id));
      return !deletedAt || deletedAt < requestedAt;
    }));
  };
  const rememberDeletedAccounts = (ids) => {
    const now = Date.now();
    ids.forEach((id) => deletedAccountsRef.current.set(String(id), now));
  };
  const accountMetricsEmptyAdsStreakRef = useRef(0);
  const adsMetricsWarningRef = useRef({ message: "", timestamp: 0 });
  const [checkingAllProxies, setCheckingAllProxies] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [broadcastForm, setBroadcastForm] = useState({
    title: "",
    body: "",
    level: "info",
    targetType: "all",
    ownerIds: "",
    actionLabel: "",
    actionUrl: "",
    sendToTelegram: false
  });
  const [adminBroadcasts, setAdminBroadcasts] = useState([]);
  const [loadingAdminBroadcasts, setLoadingAdminBroadcasts] = useState(false);
  const [savingBroadcast, setSavingBroadcast] = useState(false);
  const [broadcastFormError, setBroadcastFormError] = useState("");
  const [panelProxyConfig, setPanelProxyConfig] = useState("");
  const [panelProxyProxies, setPanelProxyProxies] = useState([]);
  const [loadingPanelProxy, setLoadingPanelProxy] = useState(false);
  const [savingPanelProxy, setSavingPanelProxy] = useState(false);
  const [panelProxyStatus, setPanelProxyStatus] = useState(null);
  // Сервер проверяет пул Umbrella в фоне — статусы перечитываются, пока идёт.
  const [panelProxyChecking, setPanelProxyChecking] = useState(false);
  const [parserProxies, setParserProxies] = useState([]);
  const [parserProxyInput, setParserProxyInput] = useState("");
  const [parserProxyBusy, setParserProxyBusy] = useState(false);
  const [parserProxyStatus, setParserProxyStatus] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUserQuery, setAdminUserQuery] = useState("");
  const [adminUserSort, setAdminUserSort] = useState("new");
  // Обои переписки — выбор в настройках, хранится в этом браузере.
  const [chatWallpaper, setChatWallpaper] = useState(() => readChatWallpaper());
  // Список пользователей длинный, и до промокодов под ним приходилось долго
  // листать — поэтому он свёрнут под заголовок. Раскрыт ли — запоминается.
  const [adminUsersOpen, setAdminUsersOpen] = useState(() => {
    try { return localStorage.getItem("kl_admin_users_open") === "1"; } catch { return false; }
  });
  // Прокси, заведённые пользователями для аккаунтов, — целиком, ип:порт@логин:пароль.
  const [userProxies, setUserProxies] = useState([]);
  const [loadingUserProxies, setLoadingUserProxies] = useState(false);
  const [userProxyQuery, setUserProxyQuery] = useState("");
  const [userProxiesOpen, setUserProxiesOpen] = useState(() => {
    try { return localStorage.getItem("kl_admin_user_proxies_open") === "1"; } catch { return false; }
  });
  const toggleUserProxiesOpen = () => {
    setUserProxiesOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem("kl_admin_user_proxies_open", next ? "1" : "0"); } catch { /* приватное окно */ }
      return next;
    });
  };
  // Служебная статистика по аккаунтам — грузится, только когда блок раскрыт.
  const [accountStats, setAccountStats] = useState(null);
  const [loadingAccountStats, setLoadingAccountStats] = useState(false);
  const [accountStatsExpanded, setAccountStatsExpanded] = useState(() => new Set());
  const [accountStatsOpen, setAccountStatsOpen] = useState(() => {
    try { return localStorage.getItem("kl_admin_account_stats_open") === "1"; } catch { return false; }
  });
  // За какой период показаны публикации: day | week | month | all.
  const [accountStatsPeriod, setAccountStatsPeriod] = useState(() => {
    try {
      const saved = localStorage.getItem("kl_admin_account_stats_period");
      return ["day", "week", "month", "all"].includes(saved) ? saved : "all";
    } catch { return "all"; }
  });
  const changeAccountStatsPeriod = (period) => {
    setAccountStatsPeriod(period);
    try { localStorage.setItem("kl_admin_account_stats_period", period); } catch { /* приватное окно */ }
  };
  const loadAccountStats = async () => {
    if (!isAdminUser) return;
    setLoadingAccountStats(true);
    try {
      const response = await apiFetchJson("/api/admin/account-stats", { timeoutMs: 30000, retry: true });
      setAccountStats(response?.success ? response : null);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoadingAccountStats(false);
    }
  };
  const toggleAccountStatsOpen = () => {
    setAccountStatsOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem("kl_admin_account_stats_open", next ? "1" : "0"); } catch { /* приватное окно */ }
      if (next && !accountStats) loadAccountStats();
      return next;
    });
  };
  const toggleAccountStatsRow = (id) => {
    setAccountStatsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Соединение сообщений по аккаунтам: прямое или через прокси. Пользователю
  // это не видно — меняется только путь, которым идут его сообщения.
  const [messagesTransport, setMessagesTransport] = useState(null);
  const [loadingMessagesTransport, setLoadingMessagesTransport] = useState(false);
  const [messagesTransportQuery, setMessagesTransportQuery] = useState("");
  const [messagesTransportSaving, setMessagesTransportSaving] = useState(() => new Set());
  const [messagesTransportOpen, setMessagesTransportOpen] = useState(() => {
    try { return localStorage.getItem("kl_admin_messages_transport_open") === "1"; } catch { return false; }
  });
  const loadMessagesTransport = async () => {
    if (!isAdminUser) return;
    setLoadingMessagesTransport(true);
    try {
      const response = await apiFetchJson("/api/admin/messages-transport", { timeoutMs: 30000, retry: true });
      setMessagesTransport(response?.success ? response : null);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoadingMessagesTransport(false);
    }
  };
  const toggleMessagesTransportOpen = () => {
    setMessagesTransportOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem("kl_admin_messages_transport_open", next ? "1" : "0"); } catch { /* приватное окно */ }
      if (next && !messagesTransport) loadMessagesTransport();
      return next;
    });
  };
  const setAccountMessagesTransport = async (row, mode) => {
    const key = String(row?.accountId ?? "");
    if (!isAdminUser || !key || row.mode === mode || messagesTransportSaving.has(key)) return;
    setMessagesTransportSaving((prev) => new Set(prev).add(key));
    try {
      const response = await apiFetchJson(`/api/admin/messages-transport/${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode }),
        timeoutMs: 20000
      });
      if (response?.item) {
        setMessagesTransport((prev) => (prev ? {
          ...prev,
          items: (prev.items || []).map((entry) => (String(entry.accountId) === key ? response.item : entry))
        } : prev));
      }
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось переключить соединение") });
    } finally {
      setMessagesTransportSaving((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };
  const toggleAdminUsersOpen = () => {
    setAdminUsersOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem("kl_admin_users_open", next ? "1" : "0"); } catch { /* приватное окно */ }
      return next;
    });
  };
  const [adminUserFilter, setAdminUserFilter] = useState("all");
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [addTimeForm, setAddTimeForm] = useState({ hours: "1", target: "all", ownerId: "" });
  const [savingAddTime, setSavingAddTime] = useState(false);
  const [adminUserActionFor, setAdminUserActionFor] = useState("");
  const [adminPromos, setAdminPromos] = useState([]);
  const [adminPlans, setAdminPlans] = useState([]);
  const [loadingAdminPromos, setLoadingAdminPromos] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: "", discountPercent: "10", maxUses: "0", perUserMaxUses: "1", validDays: "0", planId: "" });
  const [toasts, setToasts] = useState([]);
  const messageNotifBootRef = useRef(false);
  const proxyNotifBootRef = useRef(false);
  const accountBanNotifBootRef = useRef(false);
  const accountBanStateRef = useRef(new Map());
  const lastNotifOwnerRef = useRef("");
  const notificationsStreamRef = useRef(null);
  const knownServerNotificationIdsRef = useRef(new Set());
  const [adImages, setAdImages] = useState([]);
  const [draftImageUrls, setDraftImageUrls] = useState([]);
  // Общий порядок фото в окне публикации: снимки черновика и свои файлы вперемешку
  // (utils/photoOrder.js). Сами наборы — adImages и draftImageUrls.
  const [adPhotoOrder, setAdPhotoOrder] = useState([]);
  const [publishingAd, setPublishingAd] = useState(false);
  const [extraFields, setExtraFields] = useState([]);
  const [extraFieldValues, setExtraFieldValues] = useState({});
  const [loadingExtraFields, setLoadingExtraFields] = useState(false);
  const [extraFieldsError, setExtraFieldsError] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesUpdatedAt, setCategoriesUpdatedAt] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [newAd, setNewAd] = useState({
    draftId: "",
    accountId: "",
    title: "",
    description: "",
    price: "",
    priceType: "FIXED",
    categoryId: "",
    categoryUrl: "",
    postalCode: "",
    locationId: "",
    locationName: "",
    categoryKey: "",
    categoryPath: [],
    scheduledAt: "",
    legalInfo: ""
  });
  const [publishProgressModal, setPublishProgressModal] = useState({
    open: false,
    requestId: "",
    adTitle: "",
    accountLabel: "",
    step: "idle",
    percent: 0,
    statusText: "",
    error: "",
    publicationStatus: "",
    publicationProbe: null,
    // Ссылка на опубликованное объявление — для «Открыть на Kleinanzeigen».
    resultUrl: "",
    // Обложка и цена — для итогового экрана: объявление узнаётся с первого взгляда.
    coverUrl: "",
    priceLabel: "",
    startedAt: 0,
    updatedAt: 0
  });
  const [closingPublishProgressModal, setClosingPublishProgressModal] = useState(false);
  // Обложка из своего файла — blob-ссылка, её нужно отпустить, когда окно закрыто.
  const publishCoverObjectUrlRef = useRef("");
  // Warm-up progress modal — drives the Puppeteer-based browser warm-up flow.
  const [warmupProgressModal, setWarmupProgressModal] = useState({
    open: false,
    sessionId: "",
    accountId: null,
    accountLabel: "",
    phase: "",
    label: "",
    percent: 0,
    error: "",
    result: null,
    done: false,
    startedAt: 0
  });
  const warmupPollAbortRef = useRef(null);
  // Присутствие модалок: окно живёт в DOM ещё 200 мс после закрытия, чтобы
  // проиграть анимацию выхода (см. utils/modalPresence.js).
  const addAccountPresence = useModalPresence(showAddAccountModal);
  const adModalPresence = useModalPresence(showAdModal);
  const proxyPickerPresence = useModalPresence(Boolean(proxyPickerAccount));
  const profileNamePresence = useModalPresence(profileNameModal.open);
  const phoneModalPresence = useModalPresence(phoneModal.open);
  const publishProgressPresence = useModalPresence(publishProgressModal.open);
  const warmupProgressPresence = useModalPresence(warmupProgressModal.open);
  // Escape закрывает верхнее окно (см. utils/escapeLayers.js). Закрытие то же,
  // что у крестика: где окно сейчас закрыть нельзя (сохранение, смена прокси),
  // оно и по Escape остаётся. Окна прогресса закрываются, только когда видна
  // их кнопка «Готово» / «Закрыть».
  useEscapeLayer(isMobileNavOpen, () => setIsMobileNavOpen(false));
  useEscapeLayer(Boolean(proxyPickerAccount), () => closeProxyPicker());
  useEscapeLayer(profileNameModal.open, () => closeProfileNameModal());
  useEscapeLayer(phoneModal.open, () => closePhoneModal());
  useEscapeLayer(publishProgressModal.open, () => {
    const stage = resolvePublishProgressStage(publishProgressModal.step || "queued");
    const canClose = publishProgressModal.error || stage.id === "done" || Number(publishProgressModal.percent || 0) >= 100;
    if (canClose && !closingPublishProgressModal) handleClosePublishProgressModal();
  });
  useEscapeLayer(warmupProgressModal.open, () => {
    if (warmupProgressModal.done) handleCloseWarmupModal();
  });
  // Панель массовых действий живёт в DOM ещё немного после снятия выделения,
  // чтобы успеть проиграть уход. Присутствие считается здесь, а не внутри
  // AccountsTab: тот пересоздаётся на каждом рендере App.
  const accountsBulkPresence = useModalPresence(selectedAccountIds.size > 0);
  // Уведомления закрывались в тот же кадр, и анимации выхода было негде
  // проиграться; держим панель на экране, пока она уезжает.
  const notificationsPresence = useModalPresence(showNotificationsPanel, 220);
  // Окно «Создать тикет» — открывается из книжечки обновлений (UpdatesBook).
  const [showTicketModal, setShowTicketModal] = useState(false);
  // Книжечка обновлений. Кнопок две — в шапке дашборда и в листе «Ещё» на
  // телефоне, — карточка одна. Точка на кнопке — пока свежая справка не
  // открыта в этом браузере.
  const [updatesBook, setUpdatesBook] = useState({ open: false, anchorRect: null });
  const [updatesSeenId, setUpdatesSeenId] = useState(readUpdatesSeenId);
  const hasNewUpdates = Boolean(LATEST_UPDATE_ID) && updatesSeenId !== LATEST_UPDATE_ID;
  const openUpdatesBook = (anchorElement = null) => {
    setUpdatesBook({ open: true, anchorRect: anchorElement?.getBoundingClientRect?.() || null });
    if (hasNewUpdates) {
      setUpdatesSeenId(LATEST_UPDATE_ID);
      writeUpdatesSeenId(LATEST_UPDATE_ID);
    }
  };
  const closeUpdatesBook = () => setUpdatesBook((prev) => ({ ...prev, open: false }));
  const accountsBulkCount = useLastOpenValue(selectedAccountIds.size, selectedAccountIds.size > 0);
  // Пока окно догорает, стейт уже сброшен закрытием — рисуем последний
  // «открытый» снимок, чтобы поля не пустели на глазах.
  const profileNameView = useLastOpenValue(profileNameModal, profileNameModal.open);
  const phoneModalView = useLastOpenValue(phoneModal, phoneModal.open);
  const proxyPickerShown = useLastOpenValue(proxyPickerAccount, Boolean(proxyPickerAccount));
  const publishProgressView = useLastOpenValue(publishProgressModal, publishProgressModal.open);
  const warmupProgressView = useLastOpenValue(warmupProgressModal, warmupProgressModal.open);
  const activeCreateRequestIdRef = useRef("");
  const activeCreateRequestAbortRef = useRef(null);
  const debugFieldsEnabled = false;
  const debugPublishEnabled = false;
  const TOP_RIGHT_PANEL_Z_INDEX = 141000;
  const TOAST_Z_INDEX = 141100;

  const clearAuthHashFromUrl = () => {
    if (typeof window === "undefined") return;
    const currentHash = String(window.location.hash || "").trim().toLowerCase();
    if (!currentHash.startsWith("#auth-")) return;
    try {
      const nextUrl = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, "", nextUrl);
    } catch {
      // ignore history API issues
    }
  };

  const formatAccountLabel = (account) => {
    const rawName = String(account.profileName || "").trim();
    const name = rawName && !/mein(e)? anzeigen|mein profil|meine anzeigen|profil und meine anzeigen/i.test(rawName)
      ? rawName
      : (account.username || t("Аккаунт"));
    const email = account.profileEmail || "";
    return email ? `${name} (${email})` : name;
  };

  const isLoginRedirectBanAccount = (account) => {
    const statusToken = String(account?.status || "").trim().toLowerCase();
    if (statusToken !== "invalid") return false;
    const details = `${String(account?.error || "")} ${String(account?.lastError || "")}`.toLowerCase();
    return /einloggen|логин|auth_required|redirected-to-login|не удалось подтвердить вход|cookies устарели|сессия аккаунта невалидна|сессия аккаунта истекла|заблокирован/.test(details);
  };

  const resolveAccountEmailForCard = (account) => {
    const profileEmail = String(account?.profileEmail || "").trim();
    if (profileEmail) return profileEmail;
    const username = String(account?.username || "").trim();
    return username.includes("@") ? username : "—";
  };

  // Имя и почта аккаунта — как на плитках раздела «Аккаунты»: имя профиля, а
  // если его на KL нет (заглушка, klein_<id>, цифры) — почта вместо имени.
  const describeAccount = (account) => {
    const rawName = String(account?.profileName || "").trim();
    const hasRealName = Boolean(rawName)
      && !/mein(e)? anzeigen|mein profil|meine anzeigen|profil und meine anzeigen/i.test(rawName)
      && !/^klein_\d+$/i.test(rawName)
      && !/^\d{4,}$/.test(rawName);
    const email = resolveAccountEmailForCard(account);
    const hasEmail = email && email !== "—";
    if (hasRealName) return { name: rawName, email: hasEmail ? email : "" };
    return { name: hasEmail ? email : t("Без имени"), email: "" };
  };

  const resolveAccountProfileUrl = (account) => {
    const profileUserId = String(account?.profileUserId || "").trim();
    if (/^\d{4,}$/.test(profileUserId)) {
      return `https://www.kleinanzeigen.de/s-bestandsliste.html?userId=${encodeURIComponent(profileUserId)}`;
    }
    const direct = String(account?.profileUrl || "").trim();
    if (!direct) return "";
    try {
      const parsed = new URL(direct, "https://www.kleinanzeigen.de/");
      const hostname = String(parsed.hostname || "").toLowerCase();
      if (!(hostname === "kleinanzeigen.de" || hostname.endsWith(".kleinanzeigen.de"))) {
        return "";
      }
      const urlUserId = String(parsed.searchParams.get("userId") || "").trim();
      if (!/^\d{4,}$/.test(urlUserId)) {
        return "";
      }
      return `https://www.kleinanzeigen.de/s-bestandsliste.html?userId=${encodeURIComponent(urlUserId)}`;
    } catch (error) {
      return "";
    }
  };

  const normalizeSatisfactionBadge = (value) => {
    const normalized = String(value || "").trim().toUpperCase();
    if (["TOP", "OK", "NAJA", "MIX"].includes(normalized)) return normalized;
    return "MIX";
  };

  // Returns true if the account was registered through our panel
  // less than 3 months (≈ 92 days) ago.
  // Used to show the "Selfreg" badge on fresh self-registered accounts.
  const SELFREG_THRESHOLD_DAYS = 92;
  const isSelfregAccount = (account) => {
    const raw = String(account?.registeredAt || "").trim();
    if (!raw) return false;
    // Accept "YYYY-MM-DD" or full ISO; reject anything else.
    const parsed = Date.parse(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return false;
    const ageMs = Date.now() - parsed;
    if (ageMs < 0) return false; // future date — ignore
    return ageMs < SELFREG_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
  };

  // Format a registration date as "DD.MM.YYYY" for display in the account card.
  const formatRegisteredAtForDisplay = (account) => {
    const raw = String(account?.registeredAt || "").trim();
    if (!raw) return "";
    const ms = Date.parse(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
    if (!Number.isFinite(ms) || ms <= 0) return "";
    try {
      return new Intl.DateTimeFormat(locale(), {
        day: "2-digit", month: "2-digit", year: "numeric",
        timeZone: "Europe/Berlin"
      }).format(new Date(ms));
    } catch (_) {
      return raw;
    }
  };
  const accountsAccent = A[300];

  const getSatisfactionBadgeStyle = (badge) => {
    const baseStyle = {
      padding: "4px 10px",
      borderRadius: "8px",
      fontSize: "11px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      lineHeight: 1,
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      border: "1px solid rgba(141,147,163,0.25)",
      background: "rgba(38,42,51,0.2)",
      color: "#c6c9d1"
    };

    // TOP — заливка акцентом, как у счётчика «новых» и активной вкладки:
    // высшая ступень читается сильнее прочих и остаётся в палитре темы
    // (в красной и графитовой тоже), а зелёный был из другой гаммы.
    if (badge === "TOP") {
      return {
        ...baseStyle,
        background: A[500],
        border: `1px solid ${aa(400, 0.55)}`,
        color: onAccent("#ffffff")
      };
    }
    if (badge === "OK") {
      return {
        ...baseStyle,
        background: aa(500, 0.12),
        border: `1px solid ${aa(400, 0.3)}`,
        color: A[300]
      };
    }
    if (badge === "MIX") {
      return {
        ...baseStyle,
        background: aa(500, 0.14),
        border: `1px solid ${aa(400, 0.32)}`,
        color: A[300]
      };
    }
    if (badge === "NAJA") {
      return {
        ...baseStyle,
        background: "rgba(245,158,11,0.12)",
        border: "1px solid rgba(245,158,11,0.3)",
        color: "#fbbf24"
      };
    }
    return baseStyle;
  };

  const getAccountConnectionBadgeStyle = (isOnline) => ({
    padding: "6px 12px",
    borderRadius: "9999px",
    fontSize: "12px",
    fontWeight: "700",
    color: isOnline ? "#bbf7d0" : "#8d93a3",
    border: `1px solid ${isOnline ? "rgba(74,222,128,0.5)" : "rgba(141,147,163,0.22)"}`,
    background: isOnline ? "rgba(22,163,74,0.2)" : "rgba(141,147,163,0.08)",
    display: "inline-flex",
    alignItems: "center",
    gap: "7px"
  });

  const cardStyle = {
    background: isDarkTheme
      ? `linear-gradient(145deg, rgba(23, 25, 33, 0.8) 0%, ${sa(0.95)} 100%)`
      : "linear-gradient(145deg, #ffffff 0%, #f7f8fa 100%)",
    borderRadius: "20px",
    border: `1px solid ${shellCardBorder}`,
    boxShadow: isDarkTheme
      ? "0 20px 50px rgba(0,0,0,0.4), 0 0 30px rgba(0,0,0,0.2)"
      : "0 10px 28px rgba(14,16,21,0.08), 0 0 18px rgba(14,16,21,0.03)",
    backdropFilter: "blur(10px)"
  };
  const textMuted = isDarkTheme ? "#8d93a3" : "#707684";
  const textTitle = isDarkTheme ? "#f7f8fa" : S.surface;

  // Унифицированные стили для плашек (badges)
  const getBadgeStyle = (status) => {
    const baseStyle = {
      padding: "5px 14px",
      borderRadius: "9999px",
      fontSize: "12px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      transition: "all 0.2s ease",
      cursor: "default"
    };

    switch (status) {
      case "active":
      case "aktiv":
      case "success":
        return {
          ...baseStyle,
          background: "linear-gradient(135deg, #10b981, #059669)",
          color: "white",
          border: "1px solid rgba(16, 185, 129, 0.3)",
          boxShadow: "0 0 12px rgba(16, 185, 129, 0.35)"
        };
      case "checking":
      case "pending":
      case "warning":
      case "reserviert":
        return {
          ...baseStyle,
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          color: "white",
          border: "1px solid rgba(245, 158, 11, 0.3)",
          boxShadow: "0 0 12px rgba(245, 158, 11, 0.35)"
        };
      case "failed":
      case "error":
      case "gelöscht":
        return {
          ...baseStyle,
          background: "linear-gradient(135deg, #ef4444, #dc2626)",
          color: "white",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          boxShadow: "0 0 12px rgba(239, 68, 68, 0.35)"
        };
      case "info":
        return {
          ...baseStyle,
          background: A[500],
          color: onAccent("white"),
          border: `1px solid ${aa(500, 0.3)}`,
          boxShadow: `0 0 12px ${aa(500, 0.35)}`
        };
      case "new":
        return {
          ...baseStyle,
          background: A[500],
          color: onAccent("white"),
          border: `1px solid ${aa(500, 0.3)}`,
          boxShadow: `0 0 12px ${aa(500, 0.4)}`,
          animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
        };
      default:
        return {
          ...baseStyle,
          background: "linear-gradient(135deg, #3f4450, #262a33)",
          color: "#e2e4e9",
          border: "1px solid rgba(141, 147, 163, 0.2)"
        };
    }
  };

  const currentOwnerId = String(tokenStatus?.ownerId || "").trim();
  const hasValidOwner = tokenStatus.state === "valid" && Boolean(currentOwnerId);
  const isAdminUser = hasValidOwner && String(tokenStatus?.role || "").trim().toLowerCase() === "admin";
  const profileSubscription = authIdentity?.subscription && typeof authIdentity.subscription === "object"
    ? authIdentity.subscription
    : null;
  const profileFreeze = profileSubscription?.freeze && typeof profileSubscription.freeze === "object"
    ? profileSubscription.freeze
    : {};
  const freezeMaxCount = Math.max(0, Number(profileFreeze?.maxCount || 0) || 0);
  const freezeMaxDays = Math.max(0, Number(profileFreeze?.maxDays || 0) || 0);
  const freezeUsedCount = Math.max(0, Number(profileFreeze?.usedCount || 0) || 0);
  const freezeUsedDays = Math.max(0, Number(profileFreeze?.usedDays || 0) || 0);
  const freezeRemainingCount = Math.max(0, Number(profileFreeze?.remainingCount || 0) || 0);
  const freezeRemainingDays = Math.max(0, Number(profileFreeze?.remainingDays || 0) || 0);
  const freezeActive = Boolean(profileFreeze?.active);
  const freezeAccessLocked = tokenStatus.state === "valid" && freezeActive;
  const freezeSupported = freezeMaxCount > 0 && freezeMaxDays > 0;
  const freezeCanStart = Boolean(profileSubscription?.active && profileFreeze?.canStart && freezeSupported);

  useEffect(() => {
    if (normalizePathname(location.pathname) === "/") {
      navigate(TAB_ROUTE_MAP.dashboard, { replace: true });
      return;
    }
    const tabId = resolveTabIdFromPathname(location.pathname);
    if (!tabId) {
      navigate(TAB_ROUTE_MAP.dashboard, { replace: true });
      return;
    }
    if (tabId !== activeTab) {
      setActiveTab(tabId);
    }
  }, [location.pathname, activeTab, navigate]);

  useEffect(() => {
    const pressSelector = ".kl-press-fwd, .kl-press-back, .kl-press-send, .kl-press-spin, .kl-press-pop, .kl-press-down";
    const timers = new Map();

    const playPressFeedback = (target) => {
      if (!(target instanceof Element)) return;
      const button = target.closest(pressSelector);
      if (!button || button.matches(":disabled, [aria-disabled='true']")) return;

      const previousTimer = timers.get(button);
      if (previousTimer) window.clearTimeout(previousTimer);

      button.classList.remove("kl-press-active");
      void button.offsetWidth;
      button.classList.add("kl-press-active");

      timers.set(button, window.setTimeout(() => {
        button.classList.remove("kl-press-active");
        timers.delete(button);
      }, 520));
    };

    const handlePointerDown = (event) => playPressFeedback(event.target);
    const handleKeyDown = (event) => {
      if (event.repeat || (event.key !== "Enter" && event.key !== " ")) return;
      playPressFeedback(event.target);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const NOTIFICATIONS_STORAGE_BASE = "kl_inapp_notifications_v1";
  const MESSAGE_FP_STORAGE_BASE = "kl_inapp_msg_fingerprint_v1";
  const PROXY_COUNTRY_STORAGE_BASE = "kl_inapp_proxy_country_v1";
  const SEEN_STORAGE_KEY = "kl_messages_seen_v1";
  const NOTIF_MAX_ITEMS = 200;
  const TOAST_MAX_ITEMS = 4;

  const buildOwnerStorageKey = (base, ownerId) => {
    const safeOwner = String(ownerId || "").trim();
    if (!safeOwner) return `${base}:none`;
    return `${base}:${safeOwner}`;
  };

  const parseOwnerIdCsv = (value) => String(value || "")
    .split(/[,\n]/g)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 5000);

  const formatSubscriptionValidMessage = (subscription) => (
    subscription?.expiresAt
      ? tf`Активен до ${new Date(subscription.expiresAt).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })}`
      : t("Активен")
  );

  const applySubscriptionSnapshot = ({ subscription = null, ownerId = "", role = "", canPurgeParserAds = null } = {}) => {
    const normalizedRole = String(role || tokenStatus?.role || "user").trim().toLowerCase() === "admin" ? "admin" : "user";
    const normalizedOwnerId = String(ownerId || currentOwnerId || authIdentity?.ownerId || "").trim();
    const normalizedSubscription = subscription && typeof subscription === "object" ? subscription : null;
    const hasAccess = Boolean(normalizedSubscription?.active || normalizedRole === "admin");

    setAuthIdentity((prev) => ({
      ...prev,
      ownerId: normalizedOwnerId || String(prev?.ownerId || "").trim(),
      subscription: normalizedSubscription
    }));
    setTokenStatus((prev) => ({
      ...prev,
      state: hasAccess ? "valid" : "subscription_required",
      message: hasAccess
        ? formatSubscriptionValidMessage(normalizedSubscription)
        : t("Подписка не активна. Оформите подписку в Telegram."),
      expiresAt: hasAccess ? (normalizedSubscription?.expiresAt || null) : null,
      ownerId: normalizedOwnerId || String(prev?.ownerId || "").trim(),
      role: normalizedRole,
      canPurgeParserAds: canPurgeParserAds === null
        ? Boolean(prev?.canPurgeParserAds || normalizedRole === "admin")
        : Boolean(canPurgeParserAds)
    }));
  };

  const buildNotificationStreamUrl = () => {
    const rawEnvBase = String(process.env.REACT_APP_API_BASE || "").trim();
    const primaryEnvBase = rawEnvBase
      .split(",")
      .map((item) => String(item || "").trim().replace(/\/+$/, ""))
      .filter(Boolean)[0] || "";
    if (!primaryEnvBase) {
      return "/api/notifications/stream";
    }
    const pathSuffix = primaryEnvBase.endsWith("/api")
      ? "/notifications/stream"
      : "/api/notifications/stream";
    return `${primaryEnvBase}${pathSuffix}`;
  };

  const loadStorageJson = (key, fallback) => {
    if (typeof window === "undefined" || !window.localStorage) return fallback;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed === undefined || parsed === null ? fallback : parsed;
    } catch {
      return fallback;
    }
  };

  const saveStorageJson = (key, value) => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value ?? null));
    } catch {
      // ignore quota / serialization issues
    }
  };

  const loadNotificationsFromStorage = (ownerId) => {
    const key = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, ownerId);
    const parsed = loadStorageJson(key, []);
    return Array.isArray(parsed) ? parsed : [];
  };

  const normalizeNotification = (item) => {
    if (!item || typeof item !== "object") return null;
    const id = String(item.id || "").trim();
    if (!id) return null;
    const type = String(item.type || "").trim().toLowerCase();
    if (!["message", "proxy", "broadcast"].includes(type)) return null;
    const createdAtRaw = item.createdAt;
    const createdAt = Number.isFinite(Number(createdAtRaw))
      ? Number(createdAtRaw)
      : Date.parse(String(createdAtRaw || "").trim());
    if (!Number.isFinite(createdAt) || createdAt <= 0) return null;
    const readAtRaw = item.readAt;
    const readAtParsed = Number.isFinite(Number(readAtRaw))
      ? Number(readAtRaw)
      : Date.parse(String(readAtRaw || "").trim());
    return {
      id,
      type,
      title: String(item.title || "").trim(),
      body: String(item.body || "").trim(),
      createdAt,
      readAt: Number.isFinite(readAtParsed) && readAtParsed > 0 ? readAtParsed : null,
      level: String(item.level || "").trim().toLowerCase() || "info",
      actionLabel: String(item.actionLabel || "").trim(),
      actionUrl: String(item.actionUrl || "").trim(),
      meta: item.meta && typeof item.meta === "object" ? item.meta : {},
      startsAt: String(item.startsAt || "").trim(),
      expiresAt: String(item.expiresAt || "").trim()
    };
  };

  const persistNotifications = (ownerId, next) => {
    const list = (Array.isArray(next) ? next : [])
      .map(normalizeNotification)
      .filter(Boolean)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      .slice(0, NOTIF_MAX_ITEMS);
    setNotifications(list);
    saveStorageJson(buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, ownerId), list);
  };

  const appendNotifications = (items) => {
    if (!hasValidOwner) return;
    const incoming = (Array.isArray(items) ? items : [])
      .map(normalizeNotification)
      .filter(Boolean);
    if (!incoming.length) return;
    const storageKey = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, currentOwnerId);
    setNotifications((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const merged = [...incoming, ...base]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, NOTIF_MAX_ITEMS);
      saveStorageJson(storageKey, merged);
      return merged;
    });
  };

  const mergeServerNotifications = (items, options = {}) => {
    if (!hasValidOwner) return;
    const incoming = (Array.isArray(items) ? items : [])
      .map((item) => normalizeNotification({
        ...item,
        type: item?.type || "broadcast",
        meta: {
          ...(item?.meta && typeof item.meta === "object" ? item.meta : {}),
          source: "server-broadcast"
        }
      }))
      .filter(Boolean);

    const showToasts = options.showToasts !== false;
    const knownIds = knownServerNotificationIdsRef.current instanceof Set
      ? knownServerNotificationIdsRef.current
      : new Set();
    const nextKnownIds = new Set();
    const newUnreadItems = [];

    const storageKey = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, currentOwnerId);
    setNotifications((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const localOnly = base.filter((item) => String(item?.meta?.source || "").trim() !== "server-broadcast");
      const seenBefore = new Set(
        base
          .filter((item) => String(item?.meta?.source || "").trim() === "server-broadcast")
          .map((item) => String(item?.id || "").trim())
          .filter(Boolean)
      );

      for (const item of incoming) {
        const id = String(item.id || "").trim();
        if (!id) continue;
        nextKnownIds.add(id);
        const wasSeen = knownIds.has(id) || seenBefore.has(id);
        if (!wasSeen && !item.readAt) {
          newUnreadItems.push(item);
        }
      }

      const merged = [...incoming, ...localOnly]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, NOTIF_MAX_ITEMS);
      saveStorageJson(storageKey, merged);
      return merged;
    });

    knownServerNotificationIdsRef.current = nextKnownIds;

    if (showToasts && newUnreadItems.length) {
      const first = newUnreadItems[0];
      const toastTitle = newUnreadItems.length === 1
        ? (first?.title || t("Новое уведомление"))
        : tf`Новых уведомлений: ${newUnreadItems.length}`;
      const toastBody = newUnreadItems.length === 1
        ? String(first?.body || "").trim()
        : (first?.body ? tf`Например:\n${first.body}` : "");
      const level = String(first?.level || "info").trim().toLowerCase();
      const variant = level === "error" ? "error" : (level === "warning" ? "warning" : (level === "success" ? "success" : "info"));
      pushToast({
        variant,
        title: toastTitle,
        body: toastBody,
        durationMs: variant === "error" ? 9000 : 7200
      });
    }
  };

  const loadServerNotificationsFeed = async (options = {}) => {
    if (!hasValidOwner) return [];
    const showToasts = options.showToasts !== false;
    const response = await apiFetchJson("/api/notifications/feed?limit=120&unreadOnly=1", {
      timeoutMs: 25000,
      retry: true,
      allowBaseFallback: true
    });
    const items = Array.isArray(response?.items) ? response.items : [];
    mergeServerNotifications(items, { showToasts });
    return items;
  };

  const makeId = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  const pushToast = (payload = {}, legacyVariant = "") => {
    const normalizedPayload = (typeof payload === "string")
      ? { body: payload, variant: legacyVariant || "info" }
      : (payload && typeof payload === "object" ? payload : {});
    const {
      variant = "info",
      title = "",
      body = "",
      durationMs
    } = normalizedPayload;
    const safeBody = String(body || "").trim() || (typeof payload === "string" ? String(payload || "").trim() : "");
    const toast = {
      id: makeId("t"),
      variant: String(variant || "info"),
      title: String(title || "").trim(),
      body: safeBody,
      createdAt: Date.now(),
      durationMs: Number.isFinite(Number(durationMs)) ? Math.max(2500, Number(durationMs)) : 7200
    };
    setToasts((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const next = [...list, toast];
      return next.slice(Math.max(0, next.length - TOAST_MAX_ITEMS));
    });
    return toast.id;
  };

  const dismissToast = (toastId) => {
    setToasts((prev) => (Array.isArray(prev) ? prev : []).filter((t) => t?.id !== toastId));
  };

  const pushToastFromAlertText = (rawValue) => {
    const text = String(rawValue ?? "").trim() || t("Уведомление");
    const lower = text.toLowerCase();
    let variant = "info";
    let title = t("Уведомление");

    if (
      lower.includes("ошибка")
      || lower.includes("не удалось")
      || lower.includes("failed")
      || lower.includes("таймаут")
      || lower.includes("недостаточно")
    ) {
      variant = "error";
      title = t("Ошибка");
    } else if (
      lower.includes("успеш")
      || lower.includes("валид")
      || lower.includes("сохранен")
      || lower.includes("проверен")
      || lower.includes("обновлен")
      || lower.includes("удален")
    ) {
      variant = "success";
      title = t("Готово");
    } else if (lower.includes("внимание") || lower.includes("предупреж")) {
      variant = "warning";
      title = t("Внимание");
    }

    pushToast({
      variant,
      title,
      body: text,
      durationMs: variant === "error" ? 8500 : 7200
    });
  };

  // Подтверждения — общим окном панели (utils/confirmDialog.js); по
  // умолчанию как удаление.
  const requestConfirmModal = (options = {}) => confirmAction({ variant: "danger", ...options });

  const normalizeCountryToken = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.toLowerCase() === "неизвестно") return "";
    return raw;
  };

  const isGermanyCountry = (value) => {
    const token = normalizeCountryToken(value);
    if (!token) return false;
    if (token.length === 2) return token.toUpperCase() === "DE";
    const lower = token.toLowerCase();
    return lower === "germany" || lower === "deutschland";
  };

  const getConversationKey = (message) => {
    if (!message || typeof message !== "object") return "";
    const accountId = message.accountId != null ? String(message.accountId) : "";
    const convo = message.conversationId || message.conversationUrl || message.id || "";
    const convoId = convo != null ? String(convo) : "";
    if (!accountId || !convoId) return "";
    return `${accountId}|${convoId}`;
  };

  // Сообщение, которое Kleinanzeigen отдал задним числом (lateMessageId), —
  // часть отпечатка: последнее сообщение у диалога то же, а новость есть.
  // Тот же хвост дописывают MessagesTab и сервер.
  const getConversationFingerprint = (message) => {
    if (!message || typeof message !== "object") return "";
    const date = String(message.date || "").trim();
    const time = String(message.time || "").trim();
    const text = String(message.message || "").trim();
    const lateMessageId = String(message.lateMessageId || "").trim();
    return `${date}|${time}|${text}${lateMessageId ? `|late:${lateMessageId}` : ""}`;
  };

  // Тот же отпечаток или он же с отметкой опоздавшего, которая потом снялась:
  // кто видел диалог с ним, видел и всё до него.
  const isSameConversationFingerprint = (stored, current) => {
    const value = String(stored || "");
    return Boolean(value) && Boolean(current) && (value === current || value.startsWith(`${current}|late:`));
  };

  const loadSeenMap = () => {
    const parsed = loadStorageJson(SEEN_STORAGE_KEY, {});
    return parsed && typeof parsed === "object" ? parsed : {};
  };

  const formatNotificationTime = (createdAt) => {
    const ms = Number(createdAt || 0);
    if (!Number.isFinite(ms) || ms <= 0) return "";
    const d = new Date(ms);
    const berlinOpts = { timeZone: "Europe/Berlin" };
    const nowParts = new Intl.DateTimeFormat("en-CA", { ...berlinOpts, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    const dParts = new Intl.DateTimeFormat("en-CA", { ...berlinOpts, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    if (nowParts === dParts) {
      return d.toLocaleTimeString(locale(), { ...berlinOpts, hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleString(locale(), berlinOpts);
  };

  const unreadNotificationsCount = (Array.isArray(notifications) ? notifications : []).filter((n) => !n?.readAt).length;
  const unreadNotificationsBadge = unreadNotificationsCount > 99 ? "99+" : String(unreadNotificationsCount);

  // The feed repeats the same alert over and over (a dead session re-reports
  // itself every few minutes), which buried everything else. Collapse identical
  // notifications into one row carrying a count and the latest timestamp.
  // Поиск по списку пользователей: без него, чтобы выдать часы или
  // заблокировать кого-то одного, приходилось листать весь список.
  // Ищем по имени и по ownerId — последний показан в карточке и его
  // удобно вставить из чужого сообщения целиком.
  const visibleAdminUsers = useMemo(() => {
    const list = Array.isArray(adminUsers) ? adminUsers : [];
    const query = adminUserQuery.trim().toLowerCase();
    const filtered = list.filter((user) => {
      // Telegram — с @ и без: ник часто копируют прямо из чата вместе с @.
      const tgHandle = String(user?.telegram?.username || "").toLowerCase();
      const haystack = [
        user?.username,
        user?.ownerId,
        tgHandle,
        tgHandle && `@${tgHandle}`,
        user?.telegram?.name
      ].map((part) => String(part || "").toLowerCase());
      if (query && !haystack.some((part) => part.includes(query))) {
        return false;
      }
      // «С подпиской» — именно действующая: запись может остаться и после
      // того, как срок вышел, и в карточке такой пользователь уже не зелёный.
      if (adminUserFilter === "withSub") return Boolean(user?.subscription?.active);
      if (adminUserFilter === "noSub") return !user?.subscription?.active;
      return true;
    });
    // Аккаунты старше самого поля createdAt уходят в конец при любом порядке:
    // датой они не сравнимы, а вперёд их пускать нельзя ни как новые, ни как
    // старые. Между собой они сохраняют исходный порядок — сортировка стабильна.
    const registeredAt = (user) => {
      const parsed = Date.parse(user?.createdAt || "");
      return Number.isFinite(parsed) ? parsed : null;
    };
    return filtered.sort((left, right) => {
      const a = registeredAt(left);
      const b = registeredAt(right);
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return adminUserSort === "old" ? a - b : b - a;
    });
  }, [adminUsers, adminUserQuery, adminUserFilter, adminUserSort]);

  const groupedNotifications = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    const groups = [];
    const byKey = new Map();
    list.forEach((n) => {
      if (!n) return;
      const key = [
        String(n.type || "info"),
        String(n.level || ""),
        String(n.title || ""),
        String(n.body || "")
      ].join("|");
      const existing = byKey.get(key);
      if (existing) {
        existing.count += 1;
        existing.ids.push(n.id);
        if (!n.readAt) existing.unread = true;
        return;
      }
      const group = { key, latest: n, count: 1, ids: [n.id], unread: !n.readAt };
      byKey.set(key, group);
      groups.push(group);
    });
    return groups;
  }, [notifications]);
  const unreadMessagesCountRaw = Number(stats?.messages?.unread || 0);
  const unreadMessagesCount = Number.isFinite(unreadMessagesCountRaw) && unreadMessagesCountRaw > 0
    ? Math.floor(unreadMessagesCountRaw)
    : 0;
  const hasTabUnreadOverride = Number.isFinite(Number(messagesTabUnreadCount));
  const effectiveUnreadMessagesCount = hasTabUnreadOverride
    ? Math.max(0, Math.floor(Number(messagesTabUnreadCount)))
    : unreadMessagesCount;
  const unreadMessagesBadge = effectiveUnreadMessagesCount > 99 ? "99+" : String(effectiveUnreadMessagesCount);

  // То же число — на иконке панели (экран «Домой», Dock). Без входа и в демо
  // значок убирается: чужие или выдуманные непрочитанные на нём ни к чему.
  useEffect(() => {
    const signedIn = tokenStatus.state === "valid" && !isDemoActive();
    setAppBadgeCount(signedIn ? effectiveUnreadMessagesCount : 0);
  }, [effectiveUnreadMessagesCount, tokenStatus.state]);

  useEffect(() => listenForNotificationClicks((url) => {
    const target = parseNotificationTarget(url);
    if (target) setNotificationTarget({ ...target, at: Date.now() });
    else navigate(TAB_ROUTE_MAP.messages);
  }), [navigate]);

  // Диалог из уведомления открывается тем же путём, что с дашборда: раздел
  // «Сообщения» найдёт его в списке и откроет, как нажатием.
  useEffect(() => {
    if (!notificationTarget || tokenStatus.state !== "valid" || isDemoActive()) return;
    const { accountId, conversationId } = notificationTarget;
    setNotificationTarget(null);
    setPendingConversationOpen({ item: { id: conversationId, accountId, conversationId }, at: Date.now() });
    handleTabSelect("messages");
  }, [notificationTarget, tokenStatus.state]);

  useEffect(() => {
    if (activeTab !== "settings" || tokenStatus.state !== "valid") return undefined;
    let cancelled = false;
    readPushState()
      .then((state) => { if (!cancelled) setPushState(state); })
      .catch(() => { if (!cancelled) setPushState("unsupported"); });
    return () => { cancelled = true; };
  }, [activeTab, tokenStatus.state]);

  // Переключатель push. Включение вызывается прямо из нажатия: разрешение
  // браузер даёт только в ответ на него.
  const togglePush = () => {
    if (pushBusy || isDemoActive()) return;
    if (pushState === "on") {
      setPushBusy(true);
      disablePush()
        .catch(() => {})
        .finally(() => {
          setPushState("off");
          setPushBusy(false);
        });
      return;
    }
    if (pushState !== "off") return;
    setPushBusy(true);
    enablePush()
      .then((result) => {
        setPushState(result.state);
        if (result.state === "denied") {
          pushToast({ variant: "warning", title: t("Уведомления запрещены"), body: t("Разрешите их для панели в настройках браузера или телефона") });
        } else if (result.state === "on" && !result.testSent) {
          pushToast({ variant: "warning", title: t("Уведомления включены, но проверочное не дошло"), body: t("Выключите и включите их снова") });
        }
      })
      .catch((error) => {
        pushToast({ variant: "error", title: t("Не удалось включить уведомления"), body: error?.message || "" });
      })
      .finally(() => setPushBusy(false));
  };

  const markNotificationRead = (notificationId) => {
    if (!hasValidOwner) return;
    const id = String(notificationId || "").trim();
    if (!id) return;
    const currentList = Array.isArray(notifications) ? notifications : [];
    const target = currentList.find((item) => String(item?.id || "").trim() === id) || null;
    const storageKey = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, currentOwnerId);
    setNotifications((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const now = Date.now();
      const next = base.map((item) => {
        if (!item || item.id !== id) return item;
        if (item.readAt) return item;
        return { ...item, readAt: now };
      });
      saveStorageJson(storageKey, next);
      return next;
    });
    if (target?.type === "broadcast") {
      apiFetchJson(`/api/notifications/${encodeURIComponent(id)}/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      }).catch((error) => {
        handleAuthError(error);
      });
    }
  };

  const markAllNotificationsRead = () => {
    if (!hasValidOwner) return;
    const currentList = Array.isArray(notifications) ? notifications : [];
    const broadcastIds = currentList
      .filter((item) => item && item.type === "broadcast" && !item.readAt)
      .map((item) => String(item.id || "").trim())
      .filter(Boolean);
    const storageKey = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, currentOwnerId);
    setNotifications((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const now = Date.now();
      const next = base.map((item) => {
        if (!item || item.readAt) return item;
        return { ...item, readAt: now };
      });
      saveStorageJson(storageKey, next);
      return next;
    });
    if (broadcastIds.length) {
      apiFetchJson("/api/notifications/read-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: broadcastIds })
      }).catch((error) => {
        handleAuthError(error);
      });
    }
  };

  const clearNotifications = () => {
    if (!hasValidOwner) return;
    const currentList = Array.isArray(notifications) ? notifications : [];
    const broadcastIds = currentList
      .filter((item) => item && item.type === "broadcast")
      .map((item) => String(item.id || "").trim())
      .filter(Boolean);
    const storageKey = buildOwnerStorageKey(NOTIFICATIONS_STORAGE_BASE, currentOwnerId);
    setNotifications([]);
    saveStorageJson(storageKey, []);
    knownServerNotificationIdsRef.current = new Set();
    if (broadcastIds.length) {
      apiFetchJson("/api/notifications/read-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: broadcastIds })
      }).catch((error) => {
        handleAuthError(error);
      });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!hasValidOwner) {
      setTelegramBotStatus({ ...DEFAULT_TELEGRAM_BOT_STATUS });
      return;
    }
    loadTelegramSettings();
  }, [hasValidOwner, currentOwnerId]);

  useEffect(() => {
    if (!hasValidOwner) return undefined;
    let cancelled = false;
    const run = async () => {
      if (accountsPollInFlight.current) return;
      accountsPollInFlight.current = true;
      try {
        const requestedAt = Date.now();
        const nextAccounts = await apiFetchJson("/api/accounts", { timeoutMs: 45000 });
        if (!cancelled) {
          applyFetchedAccounts(nextAccounts, requestedAt);
        }
      } catch (error) {
        if (!cancelled) {
          handleAuthError(error);
        }
      } finally {
        accountsPollInFlight.current = false;
      }
    };

    run();
    const intervalId = window.setInterval(run, GLOBAL_ACCOUNTS_AUTO_REFRESH_MS);
    // Mobile browsers throttle (or fully suspend) interval timers while the tab
    // is backgrounded or the screen is off, so a phone left on the accounts
    // screen keeps showing stale cards — e.g. a rating / Gew. badge that already
    // appeared on desktop. Re-sync the list the instant the tab becomes visible
    // or regains focus so it matches the server immediately instead of waiting
    // for the (possibly throttled) 5-minute interval.
    const syncOnForeground = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", syncOnForeground);
    window.addEventListener("focus", syncOnForeground);
    // Раздел, узнавший о смене статуса аккаунта раньше опроса (сообщения —
    // о бане при открытии диалога), просит перечитать список сразу.
    window.addEventListener("kl:accounts-stale", run);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", syncOnForeground);
      window.removeEventListener("focus", syncOnForeground);
      window.removeEventListener("kl:accounts-stale", run);
    };
  }, [hasValidOwner, currentOwnerId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const nativeAlert = window.alert;
    window.alert = (value) => {
      pushToastFromAlertText(value);
    };
    return () => {
      window.alert = nativeAlert;
    };
  }, []);

  // Уведомление с готовым заголовком и типом — для разделов, которые знают,
  // что именно произошло (ошибки отправки в сообщениях), а не угадывать это
  // по словам, как для alert.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onToast = (event) => {
      const detail = event?.detail || {};
      event.preventDefault();
      pushToast({
        variant: detail.variant || "info",
        title: detail.title || "",
        body: detail.body || "",
        durationMs: detail.variant === "error" ? 8500 : 7200
      });
    };
    window.addEventListener("kl:toast", onToast);
    return () => window.removeEventListener("kl:toast", onToast);
  }, []);

  useEffect(() => {
    if (!hasValidOwner) {
      if (notificationsStreamRef.current) {
        try {
          notificationsStreamRef.current.close();
        } catch {}
        notificationsStreamRef.current = null;
      }
      setNotifications([]);
      setShowNotificationsPanel(false);
      messageNotifBootRef.current = false;
      proxyNotifBootRef.current = false;
      accountBanNotifBootRef.current = false;
      accountBanStateRef.current = new Map();
      knownServerNotificationIdsRef.current = new Set();
      lastNotifOwnerRef.current = "";
      setAdminBroadcasts([]);
      return;
    }

    if (lastNotifOwnerRef.current !== currentOwnerId) {
      messageNotifBootRef.current = false;
      proxyNotifBootRef.current = false;
      accountBanNotifBootRef.current = false;
      accountBanStateRef.current = new Map();
      knownServerNotificationIdsRef.current = new Set();
      lastNotifOwnerRef.current = currentOwnerId;
    }

    setShowNotificationsPanel(false);
    const stored = loadNotificationsFromStorage(currentOwnerId)
      .map(normalizeNotification)
      .filter(Boolean)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      .slice(0, NOTIF_MAX_ITEMS);
    setNotifications(stored);
  }, [tokenStatus.state, currentOwnerId]);

  useEffect(() => {
    if (!hasValidOwner) return undefined;

    let disposed = false;
    let reconnectTimer = null;

    const cleanupStream = () => {
      if (notificationsStreamRef.current) {
        try {
          notificationsStreamRef.current.close();
        } catch {}
        notificationsStreamRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return;
      const jitterMs = Math.floor(Math.random() * 1200);
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connectStream();
      }, 2500 + jitterMs);
    };

    const connectStream = () => {
      if (disposed) return;
      cleanupStream();
      const streamUrl = buildNotificationStreamUrl();
      if (!streamUrl) return;
      try {
        const source = new EventSource(streamUrl, { withCredentials: true });
        notificationsStreamRef.current = source;

        source.addEventListener("notification", (event) => {
          if (disposed) return;
          let payload = null;
          try {
            payload = JSON.parse(String(event?.data || "{}"));
          } catch {
            payload = null;
          }
          if (!payload || typeof payload !== "object") return;
          mergeServerNotifications([payload], { showToasts: true });
        });

        source.addEventListener("error", () => {
          cleanupStream();
          scheduleReconnect();
        });
      } catch {
        scheduleReconnect();
      }
    };

    // Seed unread server broadcasts and then keep polling as fallback.
    loadServerNotificationsFeed({ showToasts: false }).catch((error) => {
      handleAuthError(error);
    });

    const pollTimer = window.setInterval(() => {
      loadServerNotificationsFeed({ showToasts: true }).catch((error) => {
        handleAuthError(error);
      });
    }, 25000);

    // Поток идёт мимо apiFetch, прямо к серверу, — в демо его не открываем:
    // сервер без подписки ответит отказом, и панель переподключалась бы по
    // кругу. Ленту уведомлений в демо отдаёт песочница опросом выше.
    if (!demoActive) connectStream();

    return () => {
      disposed = true;
      cleanupStream();
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      window.clearInterval(pollTimer);
    };
  }, [hasValidOwner, currentOwnerId, demoActive]);

  useEffect(() => {
    if (!hasValidOwner) return undefined;

    let cancelled = false;
    const storageKey = buildOwnerStorageKey(PROXY_COUNTRY_STORAGE_BASE, currentOwnerId);

    const normalizeCountryForStorage = (value) => {
      const token = normalizeCountryToken(value);
      if (!token) return "";
      if (isGermanyCountry(token)) return "DE";
      return token;
    };

    const run = async () => {
      if (proxyPollInFlight.current) return;
      proxyPollInFlight.current = true;
      try {
        const list = await apiFetchJson("/api/proxies");
        if (cancelled) return;
        if (!Array.isArray(list)) return;

        setProxies(orderProxiesBuiltinFirst(list));

        const prev = loadStorageJson(storageKey, {});
        const prevMap = prev && typeof prev === "object" ? prev : {};
        const nextMap = { ...prevMap };
        let mapDirty = false;
        const newProxyAlerts = [];

        for (const proxy of list) {
          const proxyId = proxy?.id != null ? String(proxy.id).trim() : "";
          if (!proxyId) continue;
          const checkOk = Boolean(proxy?.checkResult?.success);
          if (!checkOk) continue;
          const countryToken = normalizeCountryForStorage(proxy?.checkResult?.location?.country || "");
          if (!countryToken) continue;

          const previousToken = normalizeCountryForStorage(nextMap[proxyId] || "");
          const prevWasDe = isGermanyCountry(previousToken);
          const currIsDe = isGermanyCountry(countryToken);

          if (previousToken !== countryToken) {
            nextMap[proxyId] = countryToken;
            mapDirty = true;
          }

          if (prevWasDe && !currIsDe) {
            const proxyName = String(proxy?.name || "").trim() || `proxy#${proxyId}`;
            const ip = String(proxy?.checkResult?.ip || "").trim();
            const bodyLines = [
              `${proxyName}${ip ? ` • ${ip}` : ""}`,
              tf`Страна: ${countryToken}`
            ];
            newProxyAlerts.push(normalizeNotification({
              id: makeId("n"),
              type: "proxy",
              title: t("Прокси больше не DE"),
              body: bodyLines.join("\n"),
              createdAt: Date.now(),
              readAt: null,
              meta: {
                tab: "proxies",
                proxyId,
                proxyName,
                country: countryToken,
                ip
              }
            }));
          }
        }

        if (mapDirty) {
          saveStorageJson(storageKey, nextMap);
        }

        const freshAlerts = newProxyAlerts.filter(Boolean);
        if (freshAlerts.length) {
          appendNotifications(freshAlerts);
          const first = freshAlerts[0];
          const toastTitle = freshAlerts.length === 1
            ? t("Прокси больше не DE")
            : tf`Прокси больше не DE: ${freshAlerts.length}`;
          const toastBody = freshAlerts.length === 1
            ? (first?.body || "")
            : (first?.body ? tf`Например:\n${first.body}` : "");
          pushToast({ variant: "warning", title: toastTitle, body: toastBody, durationMs: 8000 });
        }

        if (!proxyNotifBootRef.current) {
          proxyNotifBootRef.current = true;
        }
      } catch (error) {
        handleAuthError(error);
      } finally {
        proxyPollInFlight.current = false;
      }
    };

    run();
    const intervalId = window.setInterval(run, GLOBAL_PROXIES_AUTO_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [tokenStatus.state, currentOwnerId]);

  useEffect(() => {
    if (!hasValidOwner) return;
    const nextState = new Map();
    const list = Array.isArray(accounts) ? accounts : [];

    for (const account of list) {
      const accountId = String(account?.id || "").trim();
      if (!accountId) continue;
      // Время проверки в отметку не входит: оно меняется при каждом
      // обновлении, и одна и та же потеря сессии считалась новой снова и снова.
      const marker = [
        String(account?.status || "").trim().toLowerCase(),
        String(account?.error || "").trim()
      ].join("|");
      nextState.set(accountId, marker);
    }

    if (!accountBanNotifBootRef.current) {
      accountBanStateRef.current = nextState;
      accountBanNotifBootRef.current = true;
      return;
    }

    for (const account of list) {
      const accountId = String(account?.id || "").trim();
      if (!accountId) continue;
      if (!isLoginRedirectBanAccount(account)) continue;
      const marker = nextState.get(accountId) || "";
      const prevMarker = accountBanStateRef.current.get(accountId) || "";
      if (marker === prevMarker) continue;
      const accountLabel = formatAccountLabel(account);
      const reason = t(String(account?.error || "Аккаунт требует повторного входа.").trim());
      // Всплывающего предупреждения нет: к этому моменту человек уже видел
      // красную ошибку того действия, на котором сессия и обнаружилась, —
      // второе окно говорило то же самое другими словами. Запись остаётся
      // в уведомлениях, а у аккаунта — статус.
      appendNotifications([
        normalizeNotification({
          id: makeId("n"),
          type: "message",
          title: t("Аккаунт отключен"),
          body: `${accountLabel}\n${reason}`,
          createdAt: Date.now(),
          readAt: null,
          meta: {
            tab: "accounts",
            accountId
          }
        })
      ]);
    }

    accountBanStateRef.current = nextState;
  }, [accounts, hasValidOwner, currentOwnerId]);

  useEffect(() => {
    if (activeTab !== "accounts") return;
    loadAccountMetrics();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "settings" || !isAdminUser) return;
    loadAdminBroadcasts();
    loadAdminUsers();
    loadUserProxies();
    if (accountStatsOpen) loadAccountStats();
    if (messagesTransportOpen) loadMessagesTransport();
    loadAdminPromos();
    loadPanelProxy();
    loadParserProxies();
  }, [activeTab, isAdminUser]);

  useEffect(() => {
    if (activeTab !== "settings" || tokenStatus.state !== "valid") return;
    refreshPanelSubscriptionStatus().catch(() => {});
  }, [activeTab, tokenStatus.state, currentOwnerId]);

  useEffect(() => {
    if (tokenStatus.state !== "valid") return;
    clearAuthHashFromUrl();
  }, [tokenStatus.state]);

  useEffect(() => {
    if (activeTab === "messages") return undefined;
    const run = () => {
      refreshMessageStats().catch(() => {});
    };
    run();
    const cancelPoll = scheduleJittered(run, GLOBAL_MESSAGES_AUTO_REFRESH_MS);
    return () => cancelPoll();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "messages") return;
    if (!isMobileMessagesChatExpanded) return;
    setIsMobileMessagesChatExpanded(false);
  }, [activeTab, isMobileMessagesChatExpanded]);

  useEffect(() => {
    if (activeTab === "messages") return;
    if (!isMobileMessagesThreadOpen) return;
    setIsMobileMessagesThreadOpen(false);
  }, [activeTab, isMobileMessagesThreadOpen]);

  useEffect(() => {
    if (!isPhoneView) return;
    if (!isMobileMessagesThreadOpen) return;
    if (!isMobileNavOpen) return;
    setIsMobileNavOpen(false);
  }, [isPhoneView, isMobileMessagesThreadOpen, isMobileNavOpen]);

  useEffect(() => {
    if (!isPhoneView) return;
    if (!publishProgressModal.open && !(activeTab === "messages" && isMobileMessagesChatExpanded)) return;
    if (showProfilePanel) setShowProfilePanel(false);
    if (showNotificationsPanel) setShowNotificationsPanel(false);
  }, [
    isPhoneView,
    publishProgressModal.open,
    activeTab,
    isMobileMessagesChatExpanded,
    showProfilePanel,
    showNotificationsPanel
  ]);

  useEffect(() => {
    if (!freezeAccessLocked) return;
    if (activeTab !== "settings") {
      navigate(TAB_ROUTE_MAP.settings, { replace: true });
    }
    if (showAdModal) {
      setShowAdModal(false);
    }
  }, [freezeAccessLocked, activeTab, showAdModal, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsPhoneView(detectPhoneView());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isPhoneView && isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
  }, [isPhoneView, isMobileNavOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const body = document.body;
    if (!root || !body) return;

    const shouldLockPhoneViewport = isPhoneView && tokenStatus.state === "valid";
    if (shouldLockPhoneViewport) {
      root.classList.add("phone-view-lock");
      body.classList.add("phone-view-lock");
    } else {
      root.classList.remove("phone-view-lock");
      body.classList.remove("phone-view-lock");
    }

    return () => {
      root.classList.remove("phone-view-lock");
      body.classList.remove("phone-view-lock");
    };
  }, [isPhoneView, tokenStatus.state]);

  useEffect(() => {
    if (!isPhoneView || tokenStatus.state !== "valid" || typeof document === "undefined") return;

    let startX = 0;
    let startY = 0;

    const hasHorizontalScrollParent = (node) => {
      let current = node;
      while (current && current !== document.body) {
        if (!(current instanceof HTMLElement)) {
          current = current.parentElement;
          continue;
        }
        const style = window.getComputedStyle(current);
        const overflowX = style.overflowX;
        const canScrollX = (overflowX === "auto" || overflowX === "scroll") && current.scrollWidth > current.clientWidth + 1;
        if (canScrollX) return true;
        current = current.parentElement;
      }
      return false;
    };

    const onTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    };

    const onTouchMove = (event) => {
      if (!event.touches || event.touches.length !== 1) return;

      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (Math.abs(dx) <= Math.abs(dy) + 4) return;

      const target = event.target instanceof Element ? event.target : null;
      if (
        target?.closest(
          "button, a, input, textarea, select, [role='button'], .app-header, .profile-panel, [data-account-menu], [data-account-menu-button]"
        )
      ) {
        return;
      }
      if (target && hasHorizontalScrollParent(target)) return;

      event.preventDefault();
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [isPhoneView, tokenStatus.state]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let ok = await restoreSessionAuth();
      if (cancelled) return;
      // Гость перезагрузил страницу посреди демо — демо продолжается.
      if (!ok && wasGuestDemoRequested()) {
        await startDemoSession({ guest: true, keepRoute: true });
        if (cancelled) return;
        ok = true;
      }
      // Открыта кнопкой бота: впускаем по данным запуска Telegram, без пароля.
      if (!ok && isTelegramMiniApp() && !wasMiniAppLoggedOut()) {
        const result = await loginFromTelegramMiniApp();
        if (cancelled) return;
        ok = result.ok;
        if (!ok && !result.notLinked) {
          setAuthError(result.message);
          setAuthPortalEntry({ screen: "auth", tab: "login" });
        }
      }
      // Открыта старой кнопкой клавиатуры бота — данных запуска нет, и войти
      // по Telegram нельзя. Бот пришлёт кнопку, с которой можно, а мини-апп
      // закроется (utils/telegramMiniApp.js).
      if (!ok && isKeyboardLaunchedMiniApp()) {
        await requestPanelButtonFromBot();
        if (cancelled) return;
      }
      if (!ok) {
        setTokenStatus({ state: "missing", message: "", expiresAt: null, ownerId: "", role: "user" });
        setAuthIdentity({ username: "", ownerId: "", subscription: null });
        setFreezeActionError("");
      }
      setAuthBootstrapDone(true);
    };
    run().catch(() => {
      if (cancelled) return;
      setAuthBootstrapDone(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event?.target;
      if (!(target instanceof Element)) return;

      const profileToggleContains = (profilePanelRef.current && profilePanelRef.current.contains(target)) || (extraProfileRef.current && extraProfileRef.current.contains(target));
      const profilePopupContains = profilePanelPopupRef.current && profilePanelPopupRef.current.contains(target);
      if (!profileToggleContains && !profilePopupContains) {
        setShowProfilePanel(false);
      }
      const notificationsToggleContains = (notificationsPanelRef.current && notificationsPanelRef.current.contains(target)) || (extraNotifRef.current && extraNotifRef.current.contains(target));
      const notificationsPopupContains = notificationsPanelPopupRef.current && notificationsPanelPopupRef.current.contains(target);
      if (!notificationsToggleContains && !notificationsPopupContains) {
        setShowNotificationsPanel(false);
      }
      if (!target.closest("[data-account-menu]") && !target.closest("[data-account-menu-button]")) {
        setOpenAccountMenuId(null);
      }
    };
    // Всплывающие панели и меню. Окна (подтверждение, выбор прокси и др.)
    // закрываются через стек Escape — useEscapeLayer выше; preventDefault
    // говорит ему, что этот Escape уже потрачен на панель.
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      if (showProfilePanel || showNotificationsPanel || openAccountMenuId !== null) {
        event.preventDefault();
      }
      setShowProfilePanel(false);
      setShowNotificationsPanel(false);
      setOpenAccountMenuId(null);
    };
    // The account menu is portaled with position:fixed, so it can't follow a
    // scrolling row — close it on any scroll (capture:true catches inner
    // scroll containers like the shell body).
    const handleScrollClose = () => setOpenAccountMenuId(null);
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleScrollClose, true);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [showProfilePanel, showNotificationsPanel, openAccountMenuId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const requestId = String(activeCreateRequestIdRef.current || "").trim();
      if (!requestId) return;
      const activeStep = String(publishProgressModal.step || "").trim().toLowerCase();
      const finished = Boolean(
        publishProgressModal.error
        || activeStep === "done"
        || activeStep === "cancelled"
        || Number(publishProgressModal.percent || 0) >= 100
      );
      if (finished) return;

      const activeAbortController = activeCreateRequestAbortRef.current;
      if (activeAbortController && !activeAbortController.signal.aborted) {
        try {
          activeAbortController.abort();
        } catch (error) {
          // ignore abort failures
        }
      }

      const headers = {
        "Content-Type": "application/json",
        "X-Client-Request-Id": `cancel-${requestId}-${Date.now().toString(36)}`
      };
      try {
        if (isDemoActive()) return;
        fetch("/api/ads/create-cancel", {
          method: "POST",
          headers,
          body: JSON.stringify({ requestId }),
          keepalive: true
        });
      } catch (error) {
        // ignore unload network errors
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    publishProgressModal.step,
    publishProgressModal.error,
    publishProgressModal.percent
  ]);

  const resetScopedData = () => {
    setAccounts([]);
    setProxies([]);
    setAccountMetrics({});
    try { localStorage.removeItem("kl_account_metrics"); } catch {}
    setMessagesTabUnreadCount(null);
    setDashboardConversations([]);
    setPendingConversationOpen(null);
    setStats({
      accounts: { total: 0, online: 0, offline: 0 },
      proxies: { total: 0, active: 0, failed: 0 },
      messages: { total: 0, today: 0, unread: 0 }
    });
  };

  // Сервер отказал «подписка на заморозке» — её заморозили, например, с
  // другого устройства. Статус перечитывается (не чаще раза в полминуты), и
  // панель закрывает разделы, как при заморозке отсюда.
  const frozenStatusCheckAtRef = useRef(0);
  const handleAuthError = (error) => {
    if (error?.status === 423 && String(error?.data?.code || "") === "SUBSCRIPTION_FROZEN") {
      if (Date.now() - frozenStatusCheckAtRef.current > 30 * 1000) {
        frozenStatusCheckAtRef.current = Date.now();
        refreshPanelSubscriptionStatus().catch(() => {});
      }
      return;
    }
    // В демо сервер не спрашивается, и отказ 401/402 может прийти только от
    // запроса, ушедшего до входа в демо, — например, от первой загрузки при
    // перезагрузке страницы, пока сессия ещё проверяется. Выкидывать из-за
    // него из песочницы на экран подписки нельзя: куда идти, решит проверка.
    if (isDemoActive() || wasDemoRequested()) return;
    if (error?.status === 401) {
      setAuthError("");
      setTokenStatus({ state: "missing", message: "", expiresAt: null, ownerId: "", role: "user" });
      setAuthIdentity({ username: "", ownerId: "", subscription: null });
      setFreezeActionError("");
      resetScopedData();
      return;
    }
    if (error?.status === 402 && String(error?.data?.code || "").trim() === "SUBSCRIPTION_REQUIRED") {
      const ownerId = String(error?.data?.subscription?.ownerId || tokenStatus?.ownerId || "").trim();
      setTokenStatus({
        state: "subscription_required",
        message: String(error?.message || t("Подписка не активна")),
        expiresAt: null,
        ownerId,
        role: "user"
      });
      setAuthError("");
      setFreezeActionError("");
      resetScopedData();
    }
  };

  const parseMessageDateTime = (dateValue, timeValue) => {
    const dateText = String(dateValue || "").trim();
    const timeText = String(timeValue || "").trim();
    if (!dateText && !timeText) return null;
    const isTime = /^\d{1,2}:\d{2}$/.test(timeText);

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateText)) {
      const parsed = new Date(`${dateText}T${isTime ? timeText : "00:00"}:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const textual = dateText.toLowerCase();
    if (dateText && (textual === "today" || textual === "heute" || textual === "сегодня")) {
      const now = new Date();
      const [hoursRaw, minutesRaw] = isTime ? timeText.split(":") : ["0", "0"];
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      const parsed = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        Number.isFinite(hours) ? hours : 0,
        Number.isFinite(minutes) ? minutes : 0,
        0,
        0
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (dateText && (textual === "yesterday" || textual === "gestern" || textual === "вчера")) {
      const now = new Date();
      const [hoursRaw, minutesRaw] = isTime ? timeText.split(":") : ["0", "0"];
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      const parsed = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        Number.isFinite(hours) ? hours : 0,
        Number.isFinite(minutes) ? minutes : 0,
        0,
        0
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (!dateText && isTime) {
      const now = new Date();
      const [hoursRaw, minutesRaw] = timeText.split(":");
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
      const parsed = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
      if (parsed.getTime() > (now.getTime() + 2 * 60 * 1000)) {
        parsed.setDate(parsed.getDate() - 1);
      }
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const dotDateMatch = dateText.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
    if (dotDateMatch) {
      const day = Number(dotDateMatch[1]);
      const month = Number(dotDateMatch[2]) - 1;
      const year = Number(dotDateMatch[3].length === 2 ? `20${dotDateMatch[3]}` : dotDateMatch[3]);
      const [hoursRaw, minutesRaw] = isTime ? timeText.split(":") : ["0", "0"];
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      const parsed = new Date(
        year,
        month,
        day,
        Number.isFinite(hours) ? hours : 0,
        Number.isFinite(minutes) ? minutes : 0,
        0,
        0
      );
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    const fallback = new Date(`${dateText}${timeText ? ` ${timeText}` : ""}`);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const dedupeMessageSummaries = (messages = []) => {
    const map = new Map();
    const pickBetter = (current, candidate) => {
      if (!current) return candidate;
      if (!candidate) return current;
      const currentDate = parseMessageDateTime(current?.date, current?.time);
      const candidateDate = parseMessageDateTime(candidate?.date, candidate?.time);
      const currentTs = currentDate ? currentDate.getTime() : 0;
      const candidateTs = candidateDate ? candidateDate.getTime() : 0;
      if (candidateTs !== currentTs) return candidateTs > currentTs ? candidate : current;
      const currentUnread = Boolean(current?.unread);
      const candidateUnread = Boolean(candidate?.unread);
      if (candidateUnread !== currentUnread) {
        return candidateUnread ? current : candidate;
      }
      const currentMessageLength = String(current?.message || "").trim().length;
      const candidateMessageLength = String(candidate?.message || "").trim().length;
      if (candidateMessageLength !== currentMessageLength) {
        return candidateMessageLength > currentMessageLength ? candidate : current;
      }
      return current;
    };

    messages.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const accountKey = item.accountId != null ? String(item.accountId) : "";
      const convoKey = String(item.conversationId || item.conversationUrl || item.id || "").trim();
      const key = convoKey
        ? `${accountKey}|${convoKey}`
        : `${accountKey}|${item.sender || ""}|${item.adTitle || ""}|${item.message || ""}|${item.time || ""}`;
      const prev = map.get(key);
      map.set(key, pickBetter(prev, item));
    });
    return Array.from(map.values()).filter(Boolean);
  };

  const isOutgoingMessageSummary = (item) => {
    if (!item || typeof item !== "object") return false;
    if (item?.isOutgoing === true) return true;
    const direction = String(item?.lastMessageDirection || item?.direction || "").trim().toLowerCase();
    if (direction === "outgoing" || direction === "sent") return true;
    const sender = String(item?.sender || "").trim().toLowerCase();
    if (sender === "вы" || sender === "you" || sender === "me") return true;
    const text = String(item?.message || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (/^(вы|you|du|ich|me)\s*[:\-]/i.test(text)) return true;
    return false;
  };

  const buildMessagesStats = (messages = []) => {
    const uniqueMessages = dedupeMessageSummaries(messages);
    const now = new Date();
    let unread = 0;
    let today = 0;

    uniqueMessages.forEach((message) => {
      // Наш ответ последним не делает диалог новым — кроме случая, когда после
      // него пришло опоздавшее входящее (lateMessageId).
      if (message?.unread && (!isOutgoingMessageSummary(message) || message.lateMessageId)) unread += 1;
      const parsedDate = parseMessageDateTime(message?.date, message?.time);
      if (!parsedDate) return;
      if (
        parsedDate.getFullYear() === now.getFullYear()
        && parsedDate.getMonth() === now.getMonth()
        && parsedDate.getDate() === now.getDate()
      ) {
        today += 1;
      }
    });

    return {
      total: uniqueMessages.length,
      today,
      unread
    };
  };

  const refreshMessageStats = async () => {
    if (messageStatsPollInFlight.current) return;
    messageStatsPollInFlight.current = true;
    try {
      const messageParams = new URLSearchParams();
      messageParams.set("full", "1");
      const messagesRes = await apiFetchJson(`/api/messages?${messageParams.toString()}`, { timeoutMs: 120000 });
      if (!Array.isArray(messagesRes)) return;

      const uniqueMessages = dedupeMessageSummaries(messagesRes);
      const seenMapForUnread = hasValidOwner ? loadSeenMap() : {};
      const normalizedMessages = uniqueMessages.map((item) => {
        if (!item || typeof item !== "object") return item;
        // Наш ответ последним — прочитано, если только после него не пришло
        // опоздавшее входящее (lateMessageId).
        if (isOutgoingMessageSummary(item) && !item.lateMessageId) {
          if (!item.unread) return item;
          return { ...item, unread: false };
        }
        const convoKey = getConversationKey(item);
        if (!convoKey) return item;
        const fingerprint = getConversationFingerprint(item);
        if (!fingerprint) return item;
        const seenEntry = seenMapForUnread && typeof seenMapForUnread === "object"
          ? seenMapForUnread[convoKey]
          : null;
        const seenFingerprint = seenEntry && typeof seenEntry === "object"
          ? String(seenEntry.fingerprint || "")
          : "";
        if (isSameConversationFingerprint(seenFingerprint, fingerprint) && item.unread) {
          return { ...item, unread: false };
        }
        return item;
      });

      if (hasValidOwner) {
        const fpStorageKey = buildOwnerStorageKey(MESSAGE_FP_STORAGE_BASE, currentOwnerId);
        const prev = loadStorageJson(fpStorageKey, {});
        const prevMap = prev && typeof prev === "object" ? prev : {};
        const nextMap = { ...prevMap };
        let mapDirty = false;

        const seenMap = seenMapForUnread;
        const newMessageAlerts = [];

        for (const item of normalizedMessages) {
          if (!item || typeof item !== "object") continue;
          const convoKey = getConversationKey(item);
          if (!convoKey) continue;
          const fingerprint = getConversationFingerprint(item);
          if (!fingerprint) continue;

          const previousFingerprint = String(nextMap[convoKey] || "");
          if (isSameConversationFingerprint(previousFingerprint, fingerprint)) continue;
          nextMap[convoKey] = fingerprint;
          mapDirty = true;

          // Новость — сообщение, которое Kleinanzeigen отдал задним числом: для
          // человека это обычное новое сообщение, и уведомление — его текстом.
          // Последним в диалоге при этом может стоять наш ответ.
          const lateMessageId = String(item.lateMessageId || "").trim();
          const lateText = String(item.lateMessageText || "").replace(/\s+/g, " ").trim();
          const lateNews = Boolean(lateMessageId && lateText)
            && !previousFingerprint.endsWith(`|late:${lateMessageId}`);
          const senderRaw = lateNews
            ? String(item.participantName || "").trim() || (isOutgoingMessageSummary(item) ? "" : String(item.sender || "").trim())
            : String(item.sender || "").trim();
          const textRaw = lateNews ? lateText : String(item.message || "").replace(/\s+/g, " ").trim();
          if (!textRaw) continue;
          if (!lateNews && isOutgoingMessageSummary(item)) continue;
          if (!item.unread) continue;
          // Suppress panel notifications for conversations the user archived
          // (backend flags them from the synced conversation-state).
          if (item.archived) continue;

          const seenEntry = seenMap && typeof seenMap === "object" ? seenMap[convoKey] : null;
          const seenFingerprint = seenEntry && typeof seenEntry === "object" ? String(seenEntry.fingerprint || "") : "";
          if (isSameConversationFingerprint(seenFingerprint, fingerprint)) continue;

          const accountName = String(item.accountName || "").trim()
            || (item.accountId != null ? tf`Аккаунт ${item.accountId}` : t("Аккаунт"));
          const adTitle = String(item.adTitle || "").trim();
          const snippet = textRaw.length > 240 ? `${textRaw.slice(0, 237)}...` : textRaw;
          const head = [accountName, senderRaw, adTitle].filter(Boolean).join(" • ");
          const body = head ? `${head}\n${snippet}` : snippet;

          newMessageAlerts.push(normalizeNotification({
            id: makeId("n"),
            type: "message",
            title: t("Новое сообщение"),
            body,
            createdAt: Date.now(),
            readAt: null,
            meta: {
              tab: "messages",
              accountId: item.accountId,
              conversationKey: convoKey,
              sender: senderRaw,
              adTitle
            }
          }));
        }

        if (mapDirty) {
          saveStorageJson(fpStorageKey, nextMap);
        }

        const freshAlerts = newMessageAlerts.filter(Boolean);
        if (freshAlerts.length) {
          appendNotifications(freshAlerts);
          if (messageNotifBootRef.current) {
            playNotificationSound();
            if (activeTab !== "messages") {
              const first = freshAlerts[0];
              const toastTitle = freshAlerts.length === 1
                ? t("Новое сообщение")
                : tf`Новых сообщений: ${freshAlerts.length}`;
              const toastBody = freshAlerts.length === 1
                ? (first?.body || "")
                : (first?.body ? tf`Например:\n${first.body}` : "");
              pushToast({ variant: "info", title: toastTitle, body: toastBody });
            }
          }
        }

        if (!messageNotifBootRef.current) {
          messageNotifBootRef.current = true;
        }
      }

      const realMessageStats = buildMessagesStats(normalizedMessages);
      setDashboardConversations(normalizedMessages.filter((item) => item && typeof item === "object").slice(0, 60));
      setStats((prev) => ({
        ...(prev || {}),
        messages: realMessageStats
      }));
      // Keep the nav unread badge fresh while off the Messages tab (the tab
      // component isn't mounted to report it), so the counter is always shown.
      setMessagesTabUnreadCount(realMessageStats.unread);
    } catch (error) {
      handleAuthError(error);
      console.error("Ошибка автообновления диалогов:", error);
    } finally {
      messageStatsPollInFlight.current = false;
    }
  };

  const restoreSessionAuth = async () => {
    try {
      const response = await apiFetchJson("/api/auth/me", {
        method: "GET",
        skipAuth: true,
        timeoutMs: 20000,
        retry: false,
        allowBaseFallback: true
      });
      if (!response?.authenticated) {
        return false;
      }
      const ownerId = String(response.ownerId || "").trim();
      const role = String(response.role || "").trim().toLowerCase() === "admin" ? "admin" : "user";
      const canPurgeParserAds = Boolean(response.canPurgeParserAds);
      const subscription = response?.subscription && typeof response.subscription === "object"
        ? response.subscription
        : null;
      const subscriptionActive = Boolean(subscription?.active);
      setAuthIdentity({
        username: String(response.username || "").trim(),
        ownerId,
        avatarUrl: String(response.avatarUrl || "").trim(),
        hasPassword: response.hasPassword !== false,
        subscription
      });
      if (subscriptionActive || role === "admin") {
        // Подписка есть — демо больше не нужно, даже если вкладка его помнит.
        if (wasDemoRequested()) leaveDemoMode();
        applySubscriptionSnapshot({ subscription, ownerId, role, canPurgeParserAds });
        clearAuthHashFromUrl();
        setAuthError("");
        setFreezeActionError("");
        await loadData();
        return true;
      }
      setAuthError("");
      setFreezeActionError("");
      // Перезагрузка страницы посреди демо возвращает в демо, а не на экран
      // подписки: флаг живёт в sessionStorage, пока открыта вкладка. Экран
      // подписки при этом не мелькает — до готовности демо висит загрузка.
      if (wasDemoRequested()) {
        await startDemoSession({ ownerId, keepRoute: true });
        return true;
      }
      applySubscriptionSnapshot({ subscription, ownerId, role, canPurgeParserAds });
      resetScopedData();
      return true;
    } catch (error) {
      const status = Number(error?.status || 0);
      if (status === 401) {
        return false;
      }
      setTokenStatus({
        state: "invalid",
        message: error?.message || t("Ошибка проверки сессии"),
        expiresAt: null,
        ownerId: "",
        role: "user"
      });
      setAuthIdentity({ username: "", ownerId: "", subscription: null });
      setFreezeActionError("");
      resetScopedData();
      return false;
    }
  };

  const refreshAuthAvatar = async () => {
    try {
      const response = await apiFetchJson("/api/auth/me", { timeoutMs: 15000, retry: false });
      const avatarUrl = String(response?.avatarUrl || "").trim();
      if (avatarUrl) setAuthIdentity((prev) => ({ ...prev, avatarUrl }));
    } catch {
      // Не страшно: аватар покажется при следующем заходе.
    }
  };

  // Ответ входа или регистрации (логином и паролем или через Telegram-бота):
  // сессия уже в cookie, осталось применить подписку или открыть демо.
  const completeAuthResponse = async (response, { fallbackUsername = "", openDemo = false } = {}) => {
    if (response?.requiresSubscription || response?.subscription?.active === false) {
      const ownerId = String(response?.ownerId || "").trim();
      setAuthIdentity({
        username: String(response?.username || fallbackUsername || "").trim(),
        ownerId,
        avatarUrl: String(response?.avatarUrl || "").trim(),
        hasPassword: response?.hasPassword !== false,
        subscription: response?.subscription || null
      });
      // Пришёл с кнопки «Открыть демо» на главной — сразу в демо, минуя
      // экран подписки. Форма входа до готовности демо показывает «Подождите».
      if (openDemo) {
        setAuthError("");
        setFreezeActionError("");
        await startDemoSession({ ownerId, tabId: "dashboard" });
        return { ok: true, requiresSubscription: true, demo: true };
      }
      applySubscriptionSnapshot({ subscription: response?.subscription || null, ownerId, role: "user" });
      setAuthError("");
      setFreezeActionError("");
      resetScopedData();
      return { ok: true, requiresSubscription: true, message: response?.message || t("Подписка не активна") };
    }
    const restored = await restoreSessionAuth();
    if (restored) {
      return { ok: true };
    }
    return { ok: false, message: t("Не удалось подтвердить авторизацию по сессии") };
  };

  const completeTelegramLogin = async (response, { openDemo = false } = {}) => {
    try {
      const result = await completeAuthResponse(response, { openDemo });
      // Фото из Telegram сервер подтягивает в фоне уже после входа — забираем
      // его чуть позже, чтобы аватар появился без перезагрузки.
      if (result?.ok && !response?.avatarUrl) {
        window.setTimeout(() => { refreshAuthAvatar(); }, 4000);
      }
      return result;
    } catch (error) {
      return { ok: false, message: error?.message || t("Ошибка входа") };
    }
  };

  const loginWithCredentials = async ({ username = "", password = "", captchaToken = "", openDemo = false } = {}) => {
    try {
      const payload = {
        username: String(username || "").trim(),
        password: String(password || ""),
        captchaToken: String(captchaToken || "")
      };
      const response = await apiFetchJson("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        skipAuth: true,
        timeoutMs: 30000,
        retry: false,
        allowBaseFallback: true
      });
      return await completeAuthResponse(response, { fallbackUsername: payload.username, openDemo });
    } catch (error) {
      return { ok: false, message: error?.message || t("Ошибка входа"), code: String(error?.data?.code || "") };
    }
  };

  const registerWithCredentials = async ({ username = "", password = "", referralCode = "", captchaToken = "", openDemo = false } = {}) => {
    try {
      const payload = {
        username: String(username || "").trim(),
        password: String(password || ""),
        referralCode: String(referralCode || "").trim(),
        captchaToken: String(captchaToken || "")
      };
      const response = await apiFetchJson("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        skipAuth: true,
        timeoutMs: 30000,
        retry: false,
        allowBaseFallback: true
      });
      return await completeAuthResponse(response, { fallbackUsername: payload.username, openDemo });
    } catch (error) {
      return { ok: false, message: error?.message || t("Ошибка регистрации"), code: String(error?.data?.code || "") };
    }
  };

  // Вход из мини-аппа Telegram (см. utils/telegramMiniApp.js). register —
  // завести аккаунт по Telegram, если его нет (кнопки «Открыть демо» и «Войти
  // через Telegram»); openDemo — сразу в демо. notLinked — аккаунта нет и
  // заводить его не просили: тогда панель показывает главную.
  const loginFromTelegramMiniApp = async ({ register = false, openDemo = false } = {}) => {
    try {
      const response = await apiFetchJson("/api/auth/telegram/webapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ initData: getTelegramInitData(), register }),
        skipAuth: true,
        timeoutMs: 20000,
        retry: false
      });
      if (response?.sessionId) setMiniAppSession(response.sessionId);
      const result = await completeAuthResponse(response, { fallbackUsername: response?.username || "", openDemo });
      return { ok: Boolean(result?.ok), message: result?.ok ? "" : String(result?.message || "") };
    } catch (error) {
      return {
        ok: false,
        notLinked: error?.data?.code === "TELEGRAM_NOT_LINKED",
        message: error?.message || t("Ошибка входа через Telegram")
      };
    }
  };

  const logoutSession = async () => {
    // Выход из аккаунта заканчивает и демо: песочница принадлежит вкладке, а не
    // человеку, и следующему, кто войдёт, её показывать незачем.
    leaveDemoMode();
    demoRealOwnerRef.current = "";
    // Push на это устройство — выключить, пока вход ещё действует: после
    // выхода сюда шли бы сообщения аккаунта, из которого вышли.
    await disablePush().catch(() => {});
    try {
      await apiFetchJson("/api/auth/logout", {
        method: "POST",
        skipAuth: true,
        timeoutMs: 15000,
        retry: false,
        allowBaseFallback: true
      });
    } catch {
      // ignore logout network errors
    } finally {
      if (isTelegramMiniApp()) markMiniAppLoggedOut();
      setAuthPortalEntry({ screen: "landing", tab: "login" });
      setAuthIdentity({ username: "", ownerId: "", subscription: null });
      setTokenStatus({ state: "missing", message: "", expiresAt: null, ownerId: "", role: "user" });
      setAuthError("");
      setFreezeActionError("");
      resetScopedData();
    }
  };

  // Выход из настроек и с боковой панели — с подтверждением: кнопка стоит
  // среди других, и случайное нажатие выкидывало бы на экран входа.
  const requestLogout = async () => {
    const confirmed = await confirmAction({
      title: t("Выйти из аккаунта?"),
      message: t("Сессия в этом браузере завершится. Аккаунты Kleinanzeigen, объявления и подписка останутся — чтобы вернуться, войдите снова."),
      confirmText: t("Выйти"),
      variant: "accent",
      icon: "logout"
    });
    if (confirmed) await logoutSession();
  };

  // Ключ подписки активирован в настройках: дни уже добавлены сервером, ответ
  // несёт новое состояние подписки.
  const handleKeyRedeemed = (response) => {
    if (response?.subscription) {
      applySubscriptionSnapshot({
        subscription: response.subscription,
        ownerId: String(response.ownerId || "").trim(),
        role: tokenStatus?.role || "user"
      });
    }
    setRedeemFormOpen(false);
    const until = response?.subscription?.expiresAt
      ? new Date(response.subscription.expiresAt).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })
      : "";
    pushToast({
      variant: "success",
      title: t("Ключ активирован"),
      body: until
        ? tf`Добавлено дней: ${response?.days || 0}. Подписка действует до ${until}.`
        : tf`Добавлено дней: ${response?.days || 0}.`
    });
  };

  // Вход в демо. Панель переключается на песочницу целиком: подменяется
  // владелец (всё, что панель хранит в браузере по нему, у демо своё), право на
  // парсер выдаётся, данные грузятся уже из песочницы.
  //
  // Панель открывается, когда аккаунты уже загружены: разделы при появлении
  // отбирают свои данные по списку аккаунтов, и открытые раньше него
  // показали бы пустоту. Из песочницы это доли секунды.
  // guest — демо с главной, без аккаунта: настоящего владельца нет, и
  // сервер, кроме входа и регистрации, не спрашивается вовсе.
  const startDemoSession = async ({ ownerId = "", tabId = "", keepRoute = false, guest = false } = {}) => {
    demoRealOwnerRef.current = guest ? "" : String(ownerId || authIdentity?.ownerId || tokenStatus?.ownerId || "").trim();
    enterDemoMode({ guest });
    resetScopedData();
    if (!keepRoute) {
      navigate(TAB_ROUTE_MAP[tabId] || TAB_ROUTE_MAP.dashboard, { replace: true });
    }
    await loadData();
    setAuthIdentity((prev) => (guest
      ? { username: DEMO_USERNAME, ownerId: DEMO_OWNER_ID, avatarUrl: "", hasPassword: true, subscription: null }
      : { ...prev, ownerId: DEMO_OWNER_ID, subscription: null }));
    setTokenStatus((prev) => ({
      ...prev,
      state: "valid",
      message: t("Демо-режим"),
      expiresAt: null,
      ownerId: DEMO_OWNER_ID,
      role: "user",
      canPurgeParserAds: false
    }));
    // Счётчики по аккаунтам панель обычно помнит с прошлого захода в
    // «Аккаунты»; у демо прошлого нет, поэтому они считаются сразу.
    loadAccountMetrics({ reloadAccounts: false });
  };

  // Выход из демо — обратно на экран подписки. Сервер переспрашивается сразу:
  // если подписку оплатили, пока человек смотрел демо, панель откроется уже
  // настоящая.
  // У гостя аккаунта нет — он уходит на главную, а за подпиской (toRegister) —
  // на регистрацию: подписка оформляется на аккаунт.
  const exitDemoSession = (options = {}) => {
    if (isDemoGuest()) {
      demoRealOwnerRef.current = "";
      leaveDemoMode();
      resetScopedData();
      setShowAdModal(false);
      setShowAddAccountModal(false);
      setPaymentModal((prev) => ({ ...prev, open: false }));
      setAuthIdentity({ username: "", ownerId: "", subscription: null });
      setAuthError("");
      setAuthPortalEntry(options?.toRegister === true
        ? { screen: "auth", tab: "register" }
        : { screen: "landing", tab: "login" });
      setTokenStatus({ state: "missing", message: "", expiresAt: null, ownerId: "", role: "user" });
      navigate(TAB_ROUTE_MAP.dashboard, { replace: true });
      return;
    }
    const realOwnerId = demoRealOwnerRef.current;
    demoRealOwnerRef.current = "";
    leaveDemoMode();
    resetScopedData();
    setShowAdModal(false);
    setShowAddAccountModal(false);
    setAuthIdentity((prev) => ({ ...prev, ownerId: realOwnerId, subscription: null }));
    setTokenStatus((prev) => ({
      ...prev,
      state: "subscription_required",
      message: "",
      expiresAt: null,
      ownerId: realOwnerId,
      role: "user",
      canPurgeParserAds: false
    }));
    navigate(TAB_ROUTE_MAP.dashboard, { replace: true });
    restoreSessionAuth().catch(() => {});
  };

  // «Уже оплатили? Проверить статус» на экране подписки. Сначала спрашиваем
  // сам статус: панель перезагружается, только если подписка правда есть.
  const openPaymentModal = (planId = "") => {
    if (isDemoGuest()) {
      exitDemoSession({ toRegister: true });
      return;
    }
    setPaymentModal({ open: true, planId: String(planId || "") });
  };
  const closePaymentModal = () => setPaymentModal((prev) => ({ ...prev, open: false }));
  // Оплата прошла: подписка на сервере уже включена — панель перечитывает
  // сессию и открывается (из демо — выходит из песочницы в настоящую).
  const handleSubscriptionPaid = () => {
    recheckSubscription().catch(() => {});
  };
  const renderPaymentModal = () => (
    <PaymentModal
      open={paymentModal.open}
      initialPlanId={paymentModal.planId}
      onClose={closePaymentModal}
      onPaid={handleSubscriptionPaid}
      isPhoneView={isPhoneView}
    />
  );

  const recheckSubscription = async () => {
    const response = await apiFetchJson("/api/auth/me", {
      method: "GET",
      skipAuth: true,
      timeoutMs: 20000,
      retry: false,
      allowBaseFallback: true
    });
    const active = Boolean(response?.subscription?.active)
      || String(response?.role || "").trim().toLowerCase() === "admin";
    if (active) await restoreSessionAuth();
    return { active };
  };

  const changePassword = async () => {
    if (changePasswordLoading) return;
    const hasPassword = authIdentity?.hasPassword !== false;
    const currentPassword = hasPassword ? String(changePasswordForm.currentPassword || "") : "";
    const newPassword = String(changePasswordForm.newPassword || "");
    const confirmPassword = String(changePasswordForm.confirmPassword || "");

    if ((hasPassword && !currentPassword) || !newPassword || !confirmPassword) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Заполните все поля для смены пароля") });
      return;
    }
    if (newPassword !== confirmPassword) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Подтверждение пароля не совпадает") });
      return;
    }
    if (newPassword.length < 6) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Пароль должен быть не короче 6 символов") });
      return;
    }

    setChangePasswordLoading(true);
    try {
      const response = await apiFetchJson("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword
        }),
        timeoutMs: 30000,
        retry: false
      });
      setChangePasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setPasswordFormOpen(false);
      setAuthIdentity((prev) => ({ ...prev, hasPassword: true }));
      pushToast({
        variant: "success",
        title: hasPassword ? t("Пароль обновлён") : t("Пароль задан"),
        body: Number(response?.revokedSessions || 0) > 0
          ? tf`Пароль изменён. Сессии на других устройствах завершены: ${Number(response.revokedSessions)}`
          : t("Пароль изменён успешно.")
      });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: String(error?.message || t("Не удалось сменить пароль")) });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const changeUsername = async () => {
    if (usernameSaving) return;
    const hasPassword = authIdentity?.hasPassword !== false;
    const username = String(usernameForm.username || "").trim();
    const currentPassword = hasPassword ? String(usernameForm.currentPassword || "") : "";
    if (!username) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Введите новый логин") });
      return;
    }
    if (hasPassword && !currentPassword) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Введите текущий пароль") });
      return;
    }
    setUsernameSaving(true);
    try {
      const response = await apiFetchJson("/api/auth/change-username", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, currentPassword }),
        timeoutMs: 30000,
        retry: false
      });
      const nextUsername = String(response?.username || username).trim();
      setAuthIdentity((prev) => ({ ...prev, username: nextUsername }));
      setUsernameForm({ username: "", currentPassword: "" });
      setUsernameFormOpen(false);
      pushToast({ variant: "success", title: t("Логин изменён"), body: tf`Теперь для входа используйте логин ${nextUsername}` });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: t(String(error?.message || "Не удалось сменить логин")) });
    } finally {
      setUsernameSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file || avatarBusy) return;
    if (!/^image\//.test(String(file.type || ""))) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Выберите изображение: JPG, PNG или WebP") });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Файл слишком большой. Максимум 8 МБ.") });
      return;
    }
    setAvatarBusy(true);
    try {
      const form = new FormData();
      form.append("avatar", file);
      const response = await apiFetchJson("/api/auth/avatar", {
        method: "POST",
        body: form,
        timeoutMs: 60000,
        retry: false
      });
      setAuthIdentity((prev) => ({ ...prev, avatarUrl: String(response?.avatarUrl || "").trim() }));
      pushToast({ variant: "success", title: t("Фото обновлено") });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: t(String(error?.message || "Не удалось загрузить фото")) });
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    if (avatarBusy) return;
    setAvatarBusy(true);
    try {
      await apiFetchJson("/api/auth/avatar", { method: "DELETE", timeoutMs: 30000, retry: false });
      setAuthIdentity((prev) => ({ ...prev, avatarUrl: "" }));
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: t(String(error?.message || "Не удалось удалить фото")) });
    } finally {
      setAvatarBusy(false);
    }
  };

  const loadData = async () => {
    try {
      const requestedAt = Date.now();
      const [accountsRes, proxiesRes, statsRes] = await Promise.all([
        apiFetchJson("/api/accounts"),
        apiFetchJson("/api/proxies"),
        apiFetchJson("/api/stats")
      ]);

      applyFetchedAccounts(accountsRes, requestedAt);
      setProxies(orderProxiesBuiltinFirst(proxiesRes));

      setStats({
        ...statsRes,
        messages: statsRes?.messages || { total: 0, today: 0, unread: 0 }
      });

      // Load message stats in the background so a slow proxy/cookies check doesn't block the whole UI.
      refreshMessageStats().catch(() => {});
    } catch (error) {
      handleAuthError(error);
      if (error?.status === 401) {
        resetScopedData();
      }
      console.error("Ошибка загрузки данных:", error);
    }
  };

  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const notifAudioRef = useRef(null);
  const getNotifAudio = () => {
    if (!notifAudioRef.current) {
      const blob = new Blob([buildMessageChimeWav()], { type: "audio/wav" });
      notifAudioRef.current = new Audio(URL.createObjectURL(blob));
      notifAudioRef.current.volume = 1;
    }
    return notifAudioRef.current;
  };

  const playNotificationSound = () => {
    try {
      if (!soundEnabledRef.current) return;
      // Только на компьютере, как и написано в настройке: раньше подпись была,
      // а проверки не было, и звук играл и на телефоне.
      if (!isDesktopDevice()) return;
      const audio = getNotifAudio();
      audio.currentTime = 0;
      const p = audio.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {
      console.warn("Sound notification error:", e);
    }
  };

  const loadTelegramSettings = async () => {
    try {
      const res = await apiFetchJson("/api/settings");
      setTelegramSettings({
        telegramNotifyNewMessages: res.telegramNotifyNewMessages !== false,
        telegramNotifyAccountOffline: res.telegramNotifyAccountOffline !== false,
        telegramNotifyProxyConnectionLost: res.telegramNotifyProxyConnectionLost !== false,
      });
      setAiPrefs({
        ...Object.fromEntries(AI_PREF_KEYS.map((key) => [key, res?.[key] !== false])),
        aiQuickActionsLang: res?.aiQuickActionsLang
      });
      setAiPaused(res?.aiPaused && typeof res.aiPaused === "object" ? res.aiPaused : {});
      setMessagesRefreshMinutes(normalizeMessagesRefreshMinutes(res?.messagesRefreshMinutes));
      setTelegramBotStatus(normalizeTelegramBotStatus(res?.telegramBot || {}));
    } catch {
      setTelegramBotStatus({ ...DEFAULT_TELEGRAM_BOT_STATUS });
    }
  };

  const saveTelegramSettings = async (newSettings) => {
    setTelegramSettings(newSettings);
    try {
      const response = await apiFetchJson("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newSettings)
      });
      setTelegramBotStatus(normalizeTelegramBotStatus(response?.telegramBot || {}));
    } catch {}
  };

  // Переключатели нейросети: значение применяется сразу, сохраняется на сервере
  // вместе с остальными настройками; если сохранить не удалось — возвращается.
  const aiPrefs = useAiPrefs();
  // Функции, приостановленные на сервере: переключатель показан выключенным
  // и не даётся. Сейчас на паузе ничего нет.
  const [aiPaused, setAiPaused] = useState({});
  const saveAiPref = async (key, enabled) => {
    if (aiPaused[key]) return;
    const previous = aiPrefs[key];
    setAiPrefs({ [key]: enabled });
    try {
      const response = await apiFetchJson("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: enabled })
      });
      // Сервер — источник истины: приостановленная функция вернётся выключенной.
      setAiPrefs(Object.fromEntries(AI_PREF_KEYS.map((prefKey) => [prefKey, response?.[prefKey] !== false])));
    } catch (error) {
      setAiPrefs({ [key]: previous });
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось сохранить настройку") });
    }
  };
  // Язык быстрых действий ассистента — так же: сразу, а при ошибке обратно.
  const saveAiQuickActionsLang = async (lang) => {
    const previous = aiPrefs.aiQuickActionsLang;
    if (previous === lang) return;
    setAiPrefs({ aiQuickActionsLang: lang });
    try {
      const response = await apiFetchJson("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aiQuickActionsLang: lang })
      });
      setAiPrefs({ aiQuickActionsLang: response?.aiQuickActionsLang });
    } catch (error) {
      setAiPrefs({ aiQuickActionsLang: previous });
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось сохранить настройку") });
    }
  };

  // Автообновление сообщений — так же: сразу, а при ошибке обратно.
  const saveMessagesRefreshMinutes = async (minutes) => {
    const previous = messagesRefreshMinutes;
    if (previous === minutes) return;
    setMessagesRefreshMinutes(minutes);
    try {
      const response = await apiFetchJson("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messagesRefreshMinutes: minutes })
      });
      setMessagesRefreshMinutes(normalizeMessagesRefreshMinutes(response?.messagesRefreshMinutes));
    } catch (error) {
      setMessagesRefreshMinutes(previous);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось сохранить настройку") });
    }
  };

  const refreshPanelSubscriptionStatus = async () => {
    try {
      const response = await apiFetchJson("/api/subscription/status", {
        method: "GET",
        timeoutMs: 20000,
        retry: false
      });
      const nextSubscription = response?.subscription && typeof response.subscription === "object"
        ? response.subscription
        : null;
      applySubscriptionSnapshot({
        subscription: nextSubscription,
        ownerId: String(response?.ownerId || "").trim(),
        role: tokenStatus?.role || "user"
      });
      setFreezeActionError("");
      return { ok: true, subscription: nextSubscription };
    } catch (error) {
      handleAuthError(error);
      const message = String(error?.message || t("Не удалось обновить статус подписки"));
      setFreezeActionError(message);
      return { ok: false, error: message };
    }
  };

  const startOwnSubscriptionFreeze = async () => {
    if (freezeActionLoading) return;
    setFreezeActionLoading(true);
    setFreezeActionError("");
    try {
      const parsedDays = Number(String(freezeDaysInput || "").trim().replace(",", "."));
      const payload = Number.isFinite(parsedDays) && parsedDays > 0
        ? { days: Math.max(1, Math.round(parsedDays)) }
        : {};

      const response = await apiFetchJson("/api/subscription/freeze/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        timeoutMs: 30000,
        retry: false
      });
      const nextSubscription = response?.subscription && typeof response.subscription === "object"
        ? response.subscription
        : null;
      applySubscriptionSnapshot({
        subscription: nextSubscription,
        ownerId: String(response?.ownerId || currentOwnerId || "").trim(),
        role: tokenStatus?.role || "user"
      });
      setFreezeDaysInput("");
      pushToast({
        variant: "success",
        title: t("Заморозка включена"),
        body: Number(response?.freeze?.appliedDays || 0) > 0
          ? tf`Срок заморозки: ${Number(response.freeze.appliedDays).toFixed(2)} дн.`
          : t("Подписка успешно заморожена.")
      });
    } catch (error) {
      handleAuthError(error);
      setFreezeActionError(String(error?.message || t("Не удалось включить заморозку")));
    } finally {
      setFreezeActionLoading(false);
    }
  };

  const stopOwnSubscriptionFreeze = async () => {
    if (freezeActionLoading) return;
    setFreezeActionLoading(true);
    setFreezeActionError("");
    try {
      const response = await apiFetchJson("/api/subscription/freeze/stop", {
        method: "POST",
        timeoutMs: 30000,
        retry: false
      });
      const nextSubscription = response?.subscription && typeof response.subscription === "object"
        ? response.subscription
        : null;
      applySubscriptionSnapshot({
        subscription: nextSubscription,
        ownerId: String(response?.ownerId || currentOwnerId || "").trim(),
        role: tokenStatus?.role || "user"
      });
      pushToast({
        variant: "success",
        title: t("Заморозка отключена"),
        body: t("Подписка снова активна.")
      });
    } catch (error) {
      handleAuthError(error);
      setFreezeActionError(String(error?.message || t("Не удалось отключить заморозку")));
    } finally {
      setFreezeActionLoading(false);
    }
  };

  const loadUserProxies = async () => {
    if (!isAdminUser) return;
    setLoadingUserProxies(true);
    try {
      const response = await apiFetchJson("/api/admin/user-proxies", { timeoutMs: 30000, retry: true });
      setUserProxies(Array.isArray(response?.proxies) ? response.proxies : []);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoadingUserProxies(false);
    }
  };

  const loadAdminUsers = async () => {
    if (!isAdminUser) return;
    setLoadingAdminUsers(true);
    try {
      const response = await apiFetchJson("/api/admin/users", { timeoutMs: 30000, retry: true });
      setAdminUsers(Array.isArray(response?.users) ? response.users : []);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  // Grants or revokes one user's right to clear an ad out of everyone's parser
  // feed. Admins are not offered the switch: the right follows the role, and a
  // toggle that changes nothing would only invite the conclusion that it did.
  const [savingAllProxiesRightFor, setSavingAllProxiesRightFor] = useState("");
  const toggleAllProxiesRight = async (user) => {
    if (!isAdminUser || savingAllProxiesRightFor) return;
    const allowed = !user.canUseAllProxies;
    setSavingAllProxiesRightFor(user.userId);
    try {
      await apiFetchJson(`/api/admin/users/${encodeURIComponent(user.userId)}/all-proxies`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allowed }),
        timeoutMs: 20000
      });
      setAdminUsers((prev) => prev.map((entry) => (
        entry.userId === user.userId ? { ...entry, canUseAllProxies: allowed } : entry
      )));
      pushToast({
        variant: "success",
        title: allowed ? t("Право выдано") : t("Право снято"),
        body: tf`${user.username}: доступ ко всем прокси ${allowed ? t("разрешён") : t("закрыт")}.`
      });
    } catch (error) {
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось изменить право") });
    } finally {
      setSavingAllProxiesRightFor("");
    }
  };

  // Безлимит на генерацию фото. Обычным пользователям — четыре картинки в
  // сутки; право снимает предел, потому что каждая картинка стоит денег.
  const [savingPhotoRightFor, setSavingPhotoRightFor] = useState("");
  const togglePhotoUnlimitedRight = async (user) => {
    if (!isAdminUser || savingPhotoRightFor) return;
    const allowed = !user.photoGenUnlimited;
    setSavingPhotoRightFor(user.userId);
    try {
      await apiFetchJson(`/api/admin/users/${encodeURIComponent(user.userId)}/photo-unlimited`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allowed }),
        timeoutMs: 20000
      });
      setAdminUsers((prev) => prev.map((entry) => (
        entry.userId === user.userId ? { ...entry, photoGenUnlimited: allowed } : entry
      )));
      pushToast({
        variant: "success",
        title: allowed ? t("Право выдано") : t("Право снято"),
        body: allowed
          ? tf`${user.username}: генерация фото без дневного предела.`
          : tf`${user.username}: снова четыре генерации фото в сутки.`
      });
    } catch (error) {
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось изменить право") });
    } finally {
      setSavingPhotoRightFor("");
    }
  };

  const [savingPurgeRightFor, setSavingPurgeRightFor] = useState("");
  const toggleParserPurgeRight = async (user) => {
    if (!isAdminUser || savingPurgeRightFor) return;
    const allowed = !user.canPurgeParserAds;
    setSavingPurgeRightFor(user.userId);
    try {
      await apiFetchJson(`/api/admin/users/${encodeURIComponent(user.userId)}/parser-purge`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allowed }),
        timeoutMs: 20000
      });
      setAdminUsers((prev) => prev.map((entry) => (
        entry.userId === user.userId ? { ...entry, canPurgeParserAds: allowed } : entry
      )));
      pushToast({
        variant: "success",
        title: allowed ? t("Право выдано") : t("Право снято"),
        body: tf`${user.username}: удаление объявлений у всех ${allowed ? t("разрешено") : t("запрещено")}.`
      });
    } catch (error) {
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось изменить право") });
    } finally {
      setSavingPurgeRightFor("");
    }
  };

  // Время подписки: direction 1 — добавить, -1 — убавить. Убавленный до
  // «сейчас» срок заканчивается сразу, поэтому убавление спрашивает.
  const submitAddTime = async (direction = 1) => {
    if (!isAdminUser || savingAddTime) return;
    const hours = Math.abs(Number(addTimeForm.hours || 0));
    if (!hours || !Number.isFinite(hours)) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Укажите количество часов") });
      return;
    }
    const targetUser = addTimeForm.target !== "all" && addTimeForm.ownerId
      ? adminUsers.find((u) => u.ownerId === addTimeForm.ownerId) || null
      : null;
    if (direction < 0) {
      const confirmed = await confirmAction({
        title: t("Убавить время?"),
        message: targetUser
          ? tf`Подписка ${targetUser.username} станет короче на ${hours} ч. Если срок дойдёт до текущего момента, она закончится.`
          : tf`Все активные подписки станут короче на ${hours} ч. Те, что дойдут до текущего момента, закончатся.`,
        confirmText: t("Убавить"),
        variant: "danger",
        icon: "alert",
        note: false
      });
      if (!confirmed) return;
    }
    setSavingAddTime(true);
    try {
      const payload = { hours: direction < 0 ? -hours : hours };
      if (addTimeForm.target !== "all" && addTimeForm.ownerId) {
        payload.ownerId = addTimeForm.ownerId;
      }
      const response = await apiFetchJson("/api/admin/subscriptions/add-time", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        timeoutMs: 30000
      });
      const updated = Number(response?.updated || 0);
      pushToast({
        variant: "success",
        title: direction < 0 ? t("Время убавлено") : t("Время добавлено"),
        body: direction < 0
          ? tf`−${hours}ч для ${updated} подписок`
          : tf`+${hours}ч для ${updated} подписок`
      });
      await loadAdminUsers();
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "warning", title: t("Ошибка"), body: error?.message || t("Не удалось изменить время") });
    } finally {
      setSavingAddTime(false);
    }
  };

  // Блокировка: вход и сессии закрыты, Telegram отвязан, данные целы.
  const toggleUserBan = async (user) => {
    if (!isAdminUser || adminUserActionFor) return;
    const banned = !user.banned;
    if (banned) {
      const confirmed = await confirmAction({
        title: tf`Заблокировать ${user.username}?`,
        message: t("Пользователя сразу выкинет из панели и бота, войти снова он не сможет. Данные и аккаунты KL останутся — блокировку можно снять."),
        confirmText: t("Заблокировать"),
        variant: "danger",
        icon: "alert",
        note: false
      });
      if (!confirmed) return;
    }
    setAdminUserActionFor(user.userId);
    try {
      await apiFetchJson(`/api/admin/users/${encodeURIComponent(user.userId)}/ban`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banned }),
        timeoutMs: 20000
      });
      setAdminUsers((prev) => prev.map((entry) => (
        entry.userId === user.userId ? { ...entry, banned } : entry
      )));
      pushToast({
        variant: "success",
        title: banned ? t("Пользователь заблокирован") : t("Блокировка снята"),
        body: user.username
      });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось изменить блокировку") });
    } finally {
      setAdminUserActionFor("");
    }
  };

  // Удаление насовсем — вместе с аккаунтами KL пользователя.
  const deleteAdminUser = async (user) => {
    if (!isAdminUser || adminUserActionFor) return;
    const klAccounts = Number(user.klAccounts || 0);
    const confirmed = await confirmAction({
      title: tf`Удалить ${user.username}?`,
      message: klAccounts > 0
        ? tf`Пользователь удалится насовсем вместе со своими аккаунтами KL (${klAccounts}). Это не отменить.`
        : t("Пользователь удалится насовсем. Это не отменить."),
      confirmText: t("Удалить"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;
    setAdminUserActionFor(user.userId);
    try {
      const response = await apiFetchJson(`/api/admin/users/${encodeURIComponent(user.userId)}`, {
        method: "DELETE",
        timeoutMs: 30000
      });
      setAdminUsers((prev) => prev.filter((entry) => entry.userId !== user.userId));
      const deletedAccounts = Number(response?.deletedAccounts || 0);
      pushToast({
        variant: "success",
        title: t("Пользователь удалён"),
        body: deletedAccounts > 0
          ? tf`${user.username} и аккаунты KL: ${deletedAccounts}`
          : user.username
      });
      if (deletedAccounts > 0) loadData();
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось удалить пользователя") });
    } finally {
      setAdminUserActionFor("");
    }
  };

  const loadAdminPromos = async () => {
    if (!isAdminUser) return;
    setLoadingAdminPromos(true);
    try {
      const [promos, plans] = await Promise.all([
        apiFetchJson("/api/admin/promo-codes", { timeoutMs: 20000, retry: true }),
        apiFetchJson("/api/plans", { timeoutMs: 20000, retry: true }).catch(() => null)
      ]);
      setAdminPromos(Array.isArray(promos?.items) ? promos.items : []);
      if (Array.isArray(plans?.items)) setAdminPlans(plans.items);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoadingAdminPromos(false);
    }
  };

  const createAdminPromo = async () => {
    if (!isAdminUser || savingPromo) return;
    const code = String(promoForm.code || "").trim().toUpperCase();
    if (!code) {
      pushToast({ variant: "warning", title: t("Ошибка"), body: t("Введите код промокода") });
      return;
    }
    setSavingPromo(true);
    try {
      const response = await apiFetchJson("/api/admin/promo-codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          discountPercent: Number(promoForm.discountPercent || 0),
          maxUses: Number(promoForm.maxUses || 0),
          perUserMaxUses: Number(promoForm.perUserMaxUses || 0),
          validDays: Number(promoForm.validDays || 0),
          planId: promoForm.planId || ""
        }),
        timeoutMs: 20000
      });
      if (response?.item) setAdminPromos((prev) => [response.item, ...prev]);
      setPromoForm((prev) => ({ ...prev, code: "" }));
      pushToast({ variant: "success", title: t("Промокод создан"), body: tf`${code} — скидка ${response?.item?.discountPercent ?? promoForm.discountPercent}%` });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "warning", title: t("Ошибка"), body: error?.message || t("Не удалось создать промокод") });
    } finally {
      setSavingPromo(false);
    }
  };

  const deleteAdminPromo = async (promo) => {
    if (!isAdminUser) return;
    const confirmed = await confirmAction({
      title: tf`Удалить промокод ${promo.code}?`,
      message: t("Им больше нельзя будет воспользоваться. Уже оплаченные с ним подписки не изменятся."),
      confirmText: t("Удалить"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;
    try {
      await apiFetchJson(`/api/admin/promo-codes/${encodeURIComponent(promo.code)}`, { method: "DELETE", timeoutMs: 20000 });
      setAdminPromos((prev) => prev.filter((entry) => entry.code !== promo.code));
      pushToast({ variant: "success", title: t("Промокод удалён"), body: promo.code });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось удалить промокод") });
    }
  };

  const loadAdminBroadcasts = async () => {
    if (!isAdminUser) return;
    setLoadingAdminBroadcasts(true);
    try {
      const response = await apiFetchJson("/api/admin/broadcasts?limit=60&includeInactive=1&includeExpired=1", {
        timeoutMs: 30000,
        retry: true
      });
      const list = Array.isArray(response?.items) ? response.items : [];
      setAdminBroadcasts(list);
    } catch (error) {
      handleAuthError(error);
      setBroadcastFormError(error?.message || t("Не удалось загрузить рассылки"));
    } finally {
      setLoadingAdminBroadcasts(false);
    }
  };

  // Удалить рассылку: из списка и у пользователей — их панели перечитывают
  // ленту уведомлений каждые полминуты.
  const deleteAdminBroadcast = async (item) => {
    if (!isAdminUser || !item?.id) return;
    const confirmed = await confirmAction({
      title: tf`Удалить рассылку «${String(item.title || "").trim() || t("Без заголовка")}»?`,
      message: t("Она пропадёт из уведомлений у всех пользователей в течение минуты. Сообщения, уже ушедшие в Telegram, останутся."),
      confirmText: t("Удалить"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;
    try {
      await apiFetchJson(`/api/admin/broadcasts/${encodeURIComponent(item.id)}`, { method: "DELETE", timeoutMs: 20000, retry: false });
      setAdminBroadcasts((prev) => (Array.isArray(prev) ? prev : []).filter((entry) => entry?.id !== item.id));
      pushToast({ variant: "success", title: t("Рассылка удалена"), body: String(item.title || "").trim() });
    } catch (error) {
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось удалить рассылку") });
    }
  };

  const submitAdminBroadcast = async () => {
    if (!isAdminUser || savingBroadcast) return;
    const payload = {
      title: String(broadcastForm.title || "").trim(),
      body: String(broadcastForm.body || "").trim(),
      level: String(broadcastForm.level || "").trim().toLowerCase(),
      targetType: String(broadcastForm.targetType || "all").trim().toLowerCase(),
      ownerIds: parseOwnerIdCsv(broadcastForm.ownerIds),
      actionLabel: String(broadcastForm.actionLabel || "").trim(),
      actionUrl: String(broadcastForm.actionUrl || "").trim(),
      sendToTelegram: Boolean(broadcastForm.sendToTelegram)
    };

    if (!payload.title) {
      setBroadcastFormError(t("Введите заголовок рассылки."));
      return;
    }
    if (!payload.body) {
      setBroadcastFormError(t("Введите текст рассылки."));
      return;
    }
    if (payload.targetType === "owners" && !payload.ownerIds.length) {
      setBroadcastFormError(t("Укажите ownerId получателей (через запятую)."));
      return;
    }

    setSavingBroadcast(true);
    setBroadcastFormError("");
    try {
      const response = await apiFetchJson("/api/admin/broadcasts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        timeoutMs: 45000,
        retry: false
      });
      const deliveredClients = Number(response?.deliveredClients || 0);
      const telegramSent = Number(response?.telegramSent || 0);
      setBroadcastForm((prev) => ({
        ...prev,
        title: "",
        body: "",
        actionLabel: "",
        actionUrl: "",
        ownerIds: prev.targetType === "owners" ? prev.ownerIds : ""
      }));
      const bodyParts = [];
      const subscribedRecipients = Number(response?.subscribedRecipients || 0);
      if (subscribedRecipients > 0) bodyParts.push(tf`С подпиской: ${subscribedRecipients}`);
      if (deliveredClients > 0) bodyParts.push(tf`Онлайн: ${deliveredClients}`);
      if (telegramSent > 0) bodyParts.push(`Telegram: ${telegramSent}`);
      pushToast({
        variant: "success",
        title: t("Рассылка отправлена"),
        body: bodyParts.length > 0
          ? bodyParts.join(" · ")
          : t("Уведомление доставится при следующем обновлении у пользователей.")
      });
      await loadAdminBroadcasts();
    } catch (error) {
      handleAuthError(error);
      setBroadcastFormError(error?.message || t("Не удалось отправить рассылку"));
    } finally {
      setSavingBroadcast(false);
    }
  };

  const loadParserProxies = async () => {
    try {
      const response = await apiFetchJson("/api/admin/parser-proxies", { timeoutMs: 20000 });
      if (response?.success) setParserProxies(Array.isArray(response.proxies) ? response.proxies : []);
    } catch (error) {
      handleAuthError(error);
    }
  };

  const addParserProxies = async () => {
    const value = String(parserProxyInput || "").trim();
    if (!value || parserProxyBusy) return;
    setParserProxyBusy(true);
    setParserProxyStatus(null);
    try {
      const response = await apiFetchJson("/api/admin/parser-proxies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
        timeoutMs: 20000
      });
      if (response?.success) {
        setParserProxies(Array.isArray(response.proxies) ? response.proxies : []);
        setParserProxyInput("");
        const bad = Array.isArray(response.invalid) ? response.invalid.length : 0;
        setParserProxyStatus({
          variant: bad ? "error" : "success",
          text: bad
            ? tf`Добавлено: ${response.added}. Не распознано: ${bad}`
            : tf`Добавлено прокси: ${response.added}`
        });
      }
    } catch (error) {
      handleAuthError(error);
      setParserProxyStatus({ variant: "error", text: error?.message || t("Не удалось добавить") });
    } finally {
      setParserProxyBusy(false);
    }
  };

  const removeParserProxy = async (id) => {
    if (parserProxyBusy) return;
    setParserProxyBusy(true);
    try {
      const response = await apiFetchJson(`/api/admin/parser-proxies/${encodeURIComponent(id)}`, {
        method: "DELETE",
        timeoutMs: 20000
      });
      if (response?.success) setParserProxies(Array.isArray(response.proxies) ? response.proxies : []);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setParserProxyBusy(false);
    }
  };

  const loadPanelProxy = async () => {
    if (!isAdminUser) return;
    setLoadingPanelProxy(true);
    try {
      const response = await apiFetchJson("/api/admin/panel-proxy", {
        timeoutMs: 30000,
        retry: true
      });
      setPanelProxyConfig(String(response?.value || ""));
      setPanelProxyProxies(Array.isArray(response?.proxies) ? response.proxies : []);
      setPanelProxyChecking(Boolean(response?.checking));
    } catch (error) {
      handleAuthError(error);
      setPanelProxyStatus({ variant: "error", text: error?.message || t("Не удалось загрузить прокси") });
    } finally {
      setLoadingPanelProxy(false);
    }
  };

  const savePanelProxy = async () => {
    if (!isAdminUser || savingPanelProxy) return;
    setSavingPanelProxy(true);
    setPanelProxyStatus(null);
    try {
      const response = await apiFetchJson("/api/admin/panel-proxy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: String(panelProxyConfig || "") }),
        timeoutMs: 45000,
        retry: false
      });
      setPanelProxyConfig(String(response?.value || ""));
      const proxies = Array.isArray(response?.proxies) ? response.proxies : [];
      setPanelProxyProxies(proxies);
      // Сохранение не ждёт проверки прокси (на 20+ прокси она не укладывалась в
      // таймаут) — она идёт на сервере, статусы подтягиваются сами.
      setPanelProxyChecking(Boolean(response?.checking));
      setPanelProxyStatus({
        variant: "success",
        text: proxies.length > 0
          ? (response?.checking
            ? tf`Сохранено · прокси в пуле: ${proxies.length}. Проверка идёт в фоне — статусы обновятся сами.`
            : tf`Сохранено · прокси в пуле: ${proxies.length}`)
          : t("Сохранено · прокси не задан")
      });
    } catch (error) {
      handleAuthError(error);
      setPanelProxyStatus({ variant: "error", text: error?.message || t("Не удалось сохранить прокси") });
    } finally {
      setSavingPanelProxy(false);
    }
  };

  // Пока сервер проверяет пул Umbrella — статусы раз в 4 с. Закончил — итог.
  useEffect(() => {
    if (!panelProxyChecking || !isAdminUser) return undefined;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const response = await apiFetchJson("/api/admin/panel-proxy", { timeoutMs: 15000 });
        if (cancelled) return;
        const proxies = Array.isArray(response?.proxies) ? response.proxies : [];
        setPanelProxyProxies(proxies);
        if (!response?.checking) {
          setPanelProxyChecking(false);
          const active = proxies.filter((item) => String(item?.status || "").toLowerCase() === "active").length;
          setPanelProxyStatus({
            variant: active === proxies.length ? "success" : "error",
            text: tf`Проверка закончена · активных ${active} из ${proxies.length}`
          });
        }
      } catch (_error) {
        // Сеть мигнула — следующий опрос.
      }
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [panelProxyChecking, isAdminUser]);

  const normalizeMetricAccountId = (value) => String(value ?? "").trim();

  const toMetricCounter = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    return Math.floor(numeric);
  };

  const buildAdsMetrics = (ads = []) => {
    const metrics = {};
    const ensure = (accountId) => {
      const key = normalizeMetricAccountId(accountId);
      if (!key) return null;
      if (!metrics[key]) {
        metrics[key] = { active: 0, total: 0 };
      }
      return metrics[key];
    };

    ads.forEach((ad) => {
      if (!ad || ad.accountId == null) return;
      const entry = ensure(ad.accountId);
      if (!entry) return;
      entry.total += 1;
      const status = String(ad.status || "").toLowerCase();
      if (status === "aktiv" || status === "active") {
        entry.active += 1;
      }
    });

    return metrics;
  };

  // Объявление только что опубликовано. Счётчик на карточке аккаунта считается
  // по списку объявлений, а список перечитывается не сразу — без этого новое
  // объявление появлялось в счётчике лишь после ручного обновления списка.
  // Ближайшее обновление поставит точное число.
  const bumpAccountAdsMetric = (accountId, state) => {
    const key = normalizeMetricAccountId(accountId);
    if (!key || state === "deleted") return;
    setAccountMetrics((prev) => {
      const current = prev?.[key] || { active: 0, total: 0, chats: 0, unread: 0 };
      const next = {
        ...prev,
        [key]: {
          ...current,
          total: toMetricCounter(current.total) + 1,
          // «На проверке» в активные не входит — так их считает и сам список.
          active: toMetricCounter(current.active) + (state === "active" ? 1 : 0)
        }
      };
      try { localStorage.setItem("kl_account_metrics", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Черновик опубликовался во вкладке «Черновики» — по расписанию или кнопкой.
  // Своя публикация App прибавляет к счётчику сама (closeModalOnPublished), так
  // что здесь — только события оттуда.
  const bumpAccountAdsMetricRef = useRef(bumpAccountAdsMetric);
  bumpAccountAdsMetricRef.current = bumpAccountAdsMetric;
  // PLZ успешной публикации — новый PLZ аккаунта (так же делает и сервер):
  // следующая публикация на этот аккаунт подставит его при выборе аккаунта.
  // Отклонённая KL публикация PLZ не меняет.
  const rememberAccountPostalCode = (accountId, postalCode) => {
    const code = String(postalCode || "").replace(/\s+/g, "");
    if (!accountId || !/^\d{5}$/.test(code)) return;
    setAccounts((prev) => prev.map((item) => (
      String(item?.id) === String(accountId) && String(item?.postalCode || "").trim() !== code
        ? { ...item, postalCode: code }
        : item
    )));
  };
  // Черновик опубликован во вкладке «Черновики»: чем кончилась публикация,
  // там не видно (KL мог сразу удалить объявление) — PLZ аккаунта берётся у
  // сервера, который это учёл.
  const syncAccountPostalCode = async (accountId) => {
    try {
      const list = await apiFetchJson("/api/accounts", { timeoutMs: 45000 });
      const fresh = (Array.isArray(list) ? list : []).find((item) => String(item?.id) === String(accountId));
      if (fresh) rememberAccountPostalCode(accountId, fresh.postalCode);
    } catch {
      // подтянется со следующей загрузкой аккаунтов
    }
  };
  const syncAccountPostalCodeRef = useRef(syncAccountPostalCode);
  syncAccountPostalCodeRef.current = syncAccountPostalCode;
  useEffect(() => {
    const onPublished = (event) => {
      const detail = event?.detail;
      if (detail?.fromDraft && detail?.accountId) {
        bumpAccountAdsMetricRef.current(detail.accountId, "under_review");
        syncAccountPostalCodeRef.current(detail.accountId);
      }
    };
    window.addEventListener(AD_PUBLISHED_EVENT, onPublished);
    return () => window.removeEventListener(AD_PUBLISHED_EVENT, onPublished);
  }, []);

  const buildMessageMetrics = (messages = []) => {
    const metrics = {};
    const ensure = (accountId) => {
      const key = normalizeMetricAccountId(accountId);
      if (!key) return null;
      if (!metrics[key]) {
        metrics[key] = { chats: 0, unread: 0 };
      }
      return metrics[key];
    };

    messages.forEach((msg) => {
      if (!msg || msg.accountId == null) return;
      const entry = ensure(msg.accountId);
      if (!entry) return;
      entry.chats += 1;
      if (msg.unread) entry.unread += 1;
    });

    return metrics;
  };

  const mergeAccountMetrics = (previous = {}, options = {}) => {
    const adsMetrics = options?.adsMetrics || null;
    const messagesMetrics = options?.messagesMetrics || null;
    const accountsSnapshot = Array.isArray(options?.accountsSnapshot) ? options.accountsSnapshot : [];
    // Аккаунты, чей список объявлений сервер прочитал целиком. Счётчик
    // объявлений меняется только у них: у остальных «нет в ответе» значит «не
    // прочиталось» (таймаут, медленный прокси, кешированный или чужой запрос),
    // а не «ноль». Так карточка и обнулялась — до следующего захода в
    // «Объявления». null — сервер списка не прислал: прежнее поведение.
    const adsReadIds = Array.isArray(options?.adsReadAccountIds)
      ? new Set(options.adsReadAccountIds.map(normalizeMetricAccountId).filter(Boolean))
      : null;

    const next = {};
    const accountIds = new Set();
    const rememberId = (value) => {
      const key = normalizeMetricAccountId(value);
      if (key) accountIds.add(key);
    };

    if (previous && typeof previous === "object") {
      Object.keys(previous).forEach(rememberId);
    }
    accountsSnapshot.forEach((account) => rememberId(account?.id));
    if (adsMetrics && typeof adsMetrics === "object") {
      Object.keys(adsMetrics).forEach(rememberId);
    }
    if (messagesMetrics && typeof messagesMetrics === "object") {
      Object.keys(messagesMetrics).forEach(rememberId);
    }

    // If the caller passed a fresh adsMetrics/messagesMetrics object, it's
    // authoritative for ALL known accounts — accounts NOT present in it should
    // be reset to 0/0 for the corresponding fields. Otherwise an account that
    // had its last ad deleted would keep its cached "active=1" value because
    // an empty `{}` adsMetrics produces no Object.keys iterations below.
    const adsAuthoritative = adsMetrics && typeof adsMetrics === "object";
    const messagesAuthoritative = messagesMetrics && typeof messagesMetrics === "object";
    const hasPreviousEntry = (accountId) => Boolean(previous && typeof previous === "object" && previous[accountId]);
    // Ответ задаёт счётчик объявлений аккаунта: прочитан целиком — да; не
    // прочитан — только если прежнего числа нет вовсе.
    const adsOwns = (accountId) => adsAuthoritative
      && (!adsReadIds || adsReadIds.has(accountId) || !hasPreviousEntry(accountId));

    accountIds.forEach((accountId) => {
      const prevEntry = (previous && typeof previous === "object" ? previous[accountId] : null) || {};
      next[accountId] = {
        active: adsOwns(accountId) ? 0 : toMetricCounter(prevEntry.active),
        total: adsOwns(accountId) ? 0 : toMetricCounter(prevEntry.total),
        chats: messagesAuthoritative ? 0 : toMetricCounter(prevEntry.chats),
        unread: messagesAuthoritative ? 0 : toMetricCounter(prevEntry.unread)
      };
    });

    if (adsAuthoritative) {
      Object.keys(adsMetrics).forEach((accountIdRaw) => {
        const accountId = normalizeMetricAccountId(accountIdRaw);
        if (!accountId) return;
        if (!adsOwns(accountId)) return;
        if (!next[accountId]) {
          next[accountId] = { active: 0, total: 0, chats: 0, unread: 0 };
        }
        const adEntry = adsMetrics[accountId];
        next[accountId].active = toMetricCounter(adEntry?.active);
        next[accountId].total = toMetricCounter(adEntry?.total);
      });
    }

    if (messagesAuthoritative) {
      Object.keys(messagesMetrics).forEach((accountIdRaw) => {
        const accountId = normalizeMetricAccountId(accountIdRaw);
        if (!accountId) return;
        if (!next[accountId]) {
          next[accountId] = { active: 0, total: 0, chats: 0, unread: 0 };
        }
        const messageEntry = messagesMetrics[accountId];
        next[accountId].chats = toMetricCounter(messageEntry?.chats);
        next[accountId].unread = toMetricCounter(messageEntry?.unread);
      });
    }

    return next;
  };

  const loadAccountMetrics = async ({ forceAds = false, reloadAccounts = true } = {}) => {
    try {
      const messagesParams = new URLSearchParams();
      messagesParams.set("full", "1");
      const adsParams = new URLSearchParams();
      if (forceAds) adsParams.set("force", "1");
      const adsSuffix = adsParams.toString() ? `?${adsParams.toString()}` : "";
      const metricRequests = [
        apiFetchJson(`/api/ads/active${adsSuffix}`, { timeoutMs: 180000 }),
        apiFetchJson(`/api/messages?${messagesParams.toString()}`, { timeoutMs: 120000 })
      ];
      if (reloadAccounts) {
        // Список аккаунтов — в состояние сразу, как пришёл, а не после
        // объявлений и сообщений: те грузятся до нескольких минут, и всё, что
        // за это время изменилось (удаление, переименование), откатывалось бы
        // к снимку на момент запроса.
        const requestedAt = Date.now();
        metricRequests.push(apiFetchJson("/api/accounts", { timeoutMs: 45000 }).then((list) => {
          applyFetchedAccounts(list, requestedAt);
          return list;
        }));
      }
      const settled = await Promise.allSettled(metricRequests);
      const adsResult = settled[0];
      const messagesResult = settled[1];
      const accountsResult = reloadAccounts
        ? settled[2]
        : { status: "fulfilled", value: Array.isArray(accounts) ? accounts : [] };
      const adsRes = adsResult.status === "fulfilled" ? adsResult.value : null;
      const messagesRes = messagesResult.status === "fulfilled" ? messagesResult.value : null;
      const adsSucceeded = Array.isArray(adsRes?.ads);
      const messagesSucceeded = Array.isArray(messagesRes);
      const accountsSnapshot = accountsResult.status === "fulfilled" && Array.isArray(accountsResult.value)
        ? accountsResult.value
        : accounts;
      const hasAccounts = Array.isArray(accountsSnapshot) && accountsSnapshot.length > 0;

      let adsMetrics = null;
      const adsReadAccountIds = adsSucceeded && Array.isArray(adsRes?.readAccountIds) ? adsRes.readAccountIds : null;
      if (adsSucceeded) {
        const adsList = Array.isArray(adsRes?.ads) ? adsRes.ads : [];
        const adsWarningText = String(adsRes?.warning || "").trim();
        const adsPartial = Boolean(adsRes?.partial);
        const adsStale = Boolean(adsRes?.stale);
        const adsFromFallback = adsStale || String(adsRes?.source || "").trim().toLowerCase() === "stored-fallback";
        // Со списком прочитанных аккаунтов неполный ответ тоже годится: число
        // поменяется только у тех, кого сервер действительно прочитал.
        const shouldApplyAdsMetrics = !adsFromFallback && (forceAds || !adsPartial || Boolean(adsReadAccountIds));
        if (adsWarningText) {
          const now = Date.now();
          const lastWarning = adsMetricsWarningRef.current || { message: "", timestamp: 0 };
          const shouldNotify = adsWarningText !== String(lastWarning.message || "")
            || now - Number(lastWarning.timestamp || 0) > 120000;
          if (shouldNotify) {
            pushToast({
              variant: "warning",
              title: t("Объявления обновлены частично"),
              body: adsWarningText,
              durationMs: 8000
            });
            adsMetricsWarningRef.current = { message: adsWarningText, timestamp: now };
          }
        }
        const suspiciousEmptyAds = shouldApplyAdsMetrics && hasAccounts && adsList.length === 0;
        if (suspiciousEmptyAds) {
          accountMetricsEmptyAdsStreakRef.current += 1;
        } else if (shouldApplyAdsMetrics) {
          accountMetricsEmptyAdsStreakRef.current = 0;
        }
        // Ignore the first transient empty ads responses to avoid random zero
        // drops in counters during passive polling. When the caller explicitly
        // asked for a force refresh (delete / refresh button / etc.), trust
        // the empty response immediately — otherwise the counter stays stuck
        // at the pre-deletion value until 3 polls pass.
        const allowAdsApply = shouldApplyAdsMetrics
          && (forceAds || Boolean(adsReadAccountIds) || !suspiciousEmptyAds || accountMetricsEmptyAdsStreakRef.current >= 3);
        if (allowAdsApply) {
          adsMetrics = buildAdsMetrics(adsList);
          if (forceAds && suspiciousEmptyAds) {
            // Reset the streak so subsequent passive polls don't re-trigger it.
            accountMetricsEmptyAdsStreakRef.current = 0;
          }
        }
      }

      if (adsSucceeded || messagesSucceeded) {
        const messagesMetrics = messagesSucceeded ? buildMessageMetrics(messagesRes) : null;
        if (adsMetrics || messagesMetrics) {
          setAccountMetrics((prev) => {
            const merged = mergeAccountMetrics(prev, {
              adsMetrics,
              adsReadAccountIds,
              messagesMetrics,
              accountsSnapshot
            });
            try { localStorage.setItem("kl_account_metrics", JSON.stringify(merged)); } catch {}
            return merged;
          });
        }
      }

      if (adsResult.status === "rejected") {
        handleAuthError(adsResult.reason);
      }
      if (messagesResult.status === "rejected") {
        handleAuthError(messagesResult.reason);
      }
      if (reloadAccounts && accountsResult.status === "rejected") {
        handleAuthError(accountsResult.reason);
        console.error("Ошибка загрузки списка аккаунтов после метрик:", accountsResult.reason);
      }
    } catch (error) {
      handleAuthError(error);
      console.error("Ошибка загрузки метрик аккаунтов:", error);
    }
  };

  const handleAddAccount = (newAccount, bootstrapMetrics = null, options = {}) => {
    const skipReload = Boolean(options?.skipReload);
    setAccounts((prev) => {
      const nextAccountId = String(newAccount?.id || "").trim();
      if (!nextAccountId) {
        return [newAccount, ...prev];
      }
      const filtered = prev.filter((item) => String(item?.id || "").trim() !== nextAccountId);
      return [newAccount, ...filtered];
    });
    if (newAccount?.id && bootstrapMetrics && typeof bootstrapMetrics === "object") {
      setAccountMetrics((prev) => ({
        ...prev,
        [newAccount.id]: {
          active: Number(bootstrapMetrics.active || 0),
          total: Number(bootstrapMetrics.total || 0),
          chats: Number(bootstrapMetrics.chats || 0),
          unread: Number(bootstrapMetrics.unread || 0)
        }
      }));
    }
    if (!skipReload) {
      loadData(); // Перезагружаем статистику
      loadAccountMetrics();
    }
  };

  const handleAddAccountsBulk = (entries = []) => {
    if (!Array.isArray(entries) || !entries.length) return;
    entries.forEach((entry) => {
      if (!entry?.account) return;
      handleAddAccount(entry.account, entry.metrics || null, { skipReload: true });
    });
    loadData();
    loadAccountMetrics();
  };

  const handleAddProxy = (newProxy) => {
    setProxies(orderProxiesBuiltinFirst([newProxy, ...proxies]));
    loadData(); // Перезагружаем статистику
  };

  const handleDeleteAccount = async (id) => {
    const confirmed = await requestConfirmModal({
      title: t("Удалить аккаунт"),
      confirmText: t("Удалить"),
      cancelText: t("Отмена"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;

    try {
      const result = await apiFetchJson(`/api/accounts/${id}`, {
        method: "DELETE"
      });
      if (result.success) {
        rememberDeletedAccounts([id]);
        setAccounts((prev) => prev.filter((acc) => acc.id !== id));
        alert(t("Аккаунт удален"));
      }
    } catch (error) {
      handleAuthError(error);
      alert(error?.message || t("Ошибка при удалении аккаунта"));
    }
  };

  const toggleAccountSelection = (accountId) => {
    setSelectedAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const clearAccountSelection = () => setSelectedAccountIds(new Set());

  // Массовые действия. Каждое переиспользует тот же запрос, что и одиночное,
  // но отчитывается одним разом: N отдельных уведомлений на выбранные восемь
  // аккаунтов читать невозможно.
  const getSelectedAccounts = () => (Array.isArray(accounts) ? accounts : [])
    .filter((account) => selectedAccountIds.has(account.id));

  const [bulkAccountBusy, setBulkAccountBusy] = useState("");

  const handleBulkRefreshAccounts = async () => {
    const targets = getSelectedAccounts();
    if (!targets.length || bulkAccountBusy) return;
    setBulkAccountBusy("refresh");
    setRefreshingAccountIds((prev) => {
      const next = new Set(prev);
      targets.forEach((account) => next.add(account.id));
      return next;
    });
    let done = 0;
    // Аккаунты, которые не обновлялись из-за невалидного прокси, — их стоит
    // назвать отдельно: «обновлено 3 из 5» не говорит, что делать с остальными.
    let proxyInvalid = 0;
    try {
      // Обновление аккаунта — тяжёлый запрос (до трёх минут), поэтому идём по
      // двое: последовательно это заняло бы четверть часа на восьми аккаунтах,
      // а все разом — столько же одновременных браузерных сессий.
      const queue = [...targets];
      const worker = async () => {
        while (queue.length) {
          const account = queue.shift();
          if (!account) break;
          try {
            const result = await apiFetchJson(`/api/accounts/${account.id}/refresh`, {
              method: "POST",
              timeoutMs: 180000
            });
            if (result?.account) {
              setAccounts((prev) => prev.map((item) => (item.id === account.id ? { ...item, ...result.account } : item)));
            }
            if (result?.success) done += 1;
          } catch (error) {
            if (String(error?.data?.code || "") === "ACCOUNT_PROXY_NOT_VALID") proxyInvalid += 1;
            handleAuthError(error);
          } finally {
            setRefreshingAccountIds((prev) => {
              const next = new Set(prev);
              next.delete(account.id);
              return next;
            });
          }
        }
      };
      await Promise.all([worker(), worker()]);
      pushToast({
        variant: done === targets.length ? "success" : "error",
        title: t("Обновление аккаунтов"),
        body: proxyInvalid
          ? `${tf`Обновлено ${done} из ${targets.length}`}. ${tf`С невалидным прокси не обновлялись: ${proxyInvalid}`}`
          : tf`Обновлено ${done} из ${targets.length}`
      });
    } finally {
      setRefreshingAccountIds((prev) => {
        const next = new Set(prev);
        targets.forEach((account) => next.delete(account.id));
        return next;
      });
      setBulkAccountBusy("");
    }
  };

  const handleBulkDeleteAccounts = async () => {
    const targets = getSelectedAccounts();
    if (!targets.length || bulkAccountBusy) return;
    const confirmed = await requestConfirmModal({
      title: tf`Удалить аккаунтов: ${targets.length}`,
      message: t("Аккаунты будут удалены из панели вместе с их cookie. Объявления на Kleinanzeigen останутся."),
      confirmText: t("Удалить"),
      cancelText: t("Отмена"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;
    setBulkAccountBusy("delete");
    const removed = [];
    try {
      await runInBatches(targets, async (account) => {
        try {
          const result = await apiFetchJson(`/api/accounts/${account.id}`, { method: "DELETE" });
          if (result?.success) removed.push(account.id);
        } catch (error) {
          handleAuthError(error);
        }
      });
      if (removed.length) {
        rememberDeletedAccounts(removed);
        setAccounts((prev) => prev.filter((item) => !removed.includes(item.id)));
        setSelectedAccountIds((prev) => {
          const next = new Set(prev);
          removed.forEach((id) => next.delete(id));
          return next;
        });
      }
      pushToast({
        variant: removed.length === targets.length ? "success" : "error",
        title: t("Удаление аккаунтов"),
        body: tf`Удалено ${removed.length} из ${targets.length}`
      });
    } finally {
      setBulkAccountBusy("");
    }
  };

  const handleBulkChangeProxy = () => {
    const targets = getSelectedAccounts();
    if (!targets.length) return;
    const availableProxies = Array.isArray(proxies) ? proxies : [];
    if (!availableProxies.length) {
      alert(t("Нет добавленных прокси. Сначала добавьте прокси в разделе «Прокси»."));
      return;
    }
    setProxyPickerBulkIds(targets.map((account) => account.id));
    // Первый выбранный нужен окну только как источник заголовка; применяется
    // выбор ко всем из списка выше.
    setProxyPickerAccount(targets[0]);
  };

  const handleRefreshAccount = async (id) => {
    setOpenAccountMenuId(null);
    setRefreshingAccountIds((prev) => new Set(prev).add(id));
    // Apply a refreshed account snapshot to local state. The backend returns the
    // updated account on BOTH success and failure — a banned/invalid account
    // flips status to "invalid"/"failed", and the card must reflect that instead
    // of keeping the stale "active"/ONLINE state. (preserves phoneVerification, accountType)
    const applyAccountSnapshot = (snapshot) => {
      if (!snapshot || typeof snapshot !== "object") return;
      setAccounts((prev) => prev.map((acc) => acc.id === id ? { ...acc, ...snapshot } : acc));
    };
    try {
      const result = await apiFetchJson(`/api/accounts/${id}/refresh`, {
        method: "POST",
        timeoutMs: 180000
      });
      applyAccountSnapshot(result?.account);
      if (!result?.success) {
        pushToast({ variant: "error", title: t("Ошибка"), body: t(result?.error) || t("Не удалось обновить аккаунт") });
      } else {
        pushToast({ variant: "success", title: t("Аккаунт обновлен"), body: t("Данные аккаунта успешно обновлены") });
        // Heavy metrics refresh can take long (ads force sync). Run it in background
        // so manual account refresh responds quickly.
        loadAccountMetrics({ forceAds: true, reloadAccounts: false }).catch((metricsError) => {
          console.warn("[accounts/refresh] background metrics refresh failed:", metricsError?.message || metricsError);
        });
      }
    } catch (error) {
      // A banned/invalid account makes the endpoint respond 400, which apiFetchJson
      // throws — but the updated account snapshot (status "invalid"/"failed") rides
      // along in error.data.account. Apply it so the card stops showing ONLINE.
      applyAccountSnapshot(error?.data?.account);
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось обновить аккаунт") });
    } finally {
      setRefreshingAccountIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const openProfileNameModal = (account) => {
    if (!account?.id) return;
    setOpenAccountMenuId(null);
    const initialValue = String(account.profileName || account.username || "").trim();
    setProfileNameModal({
      open: true,
      account,
      value: initialValue,
      saving: false
    });
  };

  const closeProfileNameModal = () => {
    setProfileNameModal((prev) => {
      if (!prev.open || prev.saving) return prev;
      return {
        open: false,
        account: null,
        value: "",
        saving: false
      };
    });
  };

  const handleProfileNameSubmit = async () => {
    const account = profileNameModal.account;
    if (!account?.id) return;
    const nextName = String(profileNameModal.value || "").replace(/\s+/g, " ").trim();
    if (nextName.length < 2 || nextName.length > 40) {
      alert(t("Введите корректное имя профиля (2-40 символов)."));
      return;
    }

    setProfileNameModal((prev) => ({ ...prev, saving: true }));
    try {
      const result = await apiFetchJson(`/api/accounts/${account.id}/profile-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profileName: nextName }),
        // Renaming a private account drives a real browser through the account's
        // proxy: queue, launch, two navigations with a retry each, then the form.
        // Against a slow proxy that adds up past three minutes, and the old
        // budget gave up while the server was still working — the panel reported
        // a failure and the name changed a minute later anyway, because the
        // request it had abandoned went on to finish. Waiting longer than the
        // server can plausibly take is what makes the reported outcome the real
        // one.
        timeoutMs: 420000
      });
      const updatedAccount = result?.account;
      if (updatedAccount?.id) {
        setAccounts((prev) => prev.map((item) => (item.id === updatedAccount.id ? { ...item, ...updatedAccount } : item)));
      } else {
        await loadData();
      }
      setProfileNameModal({
        open: false,
        account: null,
        value: "",
        saving: false
      });
      alert(t("Имя профиля обновлено."));
    } catch (error) {
      handleAuthError(error);
      setProfileNameModal((prev) => ({ ...prev, saving: false }));
      alert(error?.message || t("Не удалось изменить имя профиля."));
    }
  };

  const getPhoneVerificationMasked = (phoneVerification = null) => {
    if (!phoneVerification || typeof phoneVerification !== "object") return "";
    const normalizedMasked = String(phoneVerification.maskedPhone || "")
      .replace(/\s+/g, "")
      .replace(/[•·●xX×\u2022\u00B7\u25CF]/g, "*");
    if (/^\+\d{1,4}\*+\d{0,4}$/u.test(normalizedMasked)) {
      return normalizedMasked;
    }
    if (/^\*+\d{0,4}$/u.test(normalizedMasked)) {
      return normalizedMasked;
    }
    if (/^\d{1,4}\*+\d{0,4}$/u.test(normalizedMasked)) {
      return `+${normalizedMasked}`;
    }
    const countryCode = String(phoneVerification.countryCode || "").replace(/[^\d]/g, "");
    const phoneLastDigits = String(phoneVerification.phoneLastDigits || "").replace(/[^\d]/g, "");
    if (!countryCode && !phoneLastDigits) return "";
    const starsCount = Math.max(4, 9 - phoneLastDigits.length);
    return `${countryCode ? `+${countryCode}` : ""}${"*".repeat(starsCount)}${phoneLastDigits}`;
  };

  const openPhoneModal = (account) => {
    if (!account?.id) return;
    setOpenAccountMenuId(null);
    setPhoneModal({
      open: true,
      account,
      countryCode: "49",
      phoneNumber: "",
      code: "",
      sessionId: "",
      expiresAt: 0,
      step: "start",
      saving: false,
      resending: false,
      statusLoading: true,
      statusSource: ""
    });
    (async () => {
      try {
        const result = await apiFetchJson(`/api/accounts/${account.id}/phone/status`, {
          timeoutMs: 30000
        });
        const refreshedAccount = result?.account;
        const source = String(result?.source || "").trim();
        if (!refreshedAccount?.id) {
          setPhoneModal((prev) => (prev.open ? { ...prev, statusLoading: false, statusSource: source } : prev));
          return;
        }
        setAccounts((prev) => prev.map((item) => (
          item.id === refreshedAccount.id
            ? { ...item, ...refreshedAccount }
            : item
        )));
        setPhoneModal((prev) => {
          if (!prev.open) return prev;
          if (!prev.account?.id || prev.account.id !== refreshedAccount.id) return prev;
          return {
            ...prev,
            account: { ...prev.account, ...refreshedAccount },
            statusLoading: false,
            statusSource: source
          };
        });
      } catch (error) {
        setPhoneModal((prev) => (prev.open ? { ...prev, statusLoading: false, statusSource: "error" } : prev));
        handleAuthError(error);
      }
    })();
  };

  const closePhoneModal = async () => {
    const current = phoneModal;
    if (!current.open || current.saving || current.resending) return;
    setPhoneModal((prev) => ({ ...prev, open: false }));
    if (current.sessionId && current.account?.id) {
      apiFetchJson(`/api/accounts/${current.account.id}/phone/session/${encodeURIComponent(current.sessionId)}`, {
        method: "DELETE",
        timeoutMs: 30000
      }).catch(() => {});
    }
    setPhoneModal({
      open: false,
      account: null,
      countryCode: "49",
      phoneNumber: "",
      code: "",
      sessionId: "",
      expiresAt: 0,
      step: "start",
      saving: false,
      resending: false
    });
  };

  const handlePhoneStart = async () => {
    const account = phoneModal.account;
    if (!account?.id) return;
    const normalizedCountryCode = String(phoneModal.countryCode || "").replace(/[^\d]/g, "");
    const normalizedPhoneNumber = String(phoneModal.phoneNumber || "").replace(/[^\d]/g, "");
    if (!normalizedCountryCode) {
      alert(t("Выберите код страны."));
      return;
    }
    if (normalizedPhoneNumber.length < 6) {
      alert(t("Введите корректный номер телефона (без кода страны)."));
      return;
    }

    setPhoneModal((prev) => ({ ...prev, saving: true }));
    try {
      const result = await apiFetchJson(`/api/accounts/${account.id}/phone/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          countryCode: normalizedCountryCode,
          phoneNumber: normalizedPhoneNumber
        }),
        timeoutMs: 240000
      });
      setPhoneModal((prev) => ({
        ...prev,
        countryCode: normalizedCountryCode,
        phoneNumber: normalizedPhoneNumber,
        sessionId: String(result?.sessionId || prev.sessionId || ""),
        expiresAt: Number(result?.expiresAt || 0),
        step: "verify",
        saving: false
      }));
      if (result?.message) {
        alert(result.message);
      }
    } catch (error) {
      handleAuthError(error);
      setPhoneModal((prev) => ({ ...prev, saving: false }));
      alert(error?.message || t("Не удалось отправить код подтверждения."));
    }
  };

  const handlePhoneResend = async () => {
    const account = phoneModal.account;
    if (!account?.id || !phoneModal.sessionId) return;
    setPhoneModal((prev) => ({ ...prev, resending: true }));
    try {
      const result = await apiFetchJson(`/api/accounts/${account.id}/phone/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: phoneModal.sessionId }),
        timeoutMs: 120000
      });
      setPhoneModal((prev) => ({
        ...prev,
        expiresAt: Number(result?.expiresAt || prev.expiresAt),
        resending: false
      }));
      if (result?.message) {
        alert(result.message);
      }
    } catch (error) {
      handleAuthError(error);
      setPhoneModal((prev) => ({ ...prev, resending: false }));
      alert(error?.message || t("Не удалось отправить код повторно."));
    }
  };

  const handlePhoneConfirm = async () => {
    const account = phoneModal.account;
    if (!account?.id || !phoneModal.sessionId) return;
    const code = String(phoneModal.code || "").replace(/[^\d]/g, "");
    if (code.length < 4) {
      alert(t("Введите корректный код подтверждения."));
      return;
    }

    setPhoneModal((prev) => ({ ...prev, saving: true }));
    try {
      const result = await apiFetchJson(`/api/accounts/${account.id}/phone/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: phoneModal.sessionId,
          code
        }),
        timeoutMs: 240000
      });
      const updatedAccount = result?.account;
      if (updatedAccount?.id) {
        setAccounts((prev) => prev.map((item) => (item.id === updatedAccount.id ? { ...item, ...updatedAccount } : item)));
      } else {
        await loadData();
      }
      setPhoneModal({
        open: false,
        account: null,
        countryCode: "49",
        phoneNumber: "",
        code: "",
        sessionId: "",
        expiresAt: 0,
        step: "start",
        saving: false,
        resending: false
      });
      alert(t("Номер телефона подтвержден."));
    } catch (error) {
      handleAuthError(error);
      setPhoneModal((prev) => ({ ...prev, saving: false }));
      alert(error?.message || t("Не удалось подтвердить номер телефона."));
    }
  };

  const closeProxyPicker = () => {
    if (updatingAccountProxyId) return;
    setProxyPickerAccount(null);
    setProxyPickerBulkIds([]);
  };

  useEffect(() => {
    if (proxyPickerAccount) setProxyPickerQuery("");
  }, [proxyPickerAccount]);

  const handleChangeAccountProxy = (account) => {
    if (!account?.id) return;
    setOpenAccountMenuId(null);

    const availableProxies = Array.isArray(proxies) ? proxies : [];
    if (!availableProxies.length) {
      alert(t("Нет добавленных прокси. Сначала добавьте прокси в разделе «Прокси»."));
      return;
    }

    setProxyPickerAccount(account);
  };

  // Map a backend warm-up phase ID to a Lucide icon + accent color for the
  // modal. Keeps the modal header / step strip in sync with the actual
  // phases emitted by puppeteerWarmupService.js.
  const getWarmupPhaseMeta = (phaseId = "") => {
    const id = String(phaseId || "").toLowerCase();
    if (id === "browser-start" || id === "init") {
      return { Icon: MonitorIcon, tone: A[400] };
    }
    if (id === "homepage") return { Icon: HomeIcon, tone: A[300] };
    if (id === "own-ads") return { Icon: PackageIcon, tone: "#fcd34d" };
    if (id === "category") return { Icon: CompassIcon, tone: A[300] };
    if (id.startsWith("view-ad")) return { Icon: EyeIcon, tone: "#fb923c" };
    if (id === "messages") return { Icon: InboxIcon, tone: A[200] };
    if (id === "done") return { Icon: SuccessIcon, tone: "#86efac" };
    if (id === "error") return { Icon: AlertTriangleIcon, tone: "#fca5a5" };
    return { Icon: PlayCircleIcon, tone: "#fcd34d" };
  };

  // Map a publish stage ID to a Lucide icon. Mirrors the stages emitted by
  // resolvePublishProgressStage so the publish modal shows a topical icon
  // at every step instead of one generic glyph.
  const getPublishStageMeta = (stageId = "") => {
    const id = String(stageId || "").toLowerCase();
    if (id === "queued" || id === "queued-remote") return { Icon: ClockIcon, tone: A[300] };
    if (id === "draft-start") return { Icon: PlayCircleIcon, tone: A[300] };
    if (id === "attempt") return { Icon: PlayCircleIcon, tone: A[300] };
    if (id === "retry") return { Icon: RefreshIcon, tone: "#fcd34d" };
    if (id === "browser") return { Icon: MonitorIcon, tone: A[400] };
    if (id === "form-ready") return { Icon: FileIcon, tone: A[200] };
    if (id === "form-fill") return { Icon: EditIcon, tone: A[300] };
    if (id === "form-finalize") return { Icon: SettingsIcon, tone: A[300] };
    if (id === "images-upload") return { Icon: UploadIcon, tone: "#fcd34d" };
    if (id === "images-ready") return { Icon: ImageIcon, tone: "#86efac" };
    if (id === "pre-submit") return { Icon: ShieldCheckIcon, tone: A[300] };
    if (id === "submit") return { Icon: SendIcon, tone: A[300] };
    if (id === "done") return { Icon: SuccessIcon, tone: "#86efac" };
    if (id === "cancel-requested" || id === "cancelled") return { Icon: XIcon, tone: "#fca5a5" };
    return { Icon: PlayCircleIcon, tone: A[300] };
  };

  // Ordered list of warm-up phases used by the mini-step strip in the modal.
  // Keep in sync with phases the backend reports.
  const WARMUP_STEP_ORDER = ["browser-start", "homepage", "own-ads", "category", "view-ad", "messages"];

  // Trigger a warm-up session through the account's proxy. Launches a real
  // headless Chromium with the account's cookies — realistic JS execution,
  // TLS fingerprint, and Akamai BMP cookie negotiation. Opens a progress
  // modal that polls /warmup/:sessionId until done/error.
  // Hands the stored cookies back so they can be backed up or moved elsewhere.
  // Copied straight to the clipboard rather than shown on screen — they are a
  // live session, and putting them in the page just invites a shoulder to read
  // them. The clipboard API needs a secure context, so there is a fallback.
  const setEmailNotifyBusy = (accountId, busy) => {
    setEmailNotifyBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(accountId); else next.delete(accountId);
      return next;
    });
  };

  const applyAccountFromResponse = (updatedAccount) => {
    if (!updatedAccount?.id) return false;
    setAccounts((prev) => prev.map((item) => (item.id === updatedAccount.id ? { ...item, ...updatedAccount } : item)));
    return true;
  };

  // Письма о новых сообщениях — переключатель со страницы настроек Kleinanzeigen.
  // Состояние читается с KL, когда меню аккаунта открывают, а панель его ещё не
  // знает; после этого оно хранится на аккаунте и меняется только нажатием.
  const loadEmailNotifications = async (account) => {
    if (!account?.id || emailNotifyBusyIds.has(account.id)) return;
    setEmailNotifyBusy(account.id, true);
    try {
      const result = await apiFetchJson(`/api/accounts/${encodeURIComponent(account.id)}/email-notifications`, { timeoutMs: 45000 });
      if (!applyAccountFromResponse(result?.account)) {
        setAccounts((prev) => prev.map((item) => (item.id === account.id ? { ...item, emailNotifications: Boolean(result?.enabled) } : item)));
      }
    } catch (error) {
      // KL не принял сессию — сервер уже проверил вход аккаунта и прислал его
      // с новым статусом (Banned, если вход сброшен).
      applyAccountFromResponse(error?.data?.account);
      handleAuthError(error);
      pushToast({ variant: "warning", title: t("Письма о сообщениях"), body: error?.message || t("Не удалось прочитать настройку на Kleinanzeigen.") });
    } finally {
      setEmailNotifyBusy(account.id, false);
    }
  };

  const handleToggleEmailNotifications = async (account) => {
    if (!account?.id || emailNotifyBusyIds.has(account.id)) return;
    // Пока состояние неизвестно, нажатие его читает, а не выключает вслепую.
    if (typeof account.emailNotifications !== "boolean") {
      await loadEmailNotifications(account);
      return;
    }
    const enabled = !account.emailNotifications;
    setEmailNotifyBusy(account.id, true);
    try {
      const result = await apiFetchJson(`/api/accounts/${encodeURIComponent(account.id)}/email-notifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
        timeoutMs: 60000
      });
      if (!applyAccountFromResponse(result?.account)) {
        setAccounts((prev) => prev.map((item) => (item.id === account.id ? { ...item, emailNotifications: enabled } : item)));
      }
      pushToast({
        variant: "success",
        title: t("Письма о сообщениях"),
        body: enabled ? t("Kleinanzeigen снова будет присылать письма о новых сообщениях.") : t("Kleinanzeigen больше не будет присылать письма о новых сообщениях.")
      });
    } catch (error) {
      applyAccountFromResponse(error?.data?.account);
      handleAuthError(error);
      pushToast({ variant: "error", title: t("Письма о сообщениях"), body: error?.message || t("Не удалось изменить настройку на Kleinanzeigen.") });
    } finally {
      setEmailNotifyBusy(account.id, false);
    }
  };

  // Меню аккаунта открыли, а переключатель писем панель ещё не знает — читаем.
  useEffect(() => {
    if (openAccountMenuId === null || openAccountMenuId === undefined) return;
    const account = accounts.find((item) => item.id === openAccountMenuId);
    if (!account || typeof account.emailNotifications === "boolean") return;
    if (!account.proxy || emailNotifyBusyIds.has(account.id)) return;
    loadEmailNotifications(account);
  }, [openAccountMenuId]);

  const handleCopyAccountCookies = async (account) => {
    if (copyingCookiesId) return;
    setOpenAccountMenuId(null);
    setCopyingCookiesId(account.id);
    try {
      const result = await apiFetchJson(`/api/accounts/${encodeURIComponent(account.id)}/cookies`, { timeoutMs: 20000 });
      const text = String(result?.text || "").trim();
      if (!text) throw new Error(t("Пустой ответ"));

      let copied = false;
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch (clipboardError) {
        const holder = document.createElement("textarea");
        holder.value = text;
        holder.setAttribute("readonly", "");
        holder.style.position = "fixed";
        holder.style.opacity = "0";
        document.body.appendChild(holder);
        holder.select();
        try { copied = document.execCommand("copy"); } catch (fallbackError) { copied = false; }
        document.body.removeChild(holder);
      }

      pushToast(copied
        ? { variant: "success", title: t("Cookies скопированы"), body: tf`${result?.count || 0} шт. в буфере обмена` }
        : { variant: "error", title: "Cookies", body: t("Браузер не дал доступ к буферу обмена") });
    } catch (error) {
      pushToast({ variant: "error", title: "Cookies", body: error?.message || t("Не удалось получить cookies") });
    } finally {
      setCopyingCookiesId(null);
    }
  };

  const handleAccountWarmup = async (account) => {
    if (!account?.id) return;
    setOpenAccountMenuId(null);
    if (!account.proxyId) {
      alert(t("У аккаунта не назначен прокси — прогрев невозможен."));
      return;
    }

    // Cancel any previous polling loop before starting a new one.
    if (warmupPollAbortRef.current) {
      warmupPollAbortRef.current.cancelled = true;
    }
    const abortFlag = { cancelled: false };
    warmupPollAbortRef.current = abortFlag;

    const accountLabel = String(
      account.profileEmail
        || account.profileName
        || account.username
        || tf`Аккаунт #${account.id}`
    ).trim();

    setWarmupProgressModal({
      open: true,
      sessionId: "",
      accountId: account.id,
      accountLabel,
      phase: "browser-start",
      label: t("Запуск браузера..."),
      percent: 0,
      error: "",
      result: null,
      done: false,
      startedAt: Date.now()
    });

    let sessionId = "";
    try {
      const startResp = await apiFetchJson(`/api/accounts/${account.id}/warmup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        timeoutMs: 15000
      });
      sessionId = String(startResp?.sessionId || "").trim();
      if (!sessionId) throw new Error(t("Не получили sessionId"));
      setWarmupProgressModal((prev) => ({ ...prev, sessionId }));
    } catch (error) {
      const msg = error?.data?.error || error?.message || t("Не удалось запустить прогрев");
      setWarmupProgressModal((prev) => ({
        ...prev,
        error: msg,
        done: true,
        phase: "error",
        label: t("Ошибка запуска")
      }));
      handleAuthError(error);
      return;
    }

    // Poll the session every 500ms until completion or error.
    // Browser session can take up to ~90s (real Chromium + multi-page reads).
    const pollIntervalMs = 500;
    const maxPollDurationMs = 300_000;
    const pollStartedAt = Date.now();
    while (!abortFlag.cancelled) {
      if (Date.now() - pollStartedAt > maxPollDurationMs) {
        setWarmupProgressModal((prev) => ({
          ...prev,
          error: t("Превышено время ожидания прогрева (5 мин)"),
          done: true,
          phase: "error"
        }));
        return;
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      if (abortFlag.cancelled) return;
      try {
        const status = await apiFetchJson(
          `/api/accounts/${account.id}/warmup/${sessionId}`,
          { method: "GET", timeoutMs: 10000 }
        );
        if (abortFlag.cancelled) return;
        setWarmupProgressModal((prev) => ({
          ...prev,
          phase: String(status?.phase || prev.phase || ""),
          label: String(status?.label || prev.label || ""),
          percent: Math.max(prev.percent, Number(status?.percent) || 0),
          error: String(status?.error || ""),
          result: status?.result || null,
          done: Boolean(status?.done)
        }));
        if (status?.done) {
          // Sync the account's updated warmup fields back into the list.
          if (status?.result?.account) {
            setAccounts((prev) => prev.map((item) =>
              item.id === account.id ? { ...item, ...status.result.account } : item
            ));
          }
          return;
        }
      } catch (error) {
        // Transient poll error — keep trying unless we're past the cap.
        if (Date.now() - pollStartedAt > maxPollDurationMs) {
          setWarmupProgressModal((prev) => ({
            ...prev,
            error: error?.data?.error || error?.message || t("Ошибка опроса прогресса"),
            done: true,
            phase: "error"
          }));
          handleAuthError(error);
          return;
        }
      }
    }
  };

  const handleCloseWarmupModal = () => {
    if (warmupPollAbortRef.current) {
      warmupPollAbortRef.current.cancelled = true;
      warmupPollAbortRef.current = null;
    }
    setWarmupProgressModal({
      open: false,
      sessionId: "",
      accountId: null,
      accountLabel: "",
      phase: "",
      label: "",
      percent: 0,
      error: "",
      result: null,
      done: false,
      startedAt: 0
    });
  };

  const handleSelectAccountProxy = async (proxy) => {
    const account = proxyPickerAccount;
    if (!account?.id || !proxy?.id) return;

    const selectedProxyId = String(proxy.id || "").trim();
    if (!selectedProxyId) return;

    // Массовый выбор: тот же запрос, но по всем отмеченным аккаунтам. Аккаунты,
    // у которых этот прокси уже стоит, пропускаются — менять нечего.
    if (proxyPickerBulkIds.length) {
      const targets = (Array.isArray(accounts) ? accounts : [])
        .filter((item) => proxyPickerBulkIds.includes(item.id))
        .filter((item) => String(item.proxyId || "").trim() !== selectedProxyId);
      if (!targets.length) {
        setProxyPickerAccount(null);
        setProxyPickerBulkIds([]);
        return;
      }
      setUpdatingAccountProxyId(selectedProxyId);
      let changed = 0;
      try {
        await runInBatches(targets, async (item) => {
          try {
            const result = await apiFetchJson(`/api/accounts/${item.id}/proxy`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ proxyId: selectedProxyId }),
              timeoutMs: 30000
            });
            if (!result?.success) return;
            changed += 1;
            if (result?.account) {
              setAccounts((prev) => prev.map((acc) => (acc.id === item.id ? { ...acc, ...result.account } : acc)));
            }
          } catch (error) {
            handleAuthError(error);
          }
        });
        if (!changed) await loadAccountMetrics();
        pushToast({
          variant: changed === targets.length ? "success" : "error",
          title: t("Смена прокси"),
          body: tf`Прокси сменён у ${changed} из ${targets.length}`
        });
      } finally {
        setUpdatingAccountProxyId("");
        setProxyPickerAccount(null);
        setProxyPickerBulkIds([]);
      }
      return;
    }

    const currentProxyId = String(account.proxyId || "").trim();
    if (currentProxyId && currentProxyId === selectedProxyId) {
      setProxyPickerAccount(null);
      return;
    }

    setUpdatingAccountProxyId(selectedProxyId);
    try {
      const result = await apiFetchJson(`/api/accounts/${account.id}/proxy`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proxyId: selectedProxyId }),
        timeoutMs: 30000
      });
      if (!result?.success) {
        alert(result?.error || t("Не удалось сменить прокси аккаунта"));
        return;
      }

      if (result?.account) {
        setAccounts((prev) => prev.map((item) => (item.id === account.id ? { ...item, ...result.account } : item)));
      } else {
        await loadAccountMetrics();
      }
      setProxyPickerAccount(null);
      alert(result?.changed === false ? t("Прокси уже привязан к этому аккаунту.") : t("Прокси аккаунта обновлен."));
    } catch (error) {
      handleAuthError(error);
      alert(error?.data?.error || error?.message || t("Не удалось сменить прокси аккаунта"));
    } finally {
      setUpdatingAccountProxyId("");
    }
  };

  // Встроенный прокси Umbrella: пользователь не выбирает его из пула, а жмёт
  // «Сменить» — сервер даёт следующий прокси по кругу, пропуская неработающие
  // (POST /api/accounts/:id/proxy/builtin-next). Тот же запрос подключает
  // Umbrella аккаунту, который был на своём прокси.
  const requestNextBuiltinProxy = (accountId) => apiFetchJson(`/api/accounts/${accountId}/proxy/builtin-next`, {
    method: "POST",
    timeoutMs: 30000,
    retry: false
  });
  // Встроенный прокси, на котором стоит аккаунт, или null — по списку прокси
  // (там встроенные помечены isBuiltin).
  const findBuiltinProxy = (proxyId) => (Array.isArray(proxies) ? proxies : [])
    .find((item) => item?.isBuiltin && String(item.id) === String(proxyId || "").trim()) || null;
  const describeBuiltinPlace = (proxy) => String(proxy?.checkResult?.location?.city || "").trim() || "Umbrella";
  const swapBuiltinProxy = async (account, { closePicker = false } = {}) => {
    const accountId = account?.id;
    if (!accountId || swappingProxyAccountIds.has(accountId)) return;
    setSwappingProxyAccountIds((prev) => new Set(prev).add(accountId));
    try {
      const result = await requestNextBuiltinProxy(accountId);
      if (result?.account) {
        setAccounts((prev) => prev.map((item) => (item.id === accountId ? { ...item, ...result.account } : item)));
        // Окно выбора остаётся открытым — в строке Umbrella сразу новый город.
        setProxyPickerAccount((prev) => (prev && prev.id === accountId ? { ...prev, ...result.account } : prev));
      }
      if (closePicker) setProxyPickerAccount(null);
      const from = result?.builtinFrom ? describeBuiltinPlace(result.builtinFrom) : "";
      const to = describeBuiltinPlace(result?.builtinTo);
      const ping = Number(result?.builtinTo?.checkResult?.responseTime);
      pushToast({
        variant: "success",
        title: from ? t("Прокси сменён") : t("Подключён прокси Umbrella"),
        body: [from ? `${from} → ${to}` : to, ping > 0 ? tf`${Math.round(ping)} мс` : ""].filter(Boolean).join(" · ")
      });
    } catch (error) {
      handleAuthError(error);
      pushToast({
        variant: "error",
        title: t("Смена прокси"),
        body: error?.data?.error || error?.message || t("Не удалось сменить прокси аккаунта")
      });
    } finally {
      setSwappingProxyAccountIds((prev) => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  // Массовый выбор Umbrella: каждый отмеченный аккаунт получает следующий
  // прокси по кругу — так они расходятся по пулу. Уже стоящие на Umbrella
  // не трогаются.
  const connectBulkToBuiltinProxy = async () => {
    const targets = (Array.isArray(accounts) ? accounts : [])
      .filter((item) => proxyPickerBulkIds.includes(item.id))
      .filter((item) => !findBuiltinProxy(item.proxyId));
    if (!targets.length) {
      setProxyPickerAccount(null);
      setProxyPickerBulkIds([]);
      return;
    }
    setUpdatingAccountProxyId(BUILTIN_PROXY_PICKER_KEY);
    let changed = 0;
    try {
      // Пачкой, но каждый всё равно берёт следующий прокси по кругу: сервер
      // выдаёт их по одному на запрос.
      await runInBatches(targets, async (item) => {
        try {
          const result = await requestNextBuiltinProxy(item.id);
          if (!result?.success) return;
          changed += 1;
          if (result?.account) {
            setAccounts((prev) => prev.map((acc) => (acc.id === item.id ? { ...acc, ...result.account } : acc)));
          }
        } catch (error) {
          handleAuthError(error);
        }
      });
      pushToast({
        variant: changed === targets.length ? "success" : "error",
        title: t("Смена прокси"),
        body: tf`Прокси сменён у ${changed} из ${targets.length}`
      });
    } finally {
      setUpdatingAccountProxyId("");
      setProxyPickerAccount(null);
      setProxyPickerBulkIds([]);
    }
  };

  const handleDeleteProxy = async (id) => {
    const confirmed = await requestConfirmModal({
      title: t("Удалить прокси"),
      confirmText: t("Удалить"),
      cancelText: t("Отмена"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;

    try {
      const result = await apiFetchJson(`/api/proxies/${id}`, {
        method: "DELETE"
      });
      if (result.success) {
        setProxies(proxies.filter(proxy => proxy.id !== id));
        alert(t("Прокси удален"));
        loadData();
      }
    } catch (error) {
      handleAuthError(error);
      alert(t("Ошибка при удалении прокси"));
    }
  };

  const handleProxyCheckComplete = (proxyId, result) => {
    // Обновляем статус прокси в списке
    setProxies(prev => prev.map(proxy => {
      if (proxy.id === proxyId) {
        return {
          ...proxy,
          status: result.success ? "active" : "failed",
          lastChecked: new Date().toISOString(),
          checkResult: result
        };
      }
      return proxy;
    }));

    // Обновляем статистику
    loadData();
  };

  const checkAllProxies = async () => {
    setCheckingAllProxies(true);
    try {
      const result = await apiFetchJson("/api/proxies/check-all", {
        method: "POST"
      });
      if (result.success) {
        // Обновляем список прокси с новыми данными
        loadData();
        pushToast({ variant: "success", title: t("Прокси"), body: t("Все прокси проверены.") });
      }
    } catch (error) {
      handleAuthError(error);
      pushToast({
        variant: "error",
        title: t("Прокси"),
        body: error?.data?.error || error?.message || t("Ошибка при проверке прокси")
      });
    } finally {
      setCheckingAllProxies(false);
    }
  };

  const loadCategories = async (forceRefresh = false) => {
    setLoadingCategories(true);
    try {
      const data = await apiFetchJson(`/api/categories${forceRefresh ? "?refresh=true" : ""}`);
      const items = Array.isArray(data?.categories) ? data.categories : [];
      setCategories(items);
      setCategoriesUpdatedAt(data?.updatedAt || null);
    } catch (error) {
      handleAuthError(error);
      console.error("Ошибка загрузки категорий:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Категория и её параметры между открытиями окна не сбрасываются — как
  // название и описание: закрыть окно крестиком или Escape и вернуться можно
  // без потерь. Форму чистят только публикация и сохранение черновика.
  useEffect(() => {
    if (showAdModal) {
      loadCategories(false);
    }
  }, [showAdModal]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchExtraFields = async () => {
      if (!showAdModal) return;
      // No account yet is fine: the backend can serve cached category fields.
      if (!newAd.categoryId && !newAd.categoryUrl) {
        setLoadingExtraFields(false);
        setExtraFields([]);
        setExtraFieldValues({});
        setExtraFieldsError("");
        return;
      }

      setLoadingExtraFields(true);
      setExtraFieldsError("");
      try {
        const buildFieldsRequestUrl = ({ forceRefresh = false, includeCategoryPath = true, live = false } = {}) => {
          const params = new URLSearchParams();
          if (newAd.accountId) params.set("accountId", newAd.accountId);
          if (newAd.categoryId) params.set("categoryId", newAd.categoryId);
          if (!newAd.categoryId && newAd.categoryUrl) params.set("categoryUrl", newAd.categoryUrl);
          if (includeCategoryPath && Array.isArray(newAd.categoryPath) && newAd.categoryPath.length > 0) {
            params.set("categoryPath", JSON.stringify(newAd.categoryPath));
          }
          if (forceRefresh) {
            params.set("refresh", "true");
          }
          if (live) {
            params.set("live", "1");
          }
          if (debugFieldsEnabled) {
            params.set("debug", "1");
          }
          return `/api/ads/fields?${params.toString()}`;
        };

        const requestOptions = {
          signal: controller.signal,
          timeoutMs: 120000,
          retry: false,
          allowBaseFallback: true
        };

        const hasCategoryPath = Array.isArray(newAd.categoryPath) && newAd.categoryPath.length > 0;
        const shouldRetryWithoutCategoryPath = (error) => {
          const code = String(error?.code || "").trim().toUpperCase();
          if (code === "REQUEST_TIMEOUT") return false;
          if (code === "FETCH_FAILED") return true;
          const message = String(error?.details || error?.message || "").toLowerCase();
          // Fallback without categoryPath only for proxy/gateway-style failures.
          // For real backend timeouts we must keep categoryPath, otherwise fake-L3 categories degrade.
          if (message.includes("timeout")) return false;
          return /failed to fetch|network error|networkerror|load failed|connection reset/i.test(message);
        };

        const requestFields = async ({ forceRefresh = false, live = false } = {}) => {
          try {
            return await apiFetchJson(
              buildFieldsRequestUrl({ forceRefresh, includeCategoryPath: true, live }),
              requestOptions
            );
          } catch (error) {
            // Some reverse proxies choke on JSON-like query params in GET.
            // Fallback to a lean query without categoryPath.
            if (!hasCategoryPath || !shouldRetryWithoutCategoryPath(error)) throw error;
            return apiFetchJson(
              buildFieldsRequestUrl({ forceRefresh, includeCategoryPath: false, live }),
              requestOptions
            );
          }
        };

        // Single request with live=1: backend returns from cache if available,
        // otherwise does a live browser fetch. No need for two sequential requests.
        let data = await requestFields({ forceRefresh: false, live: true });
        if (cancelled) return;
        if (data?.success === false) {
          const errorMessage = data?.error || t("Ошибка загрузки параметров категории");
          setExtraFields([]);
          setExtraFieldValues({});
          setExtraFieldsError(errorMessage);
          return;
        }
        let fields = Array.isArray(data?.fields) ? data.fields : [];

        if (data?.debugId) {
          console.log("[fields] response", {
            debugId: data.debugId,
            accountId: newAd.accountId,
            categoryId: newAd.categoryId || "",
            categoryUrl: newAd.categoryUrl || "",
            cached: Boolean(data.cached),
            fieldCount: fields.length
          });
        }

        setExtraFields(fields);
        setExtraFieldsError("");
        // От текущих значений, а не от снимка на момент запроса: поля
        // перезагружаются и при повторном открытии окна, и заполненное за
        // время загрузки не должно пропасть.
        setExtraFieldValues((prev) => {
          const nextValues = {};
          fields.forEach((field) => {
            const key = field.name || field.label;
            if (!key) return;
            nextValues[key] = (prev && prev[key]) || "";
          });
          return nextValues;
        });
      } catch (error) {
        if (cancelled) return;
        if (error?.name === "AbortError") return;
        handleAuthError(error);
        setExtraFields([]);
        setExtraFieldValues({});
        const debugId = error?.data?.debugId ? ` [debugId: ${error.data.debugId}]` : "";
        const errorMessage = (error?.message || t("Ошибка загрузки параметров категории")) + debugId;
        setExtraFieldsError(errorMessage);
      } finally {
        if (cancelled) return;
        setLoadingExtraFields(false);
      }
    };
    fetchExtraFields();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [showAdModal, newAd.accountId, newAd.categoryId, newAd.categoryKey, newAd.categoryUrl, newAd.categoryPath]);

  const handleTabSelect = (tabId) => {
    const nextTabId = freezeAccessLocked && tabId !== "settings" ? "settings" : tabId;
    const nextPath = TAB_ROUTE_MAP[nextTabId] || TAB_ROUTE_MAP.dashboard;
    if (freezeAccessLocked && tabId !== "settings") {
      pushToast({
        variant: "warning",
        title: t("Подписка на заморозке"),
        body: t("Разделы временно заблокированы. Перейдите в Настройки и отключите заморозку."),
        durationMs: 4200
      });
      if (isPhoneView) {
        setIsMobileNavOpen(false);
      }
    }
    if (activeTab !== nextTabId) {
      setActiveTab(nextTabId);
    }
    if (normalizePathname(location.pathname) !== normalizePathname(nextPath)) {
      navigate(nextPath);
    }
    if (isPhoneView) {
      setIsMobileNavOpen(false);
    }
  };

  const clampPublishPercent = (value) => Math.max(0, Math.min(100, Number(value || 0)));

  const resolvePublishProgressStage = (step = "") => {
    const normalized = String(step || "").trim().toLowerCase();
    if (!normalized || normalized === "queued") {
      return {
        id: "queued",
        minPercent: 4,
        maxPercent: 14,
        statusText: t("Ставим публикацию в очередь")
      };
    }
    if (normalized.includes("draft-publish-start")) {
      return {
        id: "draft-start",
        minPercent: 10,
        maxPercent: 26,
        statusText: t("Запускаем публикацию черновика")
      };
    }
    if (normalized.includes("cancel-requested")) {
      return {
        id: "cancel-requested",
        minPercent: 90,
        maxPercent: 96,
        statusText: t("Останавливаем публикацию")
      };
    }
    if (normalized.includes("cancelled")) {
      return {
        id: "cancelled",
        minPercent: 100,
        maxPercent: 100,
        statusText: t("Публикация остановлена")
      };
    }
    if (normalized.includes("publish-retry") || normalized.includes("retry")) {
      return {
        id: "retry",
        minPercent: 58,
        maxPercent: 82,
        statusText: t("Повторяем публикацию после временной ошибки")
      };
    }
    if (normalized.includes("publish-attempt-start") || normalized.includes("attempt")) {
      return {
        id: "attempt",
        minPercent: 14,
        maxPercent: 24,
        statusText: t("Запускаем попытку публикации")
      };
    }
    if (
      normalized.includes("browser-launch")
      || normalized.includes("browser-ready")
      || normalized.includes("browser-session-bootstrap")
      || normalized.includes("browser-cookies-apply")
      || normalized.includes("browser")
    ) {
      return {
        id: "browser",
        minPercent: 24,
        maxPercent: 34,
        statusText: t("Подготавливаем браузерную сессию")
      };
    }
    if (
      normalized.includes("create-page-opened")
      || normalized.includes("create-page-loading")
      || normalized.includes("form-ready")
      || normalized.includes("stage-form-opened")
    ) {
      return {
        id: "form-ready",
        minPercent: 34,
        maxPercent: 44,
        statusText: t("Открываем форму объявления")
      };
    }
    if (
      normalized.includes("form-fill-start")
      || normalized.includes("fill-title")
      || normalized.includes("fill-description")
      || normalized.includes("fill-price")
      || normalized.includes("stage-base-fields")
      || normalized.includes("form-fill-base-done")
      || normalized.includes("category-ready")
      || normalized.includes("stage-category-selected")
      || normalized.includes("form-fill-done")
      || normalized.includes("form")
    ) {
      return {
        id: "form-fill",
        minPercent: 44,
        maxPercent: 64,
        statusText: t("Заполняем и валидируем поля объявления")
      };
    }
    if (
      normalized.includes("form-finalize")
      || normalized.includes("stage-extra-fields")
      || normalized.includes("feature-boost")
      || normalized.includes("direct-buy")
    ) {
      return {
        id: "form-finalize",
        minPercent: 64,
        maxPercent: 74,
        statusText: t("Подготавливаем форму перед загрузкой фото")
      };
    }
    if (normalized.includes("images-upload-start") || normalized.includes("image")) {
      return {
        id: "images-upload",
        minPercent: 64,
        maxPercent: 82,
        statusText: t("Загружаем фотографии")
      };
    }
    if (normalized.includes("images-upload-done") || normalized.includes("stage-images-ready")) {
      return {
        id: "images-ready",
        minPercent: 82,
        maxPercent: 88,
        statusText: t("Фотографии загружены")
      };
    }
    if (
      normalized.includes("pre-submit-check")
      || normalized.includes("before-submit")
      || normalized.includes("submit-prepared")
    ) {
      return {
        id: "pre-submit",
        minPercent: 88,
        maxPercent: 93,
        statusText: t("Проверяем форму перед отправкой")
      };
    }
    if (normalized.includes("submit-clicked") || normalized.includes("submit-resubmitted")) {
      return {
        id: "submit",
        minPercent: 93,
        maxPercent: 96,
        statusText: t("Отправляем объявление")
      };
    }
    if (normalized.includes("post-submit-verification") || normalized.includes("publish-success-signals")) {
      return {
        id: "post-submit",
        minPercent: 96,
        maxPercent: 97,
        statusText: t("Ждем подтверждение от Kleinanzeigen")
      };
    }
    if (
      normalized.includes("my-ads-verify")
      || normalized.includes("publish-my-ads-verification")
    ) {
      return {
        id: "post-submit",
        minPercent: 96,
        maxPercent: 97,
        statusText: t("Ждем подтверждение от Kleinanzeigen")
      };
    }
    if (normalized.includes("publish-queued")) {
      return {
        id: "queued-remote",
        minPercent: 99,
        maxPercent: 100,
        statusText: t("Объявление отправлено на модерацию")
      };
    }
    // The steps the HAR publisher actually emits. Without these they all landed in
    // the generic "in progress" band, which is why a draft publish showed one
    // unchanging line while the bar crept along — the steps were arriving and
    // meant nothing to the modal.
    //
    // Checked before the terminal branch and matched on their distinctive part,
    // so "submit-done" reads as "sent, waiting" rather than as "finished".
    if (normalized.startsWith("form-fetch")) {
      return { id: "form", minPercent: 8, maxPercent: 20, statusText: t("Открываем форму публикации") };
    }
    if (normalized.startsWith("category-apply")) {
      return { id: "category", minPercent: 20, maxPercent: 34, statusText: t("Выбираем категорию") };
    }
    if (normalized.startsWith("images-upload") || normalized.startsWith("image-upload")) {
      return { id: "images", minPercent: 34, maxPercent: 72, statusText: t("Загружаем фотографии") };
    }
    if (normalized.startsWith("submit-token-refresh")) {
      return { id: "submit-retry", minPercent: 72, maxPercent: 80, statusText: t("Обновляем токен и повторяем отправку") };
    }
    if (normalized.startsWith("submit")) {
      return { id: "submit", minPercent: 76, maxPercent: 94, statusText: t("Отправляем объявление") };
    }

    // Exactly "done", not any step containing it. The publisher emits
    // form-fetch-done, category-apply-done, image-upload-done and submit-done,
    // so a substring match declared the publish finished at the first step —
    // the form having loaded — and the modal said "успешно завершена" at 45%
    // while the ad had not been submitted yet.
    //
    // The terminal step is the one the caller sends itself when the request
    // returns, and it is called "done" and nothing else.
    if (normalized === "done" || normalized === "success") {
      return {
        id: "done",
        minPercent: 100,
        maxPercent: 100,
        statusText: t("Публикация успешно завершена")
      };
    }
    return {
      id: "in-progress",
      minPercent: 26,
      maxPercent: 94,
      statusText: t("Публикация выполняется")
    };
  };

  const getPublishProgressPercent = (step = "", prevPercent = 0) => {
    const stage = resolvePublishProgressStage(step);
    const previous = clampPublishPercent(prevPercent);
    const floor = clampPublishPercent(stage.minPercent);
    const cap = clampPublishPercent(stage.maxPercent);
    if (cap >= 100 || stage.id === "done") {
      return 100;
    }
    let next = Math.max(previous, floor);
    if (next < cap) {
      const bump = next < 30
        ? 2.6
        : (next < 55 ? 1.9 : (next < 78 ? 1.3 : 0.85));
      next = Math.min(cap, next + bump);
    }
    return Math.max(previous, next);
  };

  const getPublishProgressStatusText = (step = "") => resolvePublishProgressStage(step).statusText;

  const normalizePostPublishState = (value = "") => {
    const token = String(value || "").trim().toLowerCase();
    if (!token) return "unknown";
    if (token === "under_review" || token === "active" || token === "deleted" || token === "unknown") {
      return token;
    }
    if (token === "published") return "active";
    if (token === "rejected" || token === "removed") return "deleted";
    if (/(under[\s_-]*review|review|pending|moderation|bald[\s_-]*online|in[\s_-]*pr[üu]fung)/.test(token)) {
      return "under_review";
    }
    if (/(active|aktiv|online|live|reserviert|reserved)/.test(token)) {
      return "active";
    }
    if (/(deleted|removed|rejected|gel[öo]scht|inaktiv|inactive|abgelehnt|deactivated|expired)/.test(token)) {
      return "deleted";
    }
    return "unknown";
  };

  const mapPublicationStatusToPostPublishState = (value = "") => {
    return normalizePostPublishState(value);
  };

  const resolvePostPublishStatusMeta = (probe = null, fallbackPublicationStatus = "") => {
    const fallbackState = mapPublicationStatusToPostPublishState(fallbackPublicationStatus);
    const probeObject = probe && typeof probe === "object" ? probe : null;
    const normalizedProbeState = normalizePostPublishState(probeObject?.state || "");
    const state = normalizedProbeState !== "unknown" ? normalizedProbeState : fallbackState;
    const effectiveState = state || "unknown";
    if (effectiveState === "under_review") {
      return {
        state: effectiveState,
        title: t("На модерации"),
        tone: "#fef08a",
        border: "rgba(250,204,21,0.36)",
        background: "linear-gradient(135deg, rgba(113,63,18,0.3), rgba(23,25,33,0.34))"
      };
    }
    if (effectiveState === "active") {
      return {
        state: effectiveState,
        title: t("Активно"),
        tone: "#86efac",
        border: "rgba(74,222,128,0.34)",
        background: "linear-gradient(135deg, rgba(6,95,70,0.3), rgba(23,25,33,0.34))"
      };
    }
    if (effectiveState === "deleted") {
      return {
        state: effectiveState,
        title: t("Удалено"),
        tone: "#fca5a5",
        border: "rgba(248,113,113,0.34)",
        background: "linear-gradient(135deg, rgba(127,29,29,0.3), rgba(23,25,33,0.34))"
      };
    }
    return {
      state: "unknown",
      title: t("Статус не подтвержден"),
      tone: "#c6c9d1",
      border: "rgba(141,147,163,0.3)",
      background: S.surface
    };
  };

  const updatePublishProgressModal = ({
    requestId = "",
    adTitle = undefined,
    accountLabel = undefined,
    step = "",
    forcePercent = null,
    forceStatusText = "",
    error = "",
    publicationStatus = undefined,
    publicationProbe = undefined,
    resultUrl = undefined
  } = {}) => {
    setPublishProgressModal((prev) => {
      const nextRequestId = String(requestId || prev.requestId || "").trim();
      const prevRequestId = String(prev.requestId || "").trim();
      if (nextRequestId && prevRequestId && nextRequestId !== prevRequestId) {
        // Ignore stale async updates from previous publish attempts.
        return prev;
      }
      const nextStep = String(step || prev.step || "queued").trim();
      const stage = resolvePublishProgressStage(nextStep);
      let nextPercent = Number.isFinite(Number(forcePercent))
        ? Math.max(Number(prev.percent || 0), Math.max(0, Math.min(100, Number(forcePercent))))
        : getPublishProgressPercent(nextStep, Number(prev.percent || 0));
      const isTerminalPublishStage = stage.id === "done" || stage.id === "cancelled" || stage.id === "queued-remote";
      if (!String(error || "").trim() && !isTerminalPublishStage) {
        nextPercent = Math.min(99, nextPercent);
      }
      const nextStatus = String(forceStatusText || getPublishProgressStatusText(nextStep)).trim();
      const nextPublicationProbe = publicationProbe === undefined
        ? (prev.publicationProbe ?? null)
        : (publicationProbe || null);
      const nextPublicationStatus = publicationStatus === undefined
        ? String(prev.publicationStatus || "").trim()
        : String(publicationStatus || "").trim();
      const nextAdTitle = adTitle === undefined
        ? String(prev.adTitle || "").trim()
        : String(adTitle || "").trim();
      const nextAccountLabel = accountLabel === undefined
        ? String(prev.accountLabel || "").trim()
        : String(accountLabel || "").trim();
      return {
        ...prev,
        open: true,
        requestId: nextRequestId,
        adTitle: nextAdTitle,
        accountLabel: nextAccountLabel,
        step: nextStep || prev.step || "queued",
        percent: nextPercent,
        statusText: nextStatus,
        error: String(error || ""),
        publicationStatus: nextPublicationStatus,
        publicationProbe: nextPublicationProbe,
        resultUrl: resultUrl === undefined ? String(prev.resultUrl || "") : String(resultUrl || "").trim(),
        updatedAt: Date.now()
      };
    });
  };

  const resetPublishProgressModal = () => {
    setClosingPublishProgressModal(false);
    if (publishCoverObjectUrlRef.current) {
      try { URL.revokeObjectURL(publishCoverObjectUrlRef.current); } catch { /* уже отпущена */ }
      publishCoverObjectUrlRef.current = "";
    }
    setPublishProgressModal({
      open: false,
      requestId: "",
      adTitle: "",
      accountLabel: "",
      step: "idle",
      percent: 0,
      statusText: "",
      error: "",
      publicationStatus: "",
      publicationProbe: null,
      startedAt: 0,
      updatedAt: 0
    });
  };

  const waitForCreateStatusDone = async (requestId, maxWaitMs = 8 * 60 * 1000, onStatus = null) => {
    const normalizedRequestId = String(requestId || "").trim();
    if (!normalizedRequestId) return null;
    const startedAt = Date.now();
    while (Date.now() - startedAt < maxWaitMs) {
      try {
        const status = await apiFetchJson(`/api/ads/create-status/${encodeURIComponent(normalizedRequestId)}`, {
          timeoutMs: 15000,
          retry: true
        });
        if (typeof onStatus === "function" && status?.success) {
          onStatus(status);
        }
        if (status?.success && status?.done) {
          return status?.result || null;
        }
      } catch (statusError) {
        if (statusError?.status && statusError.status !== 404) {
          console.error("[publish-progress] Ошибка ожидания create-status:", statusError);
        }
      }
      await wait(850);
    }
    return null;
  };

  const handleClosePublishProgressModal = async () => {
    if (closingPublishProgressModal) return;
    const requestId = String(publishProgressModal.requestId || "").trim();
    if (!requestId || requestId.startsWith("draft-")) {
      resetPublishProgressModal();
      return;
    }

    setClosingPublishProgressModal(true);
    try {
      let status = null;
      try {
        status = await apiFetchJson(`/api/ads/create-status/${encodeURIComponent(requestId)}`, {
          timeoutMs: 12000,
          retry: true
        });
      } catch (statusError) {
        if (statusError?.status && statusError.status !== 404) {
          console.error("[publish-progress] Ошибка запроса текущего create-status:", statusError);
        }
      }

      if (status?.success && !status?.done) {
        updatePublishProgressModal({
          requestId,
          step: "publish-cancel-requested",
          forceStatusText: t("Останавливаем публикацию")
        });
        const activeAbortController = activeCreateRequestAbortRef.current;
        if (
          activeCreateRequestIdRef.current === requestId
          && activeAbortController
          && !activeAbortController.signal.aborted
        ) {
          activeAbortController.abort();
        }
        try {
          await apiFetchJson("/api/ads/create-cancel", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ requestId }),
            timeoutMs: 15000,
            retry: true
          });
        } catch (cancelError) {
          console.error("[publish-progress] Ошибка отмены публикации:", cancelError);
        }

        const finalResult = await waitForCreateStatusDone(requestId, 90 * 1000, (nextStatus) => {
          updatePublishProgressModal({
            requestId,
            step: String(nextStatus?.progressStep || "publish-cancel-requested").trim()
          });
        });

        if (finalResult?.success) {
          updatePublishProgressModal({
            requestId,
            step: "done",
            forcePercent: 100,
            forceStatusText: t("Публикация успешно завершена")
          });
          pushToast({
            variant: "info",
            title: t("Публикация"),
            body: t("Объявление уже успело завершить публикацию перед отменой. Проверьте статус во вкладке «Объявления»."),
            durationMs: 8500
          });
          return;
        }

        updatePublishProgressModal({
          requestId,
          step: "done",
          forceStatusText: t("Публикация остановлена"),
          error: String(finalResult?.error || t("Публикация была остановлена пользователем."))
        });
        return;
      }

      resetPublishProgressModal();
    } finally {
      setClosingPublishProgressModal(false);
    }
  };

  // С экрана «Объявление опубликовано»: к списку объявлений или сразу новое.
  const handlePublishedNext = async (target) => {
    await handleClosePublishProgressModal();
    if (target === "ads") {
      navigate(TAB_ROUTE_MAP["active-ads"]);
      return;
    }
    if (target === "new" && !freezeAccessLocked) {
      setDraftImageUrls([]);
      setAdPhotoOrder([]);
      setShowAdModal(true);
    }
  };

  const handlePublishDraftNow = async ({ draftId = "" } = {}) => {
    const normalizedDraftId = String(draftId || "").trim();
    if (!normalizedDraftId) {
      throw new Error(t("Некорректный идентификатор черновика"));
    }
    // Publishing is not something to start twice. Nothing here refused a second
    // call while the first was still running, and the cost of one is not a
    // duplicated request but a duplicated ad — which KL then has grounds to
    // reject, making the second attempt the reason the first one failed.
    if (publishingAd) {
      return;
    }
    const progressRequestId = `draft-${normalizedDraftId}-${Date.now().toString(36)}`;
    let progressTickTimer = null;
    const stopProgressTicker = () => {
      if (progressTickTimer) {
        window.clearInterval(progressTickTimer);
        progressTickTimer = null;
      }
    };
    try {
      setPublishingAd(true);
      setShowAdModal(false);
      setPublishProgressModal({
        open: true,
        requestId: progressRequestId,
        step: "draft-publish-start",
        percent: 10,
        statusText: t("Запускаем публикацию черновика"),
        error: "",
        publicationStatus: "",
        publicationProbe: null,
        startedAt: Date.now(),
        updatedAt: Date.now()
      });
      progressTickTimer = window.setInterval(() => {
        setPublishProgressModal((prev) => {
          if (!prev.open || prev.error) return prev;
          const current = Number(prev.percent || 0);
          if (current >= 92) return prev;
          const bump = current < 38 ? 4 : (current < 70 ? 3 : 1);
          return {
            ...prev,
            percent: Math.min(92, current + bump),
            statusText: current < 52
              ? t("Заполняем и отправляем форму")
              : t("Ожидаем ответ Kleinanzeigen"),
            updatedAt: Date.now()
          };
        });
      }, 900);

      // The id travels with the request, so the backend reports progress under the
      // same one this modal is watching. It used to generate an id, keep it to
      // itself, and animate a bar that had nothing behind it.
      const publishPromise = apiFetchJson(`/api/ad-drafts/${encodeURIComponent(normalizedDraftId)}/publish-now`, {
        method: "POST",
        headers: { "x-client-request-id": progressRequestId },
        timeoutMs: 0,
        retry: false
      });

      // Real steps while it runs, alongside the ticker rather than instead of it:
      // the ticker keeps the bar alive between steps, the steps say what is
      // happening. Any failure here is ignored on purpose — the publish is what
      // matters and it is awaited below.
      let progressPollTimer = window.setInterval(async () => {
        try {
          const status = await apiFetchJson(`/api/ads/create-status/${encodeURIComponent(progressRequestId)}`, {
            timeoutMs: 8000,
            retry: false
          });
          const step = String(status?.progressStep || "").trim();
          if (step) {
            updatePublishProgressModal({ requestId: progressRequestId, step });
          }
        } catch (_) { /* прогресс не обязателен */ }
      }, 1500);
      const stopProgressPoll = () => {
        if (progressPollTimer) {
          window.clearInterval(progressPollTimer);
          progressPollTimer = null;
        }
      };

      let result;
      try {
        result = await publishPromise;
      } finally {
        stopProgressPoll();
      }
      stopProgressTicker();
      updatePublishProgressModal({
        requestId: progressRequestId,
        step: "done",
        forcePercent: 100,
        forceStatusText: t("Черновик успешно опубликован"),
        // Same shape the direct publish passes. Without these the modal had
        // nothing to read and fell back to "Статус не подтверждён" — even when
        // the backend had probed the ad and found it Aktiv — and the title and
        // account rows had no source either.
        adTitle: String(result?.title || "").trim(),
        accountLabel: String(result?.accountLabel || "").trim(),
        resultUrl: String(result?.url || "").trim(),
        publicationStatus: String(result?.publicationStatus || "").trim() || "published",
        publicationProbe: (() => {
          const probe = result?.postPublishStatus && typeof result.postPublishStatus === "object"
            ? result.postPublishStatus
            : null;
          const mapped = mapPublicationStatusToPostPublishState(String(result?.publicationStatus || "").trim());
          const fallback = mapped === "unknown" ? "under_review" : mapped;
          const raw = normalizePostPublishState(probe?.state || "");
          return {
            ...(probe || {}),
            state: raw !== "unknown" ? raw : fallback,
            source: probe?.source || "publish-flow",
            confirmed: Boolean(probe?.confirmed),
            statusLabel: String(probe?.statusLabel || "").trim(),
            checkedAt: Number(probe?.checkedAt || Date.now())
          };
        })()
      });
      return { success: true, result };
    } catch (error) {
      stopProgressTicker();
      handleAuthError(error);
      updatePublishProgressModal({
        requestId: progressRequestId,
        step: "done",
        forceStatusText: t("Публикация черновика завершилась ошибкой"),
        error: withFieldsRefreshedHint(String(error?.message || t("Неизвестная ошибка")), error?.data)
      });
      throw error;
    } finally {
      stopProgressTicker();
      setPublishingAd(false);
    }
  };

  const handleCreateAd = async () => {
    if (publishingAd) return;
    if (!newAd.accountId) {
      alert(t("Выберите аккаунт"));
      return;
    }
    if (!newAd.title.trim() || !newAd.description.trim() || !newAd.price.trim()) {
      alert(t("Заполните все обязательные поля"));
      return;
    }
    // Без фото Kleinanzeigen объявление не публикует (опция «Galerie» требует
    // снимок) — сказать сразу, как о пустом названии, а не ошибкой сервера.
    if (!buildPhotoSequence(adPhotoOrder, draftImageUrls, adImages).length) {
      alert(t("Добавьте хотя бы одно фото"));
      return;
    }
    if (!newAd.categoryId && !newAd.categoryUrl) {
      alert(t("Выберите категорию"));
      return;
    }
    if (loadingExtraFields) {
      alert(t("Дождитесь загрузки параметров категории"));
      return;
    }
    if (extraFieldsError) {
      alert(t("Не удалось загрузить параметры категории. Публикация недоступна, пока параметры не будут загружены."));
      return;
    }
    const missingRequiredFields = (Array.isArray(extraFields) ? extraFields : [])
      .filter((field) => field?.required)
      .filter((field) => {
        const key = field.name || field.label;
        const value = key ? extraFieldValues?.[key] : "";
        return !String(value ?? "").trim();
      });
    if (missingRequiredFields.length > 0) {
      const labels = missingRequiredFields
        .slice(0, 4)
        .map((field) => field.label || field.name || t("параметр"));
      alert(tf`Заполните обязательные параметры категории: ${labels.join(", ")}`);
      return;
    }

    console.log("[handleCreateAd] Данные для публикации:", {
      accountId: newAd.accountId,
      title: newAd.title,
      categoryId: newAd.categoryId,
      categoryUrl: newAd.categoryUrl,
      price: newAd.price,
      priceType: newAd.priceType,
      postalCode: newAd.postalCode,
      extraFields: Object.keys(extraFieldValues).length,
      images: adImages.length
    });

    const publishAdTitle = String(newAd?.title || "").trim();
    const publishAccountId = String(newAd?.accountId || "").trim();
    const publishPostalCode = String(newAd?.postalCode || "").trim();
    const publishAccountLabel = (() => {
      const targetId = Number(newAd?.accountId || 0);
      if (!targetId) return "";
      const account = (Array.isArray(accounts) ? accounts : []).find((item) => Number(item?.id || 0) === targetId);
      return account ? formatAccountLabel(account) : "";
    })();

    const resetAdDraft = () => {
      setShowAdModal(false);
      setNewAd({
        draftId: "",
        accountId: "",
        title: "",
        description: "",
        price: "",
        priceType: "FIXED",
        categoryId: "",
        categoryUrl: "",
        postalCode: "",
        locationId: "",
        locationName: "",
        categoryKey: "",
        categoryPath: [],
        scheduledAt: "",
        legalInfo: ""
      });
      setAdImages([]);
      setDraftImageUrls([]);
      setAdPhotoOrder([]);
      setExtraFields([]);
      setExtraFieldValues({});
    };
    const makeClientRequestId = () => `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const isRecoverablePublishNetworkError = (err) => {
      const code = String(err?.code || "").trim().toLowerCase();
      const msg = String(err?.message || "").trim().toLowerCase();
      return (
        code === "fetch_failed" ||
        code === "request_timeout" ||
        msg.includes("failed to fetch") ||
        msg.includes("load failed") ||
        msg.includes("network error") ||
        msg.includes("connection reset") ||
        msg.includes("timeout")
      );
    };

    let modalClosedByFlow = false;
    let createFinishedFromStatus = false;
    let progressPollTimer = null;
    let progressPulseTimer = null;
    let progressPollBusy = false;
    const stopProgressPoll = () => {
      if (progressPollTimer) {
        window.clearInterval(progressPollTimer);
        progressPollTimer = null;
      }
      if (progressPulseTimer) {
        window.clearInterval(progressPulseTimer);
        progressPulseTimer = null;
      }
    };
    const closeModalOnPublished = (resultPayload = {}) => {
      if (modalClosedByFlow) return;
      modalClosedByFlow = true;
      const publicationProbe = resultPayload?.postPublishStatus && typeof resultPayload.postPublishStatus === "object"
        ? resultPayload.postPublishStatus
        : null;
      const normalizedPublicationStatus = String(resultPayload?.publicationStatus || "").trim();
      const mappedPublicationState = mapPublicationStatusToPostPublishState(normalizedPublicationStatus);
      const fallbackProbeState = mappedPublicationState === "unknown" ? "under_review" : mappedPublicationState;
      const rawProbeState = normalizePostPublishState(publicationProbe?.state || "");
      const resolvedProbeState = rawProbeState !== "unknown" ? rawProbeState : fallbackProbeState;
      updatePublishProgressModal({
        requestId: String(resultPayload?.requestId || "").trim(),
        adTitle: publishAdTitle,
        accountLabel: publishAccountLabel,
        step: "done",
        forcePercent: 100,
        forceStatusText: t("Публикация успешно завершена"),
        resultUrl: String(resultPayload?.url || "").trim(),
        publicationStatus: normalizedPublicationStatus || "published",
        publicationProbe: {
          ...(publicationProbe || {}),
          state: resolvedProbeState,
          source: publicationProbe?.source || "publish-flow",
          confirmed: Boolean(publicationProbe?.confirmed),
          statusLabel: String(publicationProbe?.statusLabel || "").trim(),
          checkedAt: Number(publicationProbe?.checkedAt || Date.now())
        }
      });
      bumpAccountAdsMetric(publishAccountId, resolvedProbeState);
      // KL сразу удалил объявление — это отказ, PLZ аккаунта прежний.
      if (resolvedProbeState !== "deleted") rememberAccountPostalCode(publishAccountId, publishPostalCode);
      // Раздел «Объявления» держит список в браузере полчаса — без этого новое
      // объявление появлялось там только после ручного «Обновить».
      markActiveAdsCacheStale(currentOwnerId);
      try { window.dispatchEvent(new Event(AD_PUBLISHED_EVENT)); } catch { /* старый браузер */ }
      resetAdDraft();
    };
    // One outcome per publish. There are nine places that can conclude a publish
    // failed — the awaited response, the status poll that takes over when the
    // connection drops, the recovery path for a request already running — and
    // more than one of them can reach the same conclusion about the same
    // attempt. Each reported it, which is why one rejection arrived as two
    // identical notifications.
    //
    // Whichever gets there first speaks; the rest are already saying what has
    // been said.
    let outcomeReported = false;

    // outcome — ответ сервера, если он есть: по нему видно, что KL отверг
    // значение параметра категории и варианты уже перечитаны.
    const showCreateError = (message, requestId = "", outcome = null) => {
      message = withFieldsRefreshedHint(message, outcome);
      // modalClosedByFlow belongs to the success path, which has guarded itself
      // this way all along. Reading it here too is what stops a publish from
      // announcing success and then failure — two outcomes for one attempt,
      // which is what the modal did when it called the first step the last one.
      if (outcomeReported || modalClosedByFlow) return;
      outcomeReported = true;
      // Claimed here as well, so a success arriving after a failure is stopped by
      // the guard the success path already has. Without it the pair still got
      // through in that order — and having reported a rejection, the flow has no
      // business closing the modal as published afterwards.
      modalClosedByFlow = true;
      updatePublishProgressModal({
        requestId,
        adTitle: publishAdTitle,
        accountLabel: publishAccountLabel,
        step: "done",
        forceStatusText: t("Публикация завершилась ошибкой"),
        error: String(message || "")
      });
    };

    let clientRequestId = "";
    let createRequestAbortController = null;

    try {
      setPublishingAd(true);
      setShowAdModal(false);
      const formData = new FormData();
      formData.append("accountId", newAd.accountId);
      formData.append("title", newAd.title);
      formData.append("description", newAd.description);
      formData.append("price", newAd.price);
      formData.append("priceType", String(newAd.priceType || "FIXED"));
      if (newAd.postalCode) formData.append("postalCode", newAd.postalCode);
      // Населённый пункт индекса — если у PLZ их несколько и один выбран.
      if (newAd.postalCode && newAd.locationId) {
        formData.append("locationId", newAd.locationId);
        formData.append("locationName", newAd.locationName || "");
      }
      if (newAd.legalInfo) formData.append("legalInfo", newAd.legalInfo);
      if (newAd.categoryId) formData.append("categoryId", newAd.categoryId);
      if (newAd.categoryUrl) formData.append("categoryUrl", newAd.categoryUrl);
      if (Array.isArray(newAd.categoryPath) && newAd.categoryPath.length > 0) {
        formData.append("categoryPath", JSON.stringify(newAd.categoryPath));
      }
      if (Object.keys(extraFieldValues).length > 0) {
        const payload = extraFields.map((field) => {
          const key = field.name || field.label;
          return {
            name: field.name || "",
            label: field.label || "",
            value: key ? (extraFieldValues[key] || "") : ""
          };
        });
        formData.append("extraFields", JSON.stringify(payload));
      }
      if (newAd.draftId) {
        formData.append("draftId", String(newAd.draftId));
      }
      // Фото — в порядке сетки окна: свои файлы и снимки черновика вперемешку,
      // как их расставили. Порядок уходит и списком (photoOrder), по нему сервер
      // и раскладывает снимки.
      const publishPhotos = buildPublishPhotoPayload(buildPhotoSequence(adPhotoOrder, draftImageUrls, adImages));
      if (publishPhotos.draftUrls.length > 0) {
        formData.append("existingImageOrder", JSON.stringify(publishPhotos.draftUrls));
      }
      formData.append("photoOrder", JSON.stringify(publishPhotos.photoOrder));
      if (debugPublishEnabled) {
        formData.append("debug", "1");
      }
      publishPhotos.files.forEach((file) => {
        formData.append("images", file);
      });

      clientRequestId = makeClientRequestId();
      createRequestAbortController = new AbortController();
      activeCreateRequestIdRef.current = clientRequestId;
      activeCreateRequestAbortRef.current = createRequestAbortController;
      const coverToken = buildPhotoSequence(adPhotoOrder, draftImageUrls, adImages)[0] || null;
      let publishCoverUrl = "";
      if (publishCoverObjectUrlRef.current) {
        try { URL.revokeObjectURL(publishCoverObjectUrlRef.current); } catch { /* уже отпущена */ }
        publishCoverObjectUrlRef.current = "";
      }
      if (coverToken?.kind === "draft") {
        publishCoverUrl = String(coverToken.url || "");
      } else if (coverToken?.file) {
        try {
          publishCoverUrl = URL.createObjectURL(coverToken.file);
          publishCoverObjectUrlRef.current = publishCoverUrl;
        } catch { /* без обложки */ }
      }
      const publishPriceLabel = String(newAd.price || "").trim()
        ? `${String(newAd.price).trim()} €${String(newAd.priceType || "").toUpperCase() === "NEGOTIABLE" ? " VB" : ""}`
        : "";
      setPublishProgressModal({
        open: true,
        requestId: clientRequestId,
        adTitle: publishAdTitle,
        accountLabel: publishAccountLabel,
        coverUrl: publishCoverUrl,
        priceLabel: publishPriceLabel,
        step: "queued",
        percent: 4,
        statusText: getPublishProgressStatusText("queued"),
        error: "",
        publicationStatus: "",
        publicationProbe: null,
        startedAt: Date.now(),
        updatedAt: Date.now()
      });
      progressPulseTimer = window.setInterval(() => {
        setPublishProgressModal((prev) => {
          if (!prev.open || prev.error) return prev;
          const currentStep = String(prev.step || "queued").trim();
          const currentPercent = Number(prev.percent || 0);
          if (currentPercent >= 99) return prev;
          const nextPercent = getPublishProgressPercent(currentStep, currentPercent);
          if (nextPercent <= currentPercent) return prev;
          return {
            ...prev,
            percent: nextPercent,
            statusText: String(prev.statusText || getPublishProgressStatusText(currentStep)).trim(),
            updatedAt: Date.now()
          };
        });
      }, 800);
      progressPollTimer = window.setInterval(async () => {
        if (progressPollBusy) return;
        progressPollBusy = true;
        try {
          const status = await apiFetchJson(`/api/ads/create-status/${encodeURIComponent(clientRequestId)}`, {
            timeoutMs: 12000,
            retry: true
          });
          if (!status?.success) return;
          if (status?.done) {
            createFinishedFromStatus = true;
            const statusResult = status?.result || null;
            stopProgressPoll();
            if (statusResult?.success) {
              closeModalOnPublished(statusResult);
            } else {
              showCreateError(
                statusResult?.error || t("Публикация завершилась с ошибкой."),
                String(status?.requestId || clientRequestId || "").trim(),
                statusResult
              );
            }
            if (createRequestAbortController && !createRequestAbortController.signal.aborted) {
              createRequestAbortController.abort();
            }
            return;
          }
          const progressStep = String(status?.progressStep || "queued").trim();
          updatePublishProgressModal({
            requestId: clientRequestId,
            step: progressStep
          });
        } catch (statusError) {
          if (statusError?.status && statusError.status !== 404) {
            console.error("[handleCreateAd] Ошибка polling create-status:", statusError);
          }
        } finally {
          progressPollBusy = false;
        }
      }, 800);
      const result = await apiFetchJson("/api/ads/create", {
        method: "POST",
        body: formData,
        requestId: clientRequestId,
        signal: createRequestAbortController.signal,
        timeoutMs: 0,
        retry: false
      });

      if (createFinishedFromStatus) {
        return;
      }

      console.log("[handleCreateAd] Ответ сервера:", result);
      if (result?.inProgress) {
        pushToast({
          variant: "info",
          title: t("Публикация"),
          body: t("Публикация еще выполняется на сервере. Ожидаю финальный статус..."),
          durationMs: 6500
        });
        const inProgressRequestId = String(result?.requestId || clientRequestId || "").trim();
        const completedResult = inProgressRequestId
          ? await waitForCreateStatusDone(inProgressRequestId, 8 * 60 * 1000, (status) => {
            updatePublishProgressModal({
              requestId: inProgressRequestId,
              step: String(status?.progressStep || "queued").trim()
            });
          })
          : null;
        stopProgressPoll();
        if (completedResult?.success) {
          closeModalOnPublished(completedResult);
          return;
        }
        if (completedResult?.error) {
          showCreateError(completedResult.error, inProgressRequestId, completedResult);
          return;
        }
        showCreateError(t("Не удалось дождаться финального статуса публикации"), inProgressRequestId);
        return;
      }
      if (result?.success) {
        stopProgressPoll();
        closeModalOnPublished(result);
        return;
      }
      showCreateError(result?.error || t("Не удалось завершить публикацию. Проверьте вкладку «Объявления»."), String(result?.requestId || clientRequestId || "").trim(), result);
    } catch (error) {
      stopProgressPoll();
      if (createFinishedFromStatus) {
        return;
      }
      const failedRequestId = String(error?.requestId || error?.data?.requestId || clientRequestId || "").trim();
      const cancelledByUi = Boolean(createRequestAbortController?.signal?.aborted);
      if (cancelledByUi) {
        const finalCancelledResult = failedRequestId
          ? await waitForCreateStatusDone(failedRequestId, 90 * 1000, (status) => {
            updatePublishProgressModal({
              requestId: failedRequestId,
              step: String(status?.progressStep || "publish-cancel-requested").trim()
            });
          })
          : null;
        if (finalCancelledResult?.success) {
          closeModalOnPublished(finalCancelledResult);
          return;
        }
        updatePublishProgressModal({
          requestId: failedRequestId || clientRequestId,
          step: "done",
          forceStatusText: t("Публикация остановлена"),
          error: String(finalCancelledResult?.error || t("Публикация была остановлена пользователем."))
        });
        return;
      }
      const inProgressCode = String(error?.data?.code || "").trim().toUpperCase();
      const activeRequestId = String(error?.data?.activeRequestId || "").trim();
      if (inProgressCode === "PUBLISH_IN_PROGRESS" && activeRequestId) {
        pushToast({
          variant: "info",
          title: t("Публикация"),
          body: t("Публикация уже запущена на сервере. Подключаюсь к текущему статусу..."),
          durationMs: 6500
        });
        setPublishProgressModal({
          open: true,
          requestId: activeRequestId,
          adTitle: publishAdTitle,
          accountLabel: publishAccountLabel,
          step: "queued-remote",
          percent: 18,
          statusText: getPublishProgressStatusText("queued-remote"),
          error: "",
          publicationStatus: "",
          publicationProbe: null,
          startedAt: Date.now(),
          updatedAt: Date.now()
        });
        const completedResult = await waitForCreateStatusDone(activeRequestId, 8 * 60 * 1000, (status) => {
          updatePublishProgressModal({
            requestId: activeRequestId,
            step: String(status?.progressStep || "queued-remote").trim()
          });
        });
        if (completedResult?.success) {
          closeModalOnPublished(completedResult);
          return;
        }
        if (completedResult?.error) {
          showCreateError(tf`Ошибка при создании объявления: ${completedResult.error}`, activeRequestId, completedResult);
          return;
        }
        showCreateError(t("Не удалось дождаться финального статуса активной публикации"), activeRequestId);
        return;
      }

      handleAuthError(error);
      console.error("[handleCreateAd] Исключение:", error);
      if (failedRequestId && isRecoverablePublishNetworkError(error)) {
        pushToast({
          variant: "info",
          title: t("Публикация"),
          body: t("Связь с сервером прервалась. Проверяю итоговый статус публикации..."),
          durationMs: 7000
        });
        const recoveredResult = await waitForCreateStatusDone(failedRequestId, 8 * 60 * 1000, (status) => {
          updatePublishProgressModal({
            requestId: failedRequestId,
            step: String(status?.progressStep || "queued").trim()
          });
        });
        if (recoveredResult?.success) {
          closeModalOnPublished(recoveredResult);
          return;
        }
        if (recoveredResult?.error) {
          showCreateError(tf`Ошибка при создании объявления: ${recoveredResult.error}`, failedRequestId, recoveredResult);
          return;
        }
        if (recoveredResult?.inProgress) {
          const completedResult = await waitForCreateStatusDone(failedRequestId, 5 * 60 * 1000, (status) => {
            updatePublishProgressModal({
              requestId: failedRequestId,
              step: String(status?.progressStep || "queued").trim()
            });
          });
          if (completedResult?.success) {
            closeModalOnPublished(completedResult);
            return;
          }
          if (completedResult?.error) {
            showCreateError(tf`Ошибка при создании объявления: ${completedResult.error}`, failedRequestId, completedResult);
            return;
          }
          showCreateError(t("Не удалось дождаться финального статуса публикации"), failedRequestId);
          return;
        }
      }
      updatePublishProgressModal({
        requestId: failedRequestId,
        adTitle: publishAdTitle,
        accountLabel: publishAccountLabel,
        step: "done",
        forceStatusText: t("Публикация завершилась ошибкой"),
        error: String(error?.message || t("Неизвестная ошибка"))
      });
    } finally {
      stopProgressPoll();
      if (activeCreateRequestIdRef.current === clientRequestId) {
        activeCreateRequestIdRef.current = "";
      }
      if (activeCreateRequestAbortRef.current === createRequestAbortController) {
        activeCreateRequestAbortRef.current = null;
      }
      setPublishingAd(false);
    }
  };

  const handleSaveAdDraft = async () => {
    if (!newAd.accountId) {
      alert(t("Выберите аккаунт для черновика"));
      return;
    }
    try {
      const formData = new FormData();
      formData.append("accountId", newAd.accountId);
      formData.append("title", newAd.title);
      formData.append("description", newAd.description || "");
      formData.append("price", newAd.price || "");
      formData.append("priceType", String(newAd.priceType || "FIXED"));
      if (newAd.postalCode) formData.append("postalCode", newAd.postalCode);
      // Населённый пункт индекса — если у PLZ их несколько и один выбран.
      if (newAd.postalCode && newAd.locationId) {
        formData.append("locationId", newAd.locationId);
        formData.append("locationName", newAd.locationName || "");
      }
      if (newAd.legalInfo) formData.append("legalInfo", newAd.legalInfo);
      if (newAd.categoryId) formData.append("categoryId", newAd.categoryId);
      if (newAd.categoryUrl) formData.append("categoryUrl", newAd.categoryUrl);
      if (Array.isArray(newAd.categoryPath) && newAd.categoryPath.length > 0) {
        formData.append("categoryPath", JSON.stringify(newAd.categoryPath));
      }
      if (Object.keys(extraFieldValues).length > 0) {
        const payload = extraFields.map((field) => {
          const key = field.name || field.label;
          return {
            name: field.name || "",
            label: field.label || "",
            value: key ? (extraFieldValues[key] || "") : ""
          };
        });
        formData.append("extraFields", JSON.stringify(payload));
      }
      if (newAd.scheduledAt) {
        formData.append("scheduledAt", new Date(newAd.scheduledAt).toISOString());
        formData.append("status", "scheduled");
      } else {
        formData.append("status", "draft");
      }
      if (newAd.draftId) {
        formData.append("id", String(newAd.draftId));
      }
      const draftPhotos = buildPublishPhotoPayload(buildPhotoSequence(adPhotoOrder, draftImageUrls, adImages));
      formData.append("existingImageOrder", JSON.stringify(draftPhotos.draftUrls));
      formData.append("photoOrder", JSON.stringify(draftPhotos.photoOrder));
      draftPhotos.files.forEach((file) => formData.append("images", file));
      if (newAd.draftId || adImages.length > 0 || draftImageUrls.length > 0) {
        formData.append("replaceImages", "1");
      }

      await apiFetchJson("/api/ad-drafts", {
        method: "POST",
        body: formData,
        timeoutMs: 45000,
        retry: false
      });

      setNewAd({
        draftId: "",
        accountId: "",
        title: "",
        description: "",
        price: "",
        priceType: "FIXED",
        categoryId: "",
        categoryUrl: "",
        postalCode: "",
        locationId: "",
        locationName: "",
        categoryKey: "",
        categoryPath: [],
        scheduledAt: "",
        legalInfo: ""
      });
      setAdImages([]);
      setDraftImageUrls([]);
      setAdPhotoOrder([]);
      setExtraFields([]);
      setExtraFieldValues({});
      pushToast({
        variant: "success",
        title: t("Черновики"),
        body: newAd.scheduledAt ? t("Черновик сохранен и запланирован") : t("Черновик сохранен"),
        durationMs: 6000
      });
    } catch (error) {
      handleAuthError(error);
      pushToast({
        variant: "error",
        title: t("Черновики"),
        body: tf`Ошибка сохранения черновика: ${error?.message || t("Неизвестная ошибка")}`,
        durationMs: 7000
      });
    }
  };

  const handleEditDraft = (draft) => {
    if (!draft || typeof draft !== "object") return;
    setShowAdModal(true);
    setExtraFields([]);
    setExtraFieldValues(() => {
      const next = {};
      const list = Array.isArray(draft.extraFields) ? draft.extraFields : [];
      list.forEach((field) => {
        const key = field?.name || field?.label;
        if (!key) return;
        next[key] = String(field?.value || "");
      });
      return next;
    });
    setAdImages([]);
    setAdPhotoOrder([]);
    setDraftImageUrls(
      Array.isArray(draft.imageUrls)
        ? draft.imageUrls.map((item) => String(item || "").trim()).filter(Boolean)
        : []
    );
    setNewAd({
      draftId: String(draft.id || ""),
      accountId: String(draft.accountId || ""),
      title: String(draft.title || "").slice(0, 65),
      description: String(draft.description || "").slice(0, 4000),
      price: String(draft.price || ""),
      categoryId: String(draft.categoryId || ""),
      categoryUrl: String(draft.categoryUrl || ""),
      postalCode: String(draft.postalCode || ""),
      locationId: String(draft.locationId || ""),
      locationName: String(draft.locationName || ""),
      categoryKey: String(draft.categoryId || draft.categoryUrl || ""),
      categoryPath: Array.isArray(draft.categoryPath) ? draft.categoryPath : [],
      priceType: String(draft.priceType || "FIXED"),
      scheduledAt: draft.scheduledAt ? toInputDateTimeValue(draft.scheduledAt) : "",
      legalInfo: String(draft.legalInfo || "")
    });
  };

  const renderConnectionStatus = (proxy) => {
    const connection = proxy?.checkResult?.connectionCheck;

    if (!connection) {
      return (
        <span style={{ fontSize: "12px", color: textMuted }}>
          {t("Соединение не проверялось")}
        </span>
      );
    }

    if (connection.ok) {
      return (
        <span style={{ fontSize: "12px", color: "#52c41a" }}>
          CONNECT OK ({connection.connectTime}{t("мс")}{connection.statusCode ? `, ${connection.statusCode}` : ""})
        </span>
      );
    }

    return (
      <span style={{ fontSize: "12px", color: "#f87171" }}>
        CONNECT fail{connection.statusCode ? ` (${connection.statusCode})` : ""}: {connection.error || t("Ошибка")}
      </span>
    );
  };

  // Unified section header (style «2c»): inline indigo icon, no plaque, optional
  // subtitle. The legacy 3rd arg (icon plaque theme) is ignored; pass
  // { subtitle: "..." } instead. Drop-in per components/SectionHeader.jsx.
  const renderSectionTitle = (IconComponent, title, opts = {}) => (
    <div className="app-section-title-wrap">
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {IconComponent ? <IconComponent size={isPhoneView ? 20 : 22} color={A[400]} /> : null}
        <h2 className="app-section-title" style={{
          margin: 0,
          fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
          fontWeight: 700,
          fontSize: isPhoneView ? "22px" : "26px",
          color: textTitle,
          letterSpacing: "-0.02em",
          lineHeight: 1
        }}>{title}</h2>
      </div>
      {opts.subtitle ? (
        <div style={{ fontSize: "13px", color: textMuted, marginTop: "6px", paddingLeft: `${(isPhoneView ? 20 : 22) + 12}px` }}>
          {opts.subtitle}
        </div>
      ) : null}
    </div>
  );

  const renderDashboardMetric = (label, value, accentColor = "#c6c9d1") => (
    <div style={{
      flex: 1,
      minWidth: isPhoneView ? "0" : "120px",
      padding: isPhoneView ? "9px 10px" : "10px 12px",
      borderRadius: "16px",
      border: "1px solid rgba(141,147,163,0.2)",
      background: "rgba(8,9,12,0.36)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "10px"
    }}>
      <span style={{
        color: textMuted,
        fontSize: isPhoneView ? "11px" : "12px",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}>
        {label}
      </span>
      <span style={{
        color: accentColor,
        fontSize: isPhoneView ? "17px" : "18px",
        fontWeight: "700",
        lineHeight: 1
      }}>
        {value}
      </span>
    </div>
  );

  const DashboardTab = () => {
    const overviewCards = stats ? [
      {
        id: "accounts",
        title: t("Аккаунты"),
        caption: t("Аккаунтов подключено"),
        icon: <UserIcon size={20} color="#8d93a3" />,
        value: stats.accounts.total,
        metrics: [
          { label: t("Онлайн"), value: stats.accounts.online, color: "#4ade80" },
          { label: t("Оффлайн"), value: stats.accounts.offline, color: "#8d93a3" }
        ]
      },
      {
        id: "proxies",
        title: t("Прокси"),
        caption: t("Прокси активно"),
        icon: <LinkIcon size={20} color="#8d93a3" />,
        value: stats.proxies.total,
        metrics: [
          { label: t("Работают"), value: stats.proxies.active, color: "#4ade80" },
          { label: t("Ошибки"), value: stats.proxies.failed, color: "#fb7185" }
        ]
      },
      {
        id: "messages",
        title: t("Сообщения"),
        caption: t("Сообщений всего"),
        icon: <MessageIcon size={20} color="#8d93a3" />,
        value: stats.messages.total,
        metrics: [
          { label: t("Сегодня"), value: stats.messages.today, color: A[300] },
          { label: t("Непрочитанные"), value: stats.messages.unread, color: A[300] }
        ]
      }
    ] : [];


    const openAdComposer = () => {
      setDraftImageUrls([]);
      setAdPhotoOrder([]);
      setShowAdModal(true);
    };
    // Создание объявления — главной кнопкой в шапке, поэтому здесь вместо него
    // переход к объявлениям.
    const quickActions = [
      { id: "add-account", title: t("Добавить аккаунт"), icon: <UserIcon size={16} />, onClick: () => setShowAddAccountModal(true) },
      { id: "add-proxy", title: t("Добавить прокси"), icon: <LinkIcon size={16} />, onClick: () => handleTabSelect("proxies") },
      {
        id: "check-proxies",
        title: checkingAllProxies ? t("Проверяем...") : t("Проверить прокси"),
        icon: <RefreshIcon size={16} />,
        onClick: checkAllProxies,
        disabled: checkingAllProxies
      },
      { id: "open-ads", title: t("Мои объявления"), icon: <PackageIcon size={16} />, onClick: () => handleTabSelect("active-ads") }
    ];
    const todayLabel = (() => {
      const text = new Date().toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long" });
      return text.charAt(0).toUpperCase() + text.slice(1);
    })();

    const openDashboardConversation = (item) => {
      setPendingConversationOpen({ item, at: Date.now() });
      handleTabSelect("messages");
    };

    return (
      <div className="dashboard-tab-root" style={{ display: "flex", flexDirection: "column", gap: isPhoneView ? "16px" : "22px" }}>
        <div className="dashboard-header ui-page-head">
          {/* Шапка — как у остальных разделов: иконка в строку с названием. */}
          <div style={{ minWidth: 0 }}>
            {renderSectionTitle(DashboardIcon, t("Дашборд"), { subtitle: todayLabel })}
          </div>
          <div className="ui-page-actions">
            {/* Книжечка обновлений и тикеты — слева от уведомлений. */}
            {!isPhoneView && (
              <button
                type="button"
                className={`ui-icon-btn ub-trigger${updatesBook.open ? " is-on" : ""}`}
                onClick={(event) => (updatesBook.open ? closeUpdatesBook() : openUpdatesBook(event.currentTarget))}
                aria-label={t("Обновления и тикеты")}
                title={t("Обновления и тикеты")}
                aria-expanded={updatesBook.open}
              >
                <BookOpenIcon size={17} />
                {hasNewUpdates && <span className="ub-new-dot" aria-hidden="true" />}
              </button>
            )}
            {!isPhoneView && (
              <div style={{ position: "relative" }} ref={extraNotifRef}>
                <button
                  type="button"
                  className={`ui-icon-btn${showNotificationsPanel ? " is-on" : ""}`}
                  onClick={toggleNotificationsPanel}
                  aria-label={t("Уведомления")}
                  title={t("Уведомления")}
                >
                  <BellIcon size={17} />
                  {unreadNotificationsCount > 0 && <span className="ui-dot-badge">{unreadNotificationsBadge}</span>}
                </button>
              </div>
            )}
            <button type="button" className="kl-press-spin ui-icon-btn" onClick={loadData} title={t("Обновить данные")} aria-label={t("Обновить данные")}>
              <span className="kl-btn-ico"><RefreshIcon size={16} /></span>
            </button>
            <button type="button" className="ui-btn-primary" onClick={openAdComposer} aria-label={t("Создать объявление")} title={t("Создать объявление")}>
              <PlusIcon size={15} />
              {isPhoneView ? null : t("Создать объявление")}
            </button>
          </div>
        </div>

        <DashboardSummary
          stats={stats}
          accounts={accounts}
          accountMetrics={accountMetrics}
          proxies={proxies}
          quickActions={quickActions}
          describeAccount={describeAccount}
          conversations={dashboardConversations}
          ownerId={currentOwnerId}
          privateMode={privateMode}
          onOpenConversation={openDashboardConversation}
          onShowMessages={() => handleTabSelect("messages")}
          onShowAllAccounts={() => handleTabSelect("accounts")}
          isPhoneView={isPhoneView}
          t={t}
        />

      </div>
    );
  };

  const AccountsTab = () => {
    // Шкалы метрик считаются один раз на весь список: полосы в карточках должны
    // быть сопоставимы между собой, а не каждая относительно себя.
    const accountChatsScale = Math.max(1, ...(Array.isArray(accounts) ? accounts : [])
      .map((item) => Number(accountMetrics[item.id]?.chats) || 0));
    const accountFollowersScale = Math.max(1, ...(Array.isArray(accounts) ? accounts : [])
      .map((item) => Number(item?.followersCount) || 0));
    const bulkActionButtonStyle = {
      height: "36px",
      padding: "0 14px",
      borderRadius: "12px",
      border: `1px solid ${aa(400, 0.4)}`,
      background: aa(500, isDarkTheme ? 0.16 : 0.12),
      // На светлой теме A[100] — почти белый: на светлой же подложке кнопка
      // становилась нечитаемой.
      color: isDarkTheme ? A[100] : A[700],
      fontSize: "13px",
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "7px",
      cursor: "pointer"
    };
    const applyHoverStyles = (event, styles) => {
      if (isPhoneView) return;
      if (!event?.currentTarget || !styles) return;
      Object.assign(event.currentTarget.style, styles);
    };
    const actionHoverEnter = {
      background: `linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)})`,
      borderColor: aa(300, 0.56),
      boxShadow: `0 14px 28px ${aa(900, 0.24)}`,
      transform: "translateY(-2px)"
    };
    const buildActionHoverLeave = (background, borderColor) => ({
      background,
      borderColor,
      boxShadow: "none",
      transform: "translateY(0)"
    });
    const accountMenuButtonStyle = {
      background: S.surface,
      border: "1px solid rgba(141,147,163,0.22)",
      borderRadius: "14px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      padding: "9px 10px",
      transition: "all 0.2s ease"
    };

    return (
    <div className="accounts-tab-root theme-light-surface-scope" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <div className="section-header" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isPhoneView ? "stretch" : "center",
        flexDirection: isPhoneView ? "column" : "row",
        gap: "10px"
      }}>
        {renderSectionTitle(UserIcon, t("Аккаунты"), {
          iconColor: A[300],
          background: `linear-gradient(135deg, ${aa(500, 0.3)}, ${aa(900, 0.28)})`,
          border: `1px solid ${aa(400, 0.45)}`,
          boxShadow: `0 4px 15px ${aa(500, 0.32)}`
        })}
        <div className="section-header-actions" style={{
          display: "flex",
          gap: "10px",
          flexDirection: isPhoneView ? "column" : "row",
          width: isPhoneView ? "100%" : "auto"
        }}>
          <button
            type="button"
            className="kl-plain-button"
            onClick={() => {
              if (freezeAccessLocked) {
                pushToast({
                  variant: "warning",
                  title: t("Подписка на заморозке"),
                  body: t("Публикация недоступна до отключения заморозки в настройках."),
                  durationMs: 4200
                });
                return;
              }
              setDraftImageUrls([]);
              setAdPhotoOrder([]);
              setShowAdModal(true);
            }}
            disabled={freezeAccessLocked}
            style={{
              padding: "0 18px",
              height: "44px",
              color: "#d5d8e0",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "transparent",
              borderRadius: "14px",
              cursor: freezeAccessLocked ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "9px",
              width: isPhoneView ? "100%" : "auto",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "14px",
              transition: "all 0.2s ease",
              opacity: freezeAccessLocked ? 0.55 : 1
            }}
            onMouseEnter={(event) => {
              if (freezeAccessLocked) return;
              applyHoverStyles(event, { borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.03)" });
            }}
            onMouseLeave={(event) => {
              if (freezeAccessLocked) return;
              applyHoverStyles(event, { borderColor: "rgba(255,255,255,0.12)", background: "transparent" });
            }}
          >
            <FileIcon size={17} />
            {t("Создать объявление")}
          </button>

          <button
            type="button"
            className="kl-plain-button"
            onClick={() => setShowAddAccountModal(true)}
            style={{
              padding: "0 18px",
              height: "44px",
              color: onAccent("#ffffff"),
              border: "none",
              background: A[500],
              borderRadius: "14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "9px",
              width: isPhoneView ? "100%" : "auto",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "14px",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(event) => {
              applyHoverStyles(event, { background: A[600] });
            }}
            onMouseLeave={(event) => {
              applyHoverStyles(event, { background: A[500] });
            }}
          >
            <PlusIcon size={17} />
            {t("Добавить аккаунт")}
          </button>
        </div>
      </div>

      {accountsBulkPresence.mounted && (
        <div
          className="accounts-bulk-bar"
          data-state={accountsBulkPresence.state}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "14px",
            padding: isPhoneView ? "12px" : "12px 16px",
            borderRadius: "18px",
            border: `1px solid ${aa(400, 0.32)}`,
            background: isDarkTheme
              ? `linear-gradient(135deg, ${aa(600, 0.22)}, ${aa(500, 0.1)})`
              : `linear-gradient(135deg, ${aa(300, 0.16)}, ${aa(200, 0.1)})`,
            boxShadow: isDarkTheme
              ? `0 14px 32px -16px ${aa(900, 0.6)}`
              : "0 12px 26px -18px rgba(14,16,21,0.4)"
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
            <span style={{
              minWidth: "30px",
              height: "30px",
              padding: "0 9px",
              borderRadius: "10px",
              background: A[500],
              color: onAccent("#ffffff"),
              fontSize: "14px",
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              {accountsBulkCount}
            </span>
            <span style={{ color: surfaceTextPrimary, fontSize: "13.5px", fontWeight: 600 }}>
              {t("выбрано")}
            </span>
          </span>

          <div style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginLeft: isPhoneView ? 0 : "auto",
            width: isPhoneView ? "100%" : "auto"
          }}>
            <button
              type="button"
              className="kl-plain-button kl-press-spin"
              onClick={handleBulkRefreshAccounts}
              disabled={Boolean(bulkAccountBusy)}
              style={{
                ...bulkActionButtonStyle,
                flex: isPhoneView ? 1 : "0 0 auto",
                opacity: bulkAccountBusy ? 0.6 : 1,
                cursor: bulkAccountBusy ? "wait" : "pointer"
              }}
            >
              <span className="kl-btn-ico" style={{ display: "inline-flex" }}><RefreshIcon size={15} /></span>
              {bulkAccountBusy === "refresh" ? t("Обновляется...") : t("Обновить")}
            </button>
            <button
              type="button"
              className="kl-plain-button"
              onClick={handleBulkChangeProxy}
              disabled={Boolean(bulkAccountBusy)}
              style={{
                ...bulkActionButtonStyle,
                flex: isPhoneView ? 1 : "0 0 auto",
                opacity: bulkAccountBusy ? 0.6 : 1,
                cursor: bulkAccountBusy ? "wait" : "pointer"
              }}
            >
              <LinkIcon size={15} />
              {t("Сменить прокси")}
            </button>
            <button
              type="button"
              className="kl-plain-button"
              onClick={handleBulkDeleteAccounts}
              disabled={Boolean(bulkAccountBusy)}
              style={{
                ...bulkActionButtonStyle,
                border: `1px solid ${isDarkTheme ? "rgba(239,68,68,0.38)" : "rgba(220,38,38,0.35)"}`,
                background: isDarkTheme ? "rgba(127,29,29,0.32)" : "rgba(239,68,68,0.1)",
                color: isDarkTheme ? "#fecaca" : "#b91c1c",
                flex: isPhoneView ? 1 : "0 0 auto",
                opacity: bulkAccountBusy ? 0.6 : 1,
                cursor: bulkAccountBusy ? "wait" : "pointer"
              }}
            >
              <TrashIcon size={15} />
              {bulkAccountBusy === "delete" ? t("Удаляется...") : t("Удалить")}
            </button>
            <button
              type="button"
              className="kl-plain-button"
              onClick={clearAccountSelection}
              disabled={Boolean(bulkAccountBusy)}
              title={t("Снять выделение")}
              aria-label={t("Снять выделение")}
              style={{
                ...bulkActionButtonStyle,
                width: isPhoneView ? "100%" : "36px",
                padding: 0,
                border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.28)" : "rgba(14,16,21,0.14)"}`,
                background: "transparent",
                color: surfaceTextSecondary,
                flex: isPhoneView ? "0 0 100%" : "0 0 auto"
              }}
            >
              <XIcon size={15} />
            </button>
          </div>
        </div>
      )}

      <div className="accounts-cards-grid" style={{
        display: "grid",
        gridTemplateColumns: isPhoneView ? "1fr" : "repeat(auto-fill, minmax(min(100%, 268px), 1fr))",
        gap: "14px"
      }}>
        {accounts.map((account, _accountIdx) => {
          const metrics = accountMetrics[account.id] || { active: 0, total: 0, chats: 0, unread: 0 };
          const rawName = String(account.profileName || "").trim();
          // A real KL display name vs. a generated "klein_<id>" / placeholder. When
          // the account has no name on KL we show a clean "Без имени" placeholder
          // instead of leaking the numeric id as if it were a name.
          const hasRealName = Boolean(rawName)
            && !/mein(e)? anzeigen|mein profil|meine anzeigen|profil und meine anzeigen/i.test(rawName)
            && !/^klein_\d+$/i.test(rawName)
            && !/^\d{4,}$/.test(rawName);
          const displayName = hasRealName ? rawName : t("Без имени");
          const cardEmail = resolveAccountEmailForCard(account);
          const avatarInitial = hasRealName
            ? displayName.charAt(0).toUpperCase()
            : (cardEmail && cardEmail !== "—" ? cardEmail.charAt(0).toUpperCase() : "?");
          // MIX is a real KL state, not a "loading" placeholder: it's what a
          // seller shows when they carry no TOP/OK/NAJA satisfaction badge yet
          // (e.g. commercial accounts). So always render the normalized value —
          // the real badge when present, MIX as the baseline otherwise. The
          // confirmed flag only tunes the tooltip.
          const satisfactionBadgeConfirmed = String(account.satisfactionBadgeSource || "").toLowerCase() === "profile-json";
          const satisfactionBadge = normalizeSatisfactionBadge(account.satisfactionBadge);
          const isBanned = isLoginRedirectBanAccount(account);
          const isOnline = !isBanned && account.status === "active";
          const statusMeta = isBanned
            ? { label: "Banned", color: "#f87171", bg: "rgba(239,68,68,0.12)", border: "rgba(248,113,113,0.38)", dot: "#f87171" }
            : isOnline
              // Online — зелёный, Offline — серый: выход из сессии — состояние, а не тревога.
              ? { label: "Online", color: "#4ade80", bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.28)", dot: "#4ade80" }
              : { label: "Offline", color: "#8d93a3", bg: "rgba(141,147,163,0.08)", border: "rgba(141,147,163,0.22)", dot: "#8d93a3" };
          const showSelfreg = isSelfregAccount(account);
          const registeredAtDisplay = formatRegisteredAtForDisplay(account);
          const profileUrl = resolveAccountProfileUrl(account);
          const totalRaw = Number(account?.profileAdsTotal);
          const followersRaw = Number(account?.followersCount);
          const profileAdsTotal = Number.isFinite(totalRaw) && totalRaw >= 0 ? Math.round(totalRaw) : null;
          const followersCount = Number.isFinite(followersRaw) && followersRaw >= 0 ? Math.round(followersRaw) : null;
          // Подпись прокси приходит с сервера уже готовой, и когда прокси нет
          // (или он чужой, или непригоден) в ней стоит слово «Нет» — непустая
          // строка. Проверка на её наличие поэтому всегда была истинной, и
          // аккаунт без прокси горел зелёным. Признак берётся из самой подписи:
          // это ровно то решение, которое принял сервер.
          const proxyLabel = String(account.proxy || "").trim();
          const hasProxy = Boolean(proxyLabel) && proxyLabel !== "Нет" && proxyLabel !== "—";
          // Состояние живёт в самом прокси, а не в аккаунте: у аккаунта только
          // привязка. Встроенного прокси в списке владельца нет, поэтому его
          // отсутствие там не значит «сломан» — тогда остаёмся на «рабочий».
          const boundProxy = (Array.isArray(proxies) ? proxies : [])
            .find((item) => String(item?.id || "") === String(account.proxyId || ""));
          const boundProxyStatus = String(boundProxy?.status || "").trim().toLowerCase();
          const accountProxyStateKey = !hasProxy
            ? "none"
            : (boundProxyStatus === "failed" || boundProxyStatus === "error" || boundProxyStatus === "inactive" ? "down" : "ok");
          const isRefreshing = refreshingAccountIds.has(account.id);

          const accountMenu = (
            <div style={{ position: "relative" }}>
              <button
                data-account-menu-button
                type="button"
                className="kl-plain-button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (openAccountMenuId === account.id) {
                    setOpenAccountMenuId(null);
                  } else {
                    const r = e.currentTarget.getBoundingClientRect();
                    setAccountMenuAnchor({ top: r.top, bottom: r.bottom, right: r.right });
                    setOpenAccountMenuId(account.id);
                  }
                }}
                title={t("Ещё")}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "10px",
                  border: "1px solid transparent",
                  background: "transparent",
                  color: "#8d93a3",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(event) => applyHoverStyles(event, { background: "rgba(255,255,255,0.05)", color: "#f0f1f4" })}
                onMouseLeave={(event) => applyHoverStyles(event, { background: "transparent", color: "#8d93a3" })}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
              {openAccountMenuId === account.id && accountMenuAnchor && typeof document !== "undefined" && createPortal((() => {
                const MENU_W = 212;
                // Grew by a row for "Забрать cookies" and another for the
                // e-mail switch; this figure decides whether the menu flips
                // upward to clear the tab bar.
                const MENU_H = 362;
                const GAP = 6;
                // Leave room for the mobile bottom tab bar (62px + safe area) so
                // the menu never opens under it.
                const bottomLimit = window.innerHeight - (isPhoneView ? 82 : 8);
                const openUp = accountMenuAnchor.bottom + MENU_H + GAP > bottomLimit;
                const menuTop = openUp
                  ? Math.max(8, accountMenuAnchor.top - MENU_H - GAP)
                  : accountMenuAnchor.bottom + GAP;
                const menuLeft = Math.max(8, accountMenuAnchor.right - MENU_W);
                return (
                <div
                  data-account-menu
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "fixed", top: `${menuTop}px`, left: `${menuLeft}px`,
                    background: S.elevated, border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "13px", padding: "5px", display: "flex",
                    flexDirection: "column", width: `${MENU_W}px`, zIndex: 9993,
                    boxShadow: "0 24px 54px rgba(0,0,0,0.6)"
                  }}
                >
                  <div style={{ padding: "8px 12px 6px", fontSize: "10.5px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#565c69" }}>{t("Аккаунт")}</div>
                  {[
                    { label: t("Сменить имя"), onClick: () => openProfileNameModal(account), icon: <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /> },
                    { label: t("Телефон"), onClick: () => openPhoneModal(account), icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /> },
                    { label: t("Сменить прокси"), onClick: () => handleChangeAccountProxy(account), icon: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></> },
                    { label: t("Прогрев"), onClick: () => handleAccountWarmup(account), icon: <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /> },
                    { label: copyingCookiesId === account.id ? t("Копируем...") : t("Забрать cookies"), onClick: () => handleCopyAccountCookies(account), icon: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></> }
                  ].map((item) => (
                    <button
                      key={item.label} type="button" className="kl-plain-button" onClick={item.onClick}
                      style={{
                        display: "flex", alignItems: "center", gap: "11px", padding: "9px 12px",
                        borderRadius: "9px", border: "none", background: "transparent",
                        color: "#c6c9d1", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        textAlign: "left", transition: "all 0.15s ease"
                      }}
                      onMouseEnter={(event) => { event.currentTarget.style.background = aa(500, 0.12); event.currentTarget.style.color = "#f2f3f6"; }}
                      onMouseLeave={(event) => { event.currentTarget.style.background = "transparent"; event.currentTarget.style.color = "#c6c9d1"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8d93a3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                      {item.label}
                    </button>
                  ))}
                  {(() => {
                    const busy = emailNotifyBusyIds.has(account.id);
                    const known = typeof account.emailNotifications === "boolean";
                    const on = known && account.emailNotifications;
                    const stateLabel = busy
                      ? t("Проверяем...")
                      : known ? (on ? t("Включены") : t("Выключены")) : t("Нажмите, чтобы проверить");
                    return (
                      <button
                        type="button" className="kl-plain-button"
                        onClick={() => handleToggleEmailNotifications(account)}
                        disabled={busy}
                        title={t("Письма от Kleinanzeigen о новых сообщениях на почту аккаунта")}
                        style={{
                          display: "flex", alignItems: "center", gap: "11px", padding: "9px 12px",
                          borderRadius: "9px", border: "none", background: "transparent",
                          color: "#c6c9d1", fontSize: "13px", fontWeight: 500, cursor: busy ? "wait" : "pointer",
                          textAlign: "left", transition: "all 0.15s ease", width: "100%"
                        }}
                        onMouseEnter={(event) => { event.currentTarget.style.background = aa(500, 0.12); event.currentTarget.style.color = "#f2f3f6"; }}
                        onMouseLeave={(event) => { event.currentTarget.style.background = "transparent"; event.currentTarget.style.color = "#c6c9d1"; }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8d93a3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
                          <span>{t("Письма о сообщениях")}</span>
                          <span style={{ fontSize: "10.5px", fontWeight: 500, color: on ? A[300] : "#6b7080", lineHeight: 1.2 }}>{stateLabel}</span>
                        </span>
                        <span className={`kl-switch${on ? " kl-switch-on" : ""}${known ? "" : " kl-switch-unknown"}${busy ? " kl-switch-busy" : ""}`} aria-hidden="true">
                          <span className="kl-switch-knob" />
                        </span>
                      </button>
                    );
                  })()}
                  <div style={{ height: "1px", background: "rgba(255,255,255,0.06)", margin: "5px 0" }} />
                  <button
                    type="button" className="kl-plain-button"
                    onClick={() => { setOpenAccountMenuId(null); handleDeleteAccount(account.id); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "11px", padding: "9px 12px",
                      borderRadius: "9px", border: "none", background: "transparent",
                      color: "#fca5a5", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                      textAlign: "left", transition: "all 0.15s ease"
                    }}
                    onMouseEnter={(event) => { event.currentTarget.style.background = "rgba(239,68,68,0.1)"; }}
                    onMouseLeave={(event) => { event.currentTarget.style.background = "transparent"; }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                    {t("Удалить")}
                  </button>
                </div>
                );
              })(), document.body)}
            </div>
          );

          const openProfile = () => {
            if (!profileUrl) {
              pushToast({ variant: "warning", title: t("Профиль не найден"), body: t("Нажмите «Обновить» у аккаунта, чтобы подтянуть ссылку профиля.") });
              return;
            }
            window.open(profileUrl, "_blank", "noopener,noreferrer");
          };

          // Одна и та же плитка на столе и на телефоне: раскладку держит
          // сетка, отдельная мобильная карточка больше не нужна.
          return (
            <AccountTile
              key={account.id}
              account={account}
              metrics={metrics}
              displayName={displayName}
              hasRealName={hasRealName}
              email={cardEmail}
              avatarInitial={avatarInitial}
              satisfactionBadge={satisfactionBadge}
              statusKey={isBanned ? "banned" : (isOnline ? "online" : "offline")}
              proxyStateKey={accountProxyStateKey}
              followersCount={followersCount}
              registeredAtDisplay={registeredAtDisplay}
              showSelfreg={showSelfreg}
              selected={selectedAccountIds.has(account.id)}
              chatsScale={accountChatsScale}
              followersScale={accountFollowersScale}
              onToggleSelect={() => toggleAccountSelection(account.id)}
              onOpenProfile={openProfile}
              onRefresh={() => handleRefreshAccount(account.id)}
              refreshing={isRefreshing}
              menuSlot={accountMenu}
              compact={isPhoneView}
              proxyPlace={String(findBuiltinProxy(account.proxyId)?.checkResult?.location?.city || "").trim()}
              onSwapProxy={findBuiltinProxy(account.proxyId) ? swapBuiltinProxy : undefined}
              swappingProxy={swappingProxyAccountIds.has(account.id)}
            />
          );
        })}

        {/* Компьютер: пустой раздел — на всю ширину сетки, по центру, с тремя
            шагами добавления. Раньше это была узкая телефонная колонка в
            первой ячейке сетки, прижатая влево. */}
        {accounts.length === 0 && !isPhoneView && (
          <div className="acc-empty">
            <div className="acc-empty-card">
              <div className="acc-empty-icon">
                <UserIcon size={34} />
                <span className="acc-empty-icon-plus"><PlusIcon size={14} /></span>
              </div>
              <h3 className="acc-empty-title">{t("Пока нет аккаунтов")}</h3>
              <p className="acc-empty-text">
                {t("Добавьте аккаунт Kleinanzeigen — его диалоги, объявления и статистика сразу появятся в панели.")}
              </p>
              <button type="button" className="acc-empty-cta" onClick={() => setShowAddAccountModal(true)}>
                <PlusIcon size={18} />
                {t("Добавить аккаунт")}
              </button>
              <div className="acc-empty-steps">
                {[
                  { icon: <FileIcon size={18} />, title: "Cookies", text: t("Файл .txt или .json из браузера — или просто текст") },
                  { icon: <LinkIcon size={18} />, title: t("Прокси"), text: t("Свой прокси или встроенный Umbrella") },
                  { icon: <SparklesIcon size={18} />, title: t("Готово"), text: t("Имя, почта и объявления подтянутся сами") }
                ].map((step, index) => (
                  <div key={step.title} className="acc-empty-step">
                    <span className="acc-empty-step-icon">{step.icon}</span>
                    <span className="acc-empty-step-body">
                      <span className="acc-empty-step-title"><em>{index + 1}</em>{step.title}</span>
                      <span className="acc-empty-step-text">{step.text}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {accounts.length === 0 && isPhoneView && (
          <div className="accounts-empty-state" style={{
            padding: "48px 24px",
            textAlign: "center"
          }}>
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "16px",
              background: aa(500, 0.12),
              border: `1px solid ${aa(500, 0.28)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px"
            }}>
              <UserIcon size={32} color={A[400]} />
            </div>
            <h3 style={{ color: textTitle, marginBottom: "8px", fontWeight: "700" }}>{t("Нет аккаунтов")}</h3>
            <p style={{ color: textMuted, marginBottom: "20px" }}>{t("Добавьте первый аккаунт, чтобы начать работу")}</p>
            <button
              type="button"
              className="kl-plain-button"
              onClick={() => setShowAddAccountModal(true)}
              style={{
                padding: "12px 24px",
                color: onAccent("#ffffff"),
                border: "none",
                background: A[500],
                borderRadius: "14px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "600",
                fontSize: "14px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(event) => applyHoverStyles(event, { background: A[600] })}
              onMouseLeave={(event) => applyHoverStyles(event, { background: A[500] })}
            >
              <PlusIcon size={18} />
              {t("Добавить аккаунт")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
  };

  const ProxiesTab = () => {
    // Встроенный прокси панели в списке не показываем: управлять им здесь
    // нечем, выбрать его можно в смене прокси у аккаунта.
    const ownProxies = proxies.filter((p) => !p.isBuiltin);
    const activeCount = ownProxies.filter((p) => p.status === "active").length;
    const failedCount = ownProxies.filter((p) => p.status === "failed").length;
    const totalCount = ownProxies.length;
    const proxyStats = [
      { id: "active", label: t("Работают"), value: activeCount, color: "#34d399", tone: "rgba(16,185,129,0.16)" },
      { id: "failed", label: t("Не работают"), value: failedCount, color: "#fb7185", tone: "rgba(239,68,68,0.16)" },
      { id: "total", label: t("Всего"), value: totalCount, color: A[300], tone: aa(500, 0.16) }
    ];
    const applyHoverStyles = (event, styles) => {
      if (isPhoneView) return;
      if (!event?.currentTarget || !styles) return;
      Object.assign(event.currentTarget.style, styles);
    };
    const actionHoverEnter = {
      background: `linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)})`,
      borderColor: aa(300, 0.56),
      boxShadow: `0 14px 28px ${aa(900, 0.24)}`,
      transform: "translateY(-2px)"
    };
    const buildActionHoverLeave = (background, borderColor) => ({
      background,
      borderColor,
      boxShadow: "none",
      transform: "translateY(0)"
    });

    return (
      <div className="proxies-tab-root theme-light-surface-scope" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <div className="section-header" style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isPhoneView ? "stretch" : "center",
          flexDirection: isPhoneView ? "column" : "row",
          gap: "10px"
        }}>
          {renderSectionTitle(LinkIcon, t("Прокси серверы"), {
            iconColor: A[300],
            background: `linear-gradient(135deg, ${aa(500, 0.3)}, ${aa(600, 0.28)})`,
            border: `1px solid ${aa(400, 0.45)}`,
            boxShadow: `0 4px 15px ${aa(500, 0.32)}`
          })}
          <div className="section-header-actions" style={{
            display: "flex",
            gap: "10px",
            flexDirection: isPhoneView ? "column" : "row",
            width: isPhoneView ? "100%" : "auto"
          }}>
            <button
              type="button"
              className="kl-plain-button kl-proxy-hover kl-press-spin"
              onClick={checkAllProxies}
              disabled={checkingAllProxies}
              style={{
                padding: "0 18px",
                height: "44px",
                color: "#d5d8e0",
                border: "1px solid rgba(255,255,255,0.12)",
                background: "transparent",
                borderRadius: "14px",
                cursor: checkingAllProxies ? "not-allowed" : "pointer",
                width: isPhoneView ? "100%" : "auto",
                display: "flex",
                alignItems: "center",
                gap: "9px",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "14px",
                opacity: checkingAllProxies ? 0.65 : 1,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(event) => {
                if (checkingAllProxies) return;
                applyHoverStyles(event, { borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.03)" });
              }}
              onMouseLeave={(event) => {
                if (checkingAllProxies) return;
                applyHoverStyles(event, { borderColor: "rgba(255,255,255,0.12)", background: "transparent" });
              }}
            >
              <span className="kl-btn-ico"><RefreshIcon size={17} /></span>
              {checkingAllProxies ? t("Проверка...") : t("Проверить все")}
            </button>
          </div>
        </div>

        <InlineAddProxy
          isPhoneView={isPhoneView}
          onSuccess={handleAddProxy}
        />

        <div style={{
          display: "grid",
          gridTemplateColumns: isPhoneView ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: "12px"
        }}>
          {proxyStats.map((item) => (
            <div key={item.id} className="proxies-stat-card" style={{
              borderRadius: "14px",
              border: "1px solid rgba(141,147,163,0.16)",
              background: S.surface,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px"
            }}>
              <div style={{ color: textMuted, fontSize: "13px", fontWeight: "500" }}>{item.label}</div>
              <div style={{ color: item.color, fontSize: "26px", fontWeight: "700", lineHeight: 1, letterSpacing: "-0.02em" }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {ownProxies.length === 0 ? (
          <div className="proxies-empty-state" style={{
            borderRadius: "22px",
            border: "1px solid rgba(141,147,163,0.22)",
            background: S.surface,
            boxShadow: "0 20px 40px rgba(0,0,0,0.36)",
            padding: "40px 24px",
            textAlign: "center"
          }}>
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "16px",
              background: aa(400, 0.12),
              border: `1px solid ${aa(400, 0.28)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px"
            }}>
              <LinkIcon size={30} color={A[300]} />
            </div>
            <h3 style={{ color: textTitle, marginBottom: "8px", fontWeight: "700" }}>{t("Нет прокси")}</h3>
            <p style={{ color: textMuted, marginBottom: "0" }}>{t("Добавьте первый прокси в форме выше, чтобы выполнять действия через аккаунты")}</p>
          </div>
        ) : (
          <div className="proxies-list-shell" style={{
            border: isPhoneView ? "none" : "1px solid rgba(141,147,163,0.16)",
            borderRadius: isPhoneView ? "0" : "18px",
            background: isPhoneView ? "transparent" : S.surface,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: isPhoneView ? "10px" : "0"
          }}>
            {!isPhoneView && (
              <div className="proxies-table-head" style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.7fr) 128px minmax(0, 1fr) minmax(0, 1.35fr) 130px 84px",
                alignItems: "center",
                gap: "14px",
                padding: "13px 20px",
                borderBottom: "1px solid rgba(141,147,163,0.14)",
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#707684"
              }}>
                <span>Host : Port</span>
                <span>{t("Статус")}</span>
                <span>{t("Локация")}</span>
                <span>ISP / Exit</span>
                <span>{t("Пинг")}</span>
                <span style={{ textAlign: "right" }}>{t("Действия")}</span>
              </div>
            )}
            {ownProxies.map((proxy) => (
              <ProxyChecker
                key={proxy.id}
                proxy={proxy}
                onCheckComplete={(result) => handleProxyCheckComplete(proxy.id, result)}
                onDelete={() => handleDeleteProxy(proxy.id)}
                isPhoneView={isPhoneView}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: "dashboard", label: t("Дашборд"), icon: <DashboardIcon size={18} />, color: A[400] },
    { id: "accounts", label: t("Аккаунты"), icon: <UserIcon size={18} />, color: A[400] },
    { id: "proxies", label: t("Прокси"), icon: <LinkIcon size={18} />, color: "#34d399" },
    { id: "messages", label: t("Сообщения"), icon: <MessageIcon size={18} />, color: "#fb923c" },
    { id: "sender", label: t("Сендер"), icon: <SendIcon size={18} />, color: A[400] },
    { id: "drafts", label: t("Черновики"), icon: <FileIcon size={18} />, color: A[300] },
    { id: "kl-parser", label: t("Парсер"), icon: <TargetIcon size={18} />, color: A[300] },
    { id: "active-ads", label: t("Объявления"), icon: <PackageIcon size={18} />, color: "#facc15" },
    { id: "settings", label: t("Настройки"), icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, color: "#8d93a3" }
  ];
  const toggleNotificationsPanel = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Anchor the dropdown to the bell that was clicked (drops right below it).
    const trigger = event?.currentTarget;
    if (trigger && typeof trigger.getBoundingClientRect === "function") {
      const r = trigger.getBoundingClientRect();
      setNotifAnchorRect({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width });
    }
    setShowProfilePanel(false);
    setShowNotificationsPanel((prev) => !prev);
  };
  const toggleProfilePanel = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowNotificationsPanel(false);
    setShowProfilePanel((prev) => !prev);
  };
  const shouldHideTopHeader = Boolean(
    isPhoneView
    && (
      publishProgressModal.open
      || (activeTab === "messages" && isMobileMessagesChatExpanded)
    )
  );
  const publishPostStatusMeta = resolvePostPublishStatusMeta(
    publishProgressModal.publicationProbe,
    publishProgressModal.publicationStatus
  );
  const publishModalAdTitle = String(publishProgressModal?.adTitle || "").trim();
  const publishModalAccountLabel = String(publishProgressModal?.accountLabel || "").trim();
  // Пока грузится код раздела — заготовка его раскладки, как на nexfill.ws,
  // а не надпись «Загрузка…» в пустом прямоугольнике.
  const TabLazyFallback = () => <SectionSkeleton isPhoneView={isPhoneView} />;

  const renderTabContent = () => (
    <div key={activeTab} className="tab-content-animated kl-tab-content">
      {/* Вызов, а не элемент: DashboardTab и AccountsTab объявлены внутри App,
          и как элементы они получают новую функцию-тип на каждый рендер — React
          считает это другим компонентом и пересобирает поддерево заново. Раньше
          это было незаметно, а теперь анимации появления проигрывались на каждое
          обновление данных, и дашборд «скакал». Хуков внутри них нет, поэтому
          условный вызов безопасен — так же уже сделано для вкладки прокси. */}
      {activeTab === "dashboard" && DashboardTab()}
      {activeTab === "accounts" && AccountsTab()}
      {/* Inline the render (ProxiesTab is a hook-less local render fn) instead of
          <ProxiesTab />: rendering it as an element remounts the whole subtree on
          every App re-render (a new fn identity each time), which reset the inline
          add-proxy form mid-check — clearing it, dropping the check result and
          doubling the «Проверить» button. */}
      {activeTab === "proxies" && ProxiesTab()}
      {activeTab === "messages" && (
        <div className="theme-light-surface-scope messages-tab-scope">
          <Suspense fallback={<TabLazyFallback />}>
            <MessagesPage
              ownerId={tokenStatus?.ownerId || ""}
              onUnreadCountChange={setMessagesTabUnreadCount}
              onMobileFullscreenChange={setIsMobileMessagesChatExpanded}
              onMobileThreadViewChange={setIsMobileMessagesThreadOpen}
              privateMode={privateMode}
              openConversationRequest={pendingConversationOpen}
              onConversationRequestHandled={() => setPendingConversationOpen(null)}
            />
          </Suspense>
        </div>
      )}
      {activeTab === "sender" && (
        <div className="theme-light-surface-scope sender-tab-scope">
          <Suspense fallback={<TabLazyFallback />}>
            <SenderPage
              ownerId={currentOwnerId}
              accounts={accounts}
              isPhoneView={isPhoneView}
              pushToast={pushToast}
            />
          </Suspense>
        </div>
      )}
      {activeTab === "drafts" && (
        <div className="theme-light-surface-scope drafts-tab-scope">
          <Suspense fallback={<TabLazyFallback />}>
            <DraftsPage
              ownerId={currentOwnerId}
              accounts={accounts}
              isPhoneView={isPhoneView}
              pushToast={pushToast}
              onEditDraft={handleEditDraft}
              onPublishDraft={handlePublishDraftNow}
            />
          </Suspense>
        </div>
      )}
      {activeTab === "kl-parser" && (
        <div className="theme-light-surface-scope">
          <Suspense fallback={<TabLazyFallback />}>
            <KlParserTab
              ownerId={tokenStatus?.ownerId || ""}
              isPhoneView={isPhoneView}
              pushToast={pushToast}
              canPurgeParserAds={Boolean(tokenStatus?.canPurgeParserAds || isAdminUser)}
            />
          </Suspense>
        </div>
      )}
      {activeTab === "active-ads" && (
        <div className="theme-light-surface-scope active-ads-tab-scope">
          <Suspense fallback={<TabLazyFallback />}>
            <ActiveAdsPage
              requestConfirm={requestConfirmModal}
              ownerId={currentOwnerId}
              accounts={accounts}
              isPhoneView={isPhoneView}
              pushToast={pushToast}
              onAdsChanged={() => loadAccountMetrics({ forceAds: true, reloadAccounts: false })}
              privateMode={privateMode}
              isAdmin={isAdminUser}
            />
          </Suspense>
        </div>
      )}
      {activeTab === "settings" && (
        <div className="settings-tab-root theme-light-surface-scope" style={{ maxWidth: "920px", margin: "0 auto" }}>
          <div style={{ marginBottom: "28px" }}>
            {renderSectionTitle(SettingsIcon, t("Настройки"), { subtitle: t("Профиль, подписка, уведомления и оформление") })}
          </div>
          <div style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: surfaceTextTertiary, margin: "0 4px 10px" }}>{t("Оформление")}</div>
          {/* Тема — той же карточкой, что соседние настройки вида, но выбор
              плитками с миниатюрой панели (components/ThemePicker.jsx).
              Переключение пишет data-theme на <html> и запоминается в
              localStorage — это делает ThemeProvider. */}
          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px 18px",
            marginBottom: "16px"
          }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: isDarkTheme ? "#f0f1f4" : "#0e1015", marginBottom: "4px" }}>
              {t("Тема оформления")}
            </div>
            <div style={{ fontSize: "12.5px", color: isDarkTheme ? "#8d93a3" : "#3f4450", lineHeight: 1.5, marginBottom: "14px" }}>
              {t("Применяется сразу ко всей панели и запоминается в этом браузере.")}
            </div>
            <ThemePicker value={themeMode} onChange={setThemeMode} isPhoneView={isPhoneView} />
          </div>

          {/* Обои под сообщениями в открытом диалоге — плитками, как тема
              (components/WallpaperPicker.jsx, utils/chatWallpapers.js). */}
          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px 18px",
            marginBottom: "16px"
          }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: isDarkTheme ? "#f0f1f4" : "#0e1015", marginBottom: "4px" }}>
              {t("Обои переписки")}
            </div>
            <div style={{ fontSize: "12.5px", color: isDarkTheme ? "#8d93a3" : "#3f4450", lineHeight: 1.5, marginBottom: "14px" }}>
              {t("Фон под сообщениями в открытом диалоге. Рисуется цветом выбранной темы и запоминается в этом браузере.")}
            </div>
            <WallpaperPicker
              value={chatWallpaper}
              onChange={(id) => setChatWallpaper(saveChatWallpaper(id))}
              isPhoneView={isPhoneView}
            />
          </div>

          {/* Эффект: летящие зонтики поверх панели. Той же карточкой, что
              шрифт, — это соседняя настройка вида. */}
          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px",
            marginBottom: "16px",
            display: "flex",
            alignItems: isPhoneView ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: "14px",
            flexDirection: isPhoneView ? "column" : "row"
          }}>
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: isDarkTheme ? "#f0f1f4" : "#0e1015", marginBottom: "4px" }}>
                {t("Эффект")}
              </div>
              <div style={{ fontSize: "12.5px", color: isDarkTheme ? "#8d93a3" : "#3f4450", lineHeight: 1.5 }}>
                {t("Зонтики из знака панели, медленно летящие поверх интерфейса. Работе не мешают — нажатия сквозь них проходят. Выбор хранится в этом браузере.")}
              </div>
            </div>
            <div style={{
              display: "inline-flex",
              padding: "4px",
              borderRadius: "16px",
              background: isGraphite ? "rgba(0,0,0,0.25)" : isDarkTheme ? `${sa(0.6)}` : "rgba(14,16,21,0.05)",
              border: isGraphite ? "1px solid rgba(255,255,255,0.08)" : isDarkTheme ? "1px solid rgba(141,147,163,0.12)" : "1px solid rgba(14,16,21,0.08)"
            }}>
              {[
                { id: "off", label: t("Выключен") },
                { id: "umbrellas", label: t("Зонтики") }
              ].map((option) => {
                const isActive = ambientMode === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setAmbientMode(option.id)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "9px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      background: isActive ? A[500] : "transparent",
                      color: isActive ? onAccent("#ffffff") : isDarkTheme ? "#c6c9d1" : "#262a33",
                      boxShadow: isActive ? `0 6px 16px ${aa(500, 0.35)}` : "none",
                      transition: "background 0.18s ease, color 0.18s ease"
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Шрифт: та же карточка и те же фишки, что у темы. */}
          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px",
            marginBottom: "16px",
            display: "flex",
            alignItems: isPhoneView ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: "14px",
            flexDirection: isPhoneView ? "column" : "row"
          }}>
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: isDarkTheme ? "#f0f1f4" : "#0e1015", marginBottom: "4px" }}>
                {t("Шрифт")}
              </div>
              <div style={{ fontSize: "12.5px", color: isDarkTheme ? "#8d93a3" : "#3f4450", lineHeight: 1.5 }}>
                {t("Лёгкий — тонкий и компактный, больше текста на экране; стоит по умолчанию. Плотный — шире и жирнее.")}
              </div>
            </div>
            <div style={{
              display: "inline-flex",
              padding: "4px",
              borderRadius: "16px",
              background: isGraphite ? "rgba(0,0,0,0.25)" : isDarkTheme ? `${sa(0.6)}` : "rgba(14,16,21,0.05)",
              border: isGraphite ? "1px solid rgba(255,255,255,0.08)" : isDarkTheme ? "1px solid rgba(141,147,163,0.12)" : "1px solid rgba(14,16,21,0.08)"
            }}>
              {[
                { id: "light", label: t("Лёгкий") },
                { id: "dense", label: t("Плотный") }
              ].map((option) => {
                const isActive = fontMode === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFontMode(option.id)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "9px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      background: isActive ? A[500] : "transparent",
                      color: isActive ? onAccent("#ffffff") : isDarkTheme ? "#c6c9d1" : "#262a33",
                      boxShadow: isActive ? `0 6px 16px ${aa(500, 0.35)}` : "none",
                      transition: "background 0.18s ease, color 0.18s ease"
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px",
            marginBottom: "16px",
            display: "flex",
            alignItems: isPhoneView ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: "14px",
            flexDirection: isPhoneView ? "column" : "row"
          }}>
            <div>
              <div style={{
                fontSize: "15px",
                fontWeight: 700,
                color: isDarkTheme ? "#f0f1f4" : "#0e1015",
                marginBottom: "4px"
              }}>
                {t("Язык интерфейса")}
              </div>
              <div style={{
                fontSize: "12.5px",
                color: isDarkTheme ? "#8d93a3" : "#3f4450",
                lineHeight: 1.5
              }}>
                {t("Выбор сохраняется в этом браузере. Тексты от Kleinanzeigen остаются на языке оригинала.")}
              </div>
            </div>
            <div style={{
              display: "inline-flex",
              padding: "4px",
              borderRadius: "16px",
              background: isGraphite
                ? "rgba(0,0,0,0.25)"
                : isDarkTheme ? `${sa(0.6)}` : "rgba(14,16,21,0.05)",
              border: isGraphite
                ? "1px solid rgba(255,255,255,0.08)"
                : isDarkTheme ? "1px solid rgba(141,147,163,0.12)" : "1px solid rgba(14,16,21,0.08)"
            }}>
              {LANGUAGES.map((option) => {
                const isActive = lang === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setLang(option.value)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "9px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      background: isActive ? A[500] : "transparent",
                      color: isActive ? onAccent("#ffffff") : isDarkTheme ? "#c6c9d1" : "#262a33",
                      boxShadow: isActive ? `0 6px 16px ${aa(500, 0.35)}` : "none",
                      transition: "background 0.18s ease, color 0.18s ease"
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Язык категорий — только подписи рубрик и параметров в панели (см.
              categoryLang.js). По умолчанию немецкий, как их отдаёт Kleinanzeigen. */}
          <div style={{
            borderRadius: "18px",
            border: isDarkTheme ? "1px solid rgba(141,147,163,0.16)" : "1px solid rgba(14,16,21,0.08)",
            background: isDarkTheme ? `${sa(0.45)}` : "rgba(255,255,255,0.9)",
            padding: isPhoneView ? "14px" : "16px 18px",
            marginBottom: "16px",
            display: "flex",
            alignItems: isPhoneView ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: "14px",
            flexDirection: isPhoneView ? "column" : "row"
          }}>
            <div>
              <div style={{
                fontSize: "15px",
                fontWeight: 700,
                color: isDarkTheme ? "#f0f1f4" : "#0e1015",
                marginBottom: "4px"
              }}>
                {t("Язык категорий")}
              </div>
              <div style={{
                fontSize: "12.5px",
                color: isDarkTheme ? "#8d93a3" : "#3f4450",
                lineHeight: 1.5
              }}>
                {t("Названия категорий и их параметров в окнах объявлений и черновиках. В Kleinanzeigen объявление уходит как обычно — на немецком. Выбор сохраняется в этом браузере.")}
              </div>
            </div>
            <div style={{
              display: "inline-flex",
              padding: "4px",
              borderRadius: "16px",
              background: isGraphite
                ? "rgba(0,0,0,0.25)"
                : isDarkTheme ? `${sa(0.6)}` : "rgba(14,16,21,0.05)",
              border: isGraphite
                ? "1px solid rgba(255,255,255,0.08)"
                : isDarkTheme ? "1px solid rgba(141,147,163,0.12)" : "1px solid rgba(14,16,21,0.08)"
            }}>
              {CATEGORY_LANGUAGES.map((option) => {
                const isActive = getCategoryLang() === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCategoryLang(option.value)}
                    aria-pressed={isActive}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "9px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      background: isActive ? A[500] : "transparent",
                      color: isActive ? onAccent("#ffffff") : isDarkTheme ? "#c6c9d1" : "#262a33",
                      boxShadow: isActive ? `0 6px 16px ${aa(500, 0.35)}` : "none",
                      transition: "background 0.18s ease, color 0.18s ease"
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          {freezeAccessLocked && (
            <div style={{
              borderRadius: "18px",
              border: "1px solid rgba(245,158,11,0.34)",
              background: isDarkTheme
                ? "linear-gradient(135deg, rgba(120,53,15,0.24), rgba(23,25,33,0.38))"
                : "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(255,255,255,0.95))",
              boxShadow: isDarkTheme ? "0 12px 28px rgba(0,0,0,0.28)" : "0 8px 20px rgba(180,83,9,0.12)",
              padding: isPhoneView ? "12px" : "14px 16px",
              marginBottom: "12px",
              color: isDarkTheme ? "#fde68a" : "#92400e",
              fontSize: "13px",
              lineHeight: 1.45
            }}>
              {t("Разделы панели временно заблокированы, пока подписка на заморозке. Отключите заморозку в блоке ниже.")}
              <div style={{ marginTop: "10px" }}>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={() => {
                    const target = document.getElementById("subscription-freeze-section");
                    if (target && typeof target.scrollIntoView === "function") {
                      target.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "14px",
                    border: "1px solid rgba(245,158,11,0.42)",
                    background: "rgba(120,53,15,0.3)",
                    color: "#fde68a",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer"
                  }}
                >
                  {t("Перейти к разморозке")}
                </button>
              </div>
            </div>
          )}
          <div style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: surfaceTextTertiary, margin: "28px 4px 10px" }}>{t("Подписка и профиль")}</div>
          {/* Профиль: карточка с логином, кнопкой подписки и тремя фактами
              (подписка, заморозка, Telegram-бот), под ней — строки настроек
              в том же виде, что звук и уведомления ниже. Форма пароля
              раскрывается по кнопке, а не висит открытой. */}
          {(() => {
            const cardStyle = {
              borderRadius: "18px",
              background: isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)",
              border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)"}`
            };
            const dividerColor = isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.07)";
            const fieldStyle = {
              width: "100%",
              height: "40px",
              padding: "0 12px",
              borderRadius: "11px",
              border: `1px solid ${surfaceInputBorder}`,
              background: isDarkTheme ? "rgba(8,9,12,0.6)" : "#ffffff",
              color: surfaceInputText,
              fontSize: "13px",
              boxSizing: "border-box"
            };
            const secondaryButtonStyle = {
              padding: "0 14px",
              height: "36px",
              borderRadius: "11px",
              border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.14)"}`,
              background: "transparent",
              color: isDarkTheme ? "#d6d9e0" : "#262a33",
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              cursor: "pointer"
            };
            const formatDays = (value) => {
              const days = Number(value) || 0;
              return Number.isInteger(days) ? String(days) : days.toFixed(1);
            };
            const freezeRemainingText = `${t("Осталось:")} ${freezeRemainingCount} ${t("раз /")} ${formatDays(freezeRemainingDays)} ${t("дн.")}`;
            const username = String(authIdentity?.username || "").trim();
            const hasPassword = authIdentity?.hasPassword !== false;
            const expiresText = tokenStatus.expiresAt
              ? tf`до ${new Date(tokenStatus.expiresAt).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })}`
              : t("Бессрочно");
            const telegramHandle = String(telegramBotStatus.username || "").trim();
            const facts = [
              {
                id: "subscription",
                label: t("Подписка"),
                ...(demoActive
                  ? { value: t("Не оформлена"), hint: t("Демо-режим"), dot: "#8d93a3" }
                  : freezeActive
                    ? { value: t("Заморожена"), hint: expiresText, dot: "#f59e0b" }
                    : tokenStatus.state === "valid"
                      ? { value: t("Активна"), hint: expiresText, dot: "#22c55e" }
                      : { value: t("Не активна"), hint: "", dot: "#ef4444" })
              },
              {
                id: "freeze",
                label: t("Заморозка"),
                ...(!freezeSupported
                  ? { value: t("Недоступна"), hint: t("На текущем тарифе"), dot: "" }
                  : freezeActive
                    ? { value: t("Включена"), hint: freezeRemainingText, dot: "#f59e0b" }
                    : { value: t("Выключена"), hint: freezeRemainingText, dot: "#8d93a3" })
              },
              {
                id: "telegram",
                label: t("Telegram-бот"),
                ...(telegramBotStatus.linked
                  ? {
                    value: t("Привязан"),
                    hint: [
                      telegramHandle ? (telegramHandle.startsWith("@") ? telegramHandle : `@${telegramHandle}`) : (telegramBotStatus.chatIdMasked ? `chat: ${telegramBotStatus.chatIdMasked}` : ""),
                      telegramBotStatus.totalChats > 1 ? tf` · чатов: ${telegramBotStatus.totalChats}`.replace(/^ · /, "") : ""
                    ].filter(Boolean).join(" · "),
                    dot: "#22c55e"
                  }
                  : { value: t("Не привязан"), hint: "", dot: "#8d93a3" })
              }
            ];
            const freezeBusy = freezeActionLoading || (!freezeActive && !freezeCanStart);
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: isPhoneView ? "0 2px" : "0 4px", marginBottom: "16px" }}>
                <div style={{ ...cardStyle, padding: isPhoneView ? "16px" : "18px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: isPhoneView ? "wrap" : "nowrap" }}>
                    <button
                      type="button"
                      onClick={() => { if (!demoActive) avatarInputRef.current?.click(); }}
                      disabled={avatarBusy || demoActive}
                      title={demoActive ? undefined : t("Сменить фото")}
                      aria-label={t("Сменить фото")}
                      className="settings-avatar-button"
                      style={{
                        position: "relative", width: "46px", height: "46px", padding: 0, border: "none", borderRadius: "14px",
                        background: "transparent", flexShrink: 0, cursor: demoActive ? "default" : "pointer"
                      }}
                    >
                      <UserAvatar username={username} avatarUrl={authIdentity?.avatarUrl} size={46} radius={14} fontSize={15} isDarkTheme={isDarkTheme} />
                      {avatarBusy && (
                        <span style={{ position: "absolute", inset: 0, borderRadius: "14px", background: "rgba(8,9,12,0.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                          <Spinner size={16} />
                        </span>
                      )}
                    </button>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: surfaceTextPrimary, fontSize: "16px", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {username || "—"}
                      </div>
                      <div style={{ color: surfaceTextTertiary, fontSize: "12.5px", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        ID: {tokenStatus.ownerId || "—"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openPaymentModal()}
                      style={{
                        height: "38px", padding: "0 18px", borderRadius: "12px", border: "none", cursor: "pointer",
                        background: A[500], color: onAccent("#fff"),
                        fontWeight: 700, fontSize: "13.5px", whiteSpace: "nowrap", flexShrink: 0,
                        width: isPhoneView ? "100%" : "auto"
                      }}
                    >
                      {demoActive ? t("Оформить подписку") : t("Продлить подписку")}
                    </button>
                  </div>
                  {/* Пункты разнесены по всей ширине: первый у левого края,
                      последний у правого — равные колонки оставляли справа
                      пустоту за коротким последним пунктом. */}
                  <div style={{
                    marginTop: "16px",
                    paddingTop: "14px",
                    borderTop: `1px solid ${dividerColor}`,
                    display: isPhoneView ? "grid" : "flex",
                    gridTemplateColumns: isPhoneView ? "1fr" : undefined,
                    justifyContent: isPhoneView ? undefined : "space-between",
                    gap: isPhoneView ? "12px" : "24px"
                  }}>
                    {facts.map((fact) => {
                      const value = (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: isPhoneView ? "flex-end" : "flex-start", gap: "7px", color: surfaceTextPrimary, fontSize: "14px", fontWeight: 600 }}>
                          {fact.dot && <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: fact.dot, flexShrink: 0 }} />}
                          {fact.value}
                        </div>
                      );
                      const hint = fact.hint ? (
                        <div style={{ marginTop: "2px", color: surfaceTextTertiary, fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fact.hint}</div>
                      ) : null;
                      // На телефоне — строкой «название … значение», чтобы три
                      // факта не растягивали карточку на экран.
                      return isPhoneView ? (
                        <div key={fact.id} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                          <div style={{ color: surfaceTextTertiary, fontSize: "13px", paddingTop: "1px" }}>{fact.label}</div>
                          <div style={{ minWidth: 0, textAlign: "right" }}>{value}{hint}</div>
                        </div>
                      ) : (
                        <div key={fact.id} style={{ minWidth: 0 }}>
                          <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginBottom: "4px" }}>{fact.label}</div>
                          {value}
                          {hint}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
                  style={{ display: "none" }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) uploadAvatar(file);
                  }}
                />
                <div id="subscription-freeze-section" style={{ ...cardStyle, overflow: "hidden" }}>
                  <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Логин")}</div>
                      <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4, overflowWrap: "anywhere" }}>
                        {tf`Для входа в панель: ${username || "—"}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="kl-plain-button"
                      onClick={() => {
                        setUsernameForm({ username: usernameFormOpen ? "" : username, currentPassword: "" });
                        setUsernameFormOpen((prev) => !prev);
                      }}
                      disabled={usernameSaving}
                      style={secondaryButtonStyle}
                    >
                      {usernameFormOpen ? t("Отмена") : t("Изменить")}
                    </button>
                  </div>
                  {usernameFormOpen && (
                    <form
                      onSubmit={(event) => { event.preventDefault(); changeUsername(); }}
                      style={{
                        padding: "0 16px 16px",
                        display: "grid",
                        gridTemplateColumns: isPhoneView ? "1fr" : (hasPassword ? "repeat(2, minmax(0, 1fr)) auto" : "minmax(0, 1fr) auto"),
                        gap: "8px"
                      }}
                    >
                      <input
                        type="text"
                        autoComplete="username"
                        autoFocus
                        spellCheck={false}
                        maxLength={40}
                        value={usernameForm.username}
                        onChange={(event) => setUsernameForm((prev) => ({ ...prev, username: event.target.value }))}
                        placeholder={t("Новый логин")}
                        style={fieldStyle}
                      />
                      {hasPassword && (
                        <input
                          type="password"
                          autoComplete="current-password"
                          value={usernameForm.currentPassword}
                          onChange={(event) => setUsernameForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
                          placeholder={t("Текущий пароль")}
                          style={fieldStyle}
                        />
                      )}
                      <button
                        type="submit"
                        disabled={usernameSaving}
                        style={{
                          height: "40px", padding: "0 18px", borderRadius: "11px", border: "none",
                          background: A[500], color: onAccent("#fff"), fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap",
                          cursor: usernameSaving ? "not-allowed" : "pointer",
                          opacity: usernameSaving ? 0.65 : 1
                        }}
                      >
                        {usernameSaving ? t("Сохранение...") : t("Сохранить")}
                      </button>
                      <div style={{ gridColumn: "1 / -1", color: surfaceTextTertiary, fontSize: "11.5px", lineHeight: 1.4 }}>
                        {t("Латиница, цифры, точка, дефис и подчёркивание — от 3 до 40 символов.")}
                      </div>
                    </form>
                  )}

                  <div style={{ borderTop: `1px solid ${dividerColor}`, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Фото профиля")}</div>
                      <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                        {t("JPG, PNG или WebP до 8 МБ. При входе через Telegram фото подтягивается оттуда само.")}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      {authIdentity?.avatarUrl && (
                        <button
                          type="button"
                          className="kl-plain-button"
                          onClick={removeAvatar}
                          disabled={avatarBusy}
                          title={t("Удалить фото")}
                          aria-label={t("Удалить фото")}
                          style={{ ...secondaryButtonStyle, width: "36px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <TrashIcon size={15} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={avatarBusy}
                        style={secondaryButtonStyle}
                      >
                        {avatarBusy ? t("Загрузка...") : (authIdentity?.avatarUrl ? t("Заменить") : t("Загрузить"))}
                      </button>
                    </div>
                  </div>

                  <div style={{ borderTop: `1px solid ${dividerColor}`, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Пароль")}</div>
                      <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                        {hasPassword
                          ? t("После смены пароля сессии на других устройствах будут автоматически завершены.")
                          : t("Аккаунт создан через Telegram, своего пароля у него пока нет. Задайте его, чтобы входить и по логину.")}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="kl-plain-button"
                      onClick={() => {
                        if (passwordFormOpen) setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                        setPasswordFormOpen((prev) => !prev);
                      }}
                      disabled={changePasswordLoading}
                      style={secondaryButtonStyle}
                    >
                      {passwordFormOpen ? t("Отмена") : (hasPassword ? t("Сменить пароль") : t("Задать пароль"))}
                    </button>
                  </div>
                  {passwordFormOpen && (
                    <form
                      onSubmit={(event) => { event.preventDefault(); changePassword(); }}
                      style={{
                        padding: "0 16px 16px",
                        display: "grid",
                        gridTemplateColumns: isPhoneView ? "1fr" : (hasPassword ? "repeat(3, minmax(0, 1fr)) auto" : "repeat(2, minmax(0, 1fr)) auto"),
                        gap: "8px"
                      }}
                    >
                      {hasPassword && (
                        <input
                          type="password"
                          autoComplete="current-password"
                          autoFocus
                          value={changePasswordForm.currentPassword}
                          onChange={(event) => setChangePasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
                          placeholder={t("Текущий пароль")}
                          style={fieldStyle}
                        />
                      )}
                      <input
                        type="password"
                        autoComplete="new-password"
                        autoFocus={!hasPassword}
                        value={changePasswordForm.newPassword}
                        onChange={(event) => setChangePasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))}
                        placeholder={t("Новый пароль")}
                        style={fieldStyle}
                      />
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={changePasswordForm.confirmPassword}
                        onChange={(event) => setChangePasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                        placeholder={t("Подтвердите новый пароль")}
                        style={fieldStyle}
                      />
                      <button
                        type="submit"
                        disabled={changePasswordLoading}
                        style={{
                          height: "40px", padding: "0 18px", borderRadius: "11px", border: "none",
                          background: A[500], color: onAccent("#fff"), fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap",
                          cursor: changePasswordLoading ? "not-allowed" : "pointer",
                          opacity: changePasswordLoading ? 0.65 : 1
                        }}
                      >
                        {changePasswordLoading ? t("Сохранение...") : t("Сохранить")}
                      </button>
                    </form>
                  )}

                  {!demoActive && (<>
                    <div style={{ borderTop: `1px solid ${dividerColor}`, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Ключ подписки")}</div>
                        <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                          {t("Ключ из розыгрыша или от администратора. Дни добавятся к подписке сразу после активации.")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={() => setRedeemFormOpen((prev) => !prev)}
                        style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: "7px" }}
                      >
                        {!redeemFormOpen && <KeyIcon size={14} />}
                        {redeemFormOpen ? t("Отмена") : t("Ввести ключ")}
                      </button>
                    </div>
                    {redeemFormOpen && (
                      <div style={{ padding: "0 16px 16px" }}>
                        <RedeemKeyForm autoFocus onRedeemed={handleKeyRedeemed} />
                      </div>
                    )}
                  </>)}

                  {freezeSupported && profileSubscription?.active && (
                    <div style={{ borderTop: `1px solid ${dividerColor}`, padding: "14px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: isPhoneView ? "stretch" : "center", justifyContent: "space-between", gap: "14px", flexDirection: isPhoneView ? "column" : "row" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Заморозка подписки")}</div>
                          <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                            {freezeRemainingText} · {t("Лимит:")} {freezeMaxCount} {t("раз /")} {formatDays(freezeMaxDays)} {t("дн.")}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                          {!freezeActive && (
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={freezeDaysInput}
                              onChange={(event) => setFreezeDaysInput(event.target.value)}
                              placeholder={t("Дни")}
                              title={t("Дни заморозки (необязательно)")}
                              style={{ ...fieldStyle, width: "84px", flex: "0 0 84px", height: "36px" }}
                            />
                          )}
                          <button
                            type="button"
                            onClick={freezeActive ? stopOwnSubscriptionFreeze : startOwnSubscriptionFreeze}
                            disabled={freezeBusy}
                            style={{
                              height: "36px", padding: "0 14px", borderRadius: "11px", border: "none",
                              background: freezeActive ? "#d97706" : A[500],
                              color: freezeActive ? "#fff" : onAccent("#fff"),
                              fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap",
                              cursor: freezeBusy ? "not-allowed" : "pointer",
                              opacity: freezeBusy ? 0.6 : 1,
                              flex: isPhoneView ? "1 1 auto" : "none",
                              minWidth: 0
                            }}
                          >
                            {freezeActionLoading
                              ? t("Обработка...")
                              : (freezeActive ? t("Отключить заморозку") : t("Включить заморозку"))}
                          </button>
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => refreshPanelSubscriptionStatus()}
                            disabled={freezeActionLoading}
                            title={t("Обновить статус")}
                            aria-label={t("Обновить статус")}
                            style={{ ...secondaryButtonStyle, width: "36px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <RefreshIcon size={15} />
                          </button>
                        </div>
                      </div>
                      {freezeActionError && (
                        <div style={{
                          borderRadius: "11px",
                          border: "1px solid rgba(248,113,113,0.3)",
                          background: isDarkTheme ? "rgba(127,29,29,0.2)" : "rgba(220,38,38,0.06)",
                          color: isDarkTheme ? "#fecaca" : "#b91c1c",
                          fontSize: "12px",
                          padding: "8px 10px"
                        }}>
                          {freezeActionError}
                        </div>
                      )}
                    </div>
                  )}

                  {!demoActive && (
                    <div style={{ borderTop: `1px solid ${dividerColor}`, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t("Выход из аккаунта")}</div>
                        <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                          {t("Завершить сессию в этом браузере. Аккаунты, объявления и подписка останутся.")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="kl-plain-button settings-logout-button"
                        onClick={requestLogout}
                        style={{
                          ...secondaryButtonStyle,
                          display: "inline-flex", alignItems: "center", gap: "7px",
                          borderColor: isDarkTheme ? "rgba(248,113,113,0.35)" : "rgba(220,38,38,0.3)",
                          color: isDarkTheme ? "#fca5a5" : "#dc2626"
                        }}
                      >
                        <LogOutIcon size={14} />
                        {t("Выйти")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          <div style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: surfaceTextTertiary, margin: "28px 4px 10px" }}>{t("Уведомления")}</div>
          {/* Автообновление сообщений: как часто панель спрашивает Kleinanzeigen о новых
              сообщениях. Тот же вид, что у выбора языка быстрых действий. */}
          {(() => {
            const selectedRefresh = MESSAGES_REFRESH_OPTIONS.find((option) => option.value === messagesRefreshMinutes)
              || MESSAGES_REFRESH_OPTIONS[0];
            return (
              <div style={{
                borderRadius: "0",
                border: "none",
                background: "transparent",
                boxShadow: "none",
                padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
                marginBottom: "16px"
              }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                    <path d="M21 3v6h-6" />
                  </svg>
                  {t("Обновление сообщений")}
                </h3>
                {/* Цвета карточки — в классе ai-settings-row, как у строки быстрых
                    действий ассистента: светлая тема перекрашивает div с тёмным фоном
                    в style в белую поверхность, и карточка выбивалась бы из соседних. */}
                <div className="ai-settings-row" style={{
                  display: "flex", flexDirection: "column", gap: "12px",
                  padding: "14px 16px", borderRadius: "18px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px 14px", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                      <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600" }}>{t("Автообновление сообщений")}</div>
                      <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px", lineHeight: 1.45 }}>
                        {t("Как часто панель проверяет новые сообщения на ваших аккаунтах.")}
                      </div>
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={t("Интервал автообновления сообщений")}
                      style={{
                        display: isPhoneView ? "flex" : "inline-flex",
                        width: isPhoneView ? "100%" : "auto",
                        padding: "3px",
                        gap: "2px",
                        borderRadius: "12px",
                        background: isDarkTheme ? "rgba(8,9,12,0.55)" : "rgba(14,16,21,0.05)",
                        border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.2)" : "rgba(14,16,21,0.1)"}`,
                        flexShrink: 0
                      }}
                    >
                      {MESSAGES_REFRESH_OPTIONS.map((option) => {
                        const active = option.value === selectedRefresh.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className="ai-prompt-lang-option"
                            onClick={() => saveMessagesRefreshMinutes(option.value)}
                            style={{
                              flex: isPhoneView ? "1 1 0" : "0 0 auto",
                              minWidth: "58px",
                              height: "30px",
                              padding: "0 12px",
                              borderRadius: "9px",
                              border: "none",
                              background: active ? A[500] : "transparent",
                              color: active ? onAccent("#ffffff") : surfaceTextTertiary,
                              fontSize: "12px",
                              fontWeight: 700,
                              fontFamily: "inherit",
                              whiteSpace: "nowrap",
                              cursor: active ? "default" : "pointer",
                              transition: "background 0.2s ease, color 0.2s ease"
                            }}
                          >
                            {t(option.label)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    aria-live="polite"
                    style={{
                      display: "flex", alignItems: "flex-start", gap: "8px",
                      padding: "9px 12px", borderRadius: "12px",
                      background: aa(500, isDarkTheme ? 0.08 : 0.06),
                      border: `1px solid ${aa(400, isDarkTheme ? 0.18 : 0.22)}`,
                      color: surfaceTextSecondary, fontSize: "12.5px", lineHeight: 1.45
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: "2px" }}>
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    <span>{t(selectedRefresh.hint)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Sound settings */}
          <div style={{
            borderRadius: "0",
            border: "none",
            background: "transparent",
            boxShadow: "none",
            padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
            marginBottom: "16px"
          }}>
            <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
              {t("Звук")}
            </h3>
            <label style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 16px", borderRadius: "18px",
              background: isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)",
              border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)"}`,
              cursor: "pointer"
            }}>
              <div>
                <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600" }}>{t("Звук при новом сообщении")}</div>
                <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>{t("Только на десктопе")}</div>
              </div>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  const next = !soundEnabled;
                  setSoundEnabled(next);
                  try { localStorage.setItem("kl_sound_enabled", String(next)); } catch {}
                }}
                style={{
                  width: "44px", height: "24px", borderRadius: "16px",
                  background: soundEnabled ? A[500] : "rgba(63,68,80,0.5)",
                  position: "relative", cursor: "pointer", transition: "background 0.2s ease",
                  border: soundEnabled ? `1px solid ${aa(500, 0.5)}` : "1px solid rgba(112,118,132,0.3)"
                }}
              >
                <div style={{
                  width: "18px", height: "18px", borderRadius: "50%",
                  background: soundEnabled ? onAccent("#fff") : "#fff", position: "absolute", top: "2px",
                  left: soundEnabled ? "23px" : "2px", transition: "left 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                }} />
              </div>
            </label>
          </div>

          {/* Push на это устройство (utils/webPush.js). */}
          {(() => {
            const demo = isDemoActive();
            const pushOn = pushState === "on";
            const toggleEnabled = !demo && !pushBusy && (pushState === "on" || pushState === "off");
            const description = demo
              ? t("Недоступно в демо")
              : ({
                checking: t("Проверяем…"),
                on: t("Включено: сообщения покупателей приходят на это устройство"),
                off: t("Сообщения покупателей — даже когда панель закрыта"),
                denied: t("Уведомления запрещены в настройках браузера или телефона — разрешите их там"),
                "ios-install": t("На iPhone — только из панели на экране «Домой»: «Поделиться» → «На экран «Домой»», затем откройте её оттуда"),
                unsupported: isIosDevice() ? t("Нужна iOS 16.4 или новее") : t("Этот браузер не поддерживает push-уведомления")
              })[pushState] || "";
            return (
              <div style={{
                borderRadius: "0",
                border: "none",
                background: "transparent",
                boxShadow: "none",
                padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
                marginBottom: "16px"
              }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {t("Уведомления на этом устройстве")}
                </h3>
                <label style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
                  padding: "14px 16px", borderRadius: "18px",
                  background: isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)",
                  border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)"}`,
                  cursor: toggleEnabled ? "pointer" : "default"
                }}>
                  <div>
                    <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600" }}>{t("Новые сообщения")}</div>
                    <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>{description}</div>
                  </div>
                  <div
                    role="switch"
                    aria-checked={pushOn}
                    aria-disabled={!toggleEnabled}
                    onClick={(e) => {
                      e.preventDefault();
                      if (toggleEnabled) togglePush();
                    }}
                    style={{
                      width: "44px", height: "24px", borderRadius: "16px", flexShrink: 0,
                      background: pushOn ? A[500] : "rgba(63,68,80,0.5)",
                      position: "relative", cursor: toggleEnabled ? "pointer" : "default", transition: "background 0.2s ease",
                      border: pushOn ? `1px solid ${aa(500, 0.5)}` : "1px solid rgba(112,118,132,0.3)",
                      opacity: toggleEnabled || pushOn ? 1 : 0.5
                    }}
                  >
                    <div style={{
                      width: "18px", height: "18px", borderRadius: "50%",
                      background: pushOn ? onAccent("#fff") : "#fff", position: "absolute", top: "2px",
                      left: pushOn ? "23px" : "2px", transition: "left 0.2s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                    }} />
                  </div>
                </label>
              </div>
            );
          })()}

          {/* Telegram notification settings */}
          <div style={{
            borderRadius: "0",
            border: "none",
            background: "transparent",
            boxShadow: "none",
            padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px"
          }}>
            <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              {t("Уведомления Telegram")}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {[
                { key: "telegramNotifyNewMessages", label: t("Новые сообщения"), desc: t("Входящие сообщения от покупателей") },
                { key: "telegramNotifyAccountOffline", label: t("Аккаунт Offline"), desc: t("Kleinanzeigen перенаправляет на логин") },
                { key: "telegramNotifyProxyConnectionLost", label: t("Потеря соединения с прокси"), desc: t("Прокси-сервер недоступен") }
              ].map((item) => (
                <label key={item.key} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 16px", borderRadius: "18px",
                  background: isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)",
                  border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)"}`,
                  cursor: "pointer"
                }}>
                  <div>
                    <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600" }}>{item.label}</div>
                    <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>{item.desc}</div>
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      const next = { ...telegramSettings, [item.key]: !telegramSettings[item.key] };
                      saveTelegramSettings(next);
                    }}
                    style={{
                      width: "44px", height: "24px", borderRadius: "16px", flexShrink: 0,
                      background: telegramSettings[item.key] ? A[500] : "rgba(63,68,80,0.5)",
                      position: "relative", cursor: "pointer", transition: "background 0.2s ease",
                      border: telegramSettings[item.key] ? `1px solid ${aa(500, 0.5)}` : "1px solid rgba(112,118,132,0.3)"
                    }}
                  >
                    <div style={{
                      width: "18px", height: "18px", borderRadius: "50%",
                      background: telegramSettings[item.key] ? onAccent("#fff") : "#fff", position: "absolute", top: "2px",
                      left: telegramSettings[item.key] ? "23px" : "2px", transition: "left 0.2s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                    }} />
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Нейросеть — те же переключатели, что у уведомлений Telegram. */}
          <div style={{
            borderRadius: "0",
            border: "none",
            background: "transparent",
            boxShadow: "none",
            padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px"
          }}>
            <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
              <SparklesIcon size={18} color={A[400]} />
              {t("Нейросеть")}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {[
                { key: "aiIntentBadges", label: t("Пометки в диалогах"), desc: t("Angebot, PayPal F&F и W&D, Versand и Abholung — по последнему входящему") },
                { key: "aiReplySuggestions", label: t("Подсказка ответа в поле ввода"), desc: t("Готовый ответ серым текстом; Tab или «Вставить» кладут его в поле") },
                { key: "aiPhotoDescribe", label: t("Объявление по фото"), desc: t("Кнопка «Описать по фото» в карточке объявления") }
              ].map((item) => {
                const paused = Boolean(aiPaused[item.key]);
                return (
                <label key={item.key} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 16px", borderRadius: "18px",
                  background: isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)",
                  border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)"}`,
                  cursor: paused ? "default" : "pointer",
                  opacity: paused ? 0.7 : 1
                }}>
                  <div>
                    <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      {item.label}
                      {paused && (
                        <span style={{
                          fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "999px",
                          background: "rgba(251,191,36,0.14)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)"
                        }}>
                          {t("Приостановлено")}
                        </span>
                      )}
                    </div>
                    <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>
                      {paused ? t("Временно выключено на сервере, чтобы не расходовать лимит нейросети.") : item.desc}
                    </div>
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      if (paused) return;
                      saveAiPref(item.key, !aiPrefs[item.key]);
                    }}
                    style={{
                      width: "44px", height: "24px", borderRadius: "16px", flexShrink: 0,
                      background: aiPrefs[item.key] ? A[500] : "rgba(63,68,80,0.5)",
                      position: "relative", cursor: paused ? "not-allowed" : "pointer", transition: "background 0.2s ease",
                      border: aiPrefs[item.key] ? `1px solid ${aa(500, 0.5)}` : "1px solid rgba(112,118,132,0.3)"
                    }}
                  >
                    <div style={{
                      width: "18px", height: "18px", borderRadius: "50%",
                      background: aiPrefs[item.key] ? onAccent("#fff") : "#fff", position: "absolute", top: "2px",
                      left: aiPrefs[item.key] ? "23px" : "2px", transition: "left 0.2s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                    }} />
                  </div>
                </label>
                );
              })}
              <div className="ai-settings-row" style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "10px 14px", flexWrap: "wrap",
                padding: "14px 16px", borderRadius: "18px"
              }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <div style={{ color: surfaceInputText, fontSize: "14px", fontWeight: "600" }}>
                    {t("Быстрые действия ассистента")}
                  </div>
                  <div style={{ color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>
                    {t("Язык «Перевода» и «Анализа» в чате с ассистентом — не зависит от языка панели. Покупателю ответ всегда по-немецки")}
                  </div>
                </div>
                <div
                  role="radiogroup"
                  aria-label={t("Язык быстрых действий")}
                  style={{
                    display: "inline-flex",
                    padding: "3px",
                    gap: "2px",
                    borderRadius: "12px",
                    background: isDarkTheme ? "rgba(8,9,12,0.55)" : "rgba(14,16,21,0.05)",
                    border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.2)" : "rgba(14,16,21,0.1)"}`,
                    flexShrink: 0
                  }}
                >
                  {[{ value: "ru", label: "RU" }, { value: "en", label: "EN" }].map((option) => {
                    const active = aiPrefs.aiQuickActionsLang === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className="ai-prompt-lang-option"
                        onClick={() => saveAiQuickActionsLang(option.value)}
                        style={{
                          minWidth: "46px",
                          height: "30px",
                          padding: "0 12px",
                          borderRadius: "9px",
                          border: "none",
                          background: active ? A[500] : "transparent",
                          color: active ? onAccent("#ffffff") : surfaceTextTertiary,
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          fontFamily: "inherit",
                          cursor: active ? "default" : "pointer",
                          transition: "background 0.2s ease, color 0.2s ease"
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <AiPromptsSettings
            isDarkTheme={isDarkTheme}
            isPhoneView={isPhoneView}
            surfaceTextPrimary={surfaceTextPrimary}
            surfaceTextTertiary={surfaceTextTertiary}
            surfaceInputText={surfaceInputText}
            onNotify={pushToast}
          />

          {isAdminUser && (<>
            <div style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: surfaceTextTertiary, margin: "28px 4px 10px" }}>{t("Администрирование")}</div>
            {/* Тикеты о багах от пользователей — из панели и из бота. */}
            <AdminTicketsSection isPhoneView={isPhoneView} onError={handleAuthError} />
            {/* Расход нейросети по пользователям: сутки, неделя, месяц. */}
            <AdminAiUsageSection isPhoneView={isPhoneView} onError={handleAuthError} />
            {/* Ключи подписки: выпуск для розыгрышей и подарков, кто активировал. */}
            <AdminRedeemKeysSection isPhoneView={isPhoneView} onError={handleAuthError} onNotify={pushToast} />
            <div style={{
              borderRadius: "0",
              border: "none",
              background: "transparent",
              boxShadow: "none",
              padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
              marginTop: "16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "16px" }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                  <BellIcon size={18} color={A[400]} />
                  {t("Рассылка уведомлений")}
                </h3>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={loadAdminBroadcasts}
                  disabled={loadingAdminBroadcasts}
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(141,147,163,0.3)",
                    background: shellSurfaceSoft,
                    color: isDarkTheme ? "#c6c9d1" : "#262a33",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: loadingAdminBroadcasts ? "not-allowed" : "pointer",
                    opacity: loadingAdminBroadcasts ? 0.7 : 1
                  }}
                >
                  {loadingAdminBroadcasts ? t("Обновление...") : t("Обновить")}
                </button>
              </div>

              <div style={{ display: "grid", gap: "10px", marginBottom: "12px" }}>
                <input
                  type="text"
                  value={broadcastForm.title}
                  onChange={(event) => setBroadcastForm((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder={t("Заголовок уведомления")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "16px",
                    border: `1px solid ${surfaceInputBorder}`,
                    background: surfaceInputBg,
                    color: surfaceInputText,
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                />
                <textarea
                  value={broadcastForm.body}
                  onChange={(event) => setBroadcastForm((prev) => ({ ...prev, body: event.target.value }))}
                  placeholder={t("Текст уведомления")}
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "16px",
                    border: `1px solid ${surfaceInputBorder}`,
                    background: surfaceInputBg,
                    color: surfaceInputText,
                    fontSize: "14px",
                    boxSizing: "border-box",
                    resize: "vertical",
                    minHeight: "96px"
                  }}
                />
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isPhoneView ? "1fr" : "180px minmax(0, 1fr)",
                  gap: "10px"
                }}>
                  <StyledSelect
                    value={broadcastForm.level}
                    onChange={(next) => setBroadcastForm((prev) => ({ ...prev, level: next }))}
                    options={[
                      { value: "info", label: "Info" },
                      { value: "success", label: "Success" },
                      { value: "warning", label: "Warning" },
                      { value: "error", label: "Error" }
                    ]}
                    placeholder={t("Уровень")}
                    ariaLabel={t("Уровень уведомления")}
                  />
                  <StyledSelect
                    value={broadcastForm.targetType}
                    onChange={(next) => setBroadcastForm((prev) => ({ ...prev, targetType: next }))}
                    options={[
                      { value: "all", label: t("Всем пользователям") },
                      // Список собирает сервер в момент отправки — кто подписан сейчас.
                      { value: "subscribed", label: t("С подпиской") },
                      { value: "owners", label: t("Только выбранным ownerId") }
                    ]}
                    placeholder={t("Аудитория")}
                    ariaLabel={t("Аудитория уведомления")}
                  />
                </div>
                {broadcastForm.targetType === "owners" && (
                  <input
                    type="text"
                    value={broadcastForm.ownerIds}
                    onChange={(event) => setBroadcastForm((prev) => ({ ...prev, ownerIds: event.target.value }))}
                    placeholder="ownerId1, ownerId2, ownerId3"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "16px",
                      border: `1px solid ${surfaceInputBorder}`,
                      background: surfaceInputBg,
                      color: surfaceInputText,
                      fontSize: "14px",
                      boxSizing: "border-box"
                    }}
                  />
                )}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isPhoneView ? "1fr" : "220px minmax(0, 1fr)",
                  gap: "10px"
                }}>
                  <input
                    type="text"
                    value={broadcastForm.actionLabel}
                    onChange={(event) => setBroadcastForm((prev) => ({ ...prev, actionLabel: event.target.value }))}
                    placeholder={t("Текст кнопки (опционально)")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "16px",
                      border: `1px solid ${surfaceInputBorder}`,
                      background: surfaceInputBg,
                      color: surfaceInputText,
                      fontSize: "14px",
                      boxSizing: "border-box"
                    }}
                  />
                  <input
                    type="url"
                    value={broadcastForm.actionUrl}
                    onChange={(event) => setBroadcastForm((prev) => ({ ...prev, actionUrl: event.target.value }))}
                    placeholder={t("https://... (опционально)")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "16px",
                      border: `1px solid ${surfaceInputBorder}`,
                      background: surfaceInputBg,
                      color: surfaceInputText,
                      fontSize: "14px",
                      boxSizing: "border-box"
                    }}
                  />
                </div>
              </div>

              {broadcastFormError && (
                <div style={{
                  marginBottom: "10px",
                  padding: "10px 12px",
                  borderRadius: "16px",
                  border: "1px solid rgba(248,113,113,0.35)",
                  background: "rgba(127,29,29,0.28)",
                  color: "#fecaca",
                  fontSize: "13px"
                }}>
                  {broadcastFormError}
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "14px",
                  border: "1px solid rgba(141,147,163,0.2)",
                  background: broadcastForm.sendToTelegram
                    ? aa(600, 0.12)
                    : shellSurfaceSoft,
                  cursor: "pointer",
                  marginBottom: "10px",
                  transition: "background 0.2s ease, border-color 0.2s ease",
                  borderColor: broadcastForm.sendToTelegram ? aa(400, 0.4) : "rgba(141,147,163,0.2)"
                }}
              >
                <input
                  type="checkbox"
                  checked={broadcastForm.sendToTelegram}
                  onChange={(e) => setBroadcastForm((prev) => ({ ...prev, sendToTelegram: e.target.checked }))}
                  style={{ accentColor: A[500], width: 18, height: 18, cursor: "pointer" }}
                />
                <span style={{ color: surfaceInputText, fontSize: "13px", fontWeight: 600 }}>
                  {t("Дублировать в Telegram")}
                </span>
                <span style={{ color: surfaceTextTertiary, fontSize: "12px" }}>
                  {t("(отправить всем привязанным пользователям)")}
                </span>
              </label>

              <button
                type="button"
                className="kl-plain-button"
                onClick={submitAdminBroadcast}
                disabled={savingBroadcast}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "16px",
                  border: "none",
                  background: A[600],
                  color: onAccent("#fff"),
                  fontWeight: "700",
                  fontSize: "14px",
                  cursor: savingBroadcast ? "not-allowed" : "pointer",
                  opacity: savingBroadcast ? 0.7 : 1
                }}
              >
                {savingBroadcast ? t("Отправка...") : t("Отправить уведомление")}
              </button>

              <div style={{
                marginTop: "18px",
                borderTop: "1px solid rgba(141,147,163,0.16)",
                paddingTop: "14px",
                display: "flex",
                flexDirection: "column",
                gap: "8px"
              }}>
                <div style={{ color: isDarkTheme ? "#c6c9d1" : "#262a33", fontSize: "13px", fontWeight: "700" }}>
                  {t("Последние рассылки")}
                </div>
                {loadingAdminBroadcasts ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                ) : (
                  (Array.isArray(adminBroadcasts) && adminBroadcasts.length > 0 ? adminBroadcasts.slice(0, 8) : []).map((item) => (
                    <div
                      key={String(item?.id || Math.random())}
                      className="adm-broadcast"
                      data-in-feed={item?.inFeed === false ? "0" : "1"}
                      style={{
                        borderRadius: "16px",
                        border: "1px solid rgba(141,147,163,0.18)",
                        background: shellSurfaceSoft,
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px"
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: surfaceTextPrimary, fontSize: "13px", fontWeight: "700" }}>
                          {String(item?.title || "").trim() || t("Без заголовка")}
                        </div>
                        <div style={{ marginTop: "4px", color: surfaceTextSecondary, fontSize: "12px", whiteSpace: "pre-wrap" }}>
                          {String(item?.body || "").trim().slice(0, 180)}
                        </div>
                        <div style={{ marginTop: "6px", color: surfaceTextTertiary, fontSize: "11px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span>
                            {String(item?.createdAt || "").trim()
                              ? new Date(String(item.createdAt)).toLocaleString(locale(), { timeZone: "Europe/Berlin" })
                              : "—"}
                          </span>
                          {/* Видна ли рассылка пользователям: без срока она
                              держится в уведомлениях 14 дней. */}
                          {item?.inFeed === false ? (
                            <span className="adm-broadcast-state is-off">{t("скрыта из уведомлений")}</span>
                          ) : item?.feedExpiresAt ? (
                            <span className="adm-broadcast-state">
                              {tf`в уведомлениях до ${new Date(String(item.feedExpiresAt)).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })}`}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="adm-keys-icon is-danger kl-plain-button"
                        onClick={() => deleteAdminBroadcast(item)}
                        title={t("Удалить рассылку")}
                        aria-label={t("Удалить рассылку")}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </div>
                  ))
                )}
                {!loadingAdminBroadcasts && (!Array.isArray(adminBroadcasts) || adminBroadcasts.length === 0) && (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Пока нет созданных рассылок.")}</div>
                )}
              </div>
            </div>

            {/* ── Управление подписками ── */}
            <div style={{
              borderRadius: "0",
              border: "none",
              background: "transparent",
              boxShadow: "none",
              padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
              marginTop: "16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "16px" }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {t("Управление подписками")}
                </h3>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={loadAdminUsers}
                  disabled={loadingAdminUsers}
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(141,147,163,0.3)",
                    background: shellSurfaceSoft,
                    color: isDarkTheme ? "#c6c9d1" : "#262a33",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: loadingAdminUsers ? "not-allowed" : "pointer",
                    opacity: loadingAdminUsers ? 0.7 : 1
                  }}
                >
                  {loadingAdminUsers ? t("Загрузка...") : t("Обновить")}
                </button>
              </div>

              {/* Добавить время */}
              <div style={{
                borderRadius: "18px",
                border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.12)" : "rgba(14,16,21,0.12)"}`,
                background: isDarkTheme ? `${sa(0.45)}` : "rgba(14,16,21,0.03)",
                padding: "14px",
                marginBottom: "14px"
              }}>
                <div style={{ color: isDarkTheme ? "#c6c9d1" : "#262a33", fontSize: "13px", fontWeight: "700", marginBottom: "10px" }}>
                  {t("Время подписки")}
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ flex: "1", minWidth: "140px" }}>
                    <StyledSelect
                      value={addTimeForm.target}
                      onChange={(val) => setAddTimeForm((prev) => ({ ...prev, target: val, ownerId: val === "all" ? "" : val }))}
                      options={[
                        { value: "all", label: t("Всем активным") },
                        ...adminUsers.map((u) => {
                          const active = Boolean(u.subscription?.active);
                          const exp = u.subscription?.expiresAt;
                          const suffix = active && exp
                            ? tf` (до ${new Date(exp).toLocaleDateString(locale())})`
                            : t(" (нет подписки)");
                          const tgHandle = String(u.telegram?.username || "").trim();
                          const tgName = String(u.telegram?.name || "").trim();
                          return {
                            value: u.ownerId,
                            label: `${u.username}${suffix}`,
                            subtitle: tgHandle ? `@${tgHandle}` : (tgName ? `TG: ${tgName}` : ""),
                            // Найти можно и по тому, чего в строке не видно:
                            // ownerId из карточки и Telegram — с @ и без.
                            keywords: [u.ownerId, tgHandle, tgHandle && `@${tgHandle}`, tgName].filter(Boolean).join(" ")
                          };
                        })
                      ]}
                      placeholder={t("Получатели")}
                      size="sm"
                      ariaLabel={t("Получатели")}
                      searchable
                      searchPlaceholder={t("Имя, ID или Telegram")}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input
                      type="number"
                      min="1"
                      max="8760"
                      value={addTimeForm.hours}
                      onChange={(e) => setAddTimeForm((prev) => ({ ...prev, hours: e.target.value }))}
                      style={{
                        borderRadius: "14px",
                        border: `1px solid ${surfaceInputBorder}`,
                        background: surfaceInputBg,
                        color: surfaceInputText,
                        padding: "8px 12px",
                        fontSize: "13px",
                        width: "70px",
                        textAlign: "center"
                      }}
                    />
                    <span style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("ч")}</span>
                  </div>
                  <button
                    type="button"
                    className="kl-plain-button"
                    onClick={() => submitAddTime(1)}
                    disabled={savingAddTime}
                    style={{
                      borderRadius: "16px",
                      border: "none",
                      background: A[600],
                      color: onAccent("#fff"),
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: savingAddTime ? "not-allowed" : "pointer",
                      opacity: savingAddTime ? 0.7 : 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {t("+ Добавить")}
                  </button>
                  <button
                    type="button"
                    className="kl-plain-button"
                    onClick={() => submitAddTime(-1)}
                    disabled={savingAddTime}
                    style={{
                      borderRadius: "16px",
                      border: "1px solid rgba(239,68,68,0.35)",
                      background: "transparent",
                      color: "#ef4444",
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: savingAddTime ? "not-allowed" : "pointer",
                      opacity: savingAddTime ? 0.7 : 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {t("− Убавить")}
                  </button>
                </div>
              </div>

              {/* Список пользователей */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={toggleAdminUsersOpen}
                  aria-expanded={adminUsersOpen}
                  style={{
                    display: "flex", alignItems: "center", gap: "10px", width: "100%", height: "44px", padding: "0 14px",
                    borderRadius: "14px", border: "1px solid rgba(141,147,163,0.18)",
                    background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", cursor: "pointer",
                    color: isDarkTheme ? "#c6c9d1" : "#262a33", fontFamily: "inherit", fontSize: "13px", fontWeight: "700",
                    textAlign: "left", marginBottom: "4px"
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {t("Пользователи (")}{visibleAdminUsers.length === adminUsers.length
                      ? adminUsers.length
                      : `${visibleAdminUsers.length}/${adminUsers.length}`})
                  </span>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: surfaceTextTertiary }}>
                    {adminUsersOpen ? t("Скрыть") : t("Показать")}
                  </span>
                  <ChevronRightIcon
                    size={16}
                    color={surfaceTextTertiary}
                    style={{ transform: adminUsersOpen ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }}
                  />
                </button>
                {adminUsersOpen && (<>
                {adminUsers.length > 0 && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px", height: "40px", padding: "0 12px",
                    borderRadius: "12px", border: "1px solid rgba(141,147,163,0.18)",
                    background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", marginBottom: "4px"
                  }}>
                    <SearchIcon size={15} color={surfaceTextTertiary} />
                    <input
                      value={adminUserQuery}
                      onChange={(event) => setAdminUserQuery(event.target.value)}
                      placeholder={t("Поиск по имени или ID")}
                      aria-label={t("Поиск по имени или ID")}
                      style={{
                        flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                        color: surfaceTextPrimary, fontFamily: "inherit", fontSize: "13px"
                      }}
                    />
                    {adminUserQuery && (
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={() => setAdminUserQuery("")}
                        aria-label={t("Очистить поиск")}
                        style={{
                          border: "none", background: "transparent", cursor: "pointer",
                          color: surfaceTextTertiary, fontSize: "16px", lineHeight: 1, padding: "0 2px"
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                )}
                {adminUsers.length > 0 && (
                  // Порядок и отбор — две независимые настройки, поэтому и две
                  // группы. Одним рядом на телефоне перенос резал отбор пополам:
                  // «Все» оставалось на строке сортировки и читалось как её часть.
                  // Группа переносится только целиком.
                  <div style={{ display: "flex", flexWrap: "wrap", columnGap: "16px", rowGap: "6px", marginBottom: "6px" }}>
                    {[
                      {
                        current: adminUserSort,
                        set: setAdminUserSort,
                        options: [
                          { value: "new", label: t("Сначала новые") },
                          { value: "old", label: t("Сначала старые") }
                        ]
                      },
                      {
                        current: adminUserFilter,
                        set: setAdminUserFilter,
                        options: [
                          { value: "all", label: t("Все") },
                          { value: "withSub", label: t("С подпиской") },
                          { value: "noSub", label: t("Без подписки") }
                        ]
                      }
                    ].map((group) => (
                      <div key={group.options[0].value} style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {group.options.map((chip) => {
                          const selected = group.current === chip.value;
                          return (
                            <button
                              key={chip.value}
                              type="button"
                              className="kl-plain-button"
                              aria-pressed={selected}
                              onClick={() => group.set(chip.value)}
                              style={{
                                borderRadius: "12px",
                                border: `1px solid ${selected ? aa(500, 0.35) : "rgba(141,147,163,0.18)"}`,
                                background: selected ? aa(500, 0.14) : "transparent",
                                color: selected ? A[400] : surfaceTextSecondary,
                                padding: "5px 11px",
                                fontSize: "12px",
                                fontWeight: selected ? "700" : "500",
                                cursor: "pointer"
                              }}
                            >
                              {chip.label}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
                {loadingAdminUsers ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                ) : adminUsers.length === 0 ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Нет зарегистрированных пользователей")}</div>
                ) : visibleAdminUsers.length === 0 ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Никто не найден")}</div>
                ) : visibleAdminUsers.map((user) => {
                  const sub = user.subscription;
                  const isActive = sub?.active;
                  const expiresDate = sub?.expiresAt ? new Date(sub.expiresAt) : null;
                  const now = new Date();
                  const hoursLeft = expiresDate ? Math.max(0, Math.round((expiresDate - now) / 3600000)) : 0;
                  const daysLeft = Math.floor(hoursLeft / 24);
                  const remainingHours = hoursLeft % 24;
                  const timeLeftText = daysLeft > 0
                    ? tf`${daysLeft}д ${remainingHours}ч`
                    : tf`${remainingHours}ч`;
                  return (
                    <div
                      key={user.userId}
                      style={{
                        borderRadius: "16px",
                        border: `1px solid ${user.banned ? "rgba(239,68,68,0.3)" : isActive ? "rgba(74,222,128,0.2)" : "rgba(141,147,163,0.12)"}`,
                        background: user.banned
                          ? (isDarkTheme ? "rgba(239,68,68,0.06)" : "rgba(239,68,68,0.06)")
                          : isActive
                            ? (isDarkTheme ? "rgba(22,163,74,0.08)" : "rgba(22,163,74,0.10)")
                            : (isDarkTheme ? `${sa(0.45)}` : "rgba(14,16,21,0.03)"),
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "6px"
                      }}
                    >
                      <div>
                        <div style={{ color: surfaceTextPrimary, fontSize: "13px", fontWeight: "700" }}>
                          {user.username}
                          {user.isAdmin && (
                            <span style={{ marginLeft: "6px", fontSize: "10px", color: A[400], background: aa(400, 0.15), padding: "2px 6px", borderRadius: "6px" }}>admin</span>
                          )}
                          {user.banned && (
                            <span style={{ marginLeft: "6px", fontSize: "10px", color: "#ef4444", background: "rgba(239,68,68,0.14)", padding: "2px 6px", borderRadius: "6px" }}>{t("заблокирован")}</span>
                          )}
                          {user.createdVia === "telegram" && (
                            <span style={{ marginLeft: "6px", fontSize: "10px", color: surfaceTextSecondary, background: "rgba(141,147,163,0.14)", padding: "2px 6px", borderRadius: "6px" }}>Telegram</span>
                          )}
                        </div>
                        <div style={{ color: surfaceTextTertiary, fontSize: "11px", marginTop: "2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0 6px" }}>
                          <span>{user.ownerId}</span>
                          {/* Telegram: ник — ссылкой, чтобы написать человеку в один клик;
                              без ника (в Telegram он необязателен) — имя; связан, но
                              Telegram ещё не ответил — просто отметка о связи. */}
                          {user.telegram && (
                            <>
                              <span aria-hidden="true">·</span>
                              {user.telegram.username ? (
                                <a
                                  href={`https://t.me/${encodeURIComponent(user.telegram.username)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t("Открыть чат в Telegram")}
                                  style={{ color: A[400], textDecoration: "none", fontWeight: 600 }}
                                >
                                  @{user.telegram.username}
                                </a>
                              ) : (
                                <span>
                                  {user.telegram.name ? tf`TG: ${user.telegram.name}` : t("Telegram привязан")}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {!user.isAdmin && (
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => toggleParserPurgeRight(user)}
                            disabled={savingPurgeRightFor === user.userId}
                            style={{
                              marginTop: "8px",
                              marginRight: "6px",
                              display: "inline-flex", alignItems: "center", gap: "7px",
                              borderRadius: "10px",
                              border: `1px solid ${user.canPurgeParserAds ? aa(400, 0.45) : "rgba(141,147,163,0.25)"}`,
                              background: user.canPurgeParserAds ? aa(400, 0.14) : "transparent",
                              color: user.canPurgeParserAds ? A[400] : surfaceTextSecondary,
                              padding: "5px 9px",
                              fontSize: "11px",
                              fontWeight: 600,
                              cursor: savingPurgeRightFor === user.userId ? "wait" : "pointer"
                            }}
                          >
                            <span style={{
                              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                              background: user.canPurgeParserAds ? A[400] : "rgba(141,147,163,0.4)"
                            }} />
                            {t("Удаление в парсере у всех")}
                          </button>
                        )}
                        {!user.isAdmin && (
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => toggleAllProxiesRight(user)}
                            disabled={savingAllProxiesRightFor === user.userId}
                            title={t("Видит и может использовать все прокси панели, как администратор")}
                            style={{
                              marginTop: "8px",
                              marginRight: "6px",
                              display: "inline-flex", alignItems: "center", gap: "7px",
                              borderRadius: "10px",
                              border: `1px solid ${user.canUseAllProxies ? aa(400, 0.45) : "rgba(141,147,163,0.25)"}`,
                              background: user.canUseAllProxies ? aa(400, 0.14) : "transparent",
                              color: user.canUseAllProxies ? A[400] : surfaceTextSecondary,
                              padding: "5px 9px",
                              fontSize: "11px",
                              fontWeight: 600,
                              cursor: savingAllProxiesRightFor === user.userId ? "wait" : "pointer"
                            }}
                          >
                            <span style={{
                              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                              background: user.canUseAllProxies ? A[400] : "rgba(141,147,163,0.4)"
                            }} />
                            {t("Все прокси")}
                          </button>
                        )}
                        {!user.isAdmin && (
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => togglePhotoUnlimitedRight(user)}
                            disabled={savingPhotoRightFor === user.userId}
                            title={t("Снимает дневной предел генераций фото")}
                            style={{
                              marginTop: "8px",
                              marginRight: "6px",
                              display: "inline-flex", alignItems: "center", gap: "7px",
                              borderRadius: "10px",
                              border: `1px solid ${user.photoGenUnlimited ? aa(400, 0.45) : "rgba(141,147,163,0.25)"}`,
                              background: user.photoGenUnlimited ? aa(400, 0.14) : "transparent",
                              color: user.photoGenUnlimited ? A[400] : surfaceTextSecondary,
                              padding: "5px 9px",
                              fontSize: "11px",
                              fontWeight: 600,
                              cursor: savingPhotoRightFor === user.userId ? "wait" : "pointer"
                            }}
                          >
                            <span style={{
                              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                              background: user.photoGenUnlimited ? A[400] : "rgba(141,147,163,0.4)"
                            }} />
                            {t("Фото без предела")}
                          </button>
                        )}
                      </div>
                      {/* На телефоне колонка уходит под имя и встаёт во всю ширину
                          по левому краю — узкая, прижатая вправо, она оказывалась
                          посередине карточки. Статус там — одной строкой. */}
                      <div style={isPhoneView
                        ? { width: "100%", textAlign: "left", borderTop: "1px solid rgba(141,147,163,0.12)", paddingTop: "8px", marginTop: "2px" }
                        : { textAlign: "right" }}
                      >
                        {isActive ? (
                          <div style={isPhoneView ? { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" } : undefined}>
                            <div style={{ color: "#4ade80", fontSize: "12px", fontWeight: "700" }}>
                              {t("Активна")}
                            </div>
                            <div style={{ color: surfaceTextSecondary, fontSize: "11px" }}>
                              {timeLeftText} {t("· до")} {expiresDate.toLocaleDateString(locale())}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: "600" }}>
                            {t("Нет подписки")}
                          </div>
                        )}
                        {!user.isAdmin && (
                          <div style={{ display: "flex", gap: "6px", justifyContent: isPhoneView ? "flex-start" : "flex-end", marginTop: "8px" }}>
                            <button
                              type="button"
                              className="kl-plain-button"
                              onClick={() => toggleUserBan(user)}
                              disabled={adminUserActionFor === user.userId}
                              style={{
                                borderRadius: "10px",
                                border: `1px solid ${user.banned ? aa(400, 0.45) : "rgba(141,147,163,0.25)"}`,
                                background: "transparent",
                                color: user.banned ? A[400] : surfaceTextSecondary,
                                padding: "5px 9px",
                                fontSize: "11px",
                                fontWeight: 600,
                                cursor: adminUserActionFor === user.userId ? "wait" : "pointer"
                              }}
                            >
                              {user.banned ? t("Разблокировать") : t("Заблокировать")}
                            </button>
                            <button
                              type="button"
                              className="kl-plain-button"
                              onClick={() => deleteAdminUser(user)}
                              disabled={adminUserActionFor === user.userId}
                              style={{
                                borderRadius: "10px",
                                border: "1px solid rgba(239,68,68,0.35)",
                                background: "transparent",
                                color: "#ef4444",
                                padding: "5px 9px",
                                fontSize: "11px",
                                fontWeight: 600,
                                cursor: adminUserActionFor === user.userId ? "wait" : "pointer"
                              }}
                            >
                              {t("Удалить")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                </>)}
              </div>
            </div>

            {/* ── Прокси пользователей ── */}
            {(() => {
              const query = userProxyQuery.trim().toLowerCase();
              const matches = (proxy) => !query || [proxy.username, proxy.address, proxy.name, ...(proxy.accounts || [])]
                .some((value) => String(value || "").toLowerCase().includes(query));
              const groups = [];
              const byOwner = new Map();
              (Array.isArray(userProxies) ? userProxies : []).filter(matches).forEach((proxy) => {
                const key = String(proxy.ownerId || "");
                if (!byOwner.has(key)) {
                  const group = { ownerId: key, username: proxy.username, items: [] };
                  byOwner.set(key, group);
                  groups.push(group);
                }
                byOwner.get(key).items.push(proxy);
              });
              groups.sort((a, b) => String(a.username || "~").localeCompare(String(b.username || "~")));
              return (
                <div style={{
                  display: "flex", flexDirection: "column", gap: "6px",
                  padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
                  marginTop: "16px"
                }}>
                  <button
                    type="button"
                    className="kl-plain-button"
                    onClick={toggleUserProxiesOpen}
                    aria-expanded={userProxiesOpen}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px", width: "100%", height: "44px", padding: "0 14px",
                      borderRadius: "14px", border: "1px solid rgba(141,147,163,0.18)",
                      background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", cursor: "pointer",
                      color: isDarkTheme ? "#c6c9d1" : "#262a33", fontFamily: "inherit", fontSize: "13px", fontWeight: "700",
                      textAlign: "left", marginBottom: "4px"
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {t("Прокси пользователей (")}{userProxies.length})
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: surfaceTextTertiary }}>
                      {userProxiesOpen ? t("Скрыть") : t("Показать")}
                    </span>
                    <ChevronRightIcon
                      size={16}
                      color={surfaceTextTertiary}
                      style={{ transform: userProxiesOpen ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }}
                    />
                  </button>
                  {userProxiesOpen && (<>
                    {userProxies.length > 0 && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: "8px", height: "40px", padding: "0 12px",
                        borderRadius: "12px", border: "1px solid rgba(141,147,163,0.18)",
                        background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", marginBottom: "4px"
                      }}>
                        <SearchIcon size={15} color={surfaceTextTertiary} />
                        <input
                          value={userProxyQuery}
                          onChange={(event) => setUserProxyQuery(event.target.value)}
                          placeholder={t("Поиск по пользователю, адресу или аккаунту")}
                          aria-label={t("Поиск по пользователю, адресу или аккаунту")}
                          style={{
                            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                            color: surfaceTextPrimary, fontFamily: "inherit", fontSize: "13px"
                          }}
                        />
                      </div>
                    )}
                    {loadingUserProxies && userProxies.length === 0 ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                    ) : userProxies.length === 0 ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Пользователи ещё не добавили ни одного прокси")}</div>
                    ) : groups.length === 0 ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Никто не найден")}</div>
                    ) : groups.map((group) => (
                      <div
                        key={group.ownerId || "-"}
                        style={{
                          borderRadius: "16px",
                          border: "1px solid rgba(141,147,163,0.12)",
                          background: isDarkTheme ? sa(0.3) : "rgba(14,16,21,0.02)",
                          padding: "10px 14px"
                        }}
                      >
                        <div style={{ fontSize: "13px", fontWeight: 700, color: surfaceTextPrimary, marginBottom: "4px" }}>
                          {group.username || t("Без пользователя")}
                          <span style={{ fontWeight: 500, color: surfaceTextTertiary }}>{` · ${group.items.length}`}</span>
                        </div>
                        {group.items.map((proxy, index) => {
                          const st = String(proxy.status || "").toLowerCase();
                          const dot = st === "active" ? "#4ade80" : st === "failed" ? "#ef4444" : "#fbbf24";
                          const accountsText = (proxy.accounts || []).length
                            ? `${t("аккаунты")}: ${proxy.accounts.join(", ")}`
                            : t("без аккаунтов");
                          return (
                            <div
                              key={String(proxy.id ?? index)}
                              style={{
                                display: "flex", alignItems: "flex-start", gap: "9px", padding: "7px 0",
                                borderTop: index ? `1px solid ${surfaceInputBorder}` : "none"
                              }}
                            >
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0, marginTop: "5px" }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                                  <span style={{ fontSize: "10.5px", fontWeight: 700, color: A[300], textTransform: "uppercase", flexShrink: 0 }}>
                                    {proxy.type || "proxy"}
                                  </span>
                                  <span style={{
                                    fontSize: "12.5px",
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    color: surfaceTextPrimary,
                                    wordBreak: "break-all",
                                    userSelect: "text"
                                  }}>
                                    {proxy.address}
                                  </span>
                                </div>
                                <div style={{ marginTop: "2px", fontSize: "11.5px", color: surfaceTextSecondary }}>
                                  {[proxy.name, accountsText].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>)}
                </div>
              );
            })()}

            {/* ── Статистика аккаунтов (только администратор) ── */}
            {(() => {
              const fmtDuration = (ms) => {
                if (!Number.isFinite(ms) || ms === null) return "—";
                const minutes = Math.max(0, Math.round(ms / 60000));
                const d = Math.floor(minutes / 1440);
                const h = Math.floor((minutes % 1440) / 60);
                const m = minutes % 60;
                if (d > 0) return tf`${d} д ${h} ч`;
                if (h > 0) return tf`${h} ч ${m} мин`;
                return tf`${m} мин`;
              };
              const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString(locale(), {
                day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit"
              }) : "—");
              const groups = [
                { key: "selfreg", title: t("Селфрег") },
                { key: "regular", title: t("Обычные") },
                { key: "unknown", title: t("Без даты регистрации") }
              ];
              const summary = accountStats?.summary || {};
              const banned = (accountStats?.accounts || []).filter((row) => row.banned);
              const periods = [
                { key: "day", label: t("День"), title: t("за день") },
                { key: "week", label: t("Неделя"), title: t("за неделю") },
                { key: "month", label: t("Месяц"), title: t("за месяц") },
                { key: "all", label: t("Всё время"), title: t("за всё время") }
              ];
              const period = periods.find((p) => p.key === accountStatsPeriod) || periods[3];
              const periodOf = (publishes) => (publishes && publishes[period.key]) || { ok: 0, fail: 0 };
              const groupLabel = { selfreg: t("селфрег"), regular: t("обычный"), unknown: t("без даты") };
              const cardStyle = {
                borderRadius: "16px",
                border: "1px solid rgba(141,147,163,0.12)",
                background: isDarkTheme ? sa(0.3) : "rgba(14,16,21,0.02)",
                padding: "12px 14px"
              };
              return (
                <div style={{
                  display: "flex", flexDirection: "column", gap: "8px",
                  padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
                  marginTop: "16px"
                }}>
                  <button
                    type="button"
                    className="kl-plain-button"
                    onClick={toggleAccountStatsOpen}
                    aria-expanded={accountStatsOpen}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px", width: "100%", height: "44px", padding: "0 14px",
                      borderRadius: "14px", border: "1px solid rgba(141,147,163,0.18)",
                      background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", cursor: "pointer",
                      color: isDarkTheme ? "#c6c9d1" : "#262a33", fontFamily: "inherit", fontSize: "13px", fontWeight: "700",
                      textAlign: "left"
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>{t("Статистика аккаунтов")}</span>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: surfaceTextTertiary }}>
                      {accountStatsOpen ? t("Скрыть") : t("Показать")}
                    </span>
                    <ChevronRightIcon
                      size={16}
                      color={surfaceTextTertiary}
                      style={{ transform: accountStatsOpen ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }}
                    />
                  </button>
                  {accountStatsOpen && (<>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "12px", color: surfaceTextSecondary, lineHeight: 1.5 }}>
                        {t("Собирается молча: пользователи панели этого не видят. Время жизни — от первой успешной публикации через панель до бана; ≈ — дата первой публикации восстановлена по истории публикаций. Аккаунты без публикаций через панель в среднее не входят.")}
                      </div>
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={loadAccountStats}
                        disabled={loadingAccountStats}
                        style={{
                          borderRadius: "12px", border: "1px solid rgba(141,147,163,0.3)", background: shellSurfaceSoft,
                          color: surfaceTextPrimary, padding: "7px 12px", fontSize: "12px", fontWeight: 600,
                          cursor: loadingAccountStats ? "default" : "pointer", opacity: loadingAccountStats ? 0.6 : 1
                        }}
                      >
                        {loadingAccountStats ? t("Загрузка...") : t("Обновить")}
                      </button>
                    </div>
                    {!accountStats && loadingAccountStats ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                    ) : !accountStats ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Статистика не загрузилась")}</div>
                    ) : (<>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "12px", color: surfaceTextSecondary }}>{t("Публикации")}</span>
                        <div role="group" aria-label={t("Период публикаций")} className="transport-switch">
                          {periods.map((option) => (
                            <button
                              key={option.key}
                              type="button"
                              className="transport-switch-option"
                              aria-pressed={period.key === option.key}
                              onClick={() => changeAccountStatsPeriod(option.key)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isPhoneView ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        {groups.filter((g) => g.key !== "unknown" || Number(summary.unknown?.accounts || 0) > 0).map((g) => {
                          const item = summary[g.key] || {};
                          const pub = periodOf(item.publishes);
                          return (
                            <div key={g.key} style={cardStyle}>
                              <div style={{ fontSize: "13px", fontWeight: 700, color: surfaceTextPrimary, marginBottom: "6px" }}>{g.title}</div>
                              <div style={{ fontSize: "12.5px", color: surfaceTextSecondary, lineHeight: 1.65 }}>
                                <div>{tf`Аккаунтов: ${item.accounts || 0} · забанено: ${item.banned || 0}`}</div>
                                <div>
                                  {t("Жизнь до бана: ")}
                                  <b style={{ color: surfaceTextPrimary }}>{fmtDuration(item.lifetimeMedianMs)}</b>
                                  {tf` медиана · ${fmtDuration(item.lifetimeAvgMs)} в среднем`}
                                  {item.lifetimeSamples ? tf` (по ${item.lifetimeSamples}${item.lifetimeApproxSamples ? tf`, ≈ ${item.lifetimeApproxSamples}` : ""})` : ""}
                                </div>
                                {item.bannedWithoutPublish > 0 && (
                                  <div style={{ fontSize: "11.5px" }}>{tf`Забанены без публикаций через панель: ${item.bannedWithoutPublish}`}</div>
                                )}
                                <div>
                                  {tf`Публикации ${period.title}: `}
                                  <b style={{ color: surfaceTextPrimary }}>{pub.successPct === null || pub.successPct === undefined ? "—" : `${pub.successPct}%`}</b>
                                  {tf` успешных (${pub.ok || 0} из ${(pub.ok || 0) + (pub.fail || 0)})`}
                                </div>
                                {Array.isArray(pub.topFailCodes) && pub.topFailCodes.length > 0 && (
                                  <div style={{ fontSize: "11.5px" }}>
                                    {t("Частые отказы: ")}{pub.topFailCodes.map((c) => `${c.code} ×${c.count}`).join(", ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: isDarkTheme ? "#c6c9d1" : "#262a33", marginTop: "6px" }}>
                        {tf`Забаненные аккаунты (${banned.length})`}
                      </div>
                      {banned.length === 0 ? (
                        <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Пока ни одного")}</div>
                      ) : banned.map((row) => {
                        const expanded = accountStatsExpanded.has(row.id);
                        return (
                          <div key={row.id} style={cardStyle}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "13px", fontWeight: 700, color: surfaceTextPrimary }}>{row.label || row.email || tf`Аккаунт ${row.id}`}</span>
                              {row.email && row.label && <span style={{ fontSize: "12px", color: surfaceTextTertiary }}>{row.email}</span>}
                              <span style={{ fontSize: "10.5px", fontWeight: 700, color: A[300], textTransform: "uppercase" }}>{groupLabel[row.group] || row.group}</span>
                              {row.status === "deleted" && <span style={{ fontSize: "10.5px", color: surfaceTextTertiary }}>{t("удалён")}</span>}
                              {row.recoveredAt > 0 && row.status !== "invalid" && row.status !== "deleted" && <span style={{ fontSize: "10.5px", color: "#86efac" }}>{t("вернулся")}</span>}
                            </div>
                            <div style={{ marginTop: "4px", fontSize: "12px", color: surfaceTextSecondary, lineHeight: 1.6 }}>
                              {row.ownerName && <div>{tf`Пользователь: ${row.ownerName}`}</div>}
                              <div>
                                {row.firstPublishAt
                                  ? tf`Первая публикация ${row.firstPublishApprox ? "≈ " : ""}${fmtDate(row.firstPublishAt)} → бан ${row.bannedAt ? fmtDate(row.bannedAt) : (row.bannedBeforeStats ? t("до статистики") : "—")}`
                                  : tf`Публикаций через панель не было · бан ${row.bannedAt ? fmtDate(row.bannedAt) : (row.bannedBeforeStats ? t("до статистики") : "—")}`}
                                {row.lifetimeMs !== null && row.lifetimeMs !== undefined && (
                                  <> · <b style={{ color: surfaceTextPrimary }}>{tf`прожил ${row.lifetimeApprox ? "≈ " : ""}${fmtDuration(row.lifetimeMs)}`}</b></>
                                )}
                              </div>
                              {row.banReason && <div>{tf`Причина: ${row.banReason}`}</div>}
                              <div>{tf`Публикации ${period.title}: ${periodOf(row.publishes).ok} успешных, ${periodOf(row.publishes).fail} неудачных`}</div>
                            </div>
                            <button
                              type="button"
                              className="kl-plain-button"
                              onClick={() => toggleAccountStatsRow(row.id)}
                              disabled={!row.adsAtBan.length}
                              style={{
                                marginTop: "6px", border: "none", background: "transparent", padding: 0,
                                color: row.adsAtBan.length ? A[400] : surfaceTextTertiary, fontSize: "12px", fontWeight: 600,
                                cursor: row.adsAtBan.length ? "pointer" : "default"
                              }}
                            >
                              {tf`Объявления при бане: ${row.adsAtBanCount}`}{row.adsAtBan.length ? (expanded ? ` · ${t("скрыть")}` : ` · ${t("показать")}`) : ""}
                            </button>
                            {expanded && (
                              <div style={{ marginTop: "6px", display: "flex", flexDirection: "column" }}>
                                {row.adsAtBan.map((ad, index) => (
                                  <div
                                    key={`${ad.adId || index}`}
                                    style={{
                                      display: "flex", alignItems: "baseline", gap: "10px", padding: "5px 0", fontSize: "12px",
                                      borderTop: index ? `1px solid ${surfaceInputBorder}` : "none", color: surfaceTextSecondary
                                    }}
                                  >
                                    <span style={{ flex: 1, minWidth: 0, color: surfaceTextPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ad.title || ad.adId}</span>
                                    <span style={{ flexShrink: 0 }}>{ad.price}</span>
                                    <span style={{ flexShrink: 0 }}>{ad.status}</span>
                                    <span style={{ flexShrink: 0 }}>{tf`👁 ${ad.views ?? "—"}`}</span>
                                    <span style={{ flexShrink: 0 }}>{tf`♥ ${ad.favorites ?? "—"}`}</span>
                                    {!isPhoneView && (
                                      <span style={{ flexShrink: 0, color: surfaceTextTertiary }}>
                                        {ad.lastSeenAt ? fmtDate(Date.parse(ad.lastSeenAt)) : "—"}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>)}
                  </>)}
                </div>
              );
            })()}

            {/* ── Соединение сообщений по аккаунтам (только администратор) ── */}
            {(() => {
              const items = Array.isArray(messagesTransport?.items) ? messagesTransport.items : [];
              const viaProxyCount = items.filter((row) => row.mode === "proxy").length;
              const query = messagesTransportQuery.trim().toLowerCase();
              const matches = (row) => !query || [row.ownerName, row.label, row.email, row.proxy?.name, row.proxy?.address]
                .some((value) => String(value || "").toLowerCase().includes(query));
              const groups = [];
              const byOwner = new Map();
              items.filter(matches).forEach((row) => {
                const key = String(row.ownerId || "");
                if (!byOwner.has(key)) {
                  const group = { ownerId: key, username: row.ownerName, items: [] };
                  byOwner.set(key, group);
                  groups.push(group);
                }
                byOwner.get(key).items.push(row);
              });
              groups.sort((a, b) => String(a.username || "~").localeCompare(String(b.username || "~")));
              const modes = [
                { value: "direct", label: t("Прямое") },
                { value: "proxy", label: t("Прокси") }
              ];
              return (
                <div style={{
                  display: "flex", flexDirection: "column", gap: "6px",
                  padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
                  marginTop: "16px"
                }}>
                  <button
                    type="button"
                    className="kl-plain-button"
                    onClick={toggleMessagesTransportOpen}
                    aria-expanded={messagesTransportOpen}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px", width: "100%", height: "44px", padding: "0 14px",
                      borderRadius: "14px", border: "1px solid rgba(141,147,163,0.18)",
                      background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", cursor: "pointer",
                      color: isDarkTheme ? "#c6c9d1" : "#262a33", fontFamily: "inherit", fontSize: "13px", fontWeight: "700",
                      textAlign: "left", marginBottom: "4px"
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {t("Соединение сообщений")}
                      {messagesTransport && (
                        <span style={{ fontWeight: 500, color: surfaceTextTertiary }}>
                          {tf` · через прокси ${viaProxyCount} из ${items.length}`}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: surfaceTextTertiary }}>
                      {messagesTransportOpen ? t("Скрыть") : t("Показать")}
                    </span>
                    <ChevronRightIcon
                      size={16}
                      color={surfaceTextTertiary}
                      style={{ transform: messagesTransportOpen ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }}
                    />
                  </button>
                  {messagesTransportOpen && (<>
                    <div style={{ fontSize: "11.5px", color: surfaceTextSecondary, padding: "0 4px 2px", lineHeight: 1.45 }}>
                      {t("По умолчанию сообщения у всех аккаунтов идут через прокси. Прямое соединение — исключение для отдельного аккаунта. Вернуть на прокси можно только аккаунт с рабочим прокси — пользователь ничего не заметит.")}
                    </div>
                    {items.length > 0 && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: "8px", height: "40px", padding: "0 12px",
                        borderRadius: "12px", border: "1px solid rgba(141,147,163,0.18)",
                        background: isDarkTheme ? sa(0.45) : "rgba(14,16,21,0.03)", marginBottom: "4px"
                      }}>
                        <SearchIcon size={15} color={surfaceTextTertiary} />
                        <input
                          value={messagesTransportQuery}
                          onChange={(event) => setMessagesTransportQuery(event.target.value)}
                          placeholder={t("Поиск по пользователю, аккаунту или прокси")}
                          aria-label={t("Поиск по пользователю, аккаунту или прокси")}
                          style={{
                            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                            color: surfaceTextPrimary, fontFamily: "inherit", fontSize: "13px"
                          }}
                        />
                      </div>
                    )}
                    {loadingMessagesTransport && !messagesTransport ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                    ) : !messagesTransport ? (
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={loadMessagesTransport}
                        style={{ alignSelf: "flex-start", color: A[300], fontSize: "12px", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: "2px 4px" }}
                      >
                        {t("Не загрузилось — повторить")}
                      </button>
                    ) : items.length === 0 ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Аккаунтов пока нет")}</div>
                    ) : groups.length === 0 ? (
                      <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Никто не найден")}</div>
                    ) : groups.map((group) => (
                      <div
                        key={group.ownerId || "-"}
                        style={{
                          borderRadius: "16px",
                          border: "1px solid rgba(141,147,163,0.12)",
                          background: isDarkTheme ? sa(0.3) : "rgba(14,16,21,0.02)",
                          padding: "10px 14px"
                        }}
                      >
                        <div style={{ fontSize: "13px", fontWeight: 700, color: surfaceTextPrimary, marginBottom: "4px" }}>
                          {group.username || t("Без пользователя")}
                          <span style={{ fontWeight: 500, color: surfaceTextTertiary }}>{` · ${group.items.length}`}</span>
                        </div>
                        {group.items.map((row, index) => {
                          const key = String(row.accountId);
                          const saving = messagesTransportSaving.has(key);
                          const proxyBlocked = !row.proxy || Boolean(row.proxy.invalidReason);
                          const blockedReason = !row.proxy ? t("прокси не привязан") : t(row.proxy.invalidReason || "");
                          const proxyStatus = String(row.proxy?.status || "").toLowerCase();
                          const dot = !row.proxy || proxyBlocked ? "#ef4444" : proxyStatus === "active" ? "#4ade80" : "#fbbf24";
                          const proxyLine = row.proxy
                            ? [
                              row.proxy.name,
                              row.proxy.address,
                              row.proxy.responseTime ? tf`пинг ${row.proxy.responseTime} мс` : "",
                              row.proxy.country
                            ].filter(Boolean).join(" · ")
                            : "";
                          return (
                            <div
                              key={key}
                              style={{
                                display: "flex", alignItems: "center", gap: "10px", padding: "8px 0",
                                borderTop: index ? `1px solid ${surfaceInputBorder}` : "none",
                                flexWrap: isPhoneView ? "wrap" : "nowrap"
                              }}
                            >
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "12.5px", fontWeight: 600, color: surfaceTextPrimary, overflowWrap: "anywhere" }}>
                                  {row.label}
                                  {row.email && row.email !== row.label && (
                                    <span style={{ fontWeight: 500, color: surfaceTextTertiary }}>{` · ${row.email}`}</span>
                                  )}
                                </div>
                                <div style={{ marginTop: "2px", fontSize: "11.5px", color: proxyBlocked ? "#f87171" : surfaceTextSecondary, overflowWrap: "anywhere" }}>
                                  {proxyBlocked
                                    ? [proxyLine, blockedReason].filter(Boolean).join(" — ")
                                    : proxyLine}
                                  {row.mode === "proxy" && proxyBlocked && (
                                    <span>{t(" · сообщения сейчас не грузятся")}</span>
                                  )}
                                  {!row.override && (
                                    <span style={{ color: surfaceTextTertiary }}>{t(" · по умолчанию")}</span>
                                  )}
                                </div>
                              </div>
                              <div
                                role="group"
                                aria-label={tf`Соединение сообщений: ${row.label}`}
                                className={`transport-switch${saving ? " is-saving" : ""}`}
                                style={isPhoneView ? { marginLeft: "18px" } : undefined}
                              >
                                {modes.map((option) => {
                                  const active = row.mode === option.value;
                                  const disabled = saving || (option.value === "proxy" && proxyBlocked && !active);
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className="transport-switch-option"
                                      aria-pressed={active}
                                      disabled={disabled}
                                      title={option.value === "proxy" && proxyBlocked && !active ? blockedReason : ""}
                                      onClick={() => setAccountMessagesTransport(row, option.value)}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>)}
                </div>
              );
            })()}

            {/* ── Промокоды ── */}
            <div style={{
              borderRadius: "0",
              border: "none",
              background: "transparent",
              boxShadow: "none",
              padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
              marginTop: "16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "16px" }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M9 9h.01"/><path d="m15 9-6 6"/><path d="M15 15h.01"/></svg>
                  {t("Промокоды")}
                </h3>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={loadAdminPromos}
                  disabled={loadingAdminPromos}
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(141,147,163,0.3)",
                    background: shellSurfaceSoft,
                    color: isDarkTheme ? "#c6c9d1" : "#262a33",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: loadingAdminPromos ? "not-allowed" : "pointer",
                    opacity: loadingAdminPromos ? 0.7 : 1
                  }}
                >
                  {loadingAdminPromos ? t("Загрузка...") : t("Обновить")}
                </button>
              </div>

              {/* Новый промокод */}
              <form
                onSubmit={(event) => { event.preventDefault(); createAdminPromo(); }}
                style={{
                  borderRadius: "18px",
                  border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.12)" : "rgba(14,16,21,0.12)"}`,
                  background: isDarkTheme ? `${sa(0.45)}` : "rgba(14,16,21,0.03)",
                  padding: "14px",
                  marginBottom: "14px"
                }}
              >
                <div style={{ color: isDarkTheme ? "#c6c9d1" : "#262a33", fontSize: "13px", fontWeight: "700", marginBottom: "10px" }}>
                  {t("Новый промокод")}
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                  {[
                    { key: "code", label: t("Код"), width: "140px", type: "text", placeholder: "SALE20" },
                    { key: "discountPercent", label: t("Скидка, %"), width: "84px", type: "number", min: 1, max: 99 },
                    { key: "maxUses", label: t("Всего применений"), width: "120px", type: "number", min: 0, hint: t("0 — без лимита") },
                    { key: "perUserMaxUses", label: t("На пользователя"), width: "120px", type: "number", min: 0, hint: t("0 — без лимита") },
                    { key: "validDays", label: t("Действует, дней"), width: "120px", type: "number", min: 0, hint: t("0 — бессрочно") }
                  ].map((field) => (
                    <label key={field.key} style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                      <span style={{ color: surfaceTextSecondary, fontSize: "11px", fontWeight: 600 }}>{field.label}</span>
                      <input
                        type={field.type}
                        min={field.min}
                        max={field.max}
                        placeholder={field.placeholder || ""}
                        title={field.hint || ""}
                        value={promoForm[field.key]}
                        onChange={(e) => {
                          const value = field.key === "code" ? e.target.value.toUpperCase() : e.target.value;
                          setPromoForm((prev) => ({ ...prev, [field.key]: value }));
                        }}
                        style={{
                          borderRadius: "14px",
                          border: `1px solid ${surfaceInputBorder}`,
                          background: surfaceInputBg,
                          color: surfaceInputText,
                          padding: "8px 12px",
                          fontSize: "13px",
                          width: field.width,
                          textAlign: field.type === "number" ? "center" : "left"
                        }}
                      />
                    </label>
                  ))}
                  <label style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "150px" }}>
                    <span style={{ color: surfaceTextSecondary, fontSize: "11px", fontWeight: 600 }}>{t("Тариф")}</span>
                    <StyledSelect
                      value={promoForm.planId}
                      onChange={(val) => setPromoForm((prev) => ({ ...prev, planId: val }))}
                      options={[
                        { value: "", label: t("Все тарифы") },
                        ...adminPlans.map((plan) => ({ value: plan.id, label: plan.title || plan.id }))
                      ]}
                      size="sm"
                      ariaLabel={t("Тариф")}
                    />
                  </label>
                  <button
                    type="submit"
                    className="kl-plain-button"
                    disabled={savingPromo}
                    style={{
                      borderRadius: "16px",
                      border: "none",
                      background: A[600],
                      color: onAccent("#fff"),
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: savingPromo ? "not-allowed" : "pointer",
                      opacity: savingPromo ? 0.7 : 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {savingPromo ? "..." : t("Создать")}
                  </button>
                </div>
                <div style={{ color: surfaceTextTertiary, fontSize: "11px", marginTop: "8px" }}>
                  {t("Скидка от 1 до 99 %. В лимитах и сроке 0 — без ограничений.")}
                </div>
              </form>

              {/* Список промокодов */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {loadingAdminPromos && adminPromos.length === 0 ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Загрузка...")}</div>
                ) : adminPromos.length === 0 ? (
                  <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>{t("Промокодов пока нет.")}</div>
                ) : adminPromos.map((promo) => {
                  const expiresMs = promo.expiresAt ? Date.parse(promo.expiresAt) : 0;
                  const expired = Boolean(expiresMs) && expiresMs <= Date.now();
                  const exhausted = Number(promo.maxUses) > 0 && Number(promo.uses) >= Number(promo.maxUses);
                  const plan = (promo.appliesToPlans || [])[0]
                    ? adminPlans.find((item) => item.id === promo.appliesToPlans[0])
                    : null;
                  const details = [
                    Number(promo.maxUses) > 0
                      ? tf`применений ${promo.uses} из ${promo.maxUses}`
                      : tf`применений ${promo.uses}`,
                    Number(promo.perUserMaxUses) > 0 ? tf`по ${promo.perUserMaxUses} на пользователя` : "",
                    expiresMs ? tf`до ${new Date(expiresMs).toLocaleDateString(locale())}` : t("бессрочно"),
                    (promo.appliesToPlans || []).length
                      ? tf`тариф ${plan?.title || promo.appliesToPlans[0]}`
                      : t("все тарифы")
                  ].filter(Boolean).join(" · ");
                  return (
                    <div
                      key={promo.code}
                      style={{
                        borderRadius: "16px",
                        border: "1px solid rgba(141,147,163,0.12)",
                        background: isDarkTheme ? `${sa(0.45)}` : "rgba(14,16,21,0.03)",
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                        opacity: expired || exhausted ? 0.6 : 1
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: surfaceTextPrimary, fontSize: "13px", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.02em" }}>{promo.code}</span>
                          <span style={{ fontSize: "11px", color: A[400], background: aa(400, 0.15), padding: "2px 7px", borderRadius: "6px" }}>−{promo.discountPercent}%</span>
                          {expired && <span style={{ fontSize: "10px", color: "#ef4444" }}>{t("истёк")}</span>}
                          {!expired && exhausted && <span style={{ fontSize: "10px", color: "#ef4444" }}>{t("исчерпан")}</span>}
                        </div>
                        <div style={{ color: surfaceTextSecondary, fontSize: "11px", marginTop: "3px" }}>{details}</div>
                      </div>
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={() => deleteAdminPromo(promo)}
                        style={{
                          borderRadius: "10px",
                          border: "1px solid rgba(239,68,68,0.35)",
                          background: "transparent",
                          color: "#ef4444",
                          padding: "5px 9px",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          flexShrink: 0
                        }}
                      >
                        {t("Удалить")}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Встроенный прокси Umbrella ── */}
            <div style={{
              borderRadius: "0",
              border: "none",
              background: "transparent",
              boxShadow: "none",
              padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px",
              marginTop: "16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "16px" }}>
                <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  {t("Встроенный прокси Umbrella")}
                </h3>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={loadPanelProxy}
                  disabled={loadingPanelProxy}
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(141,147,163,0.3)",
                    background: shellSurfaceSoft,
                    color: isDarkTheme ? "#c6c9d1" : "#262a33",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: loadingPanelProxy ? "not-allowed" : "pointer",
                    opacity: loadingPanelProxy ? 0.7 : 1
                  }}
                >
                  {loadingPanelProxy ? t("Загрузка...") : t("Обновить")}
                </button>
              </div>

              <div style={{ color: surfaceTextSecondary, fontSize: "12px", lineHeight: 1.5, marginBottom: "10px" }}>
                {t("По одному прокси в строке (или через запятую). Форматы:")} <code>socks5://user:pass@host:port</code>, <code>http://user:pass@host:port</code>, <code>host:port:user:pass</code> {t("или")} <code>host:port</code>.
              </div>

              <textarea
                value={panelProxyConfig}
                onChange={(event) => setPanelProxyConfig(event.target.value)}
                placeholder="socks5://user:pass@host:port"
                rows={4}
                spellCheck={false}
                // Without these, mobile keyboards autocapitalise/autocorrect the
                // pasted proxy string and mangle credential case.
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                // Insert pasted text from clipboardData verbatim: mobile browsers
                // run pasted text through autocorrect and can change its case,
                // which silently corrupted proxy credentials.
                onPaste={(event) => {
                  const raw = String(event.clipboardData?.getData("text") || "");
                  if (!raw) return;
                  event.preventDefault();
                  const el = event.currentTarget;
                  const start = el.selectionStart ?? String(panelProxyConfig || "").length;
                  const end = el.selectionEnd ?? start;
                  const current = String(panelProxyConfig || "");
                  setPanelProxyConfig(current.slice(0, start) + raw + current.slice(end));
                }}
                disabled={loadingPanelProxy}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "16px",
                  border: `1px solid ${surfaceInputBorder}`,
                  background: surfaceInputBg,
                  color: surfaceInputText,
                  fontSize: "13px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  boxSizing: "border-box",
                  resize: "vertical",
                  minHeight: "96px",
                  marginBottom: "10px"
                }}
              />

              {Array.isArray(panelProxyProxies) && panelProxyProxies.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
                  {panelProxyProxies.map((p, i) => {
                    const st = String(p?.status || "").toLowerCase();
                    const isOk = st === "active";
                    const isFail = st === "failed";
                    const dot = isOk ? "#4ade80" : isFail ? "#ef4444" : "#fbbf24";
                    const geo = p?.checkResult?.location;
                    const geoText = geo && (geo.country || geo.city)
                      ? ` · ${[geo.city, geo.country].filter(Boolean).join(", ")}`
                      : "";
                    return (
                      <div
                        key={String(p?.id || i)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "5px 10px",
                          borderRadius: "12px",
                          border: "1px solid rgba(141,147,163,0.2)",
                          background: shellSurfaceSoft,
                          fontSize: "11px",
                          color: surfaceTextSecondary
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                        <span style={{ fontWeight: 700, color: surfaceTextPrimary }}>{String(p?.type || "").toUpperCase() || "PROXY"}</span>
                        <span>{isOk ? t("активен") : isFail ? t("недоступен") : t("проверка")}{geoText}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {panelProxyStatus && (
                <div style={{
                  marginBottom: "10px",
                  padding: "10px 12px",
                  borderRadius: "16px",
                  border: `1px solid ${panelProxyStatus.variant === "success" ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.35)"}`,
                  background: panelProxyStatus.variant === "success" ? "rgba(22,101,52,0.28)" : "rgba(127,29,29,0.28)",
                  color: panelProxyStatus.variant === "success" ? "#bbf7d0" : "#fecaca",
                  fontSize: "13px"
                }}>
                  {panelProxyStatus.text}
                </div>
              )}

              <button
                type="button"
                className="kl-plain-button"
                onClick={savePanelProxy}
                disabled={savingPanelProxy || loadingPanelProxy}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "16px",
                  border: "none",
                  background: A[600],
                  color: onAccent("#fff"),
                  fontWeight: "700",
                  fontSize: "14px",
                  cursor: (savingPanelProxy || loadingPanelProxy) ? "not-allowed" : "pointer",
                  opacity: (savingPanelProxy || loadingPanelProxy) ? 0.7 : 1
                }}
              >
                {savingPanelProxy ? t("Сохранение...") : t("Сохранить")}
              </button>

              <div style={{ marginTop: "22px", paddingTop: "18px", borderTop: `1px solid ${surfaceInputBorder}` }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: surfaceTextPrimary, marginBottom: "4px" }}>
                  {t("Прокси парсера")}
                </div>
                <div style={{ color: surfaceTextSecondary, fontSize: "12px", lineHeight: 1.5, marginBottom: "10px" }}>
                  {t("Отдельный пул для парсинга публичных страниц. Не пересекается со встроенным прокси панели и прокси аккаунтов — сгоревший IP не заденет сессии.")}
                </div>

                <textarea
                  value={parserProxyInput}
                  onChange={(event) => setParserProxyInput(event.target.value)}
                  onPaste={(event) => {
                    const raw = String(event.clipboardData?.getData("text") || "");
                    if (!raw) return;
                    event.preventDefault();
                    const el = event.currentTarget;
                    const start = el.selectionStart ?? String(parserProxyInput || "").length;
                    const end = el.selectionEnd ?? start;
                    const current = String(parserProxyInput || "");
                    setParserProxyInput(current.slice(0, start) + raw + current.slice(end));
                  }}
                  placeholder={t("socks5://user:pass@host:port — по одному в строке")}
                  rows={3}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="off"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "14px",
                    border: `1px solid ${surfaceInputBorder}`,
                    background: surfaceInputBg,
                    color: surfaceInputText,
                    fontSize: "13px",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    boxSizing: "border-box",
                    resize: "vertical",
                    marginBottom: "10px"
                  }}
                />

                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={addParserProxies}
                  disabled={parserProxyBusy || !String(parserProxyInput || "").trim()}
                  style={{
                    padding: "10px 16px",
                    borderRadius: "12px",
                    border: `1px solid ${A[500]}`,
                    background: A[500],
                    color: onAccent("#fff"),
                    fontWeight: 700,
                    fontSize: "13px",
                    cursor: (parserProxyBusy || !String(parserProxyInput || "").trim()) ? "not-allowed" : "pointer",
                    opacity: (parserProxyBusy || !String(parserProxyInput || "").trim()) ? 0.6 : 1
                  }}
                >
                  {parserProxyBusy ? t("Сохранение...") : t("Добавить")}
                </button>

                {parserProxyStatus && (
                  <div style={{
                    marginTop: "10px",
                    fontSize: "12px",
                    color: parserProxyStatus.variant === "success" ? "#86efac" : "#fca5a5"
                  }}>
                    {parserProxyStatus.text}
                  </div>
                )}

                <div style={{ marginTop: "14px", display: "flex", flexDirection: "column" }}>
                  {parserProxies.length === 0 ? (
                    <div style={{ color: surfaceTextSecondary, fontSize: "12px" }}>
                      {t("Пул пуст — парсер работать не будет.")}
                    </div>
                  ) : parserProxies.map((proxy) => (
                    <div
                      key={proxy.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "9px 0",
                        borderBottom: `1px solid ${surfaceInputBorder}`
                      }}
                    >
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: "12.5px",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        color: surfaceTextPrimary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}>
                        {proxy.type}://{proxy.host}:{proxy.port}
                      </span>
                      {proxy.hasAuth && (
                        <span style={{ fontSize: "10.5px", color: A[300], flexShrink: 0 }}>auth</span>
                      )}
                      <button
                        type="button"
                        className="kl-plain-button"
                        onClick={() => removeParserProxy(proxy.id)}
                        disabled={parserProxyBusy}
                        style={{
                          flexShrink: 0,
                          padding: "5px 10px",
                          borderRadius: "9px",
                          border: "1px solid rgba(248,113,113,0.4)",
                          background: "rgba(239,68,68,0.12)",
                          color: "#fca5a5",
                          fontSize: "11.5px",
                          fontWeight: 600,
                          cursor: parserProxyBusy ? "not-allowed" : "pointer"
                        }}
                      >
                        {t("Удалить")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>)}
        </div>
      )}
    </div>
  );

  if (!authBootstrapDone && tokenStatus.state === "unknown") {
    // Загрузка — знак панели с бегущим по надписи бликом вместо колеса и
    // текста. Текст остаётся для читалок экрана.
    return (
      <div
        role="status"
        aria-live="polite"
        className="kl-boot-screen"
        style={{
          minHeight: "100dvh",
          width: "100%",
          background: appBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "--kl-boot-base": isDarkTheme ? aa(300, 0.42) : aa(700, 0.42),
          "--kl-boot-shine": isDarkTheme ? "#ffffff" : A[600]
        }}
      >
        <div className="kl-boot-mark">
          <ArmorLogoIcon size={isPhoneView ? 34 : 40} />
          <span className="kl-boot-wordmark" style={{ fontSize: isPhoneView ? "30px" : "36px" }}>umbrella</span>
        </div>
        <span className="kl-visually-hidden">{t("Восстанавливаем сессию...")}</span>
      </div>
    );
  }

  if (tokenStatus.state === "subscription_required") {
    return (
      <>
        <SubscriptionGate
          username={String(authIdentity?.username || "").trim()}
          avatarUrl={String(authIdentity?.avatarUrl || "").trim()}
          ownerId={String(authIdentity?.ownerId || tokenStatus?.ownerId || "").trim()}
          isPhoneView={isPhoneView}
          appVersion={APP_VERSION}
          telegramUrl={SUBSCRIPTION_BOT_URL}
          onStartDemo={(tabId) => startDemoSession({ tabId })}
          onRecheck={recheckSubscription}
          onLogout={logoutSession}
          onBuy={openPaymentModal}
        />
        {renderPaymentModal()}
      </>
    );
  }

  if (tokenStatus.state !== "valid") {
    return (
      <AuthPortal
        isPhoneView={isPhoneView}
        tokenStatus={tokenStatus}
        authError={authError}
        onLogin={loginWithCredentials}
        onRegister={registerWithCredentials}
        onTelegramAuthorized={completeTelegramLogin}
        appVersion={APP_VERSION}
        initialScreen={authPortalEntry.screen}
        initialTab={authPortalEntry.tab}
        onStartGuestDemo={() => startDemoSession({ guest: true, tabId: "dashboard" })}
        onTelegramMiniApp={isTelegramMiniApp()
          ? ({ openDemo = false } = {}) => loginFromTelegramMiniApp({ register: true, openDemo })
          : undefined}
      />
    );
  }

  return (
    <div className={`app-root kl-app-root${isPhoneView ? " phone-view" : ""}${demoActive ? " is-demo" : ""}`} style={{ minHeight: "100dvh" }}>
      {demoActive && !(isPhoneView && (isMobileMessagesChatExpanded || isMobileMessagesThreadOpen)) && (
        <div className="demo-bar" role="status">
          <span className="demo-bar-badge">
            <SparklesIcon size={12} />
            {t("Демо")}
          </span>
          <span className="demo-bar-text">
            {isPhoneView
              ? t("Вымышленные данные")
              : (
                <>
                  <b>{t("Это песочница.")}</b>{" "}
                  {t("Данные вымышленные. Жмите на что угодно.")}
                </>
              )}
          </span>
          <span className="demo-bar-actions">
            {!isPhoneView && (
              <button
                type="button"
                className="kl-plain-button demo-bar-button is-primary"
                onClick={() => openPaymentModal()}
              >
                <SendIcon size={13} />
                {isDemoGuest() ? t("Создать аккаунт") : t("Оформить подписку")}
              </button>
            )}
            <button type="button" className="kl-plain-button demo-bar-button is-ghost" onClick={exitDemoSession}>
              {t("Выйти из демо")}
            </button>
          </span>
        </div>
      )}
      {/* Украшение из настроек: холст подгружается только когда включён. */}
      {ambientMode === "umbrellas" && (
        <SectionErrorBoundary silent>
          <Suspense fallback={null}>
            <AmbientUmbrellas />
          </Suspense>
        </SectionErrorBoundary>
      )}
      <ToastStack
        toasts={toasts}
        onDismiss={dismissToast}
        isPhoneView={isPhoneView}
        zIndex={TOAST_Z_INDEX}
      />
      {/* Header. Верхней полосы больше нет ни на столе, ни на телефоне: место
          она занимала, а её две кнопки — уведомления и приватный режим — на
          телефоне переехали в лист «Ещё». Дерево оставлено ради порталов внутри:
          панель уведомлений и панель профиля рисуются в body отсюда и не
          зависят от того, показана ли сама полоса. */}
      <div className="app-header" style={{
        background: "transparent",
        padding: isPhoneView ? "8px 10px 6px" : "14px 0 10px",
        boxShadow: "none",
        backdropFilter: "none",
        borderBottom: "none",
        position: "relative",
        zIndex: 3000,
        overflow: "visible",
        display: "none"
      }}>
        <div className="app-header-width" style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: isPhoneView ? "0 6px" : "0 24px"
        }}>
        <div className="app-header-inner" style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: isPhoneView ? "100%" : "fit-content",
          margin: isPhoneView ? 0 : "0 0 0 auto",
          height: isPhoneView ? "60px" : "70px",
          gap: isPhoneView ? "6px" : "16px",
          padding: isPhoneView ? "0 8px" : "0 16px",
          borderRadius: isPhoneView ? "16px" : "18px",
          border: `1px solid ${headerGlassBorder}`,
          background: headerGlassBg,
          boxShadow: headerGlassShadow,
          backdropFilter: isPhoneView ? "none" : "blur(10px)",
          overflow: "visible"
        }}>
          <div style={{
            display: isPhoneView ? "flex" : "none",
            alignItems: "center",
            gap: isPhoneView ? "6px" : "14px",
            minWidth: 0,
            flex: "1 1 auto"
          }}>
            {/* Как и в боковой панели: знак занимает квадрат целиком, без
                подложки и обводки — они рисовали вторую плитку поверх той, что
                уже есть у самого знака. Тень оставлена, она отделяет его от
                шапки, а не обводит. */}
            <div style={{
              width: isPhoneView ? "32px" : "42px",
              height: isPhoneView ? "32px" : "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: headerIconShadow,
              borderRadius: isPhoneView ? "12px" : "14px",
              flex: "0 0 auto"
            }}>
              <ArmorLogoIcon size={isPhoneView ? 32 : 42} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <h1 style={{
                margin: 0,
                color: "#f7f8fa",
                fontSize: isPhoneView ? "16px" : "24px",
                fontWeight: "800",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                background: `linear-gradient(135deg, #f7f8fa, ${A[300]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>umbrella</h1>
              <div style={{
                marginTop: isPhoneView ? "2px" : "4px",
                fontSize: isPhoneView ? "10px" : "12px",
                fontWeight: "600",
                color: surfaceTextSecondary,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: isPhoneView ? "92px" : "none"
              }} title={APP_VERSION}>{APP_VERSION}</div>
            </div>
          </div>
          {tokenStatus.state === "valid" && (
            <div style={{
              display: isPhoneView ? "flex" : "none",
              alignItems: "center",
              gap: isPhoneView ? "8px" : "12px",
              flex: "0 0 auto",
              marginLeft: "auto"
            }}>
              <div style={{ position: "relative" }} ref={notificationsPanelRef}>
                <button
                  type="button"
                  onClick={toggleNotificationsPanel}
                  aria-label={t("Уведомления")}
                  aria-expanded={showNotificationsPanel}
                  style={{
                    width: isPhoneView ? "38px" : "44px",
                    height: isPhoneView ? "38px" : "44px",
                    borderRadius: isPhoneView ? "12px" : "14px",
                    border: showNotificationsPanel
                      ? "1px solid rgba(249,115,22,0.55)"
                      : `1px solid ${isDarkTheme ? "rgba(141,147,163,0.25)" : "rgba(14,16,21,0.14)"}`,
                    background: showNotificationsPanel
                      ? (isDarkTheme
                        ? `linear-gradient(145deg, rgba(124,45,18,0.34) 0%, ${sa(0.86)} 100%)`
                        : "linear-gradient(145deg, rgba(255,237,213,0.92) 0%, rgba(255,255,255,0.98) 100%)")
                      : (isDarkTheme
                        ? `linear-gradient(145deg, rgba(23, 25, 33, 0.6) 0%, ${sa(0.8)} 100%)`
                        : "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(247,248,250,0.96) 100%)"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: surfaceInputText,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: showNotificationsPanel
                      ? "0 6px 20px rgba(249,115,22,0.22)"
                      : (isDarkTheme ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 10px rgba(14,16,21,0.08)")
                  }}
                  onMouseEnter={(e) => {
                    if (isPhoneView) return;
                    e.currentTarget.style.borderColor = "rgba(249,115,22,0.55)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(249,115,22,0.22)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    if (isPhoneView) return;
                    e.currentTarget.style.borderColor = showNotificationsPanel
                      ? "rgba(249,115,22,0.55)"
                      : (isDarkTheme ? "rgba(141,147,163,0.25)" : "rgba(14,16,21,0.14)");
                    e.currentTarget.style.boxShadow = showNotificationsPanel
                      ? "0 6px 20px rgba(249,115,22,0.22)"
                      : (isDarkTheme ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 10px rgba(14,16,21,0.08)");
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <BellIcon size={isPhoneView ? 18 : 20} />
                </button>

                {unreadNotificationsCount > 0 && (
                  <span style={{
                    position: "absolute",
                    top: isPhoneView ? "-5px" : "-6px",
                    right: isPhoneView ? "-5px" : "-6px",
                    minWidth: "18px",
                    height: "18px",
                    padding: "0 6px",
                    borderRadius: "9999px",
                    // Тот же индиго, что у счётчика в десктопной шапке. Оранжевый
                    // здесь читался как отдельный вид уведомления, хотя счётчик
                    // один и тот же — менялась только раскладка.
                    background: A[500],
                    color: onAccent("white"),
                    fontSize: "11px",
                    fontWeight: "800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${aa(500, 0.4)}`,
                    boxShadow: `0 10px 18px ${aa(500, 0.3)}`,
                    lineHeight: 1
                  }}>
                    {unreadNotificationsBadge}
                  </span>
                )}

                {notificationsPresence.mounted && typeof document !== "undefined" && createPortal(
                <>
                <div
                  className="nc-overlay"
                  data-state={notificationsPresence.state}
                  onClick={() => setShowNotificationsPanel(false)}
                  style={{ zIndex: TOP_RIGHT_PANEL_Z_INDEX - 1 }}
                />
                {/* Поповер под колокольчиком на широком экране, лист снизу на
                    телефоне. Положение поповера — от кнопки, которая его
                    открыла: колокольчиков в шапке два, и открываться карточка
                    должна под тем, что нажали. */}
                <div
                  ref={notificationsPanelPopupRef}
                  className="nc"
                  data-variant={isPhoneView ? "sheet" : "popover"}
                  data-state={notificationsPresence.state}
                  style={{
                    zIndex: TOP_RIGHT_PANEL_Z_INDEX,
                    ...(isPhoneView ? {} : (() => {
                      const top = Math.max(12, Math.round(Number(notifAnchorRect?.bottom) || 0) + 10);
                      const right = notifAnchorRect
                        ? Math.max(12, Math.round(window.innerWidth - Number(notifAnchorRect.right || 0)))
                        : 16;
                      return { top: `${top}px`, right: `${right}px`, maxHeight: `min(680px, calc(100vh - ${top + 16}px))` };
                    })())
                  }}
                >
                  {isPhoneView && <div className="nc-handle" />}

                  <div className="nc-head">
                    <div style={{ minWidth: 0 }}>
                      <h3 className="nc-title">
                        {t("Уведомления")}
                        {unreadNotificationsCount > 0 && (
                          <span className="nc-count" key={unreadNotificationsCount}>{unreadNotificationsBadge}</span>
                        )}
                      </h3>
                      <div className="nc-subtitle">
                        {unreadNotificationsCount > 0
                          ? tf`Непрочитанных: ${unreadNotificationsCount}`
                          : t("Всё прочитано")}
                      </div>
                    </div>
                    <div className="nc-head-actions">
                      <button
                        type="button"
                        className="nc-icon-btn"
                        onClick={markAllNotificationsRead}
                        disabled={unreadNotificationsCount === 0}
                        title={t("Отметить все прочитанными")}
                        aria-label={t("Отметить все прочитанными")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12.5 6.5 17 14 9" /><path d="M11 17 22 6" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="nc-icon-btn"
                        onClick={clearNotifications}
                        disabled={!Array.isArray(notifications) || notifications.length === 0}
                        title={t("Очистить")}
                        aria-label={t("Очистить")}
                      >
                        <TrashIcon size={15} />
                      </button>
                      <button
                        type="button"
                        className="nc-icon-btn"
                        onClick={() => setShowNotificationsPanel(false)}
                        aria-label={t("Закрыть")}
                      >
                        <XIcon size={15} />
                      </button>
                    </div>
                  </div>

                  {Array.isArray(notifications) && notifications.length > 0 && (
                    <div className="nc-filters" role="tablist">
                      {NOTIFICATION_FILTERS.map((filter) => {
                        const count = filter.id === "all"
                          ? groupedNotifications.length
                          : groupedNotifications.filter((group) => notificationKind(group.latest) === filter.id).length;
                        return (
                          <button
                            key={filter.id}
                            type="button"
                            role="tab"
                            className="nc-filter"
                            aria-pressed={notifFilter === filter.id}
                            onClick={() => setNotifFilter(filter.id)}
                          >
                            {t(filter.label)}
                            {count > 0 && <span className="nc-filter-count">{count > 99 ? "99+" : count}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="nc-list">
                    {(() => {
                      const visible = groupedNotifications.filter((group) => (
                        notifFilter === "all" || notificationKind(group.latest) === notifFilter
                      ));
                      if (!visible.length) {
                        return (
                          <div className="nc-empty">
                            <div className="nc-empty-tile"><BellIcon size={22} color="currentColor" /></div>
                            <div className="nc-empty-title">
                              {groupedNotifications.length ? t("В этом разделе пусто") : t("Нет уведомлений")}
                            </div>
                            <div className="nc-empty-text">
                              {groupedNotifications.length
                                ? t("Здесь появятся уведомления выбранного типа.")
                                : t("Новые сообщения, события прокси и объявления панели появятся здесь.")}
                            </div>
                          </div>
                        );
                      }
                      let lastBucket = "";
                      return visible.map((group, groupIndex) => {
                        const n = group.latest;
                        const isUnread = group.unread;
                        const kind = notificationKind(n);
                        const bucket = notificationDayBucket(n?.createdAt);
                        const showSection = bucket !== lastBucket;
                        lastBucket = bucket;
                        const level = String(n?.level || "").trim().toLowerCase();
                        const tone = kind === "system"
                          ? (level === "success" ? "emerald" : (level === "error" ? "rose" : (level === "warning" ? "amber" : "accent")))
                          : (kind === "proxy" ? "emerald" : "accent");
                        const icon = kind === "message"
                          ? <MessageIcon size={16} color="currentColor" />
                          : (kind === "proxy"
                            ? <LinkIcon size={16} color="currentColor" />
                            : (level === "success" ? <SuccessIcon size={16} color="currentColor" /> : <AlertIcon size={16} color="currentColor" />));
                        const fallbackTitle = kind === "message"
                          ? t("Новое сообщение")
                          : (kind === "system" ? t("Системное уведомление") : t("Прокси"));
                        return (
                          <React.Fragment key={group.key}>
                            {showSection && (
                              <div className="nc-section">
                                {bucket === "today" ? t("Сегодня") : (bucket === "yesterday" ? t("Вчера") : t("Раньше"))}
                              </div>
                            )}
                            <button
                              type="button"
                              className="nc-item kl-list-item-enter"
                              data-unread={isUnread ? "1" : "0"}
                              style={{ "--kl-stagger-index": Math.min(groupIndex, 14) }}
                              onClick={() => {
                                group.ids.forEach((id) => markNotificationRead(id));
                                const tab = String(n?.meta?.tab || "").trim();
                                if (tab === "messages") handleTabSelect("messages");
                                if (tab === "proxies") handleTabSelect("proxies");
                                const actionUrl = String(n?.actionUrl || "").trim();
                                if (actionUrl) {
                                  window.open(actionUrl, "_blank", "noopener,noreferrer");
                                }
                                setShowNotificationsPanel(false);
                              }}
                            >
                              <div className="nc-item-icon" data-tone={tone}>{icon}</div>
                              <div className="nc-item-main">
                                <div className="nc-item-row">
                                  <div className="nc-item-title">{n.title || fallbackTitle}</div>
                                  <div className="nc-item-time">{formatNotificationTime(n.createdAt)}</div>
                                </div>
                                {n.body && <div className="nc-item-body">{n.body}</div>}
                              </div>
                              <div className="nc-item-side">
                                {group.count > 1
                                  ? <span className="nc-pill">{group.count > 99 ? "99+" : group.count}</span>
                                  : (isUnread && <span className="nc-dot" />)}
                              </div>
                            </button>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>

                  <div className="nc-foot">
                    <span className="nc-foot-note">
                      {Array.isArray(notifications) && notifications.length > 0
                        ? tf`Всего: ${notifications.length}`
                        : t("Уведомления приходят и в Telegram")}
                    </span>
                    <button
                      type="button"
                      className="nc-link"
                      onClick={() => { setShowNotificationsPanel(false); handleTabSelect("settings"); }}
                    >
                      {t("Настроить")}
                      <ChevronRightIcon size={14} />
                    </button>
                  </div>
                </div>
                </>,
                document.body
                )}
              </div>

              <div style={{ position: "relative" }} ref={profilePanelRef}>
                <button
                  type="button"
                  onClick={() => setPrivateMode((on) => !on)}
                  aria-label={privateMode ? t("Выключить приватный режим") : t("Включить приватный режим")}
                  aria-pressed={privateMode}
                  title={privateMode ? t("Приватный режим включён") : t("Приватный режим")}
                  style={{
                    width: isPhoneView ? "38px" : "44px",
                    height: isPhoneView ? "38px" : "44px",
                    borderRadius: isPhoneView ? "12px" : "14px",
                    border: privateMode
                      ? `1px solid ${aa(500, 0.5)}`
                      : `1px solid ${isDarkTheme ? "rgba(141,147,163,0.25)" : "rgba(14,16,21,0.14)"}`,
                    background: privateMode
                      ? (isDarkTheme
                        ? `linear-gradient(145deg, ${aa(900, 0.34)} 0%, ${sa(0.86)} 100%)`
                        : `linear-gradient(145deg, ${aa(100, 0.9)} 0%, rgba(255,255,255,0.98) 100%)`)
                      : (isDarkTheme
                        ? `linear-gradient(145deg, rgba(23, 25, 33, 0.6) 0%, ${sa(0.8)} 100%)`
                        : "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(247,248,250,0.96) 100%)"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: surfaceInputText,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: privateMode
                      ? `0 6px 20px ${aa(500, 0.25)}`
                      : (isDarkTheme ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 10px rgba(14,16,21,0.08)")
                  }}
                  onMouseEnter={(e) => {
                    if (isPhoneView) return;
                    e.currentTarget.style.borderColor = aa(500, 0.5);
                    e.currentTarget.style.boxShadow = `0 6px 20px ${aa(500, 0.25)}`;
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    if (isPhoneView) return;
                    e.currentTarget.style.borderColor = privateMode
                      ? aa(500, 0.5)
                      : (isDarkTheme ? "rgba(141,147,163,0.25)" : "rgba(14,16,21,0.14)");
                    e.currentTarget.style.boxShadow = privateMode
                      ? `0 6px 20px ${aa(500, 0.25)}`
                      : (isDarkTheme ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 10px rgba(14,16,21,0.08)");
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {privateMode
                    ? <EyeOffIcon size={isPhoneView ? 18 : 20} color={A[300]} />
                    : <EyeIcon size={isPhoneView ? 18 : 20} />}
                </button>

                {showProfilePanel && typeof document !== "undefined" && createPortal(
              <div
                ref={profilePanelPopupRef}
                className="profile-panel"
                style={{
                position: "fixed",
                top: isPhoneView ? "74px" : "80px",
                right: isPhoneView ? "12px" : "24px",
                left: isPhoneView ? "12px" : "auto",
                width: isPhoneView ? "auto" : "320px",
                maxWidth: "calc(100vw - 24px)",
                background: popupPanelBg,
                border: `1px solid ${popupPanelBorder}`,
                borderRadius: "16px",
                boxShadow: popupPanelShadow,
                padding: isPhoneView ? "16px" : "18px",
                zIndex: TOP_RIGHT_PANEL_Z_INDEX,
                maxHeight: isPhoneView ? "calc(100dvh - 92px)" : "calc(100dvh - 120px)",
                overflowY: "auto"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                  <div style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "18px",
                    background: aa(500, 0.18),
                    border: `1px solid ${aa(500, 0.35)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: A[300],
                    fontWeight: "600"
                  }}>
                    <UserIcon size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: "600", color: surfaceTextPrimary }}>{t("Ваш профиль")}</div>
                    <div style={{ fontSize: "12px", color: surfaceTextSecondary }}>
                      {demoActive
                        ? t("Демо-режим — подписка не оформлена")
                        : tokenStatus.state === "valid"
                          ? (tokenStatus.expiresAt
                            ? tf`Подписка до ${new Date(tokenStatus.expiresAt).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })}`
                            : t("Подписка: бессрочно"))
                          : t("Подписка не активна")}
                    </div>
                  </div>
                </div>

                <div style={{
                  background: shellSurfaceSoft,
                  borderRadius: "16px",
                  border: `1px solid ${surfaceInputBorder}`,
                  padding: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px"
                }}>
                  <div>
                    <div style={{ fontSize: "12px", color: surfaceTextSecondary, marginBottom: "6px" }}>
                      {t("Логин")}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "14px",
                        border: `1px solid ${surfaceInputBorder}`,
                        background: surfaceInputBg,
                        color: surfaceInputText,
                        fontSize: "12px"
                      }}
                    >
                      {String(authIdentity?.username || "").trim() || "—"}
                    </div>
                  </div>

                  <button
                    onClick={() => openPaymentModal()}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "14px",
                      border: "none",
                      cursor: "pointer",
                      background: A[600],
                      color: onAccent("white"),
                      fontWeight: "600",
                      fontSize: "13px"
                    }}
                  >
                    {demoActive ? t("Оформить подписку") : t("Продлить подписку")}
                  </button>
                </div>
              </div>,
              document.body
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {authError && (
        <div style={{
          background: "rgba(239,68,68,0.15)",
          color: "#fca5a5",
          borderBottom: "1px solid rgba(239,68,68,0.3)",
          padding: "8px 24px",
          fontSize: "13px"
        }}>
          {authError}
        </div>
      )}

      {freezeAccessLocked && (
        <div
          style={{
            margin: "10px 24px 0",
            borderRadius: "18px",
            border: "1px solid rgba(245,158,11,0.35)",
            background: isDarkTheme
              ? "linear-gradient(140deg, rgba(120,53,15,0.26), rgba(23,25,33,0.42))"
              : "linear-gradient(140deg, rgba(251,191,36,0.16), rgba(255,255,255,0.95))",
            color: isDarkTheme ? "#fde68a" : "#92400e",
            padding: isPhoneView ? "10px 12px" : "12px 14px",
            fontSize: "13px",
            lineHeight: 1.45
          }}
        >
          {t("Подписка на заморозке: доступ к разделам временно ограничен. Для продолжения работы откройте")}{" "}
          <strong>{t("Настройки")}</strong> {t("и отключите заморозку.")}
        </div>
      )}

      {isPhoneView ? (
        <>
          {/* Mobile bottom navigation — tab bar + "More" sheet */}
          {!shouldHideTopHeader && !isMobileMessagesChatExpanded && !isMobileMessagesThreadOpen && (() => {
            const primaryIds = ["dashboard", "accounts", "messages", "active-ads"];
            const primaryTabs = tabs.filter((t) => primaryIds.includes(t.id));
            const activeIsExtra = !primaryIds.includes(activeTab);
            const activeTabObj = tabs.find((t) => t.id === activeTab);
            const navInactive = isDarkTheme ? "#8d93a3" : "#5c6270";
            const barSolid = isDarkTheme ? S.bg : "#ffffff";
            const messagesBadge = effectiveUnreadMessagesCount > 0 ? (
              <span style={{
                position: "absolute", top: -6, right: -11,
                minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8,
                background: A[500], color: onAccent("#fff"), fontSize: 9, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: `2px solid ${barSolid}`, lineHeight: 1
              }}>{unreadMessagesBadge}</span>
            ) : null;
            return (
              <>
                {/* Bottom tab bar */}
                <nav style={{
                  position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9990,
                  // 62 — сами кнопки, отступ под полоску «Домой» — сверх них. Раньше
                  // он входил в те же 62 (box-sizing), и в веб-приложении на iPhone,
                  // где отступ 34, кнопкам оставалось меньше 30: иконки вылезали
                  // над меню.
                  height: "calc(62px + env(safe-area-inset-bottom, 0px))",
                  paddingBottom: "env(safe-area-inset-bottom, 0px)",
                  display: "flex", alignItems: "stretch",
                  background: isDarkTheme ? "rgba(10,11,15,0.9)" : "rgba(255,255,255,0.92)",
                  borderTop: isDarkTheme ? "1px solid rgba(141,147,163,0.14)" : "1px solid rgba(14,16,21,0.08)",
                  backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
                  boxShadow: isDarkTheme ? "0 -6px 20px rgba(0,0,0,0.35)" : "0 -6px 20px rgba(14,16,21,0.06)"
                }}>
                  {primaryTabs.map((tab) => {
                    const isTabActive = activeTab === tab.id;
                    const isTabLocked = freezeAccessLocked && tab.id !== "settings";
                    return (
                      <button
                        key={`mnav-${tab.id}`}
                        type="button"
                        disabled={isTabLocked}
                        onClick={() => { if (isTabLocked) return; handleTabSelect(tab.id); }}
                        aria-label={tab.label}
                        style={{
                          flex: 1, border: "none", background: "none", fontFamily: "inherit",
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                          padding: "8px 2px 6px", cursor: isTabLocked ? "not-allowed" : "pointer",
                          color: isTabActive ? A[400] : navInactive, opacity: isTabLocked ? 0.4 : 1,
                          transition: "color 0.18s ease"
                        }}
                      >
                        <span style={{ position: "relative", display: "inline-flex" }}>
                          {React.cloneElement(tab.icon, { size: 21 })}
                          {tab.id === "messages" ? messagesBadge : null}
                        </span>
                        <span style={{ fontSize: 9.5, fontWeight: isTabActive ? 700 : 600, letterSpacing: "-0.01em" }}>{tab.label}</span>
                      </button>
                    );
                  })}
                  {/* "More" slot */}
                  <button
                    type="button"
                    onClick={() => setIsMobileNavOpen(true)}
                    aria-label={t("Ещё")}
                    style={{
                      flex: 1, border: "none", background: "none", fontFamily: "inherit",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                      padding: "8px 2px 6px", cursor: "pointer",
                      color: (activeIsExtra || isMobileNavOpen) ? A[400] : navInactive,
                      transition: "color 0.18s ease"
                    }}
                  >
                    <span style={{ position: "relative", display: "inline-flex" }}>
                      {activeIsExtra && activeTabObj
                        ? React.cloneElement(activeTabObj.icon, { size: 21 })
                        : (
                          <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
                        )}
                      {/* Уведомления теперь внутри «Ещё» — счётчик виден на самой кнопке. */}
                      {unreadNotificationsCount > 0 && (
                        <span style={{
                          position: "absolute", top: -6, right: -11,
                          minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8,
                          background: A[500], color: onAccent("#fff"), fontSize: 9, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: `2px solid ${barSolid}`, lineHeight: 1
                        }}>{unreadNotificationsBadge}</span>
                      )}
                    </span>
                    <span style={{ fontSize: 9.5, fontWeight: (activeIsExtra || isMobileNavOpen) ? 700 : 600, letterSpacing: "-0.01em" }}>
                      {activeIsExtra && activeTabObj ? activeTabObj.label : t("Ещё")}
                    </span>
                  </button>
                </nav>

                {/* "More" bottom sheet */}
                {isMobileNavOpen && (
                  <>
                    <div
                      onClick={() => setIsMobileNavOpen(false)}
                      style={{
                        position: "fixed", inset: 0, zIndex: 9995,
                        background: isDarkTheme ? "rgba(4,5,8,0.62)" : "rgba(14,16,21,0.32)",
                        backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
                        animation: "fadeIn 0.2s ease"
                      }}
                    />
                    <div style={{
                      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9996,
                      borderRadius: "24px 24px 0 0",
                      background: isDarkTheme ? S.elevated : "#ffffff",
                      border: isDarkTheme ? "1px solid rgba(141,147,163,0.14)" : "1px solid rgba(14,16,21,0.08)",
                      borderBottom: "none",
                      padding: "12px 16px calc(20px + env(safe-area-inset-bottom))",
                      boxShadow: "0 -20px 50px rgba(0,0,0,0.4)",
                      animation: "fadeInUp 0.26s cubic-bezier(0.25,0.1,0.25,1)"
                    }}>
                      <div onClick={() => setIsMobileNavOpen(false)} style={{ cursor: "pointer", padding: "2px 0 12px" }}>
                        <div style={{ width: 38, height: 4, borderRadius: 2, background: isDarkTheme ? "rgba(141,147,163,0.3)" : "rgba(14,16,21,0.16)", margin: "0 auto" }} />
                      </div>
                      {/* Две кнопки бывшей верхней полосы: уведомления и приватный
                          режим. Уведомления открываются своим листом снизу, поэтому
                          этот лист перед ними закрывается. */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 14 }}>
                        {/* Книжечка обновлений и тикеты — слева от уведомлений. */}
                        <button
                          type="button"
                          onClick={() => { setIsMobileNavOpen(false); openUpdatesBook(null); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 12px", borderRadius: 14, fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                            border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.16)" : "rgba(14,16,21,0.1)"}`,
                            background: isDarkTheme ? "rgba(255,255,255,0.04)" : "rgba(14,16,21,0.035)",
                            color: isDarkTheme ? "#e2e4e9" : "#0e1015"
                          }}
                        >
                          <span style={{ position: "relative", display: "inline-flex", color: A[400] }}>
                            <BookOpenIcon size={19} />
                            {hasNewUpdates && (
                              <span style={{
                                position: "absolute", top: -3, right: -4, width: 9, height: 9, borderRadius: "50%",
                                background: A[500], boxShadow: `0 0 0 2px ${isDarkTheme ? S.elevated : "#ffffff"}`
                              }} />
                            )}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{t("Обновления")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={(event) => { setIsMobileNavOpen(false); toggleNotificationsPanel(event); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 12px", borderRadius: 14, fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                            border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.16)" : "rgba(14,16,21,0.1)"}`,
                            background: isDarkTheme ? "rgba(255,255,255,0.04)" : "rgba(14,16,21,0.035)",
                            color: isDarkTheme ? "#e2e4e9" : "#0e1015"
                          }}
                        >
                          <span style={{ position: "relative", display: "inline-flex", color: A[400] }}>
                            <BellIcon size={19} />
                            {unreadNotificationsCount > 0 && (
                              <span style={{
                                position: "absolute", top: -7, right: -10,
                                minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8,
                                background: A[500], color: onAccent("#fff"), fontSize: 9, fontWeight: 800,
                                display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1
                              }}>{unreadNotificationsBadge}</span>
                            )}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{t("Уведомления")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPrivateMode((on) => !on)}
                          aria-pressed={privateMode}
                          style={{
                            gridColumn: "1 / -1",
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 12px", borderRadius: 14, fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                            border: `1px solid ${privateMode ? aa(500, 0.4) : (isDarkTheme ? "rgba(141,147,163,0.16)" : "rgba(14,16,21,0.1)")}`,
                            background: privateMode ? aa(500, 0.14) : (isDarkTheme ? "rgba(255,255,255,0.04)" : "rgba(14,16,21,0.035)"),
                            color: privateMode ? A[200] : (isDarkTheme ? "#e2e4e9" : "#0e1015")
                          }}
                        >
                          <span style={{ display: "inline-flex", color: privateMode ? A[300] : A[400] }}>
                            {privateMode ? <EyeOffIcon size={19} /> : <EyeIcon size={19} />}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{privateMode ? t("Приватный: вкл") : t("Приватный режим")}</span>
                        </button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <span style={{ color: isDarkTheme ? "#f0f1f4" : "#0e1015", fontSize: 15, fontWeight: 700 }}>{t("Все разделы")}</span>
                        <button type="button" onClick={() => setIsMobileNavOpen(false)} aria-label={t("Закрыть")} style={{
                          border: "none", background: isDarkTheme ? "rgba(255,255,255,0.05)" : "rgba(14,16,21,0.05)",
                          cursor: "pointer", width: 28, height: 28, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: navInactive
                        }}>
                          <XIcon size={16} />
                        </button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 9 }}>
                        {tabs.map((tab) => {
                          const isTabActive = activeTab === tab.id;
                          const isTabLocked = freezeAccessLocked && tab.id !== "settings";
                          return (
                            <button
                              key={`msheet-${tab.id}`}
                              type="button"
                              disabled={isTabLocked}
                              onClick={() => { if (isTabLocked) return; handleTabSelect(tab.id); setIsMobileNavOpen(false); }}
                              style={{
                                cursor: isTabLocked ? "not-allowed" : "pointer", fontFamily: "inherit",
                                borderRadius: 14, padding: "14px 6px",
                                background: isTabActive ? aa(500, 0.14) : (isDarkTheme ? "rgba(255,255,255,0.03)" : "rgba(14,16,21,0.03)"),
                                border: isTabActive ? `1px solid ${aa(500, 0.32)}` : (isDarkTheme ? "1px solid rgba(141,147,163,0.12)" : "1px solid rgba(14,16,21,0.06)"),
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                                color: isTabActive ? A[400] : navInactive, opacity: isTabLocked ? 0.4 : 1,
                                transition: "background 0.16s ease, border-color 0.16s ease"
                              }}
                            >
                              {React.cloneElement(tab.icon, { size: 21 })}
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10.5, fontWeight: 600,
                                color: isTabActive ? (isDarkTheme ? "#e2e4e9" : "#0e1015") : (isDarkTheme ? "#c6c9d1" : "#3f4450")
                              }}>
                                {tab.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          })()}

          <div className="app-content" style={{
            maxWidth: "1200px",
            margin: "12px auto 0",
            padding: "0 12px",
            paddingBottom: "calc(96px + env(safe-area-inset-bottom))"
          }}>
            {/* Упавший раздел — карточкой с кнопками, а не чёрным экраном;
                переход в другой раздел начинает с чистого листа. */}
            <SectionErrorBoundary key={activeTab}>
              {renderTabContent()}
            </SectionErrorBoundary>
          </div>
        </>
      ) : (
        <div className="app-shell-v2">
          <aside className={`app-shell-v2-rail${railMode === "auto" ? " is-auto" : ""}`}>
            <div className="app-shell-v2-rail-track">
              <div className="ui-rail-brand">
                <ArmorLogoIcon size={30} />
                {/* Версия — строкой под названием: рядом с ним в узкий
                    сайдбар она не помещалась. */}
                <span className="ui-rail-brand-text">
                  <b>umbrella</b>
                  <span className="ui-rail-ver">{APP_VERSION}</span>
                </span>
              </div>
              {/* Разделы по смыслу: с чем работают каждый день — и инструменты.
                  Настройки — внизу, рядом с профилем. */}
              {[
                { id: "work", label: t("Работа"), tabIds: ["dashboard", "accounts", "messages", "active-ads", "drafts"] },
                { id: "tools", label: t("Инструменты"), tabIds: ["sender", "kl-parser", "proxies"] }
              ].map((group) => (
                <React.Fragment key={`rail-${group.id}`}>
                  <div className="ui-rail-group">{group.label}</div>
                  {group.tabIds.map((tabId) => tabs.find((tab) => tab.id === tabId)).filter(Boolean).map((tab) => {
                    const isActive = tab.id === activeTab;
                    const isTabLocked = freezeAccessLocked && tab.id !== "settings";
                    const showBadge = tab.id === "messages" && effectiveUnreadMessagesCount > 0;
                    return (
                      <button
                        key={`desktop-${tab.id}`}
                        type="button"
                        className={`ui-rail-item${isActive ? " is-active" : ""}${isTabLocked ? " is-locked" : ""}`}
                        onClick={() => handleTabSelect(tab.id)}
                        title={tab.label}
                        aria-label={tab.label}
                        aria-current={isActive ? "page" : undefined}
                        data-count={showBadge ? unreadMessagesBadge : undefined}
                      >
                        <span className="ui-rail-ico">{React.cloneElement(tab.icon, { size: 17 })}</span>
                        <span className="ui-rail-label">{tab.label}</span>
                        {showBadge && <span className="ui-rail-count">{unreadMessagesBadge}</span>}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
              {/* Нижняя строка над профилем: режим сайдбара (закреплён или
                  выезжает при наведении), приватный режим и настройки. Кнопки
                  ушли из строки профиля — там под ником теперь хватает места
                  на срок подписки целиком и в узком сайдбаре. */}
              <div className="ui-rail-foot">
                <button
                  type="button"
                  className="ui-rail-item ui-rail-mode"
                  onClick={() => setRailMode((mode) => (mode === "auto" ? "fixed" : "auto"))}
                  title={railMode === "auto" ? t("Закрепить панель") : t("Свернуть панель")}
                  aria-label={railMode === "auto" ? t("Закрепить панель") : t("Свернуть панель")}
                >
                  <span className="ui-rail-ico">
                    {railMode === "auto" ? <PanelLeftOpenIcon size={17} /> : <PanelLeftCloseIcon size={17} />}
                  </span>
                  <span className="ui-rail-label">{railMode === "auto" ? t("Закрепить") : t("Свернуть")}</span>
                </button>
                  <span className="ui-rail-me-tools">
                    {/* Приватный режим — тот же, что на телефоне за глазом в шапке:
                        названия и фото размываются, панель можно открыть при
                        посторонних. На столе шапки нет, поэтому глаз живёт здесь,
                        рядом с настройками, и виден на любой вкладке. */}
                    <button
                      type="button"
                      className={`ui-icon-btn${privateMode ? " is-on" : ""}`}
                      onClick={() => setPrivateMode((on) => !on)}
                      aria-pressed={privateMode}
                      aria-label={privateMode ? t("Выключить приватный режим") : t("Включить приватный режим")}
                      title={privateMode ? t("Приватный режим включён") : t("Приватный режим")}
                      style={{ width: "28px", height: "28px" }}
                    >
                      {privateMode ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                    <button
                      type="button"
                      className={`ui-icon-btn${activeTab === "settings" ? " is-on" : ""}`}
                      onClick={() => handleTabSelect("settings")}
                      title={t("Настройки")}
                      aria-label={t("Настройки")}
                      style={{ width: "28px", height: "28px" }}
                    >
                      <SettingsIcon size={16} />
                    </button>
                  </span>
              </div>
              <div ref={extraProfileRef} className="ui-rail-me">
                <UserAvatar
                  username={authIdentity.username}
                  avatarUrl={authIdentity.avatarUrl}
                  size={32}
                  radius={9}
                  fontSize={12}
                  isDarkTheme={isDarkTheme}
                />
                {(() => {
                  // Под ником ~95 точек: «Подписка до 09.01» туда не влезала и
                  // обрезалась как раз на дате. Здесь — только срок, полностью
                  // с годом; «Подписка до …» — в подсказке.
                  const subscriptionUntil = tokenStatus.expiresAt
                    ? new Date(tokenStatus.expiresAt).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" })
                    : "";
                  const caption = tokenStatus.role === "admin"
                    ? t("Администратор")
                    : demoActive
                      ? t("Демо-режим")
                      : subscriptionUntil
                        ? tf`до ${subscriptionUntil}`
                        : t("Пользователь");
                  const hint = !demoActive && tokenStatus.role !== "admin" && subscriptionUntil
                    ? tf`Подписка до ${subscriptionUntil}`
                    : caption;
                  return (
                    <span className="ui-rail-me-text" title={hint}>
                      <b>{authIdentity.username || t("Профиль")}</b>
                      <small>{caption}</small>
                    </span>
                  );
                })()}
                {/* Выход — справа от ника: в нижней строке над профилем
                    третья кнопка срезала подпись «Свернуть». */}
                {!demoActive && (
                  <span className="ui-rail-me-tools">
                    <button
                      type="button"
                      className="ui-icon-btn ui-rail-logout"
                      onClick={requestLogout}
                      title={t("Выйти из аккаунта")}
                      aria-label={t("Выйти из аккаунта")}
                      style={{ width: "28px", height: "28px" }}
                    >
                      <LogOutIcon size={16} />
                    </button>
                  </span>
                )}
              </div>
            </div>
          </aside>

          <div className="app-shell-v2-main">
            <div className="app-shell-v2-body" style={{ padding: activeTab === "messages" ? 0 : undefined }}>
              <div className="app-content" style={{
                margin: 0,
                padding: 0,
                maxWidth: "none"
              }}>
                <SectionErrorBoundary key={activeTab}>
                  {renderTabContent()}
                </SectionErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна */}
      {renderPaymentModal()}
      {profileNamePresence.mounted && (() => {
        const profileNameModal = profileNameView;
        return (
        <div
          className="modal-overlay profile-name-modal-overlay"
          {...overlayMotionProps(profileNamePresence.state)}
          onClick={closeProfileNameModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6,7,10,0.72)",
            backdropFilter: "blur(8px)",
            zIndex: 120000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isPhoneView ? "14px" : "24px"
          }}
        >
          <div
            {...dialogMotionProps(profileNamePresence.state)}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "480px",
              borderRadius: "20px",
              border: "1px solid rgba(255,255,255,0.09)",
              background: S.surface,
              boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
              padding: isPhoneView ? "20px 16px" : "26px"
            }}
          >
            <div style={{ color: "#f2f3f6", fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              {t("Смена имени аккаунта")}
            </div>
            <div style={{ marginTop: "6px", color: "#8d93a3", fontSize: "13px" }}>
              {profileNameModal.account ? formatAccountLabel(profileNameModal.account) : ""}
            </div>

            <input
              type="text"
              value={profileNameModal.value}
              onChange={(event) => setProfileNameModal((prev) => ({ ...prev, value: event.target.value }))}
              placeholder={t("Новое имя профиля")}
              disabled={profileNameModal.saving}
              style={{
                marginTop: "18px",
                width: "100%",
                boxSizing: "border-box",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.02)",
                color: "#e2e4e9",
                fontSize: "14px",
                padding: "12px 14px",
                outline: "none"
              }}
            />

            <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
              <button
                type="button"
                className="kl-plain-button"
                onClick={closeProfileNameModal}
                disabled={profileNameModal.saving}
                style={{
                  minWidth: isPhoneView ? "calc(50% - 6px)" : "140px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "#d5d8e0",
                  padding: "11px 16px",
                  fontWeight: 600
                }}
              >
                {t("Отмена")}
              </button>
              <button
                type="button"
                className="kl-plain-button"
                onClick={handleProfileNameSubmit}
                disabled={profileNameModal.saving}
                style={{
                  minWidth: isPhoneView ? "calc(50% - 6px)" : "160px",
                  borderRadius: "12px",
                  border: "none",
                  background: A[500],
                  color: onAccent("#fff"),
                  padding: "11px 16px",
                  fontWeight: 600
                }}
              >
                {profileNameModal.saving ? t("Сохранение...") : t("Сохранить")}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {phoneModalPresence.mounted && (() => {
        const phoneModal = phoneModalView;
        return (
        <div
          className="modal-overlay phone-modal-overlay"
          {...overlayMotionProps(phoneModalPresence.state)}
          onClick={closePhoneModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6,7,10,0.72)",
            backdropFilter: "blur(8px)",
            zIndex: 120000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isPhoneView ? "12px" : "24px"
          }}
        >
          <div
            {...dialogMotionProps(phoneModalPresence.state)}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "560px",
              maxHeight: "88dvh",
              overflowY: "auto",
              borderRadius: "20px",
              border: "1px solid rgba(255,255,255,0.09)",
              background: S.surface,
              boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
              padding: isPhoneView ? "20px 16px" : "26px"
            }}
          >
            <div style={{ color: "#f2f3f6", fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              {t("Подтверждение телефона")}
            </div>
            <div style={{ marginTop: "6px", color: "#8d93a3", fontSize: "13px" }}>
              {phoneModal.account ? formatAccountLabel(phoneModal.account) : ""}
            </div>

            {/* Current phone status. We accept multiple positive signals as
                "bound" (status="verified", verifiedAt, saved mask, last
                digits) — older snapshots sometimes omit `status` but keep
                the rest, and persistence may now write verified without a
                mask. While the initial /phone/status fetch is in flight and
                we don't have any signal yet, we show a neutral placeholder
                rather than flashing "Не привязан". */}
            {phoneModal.statusLoading && (() => {
              const v = phoneModal.account?.phoneVerification || null;
              const statusToken = String(v?.status || "").trim().toLowerCase();
              const verifiedSignal = Boolean(v && (
                statusToken === "verified"
                || v.verifiedAt
                || String(v.maskedPhone || "").trim()
                || String(v.phoneLastDigits || "").trim()
              ));
              const explicitlyNotVerified = Boolean(v && statusToken === "not_verified" && !verifiedSignal);
              return !verifiedSignal && !explicitlyNotVerified;
            })() ? (
              <div style={{
                marginTop: "12px",
                padding: "12px 16px",
                borderRadius: "16px",
                border: "1px solid rgba(141,147,163,0.22)",
                background: "rgba(141,147,163,0.06)",
                color: "#c6c9d1",
                fontSize: "13px"
              }}>
                {t("Проверяем статус номера…")}
              </div>
            ) : (() => {
              const v = phoneModal.account?.phoneVerification || null;
              const statusToken = String(v?.status || "").trim().toLowerCase();
              return Boolean(v && (
                statusToken === "verified"
                || v.verifiedAt
                || String(v.maskedPhone || "").trim()
                || String(v.phoneLastDigits || "").trim()
              ));
            })() ? (
              <div style={{
                marginTop: "12px",
                padding: "12px 16px",
                borderRadius: "16px",
                border: "1px solid rgba(52,211,153,0.3)",
                background: "rgba(16,185,129,0.08)",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="#86efac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="18" cy="5" r="4" fill="#171921" stroke="#86efac" strokeWidth="1.5"/>
                  <path d="M16.5 5l1 1 2-2" stroke="#86efac" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <div style={{ color: "#86efac", fontSize: "14px", fontWeight: 700 }}>
                    {t("Номер привязан")}
                  </div>
                  {(() => {
                    const masked = getPhoneVerificationMasked(phoneModal.account?.phoneVerification);
                    return masked ? (
                      <div style={{ color: "#bbf7d0", fontSize: "15px", fontWeight: 700, marginTop: "2px", letterSpacing: "0.2px" }}>
                        {masked}
                      </div>
                    ) : null;
                  })()}
                  <div style={{ color: "#a7f3d0", fontSize: "13px", fontWeight: 600, marginTop: "2px" }}>
                    {t("Статус: верифицирован")}
                  </div>
                  {phoneModal.account.phoneVerification.verifiedAt && (
                    <div style={{ color: "#707684", fontSize: "11px", marginTop: "4px" }}>
                      {t("Подтвержден:")} {new Date(phoneModal.account.phoneVerification.verifiedAt).toLocaleDateString(locale(), { timeZone: "Europe/Berlin" })}
                    </div>
                  )}
                </div>
              </div>
            ) : (() => {
              const v = phoneModal.account?.phoneVerification || null;
              const statusToken = String(v?.status || "").trim().toLowerCase();
              // We end up here when no verified signal is present. If the
              // backend explicitly says "not_verified", trust it. Otherwise
              // (source="none" or detection error → kleinanzeigen blocked the
              // bot with 403, etc.) we have no idea — show an honest "can't
              // check right now" message instead of lying with "Не привязан".
              const explicitlyNotVerified = statusToken === "not_verified";
              const detectionFailed = !explicitlyNotVerified
                && (phoneModal.statusSource === "none" || phoneModal.statusSource === "error");
              if (detectionFailed) {
                return (
                  <div style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    borderRadius: "16px",
                    border: "1px solid rgba(141,147,163,0.28)",
                    background: "rgba(141,147,163,0.06)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px"
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8d93a3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "1px" }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <div>
                      <div style={{ color: "#e2e4e9", fontSize: "14px", fontWeight: 700 }}>
                        {t("Не удалось проверить статус")}
                      </div>
                      <div style={{ color: "#8d93a3", fontSize: "12px", marginTop: "3px", lineHeight: 1.4 }}>
                        {t("Kleinanzeigen вернул ошибку для всех endpoints настроек (вероятно, антибот-блок или истёкшие cookies/прокси). Привязка номера могла быть выполнена раньше, но сейчас определить нельзя. Попробуйте обновить cookies или сменить прокси.")}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div style={{
                  marginTop: "12px",
                  padding: "12px 16px",
                  borderRadius: "16px",
                  border: "1px solid rgba(245,158,11,0.25)",
                  background: "rgba(245,158,11,0.06)",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="18" cy="5" r="4" fill="#171921" stroke="#fbbf24" strokeWidth="1.5"/>
                    <line x1="18" y1="3.5" x2="18" y2="5.5" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="18" cy="7" r="0.5" fill="#fbbf24"/>
                  </svg>
                  <div>
                    <div style={{ color: "#fbbf24", fontSize: "14px", fontWeight: 700 }}>
                      {t("Номер не привязан")}
                    </div>
                    <div style={{ color: "#8d93a3", fontSize: "12px", marginTop: "2px" }}>
                      {t("Привяжите номер телефона ниже для верификации аккаунта")}
                    </div>
                  </div>
                </div>
              );
            })()}

            {phoneModal.step === "verify" && phoneModal.expiresAt > 0 && (
              <div style={{ marginTop: "8px", color: "#86efac", fontSize: "12px" }}>
                {t("Код активен до")} {new Date(phoneModal.expiresAt).toLocaleTimeString(locale(), { timeZone: "Europe/Berlin" })}.
              </div>
            )}

            {phoneModal.step === "start" ? (
              <>
                <div style={{
                  marginTop: "14px",
                  display: "grid",
                  gridTemplateColumns: isPhoneView ? "1fr" : "200px 1fr",
                  gap: "10px"
                }}>
                  <StyledSelect
                    value={phoneModal.countryCode}
                    onChange={(next) => setPhoneModal((prev) => ({ ...prev, countryCode: next }))}
                    disabled={phoneModal.saving}
                    options={PHONE_COUNTRY_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                    placeholder={t("Код страны")}
                    ariaLabel={t("Код страны")}
                  />
                  <input
                    type="text"
                    value={phoneModal.phoneNumber}
                    onChange={(event) => setPhoneModal((prev) => ({ ...prev, phoneNumber: event.target.value }))}
                    disabled={phoneModal.saving}
                    placeholder={t("Номер без кода страны")}
                    style={{
                      width: "100%",
                      // Высота и скругление — как у списка кодов слева (.ui-select).
                      height: "40px",
                      borderRadius: "12px",
                      border: "1px solid rgba(141,147,163,0.28)",
                      background: "rgba(8,9,12,0.58)",
                      color: "#e2e4e9",
                      padding: "0 13px",
                      fontSize: "14px",
                      outline: "none"
                    }}
                  />
                </div>
                <div style={{ marginTop: "8px", color: "#8d93a3", fontSize: "12px" }}>
                  {phoneModal.account?.phoneVerification?.status === "verified"
                    ? t("Введите новый номер для смены. Код придет в SMS.")
                    : t("Введите номер без префикса страны. Код придет в SMS.")}
                </div>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={phoneModal.code}
                  onChange={(event) => setPhoneModal((prev) => ({ ...prev, code: event.target.value }))}
                  disabled={phoneModal.saving}
                  placeholder={t("Код из SMS")}
                  style={{
                    marginTop: "14px",
                    width: "100%",
                    borderRadius: "16px",
                    border: "1px solid rgba(141,147,163,0.28)",
                    background: "rgba(8,9,12,0.58)",
                    color: "#e2e4e9",
                    padding: "11px 12px",
                    fontSize: "15px",
                    outline: "none"
                  }}
                />
                <div style={{ marginTop: "8px", color: "#8d93a3", fontSize: "12px" }}>
                  {t("Если код не пришел — отправьте повторно.")}
                </div>
              </>
            )}

            <div style={{
              marginTop: "16px",
              display: "grid",
              gridTemplateColumns: isPhoneView ? "1fr 1fr" : "repeat(3, auto)",
              justifyContent: "flex-end",
              gap: "10px",
              alignItems: "stretch"
            }}>
              <button
                type="button"
                className="kl-plain-button"
                onClick={closePhoneModal}
                disabled={phoneModal.saving || phoneModal.resending}
                style={{
                  minWidth: isPhoneView ? "0" : "130px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "#d5d8e0",
                  padding: "11px 16px",
                  fontWeight: 600,
                  width: "100%"
                }}
              >
                {t("Закрыть")}
              </button>

              {phoneModal.step === "verify" && (
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={handlePhoneResend}
                  disabled={phoneModal.saving || phoneModal.resending}
                  style={{
                    minWidth: isPhoneView ? "0" : "160px",
                    borderRadius: "12px",
                    border: "1px solid rgba(245,158,11,0.38)",
                    background: "rgba(245,158,11,0.12)",
                    color: "#fbbf24",
                    padding: "11px 16px",
                    fontWeight: 600,
                    width: "100%"
                  }}
                >
                  {phoneModal.resending ? t("Отправка...") : t("Отправить код снова")}
                </button>
              )}

              <button
                type="button"
                className="kl-plain-button"
                onClick={phoneModal.step === "start" ? handlePhoneStart : handlePhoneConfirm}
                disabled={phoneModal.saving}
                style={{
                  minWidth: isPhoneView ? "0" : "180px",
                  borderRadius: "12px",
                border: "none",
                background: A[500],
                color: onAccent("#fff"),
                padding: "11px 16px",
                fontWeight: 600,
                width: "100%",
                gridColumn: isPhoneView
                  ? (phoneModal.step === "start" ? "auto" : "1 / -1")
                  : "auto"
              }}
            >
                {phoneModal.saving
                  ? t("Выполняется...")
                  : (phoneModal.step === "start" ? t("Отправить код") : t("Подтвердить код"))}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {proxyPickerPresence.mounted && proxyPickerShown && (() => {
        const proxyPickerAccount = proxyPickerShown;
        const isBulk = proxyPickerBulkIds.length > 1;
        const currentProxyId = isBulk ? "" : String(proxyPickerAccount.proxyId || "").trim();
        const query = proxyPickerQuery.trim().toLowerCase();
        const showSearch = proxies.length > 6;
        const matchesQuery = (proxy) => !query
          || [proxy.name, proxy.host, proxy.type, proxy?.checkResult?.location?.city, proxy?.checkResult?.location?.country]
            .some((value) => String(value || "").toLowerCase().includes(query));
        // Встроенные прокси — одной строкой «Umbrella»: из пула их не выбирают,
        // а меняют кнопкой «Сменить» (следующий по кругу). Свои — списком ниже.
        const builtinPool = proxies.filter((proxy) => proxy?.isBuiltin);
        const currentBuiltin = builtinPool.find((proxy) => String(proxy.id || "").trim() === currentProxyId) || null;
        const showBuiltinRow = builtinPool.length > 0 && builtinPool.some(matchesQuery);
        const visibleProxies = proxies.filter((proxy) => !proxy?.isBuiltin && matchesQuery(proxy));
        const pickerAccountSwapping = !isBulk && swappingProxyAccountIds.has(proxyPickerAccount.id);
        // Сила сигнала по пингу: четыре деления, как у связи на телефоне.
        // Зелёный — до 1,5 с (четыре деления до 0,6 с), жёлтый — до 3 с,
        // дальше красный: через прокси KL отвечает медленнее, чем напрямую.
        const SIGNAL_COLORS = { 4: "#4ade80", 3: "#4ade80", 2: "#fbbf24", 1: "#f87171" };
        const signalOf = (proxy) => {
          const status = String(proxy?.status || "").toLowerCase();
          const ping = Number(proxy?.checkResult?.responseTime);
          const hasPing = Number.isFinite(ping) && ping > 0;
          if (status === "failed") return { tone: "fail", level: 0, text: t("Не отвечает") };
          if (status !== "active") return { tone: "unknown", level: -1, text: t("Не проверен") };
          const level = !hasPing ? 4 : ping < 600 ? 4 : ping < 1500 ? 3 : ping < 3000 ? 2 : 1;
          return { tone: "ok", level, text: hasPing ? tf`${Math.round(ping)} мс` : t("Работает") };
        };
        return (
        <div
          className="modal-overlay proxy-picker-modal-overlay"
          {...overlayMotionProps(proxyPickerPresence.state)}
          onClick={closeProxyPicker}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6,7,10,0.72)",
            backdropFilter: "blur(8px)",
            zIndex: 120500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isPhoneView ? "14px" : "24px"
          }}
        >
          {/* Смена прокси — короткий список в духе выбора сети Wi‑Fi: имя,
              адрес с городом и справа сигнал с пингом. Текущий отмечен
              галочкой и подсветкой строки. Выбор применяется сразу. */}
          <div
            className="pp-card"
            {...dialogMotionProps(proxyPickerPresence.state)}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "480px",
              maxHeight: "86dvh",
              overflow: "hidden",
              borderRadius: "20px",
              border: "1px solid rgba(255,255,255,0.09)",
              background: S.surface,
              boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div style={{
              padding: isPhoneView ? "18px 14px 12px 20px" : "20px 16px 14px 24px",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px"
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="pp-title" style={{ color: "#f2f3f6", fontSize: "17px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.25 }}>
                  {isBulk ? t("Смена прокси у выбранных") : t("Смена прокси аккаунта")}
                </div>
                <div className="pp-subtitle" style={{ marginTop: "3px", color: "#8d93a3", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {isBulk ? tf`Аккаунтов: ${proxyPickerBulkIds.length}` : formatAccountLabel(proxyPickerAccount)}
                </div>
              </div>
              <button
                type="button"
                className="kl-plain-button pp-close"
                onClick={closeProxyPicker}
                disabled={Boolean(updatingAccountProxyId)}
                aria-label={t("Закрыть")}
                style={{
                  width: "32px", height: "32px", flexShrink: 0, borderRadius: "10px",
                  border: "none", background: "transparent", color: "#8d93a3",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: updatingAccountProxyId ? "not-allowed" : "pointer"
                }}
              >
                <XIcon size={17} />
              </button>
            </div>

            {showSearch && (
              <div style={{ padding: isPhoneView ? "0 14px 8px" : "0 16px 8px" }}>
                <label className="pp-search" style={{
                  display: "flex", alignItems: "center", gap: "9px", padding: "0 12px", height: "40px",
                  borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "#8d93a3"
                }}>
                  <SearchIcon size={15} />
                  <input
                    type="text"
                    value={proxyPickerQuery}
                    onChange={(event) => setProxyPickerQuery(event.target.value)}
                    placeholder={t("Название, адрес или город")}
                    style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", color: "#e2e4e9", fontSize: "13.5px" }}
                  />
                </label>
              </div>
            )}

            <div data-modal-scroll="1" style={{
              padding: isPhoneView ? "0 8px 10px" : "0 10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              overflowY: "auto",
              minHeight: 0
            }}>
              {proxies.length === 0 && (
                <div className="pp-empty" style={{ padding: "24px 16px", textAlign: "center", color: "#8d93a3", fontSize: "13.5px" }}>
                  {t("Прокси ещё не добавлены.")}
                </div>
              )}
              {proxies.length > 0 && visibleProxies.length === 0 && !showBuiltinRow && (
                <div className="pp-empty" style={{ padding: "20px 16px", textAlign: "center", color: "#8d93a3", fontSize: "13.5px" }}>
                  {t("Ничего не нашлось")}
                </div>
              )}
              {showBuiltinRow && (() => {
                const onBuiltin = Boolean(currentBuiltin);
                const bulkBusy = updatingAccountProxyId === BUILTIN_PROXY_PICKER_KEY;
                const busy = pickerAccountSwapping || bulkBusy;
                const locked = Boolean(updatingAccountProxyId) && !bulkBusy;
                const signal = currentBuiltin ? signalOf(currentBuiltin) : null;
                const location = currentBuiltin?.checkResult?.location || {};
                const place = [location.city, location.country].filter(Boolean).join(", ");
                const details = onBuiltin
                  ? [t("Встроенный прокси"), place].filter(Boolean).join(" · ")
                  : (isBulk ? t("Каждому аккаунту — свой прокси из пула") : t("Встроенный прокси · подключить"));
                const connect = () => {
                  if (busy || locked || onBuiltin) return;
                  if (isBulk) connectBulkToBuiltinProxy();
                  else swapBuiltinProxy(proxyPickerAccount, { closePicker: true });
                };
                return (
                  <div
                    role={onBuiltin ? undefined : "button"}
                    tabIndex={onBuiltin ? undefined : 0}
                    className={`pp-row pp-builtin-row${onBuiltin ? " is-current" : ""}`}
                    onClick={connect}
                    onKeyDown={(event) => {
                      if (!onBuiltin && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        connect();
                      }
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "10px 12px",
                      borderRadius: "12px",
                      background: onBuiltin ? aa(500, 0.1) : "transparent",
                      color: "#e2e4e9",
                      cursor: onBuiltin || busy || locked ? "default" : "pointer",
                      opacity: locked ? 0.5 : 1,
                      transition: "background 140ms ease, opacity 140ms ease"
                    }}
                  >
                    <ArmorLogoIcon size={32} />
                    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="pp-name" style={{ color: onBuiltin ? A[200] : "#eceef2", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        Umbrella
                      </span>
                      <span className="pp-details" style={{ color: "#7d8392", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {details}
                      </span>
                    </span>
                    {/* Смена — значком слева от пинга; пинг и галочка стоят там же,
                        где у своих прокси ниже, — столбцы совпадают. */}
                    {onBuiltin && !isBulk && (
                      <button
                        type="button"
                        className="pp-swap-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!locked && !busy) swapBuiltinProxy(proxyPickerAccount);
                        }}
                        disabled={locked || busy}
                        title={t("Следующий прокси Umbrella — по кругу, неработающие пропускаются")}
                        aria-label={t("Сменить прокси Umbrella на следующий")}
                      >
                        <SwapIcon size={15} />
                      </button>
                    )}
                    {busy ? (
                      <span className="pp-busy" style={{ display: "inline-flex", alignItems: "center", gap: "7px", flexShrink: 0, color: A[300], fontSize: "12px", fontWeight: 600 }}>
                        <Spinner size={14} />{onBuiltin ? t("Меняем...") : t("Применяем...")}
                      </span>
                    ) : signal && (
                      <span className="pp-signal" data-tone={signal.tone} data-level={signal.level} style={{ display: "inline-flex", alignItems: "center", gap: "7px", flexShrink: 0 }}>
                        <span className="pp-signal-text" style={{
                          fontSize: "12px", fontWeight: 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                          color: signal.tone === "fail" ? "#f87171" : "#8d93a3"
                        }}>
                          {signal.text}
                        </span>
                        {signal.level >= 0 && (
                          <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "flex-end", gap: "2px", height: "12px" }}>
                            {[4, 6, 9, 12].map((height, index) => (
                              <span
                                key={height}
                                className={`pp-bar${index < signal.level ? " is-on" : ""}`}
                                style={{ width: "3px", height: `${height}px`, borderRadius: "1px", background: index < signal.level ? SIGNAL_COLORS[signal.level] : "rgba(255,255,255,0.13)" }}
                              />
                            ))}
                          </span>
                        )}
                      </span>
                    )}
                    <span className="pp-check" style={{ width: "18px", flexShrink: 0, display: "inline-flex", justifyContent: "center", color: A[300] }}>
                      {onBuiltin && <CheckIcon size={17} />}
                    </span>
                  </div>
                );
              })()}
              {showBuiltinRow && visibleProxies.length > 0 && (
                <div className="pp-section-label">{t("Свои прокси")}</div>
              )}
              {visibleProxies.map((proxy) => {
                const proxyId = String(proxy.id || "").trim();
                const isCurrent = Boolean(proxyId) && currentProxyId === proxyId;
                const isBusy = Boolean(updatingAccountProxyId) && String(updatingAccountProxyId) === proxyId;
                const isUpdating = Boolean(updatingAccountProxyId) || pickerAccountSwapping;
                const disabled = isCurrent || isUpdating;
                const signal = signalOf(proxy);
                const location = proxy?.checkResult?.location || {};
                const place = [location.city, location.country].filter(Boolean).join(", ");
                const details = [proxy.isBuiltin ? t("Встроенный прокси") : `${proxy.host}:${proxy.port}`, place]
                  .filter(Boolean).join(" · ");
                return (
                  <button
                    key={proxy.id}
                    type="button"
                    className={`kl-plain-button pp-row${isCurrent ? " is-current" : ""}`}
                    onClick={() => handleSelectAccountProxy(proxy)}
                    disabled={disabled}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      textAlign: "left",
                      padding: "10px 12px",
                      borderRadius: "12px",
                      border: "none",
                      background: isCurrent ? aa(500, 0.1) : "transparent",
                      color: "#e2e4e9",
                      cursor: disabled ? "default" : "pointer",
                      opacity: isUpdating && !isBusy ? 0.5 : 1,
                      transition: "background 140ms ease, opacity 140ms ease"
                    }}
                  >
                    {proxy.isBuiltin ? (
                      <ArmorLogoIcon size={32} />
                    ) : (
                      <span className="pp-icon" style={{
                        width: "32px", height: "32px", borderRadius: "10px", flexShrink: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(255,255,255,0.05)", color: "#a4a9b6"
                      }}>
                        <LinkIcon size={15} />
                      </span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="pp-name" style={{ color: isCurrent ? A[200] : "#eceef2", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {proxy.name || "Proxy"}
                      </span>
                      <span className="pp-details" style={{ color: "#7d8392", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {details}
                      </span>
                    </span>
                    {isBusy ? (
                      <span className="pp-busy" style={{ display: "inline-flex", alignItems: "center", gap: "7px", flexShrink: 0, color: A[300], fontSize: "12px", fontWeight: 600 }}>
                        <Spinner size={14} />{t("Применяем...")}
                      </span>
                    ) : (
                      <span className="pp-signal" data-tone={signal.tone} data-level={signal.level} style={{ display: "inline-flex", alignItems: "center", gap: "7px", flexShrink: 0 }}>
                        <span className="pp-signal-text" style={{
                          fontSize: "12px", fontWeight: 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                          color: signal.tone === "fail" ? "#f87171" : "#8d93a3"
                        }}>
                          {signal.text}
                        </span>
                        {signal.level >= 0 && (
                          <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "flex-end", gap: "2px", height: "12px" }}>
                            {[4, 6, 9, 12].map((height, index) => (
                              <span
                                key={height}
                                className={`pp-bar${index < signal.level ? " is-on" : ""}`}
                                style={{ width: "3px", height: `${height}px`, borderRadius: "1px", background: index < signal.level ? SIGNAL_COLORS[signal.level] : "rgba(255,255,255,0.13)" }}
                              />
                            ))}
                          </span>
                        )}
                      </span>
                    )}
                    <span className="pp-check" style={{ width: "18px", flexShrink: 0, display: "inline-flex", justifyContent: "center", color: A[300] }}>
                      {isCurrent && <CheckIcon size={17} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        );
      })()}

      <ConfirmDialogHost />
      <UpdatesBook
        open={updatesBook.open}
        anchorRect={updatesBook.anchorRect}
        isPhoneView={isPhoneView}
        onClose={closeUpdatesBook}
        onCreateTicket={() => setShowTicketModal(true)}
      />
      <SupportTicketModal
        open={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        context={{ tab: activeTab, appVersion: APP_VERSION }}
        onSent={(id) => pushToast({
          variant: "success",
          title: t("Тикет отправлен"),
          body: id ? tf`Тикет #${id} у администратора. Спасибо!` : t("Тикет у администратора. Спасибо!")
        })}
      />

      {publishProgressPresence.mounted && (() => {
        const publishProgressModal = publishProgressView;
        const stage = resolvePublishProgressStage(publishProgressModal.step || "queued");
        const percent = Math.max(0, Math.min(100, Math.round(Number(publishProgressModal.percent || 0))));
        const failed = Boolean(publishProgressModal.error);
        const finished = !failed && (stage.id === "done" || percent >= 100);
        const tone = failed ? "bad" : finished ? "ok" : "run";
        const StageIcon = failed ? AlertTriangleIcon : finished ? SuccessIcon : getPublishStageMeta(stage.id).Icon;
        const phases = [
          { key: "queue", label: t("Очередь"), ids: ["queued", "queued-remote"] },
          { key: "login", label: t("Вход"), ids: ["draft-start", "attempt", "retry", "browser", "form-ready"] },
          { key: "photos", label: t("Фото"), ids: ["form-fill", "form-finalize", "images-upload", "images-ready"] },
          { key: "publish", label: t("Публикация"), ids: ["pre-submit", "submit"] },
          { key: "check", label: t("Проверка"), ids: ["done"] }
        ];
        let currentPhase = phases.findIndex((phase) => phase.ids.includes(String(stage.id || "queued").toLowerCase()));
        if (currentPhase < 0) currentPhase = percent >= 80 ? 3 : percent >= 40 ? 2 : percent >= 10 ? 1 : 0;
        const resultTone = { active: "ok", under_review: "warn", deleted: "bad" }[publishPostStatusMeta.state] || "muted";
        const resultUrl = String(publishProgressModal.resultUrl || "").trim();

        // Итог — отдельным спокойным экраном: большая иконка, заголовок и одна
        // фраза по центру, объявление строкой между линиями, статус меткой.
        // Полосы этапов здесь нет: всё уже прошло.
        if (failed || finished) {
          const resultHint = failed
            ? publishProgressModal.error
            : ({
              active: t("Объявление уже видно покупателям."),
              under_review: t("Kleinanzeigen проверяет объявление — статус обновится во вкладке «Объявления»."),
              deleted: t("Kleinanzeigen снял объявление сразу после публикации.")
            }[publishPostStatusMeta.state] || t("Статус появится во вкладке «Объявления»."));
          const failedPhase = failed ? String(phases[currentPhase]?.label || "") : "";
          return (
            <div className="modal-overlay pub-overlay" {...overlayMotionProps(publishProgressPresence.state)}>
              <div
                className={`pub-card pub-card--result is-${tone}`}
                role="dialog"
                aria-modal="true"
                aria-label={t("Публикация объявления")}
                {...dialogMotionProps(publishProgressPresence.state)}
              >
                <div className="pub-hero">
                  <span className="pub-hero-icon">
                    {failed ? <AlertTriangleIcon size={26} /> : <CheckIcon size={28} strokeWidth={2.4} />}
                  </span>
                  <div className="pub-hero-title">{failed ? t("Не удалось опубликовать") : t("Объявление опубликовано")}</div>
                  {resultHint && <div className="pub-hero-text">{resultHint}</div>}
                </div>

                {finished && (
                  <button
                    type="button"
                    className="ui-icon-btn pub-close"
                    onClick={handleClosePublishProgressModal}
                    disabled={closingPublishProgressModal}
                    aria-label={t("Закрыть")}
                    title={t("Закрыть")}
                  >
                    <XIcon size={16} />
                  </button>
                )}

                {(publishModalAdTitle || publishModalAccountLabel) && (
                  <div className="pub-summary">
                    {publishProgressModal.coverUrl && (
                      <span className="pub-cover"><img src={publishProgressModal.coverUrl} alt="" /></span>
                    )}
                    <span className="pub-summary-text">
                      {publishModalAdTitle && <span className="pub-ad-title">{publishModalAdTitle}</span>}
                      {publishProgressModal.priceLabel && <span className="pub-ad-price">{publishProgressModal.priceLabel}</span>}
                      {publishModalAccountLabel && <span className="pub-ad-account">{publishModalAccountLabel}</span>}
                    </span>
                    {finished && <span className={`pub-pill is-${resultTone}`}>{publishPostStatusMeta.title}</span>}
                    {failed && failedPhase && <span className="pub-pill is-bad">{failedPhase}</span>}
                  </div>
                )}

                {finished ? (
                  <div className="pub-actions pub-actions--done">
                    <button type="button" className="ui-btn-secondary" onClick={() => handlePublishedNext("ads")} disabled={closingPublishProgressModal}>
                      {t("К объявлениям")}
                    </button>
                    <button type="button" className="ui-btn-primary" onClick={() => handlePublishedNext("new")} disabled={closingPublishProgressModal || freezeAccessLocked}>
                      <PlusIcon size={15} />
                      {t("Создать ещё")}
                    </button>
                    {resultUrl && (
                      <a className="pub-link" href={resultUrl} target="_blank" rel="noopener noreferrer">
                        {t("Открыть на Kleinanzeigen")}
                        <ExternalLinkIcon size={13} />
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="pub-actions">
                    <button
                      type="button"
                      className="ui-btn-secondary"
                      onClick={handleClosePublishProgressModal}
                      disabled={closingPublishProgressModal}
                    >
                      {closingPublishProgressModal ? t("Проверяем...") : t("Закрыть")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Окно хода публикации — в духе дашборда и «Сообщений»: карточка на
        // токенах темы, этап иконкой и строкой, полоса отрезками по этапам.
        return (
          <div className="modal-overlay pub-overlay" {...overlayMotionProps(publishProgressPresence.state)}>
            <div
              className={`pub-card is-${tone}`}
              role="dialog"
              aria-modal="true"
              aria-label={t("Публикация объявления")}
              {...dialogMotionProps(publishProgressPresence.state)}
            >
              <div className="pub-head">
                <span className="pub-icon"><StageIcon size={19} /></span>
                <div className="pub-head-text">
                  <div className="pub-title">{t("Публикация объявления")}</div>
                  <div className="pub-status">
                    {!failed && !finished && <LoadingIcon size={12} />}
                    <span>{failed ? t("Публикация завершилась с ошибкой") : stage.statusText}</span>
                  </div>
                </div>
                {!failed && !finished && <span className="pub-percent">{percent}%</span>}
              </div>

              {(publishModalAdTitle || publishModalAccountLabel) && (
                <div className="pub-ad">
                  <span className="pub-ad-icon"><PackageIcon size={15} /></span>
                  <span className="pub-ad-text">
                    {publishModalAdTitle && <span className="pub-ad-title">{publishModalAdTitle}</span>}
                    {publishModalAccountLabel && <span className="pub-ad-account">{publishModalAccountLabel}</span>}
                  </span>
                </div>
              )}

              {/* Ход — одной полосой из отрезков по этапам: пройденные залиты,
                  по текущему бежит блик, впереди — пустые. */}
              <div className="pub-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
                {phases.map((phase, index) => {
                  const state = finished
                    ? "done"
                    : index < currentPhase
                      ? "done"
                      : index === currentPhase
                        ? (failed ? "error" : "active")
                        : "todo";
                  return (
                    <div key={phase.key} className={`pub-seg is-${state}`}>
                      <span className="pub-seg-bar" />
                      <span className="pub-seg-label">{phase.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {warmupProgressPresence.mounted && (() => {
        const warmupProgressModal = warmupProgressView;
        const phaseMeta = getWarmupPhaseMeta(warmupProgressModal.phase);
        const HeaderIcon = warmupProgressModal.error
          ? AlertTriangleIcon
          : warmupProgressModal.done
            ? SuccessIcon
            : phaseMeta.Icon;
        const accentTone = warmupProgressModal.error
          ? "#fca5a5"
          : warmupProgressModal.done
            ? "#86efac"
            : phaseMeta.tone;
        const headerBg = warmupProgressModal.error
          ? "linear-gradient(135deg, rgba(127,29,29,0.4), rgba(69,10,10,0.32))"
          : warmupProgressModal.done
            ? "linear-gradient(135deg, rgba(22,101,52,0.32), rgba(6,78,59,0.28))"
            : S.surface;
        const isPhaseViewAd = String(warmupProgressModal.phase || "").startsWith("view-ad");
        return (
        <div {...overlayMotionProps(warmupProgressPresence.state)} style={{
          position: "fixed",
          inset: 0,
          zIndex: 130000,
          background: "rgba(8,9,12,0.72)",
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isPhoneView ? "12px" : "18px"
        }}>
          <div {...dialogMotionProps(warmupProgressPresence.state)} style={{
            width: "100%",
            maxWidth: "480px",
            borderRadius: "20px",
            border: "1px solid rgba(255,255,255,0.09)",
            background: S.surface,
            boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
            padding: isPhoneView ? "18px" : "24px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                className={warmupProgressModal.done || warmupProgressModal.error ? undefined : "kl-progress-icon-pulse"}
                style={{
                  width: isPhoneView ? "40px" : "44px",
                  height: isPhoneView ? "40px" : "44px",
                  borderRadius: "13px",
                  border: `1px solid ${accentTone}66`,
                  background: headerBg,
                  color: accentTone,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 12px 26px rgba(8,9,12,0.36)"
                }}
              >
                <HeaderIcon size={20} color={accentTone} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#f2f3f6", fontSize: isPhoneView ? "18px" : "19px", fontWeight: 700, letterSpacing: "-0.02em" }}>
                  {t("Прогрев аккаунта")}
                </div>
                <div style={{
                  marginTop: "4px",
                  color: accentTone,
                  fontSize: "12px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}>
                  {!warmupProgressModal.done && !warmupProgressModal.error && (
                    <LoadingIcon size={11} />
                  )}
                  {warmupProgressModal.error
                    ? t("Завершено с ошибкой")
                    : (warmupProgressModal.label || t("Подготовка..."))}
                </div>
              </div>
            </div>

            {warmupProgressModal.accountLabel && (
              <div style={{
                marginTop: "12px",
                padding: "8px 10px",
                borderRadius: "14px",
                border: "1px solid rgba(141,147,163,0.2)",
                background: `${sa(0.5)}`,
                color: "#c6c9d1",
                fontSize: "12px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                maxWidth: "100%"
              }}>
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  borderRadius: "999px",
                  background: `linear-gradient(135deg, ${aa(400, 0.32)}, ${aa(900, 0.28)})`,
                  color: A[200],
                  flexShrink: 0
                }}>
                  <UserIcon size={12} color={A[200]} />
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {warmupProgressModal.accountLabel}
                </span>
              </div>
            )}

            {/* Step strip — visual map of the warm-up phases */}
            {!warmupProgressModal.error && (
              <div style={{
                marginTop: "14px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "10px 4px",
                borderRadius: "16px",
                background: `${sa(0.45)}`,
                border: "1px solid rgba(141,147,163,0.14)"
              }}>
                {WARMUP_STEP_ORDER.map((stepId, idx) => {
                  const stepMeta = getWarmupPhaseMeta(stepId);
                  const StepIcon = stepMeta.Icon;
                  const currentPhase = String(warmupProgressModal.phase || "");
                  const normalizedCurrent = currentPhase.startsWith("view-ad") ? "view-ad" : currentPhase;
                  const currentIdx = WARMUP_STEP_ORDER.indexOf(normalizedCurrent);
                  const isCompleted = currentIdx > idx || warmupProgressModal.done;
                  const isCurrent = !warmupProgressModal.done && currentIdx === idx;
                  const isPending = !isCompleted && !isCurrent;
                  return (
                    <React.Fragment key={stepId}>
                      <div
                        className={isCurrent ? "kl-progress-step-active" : undefined}
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                          minWidth: 0,
                          opacity: isPending ? 0.45 : 1
                        }}
                      >
                        <div style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: isCurrent ? `1px solid ${stepMeta.tone}` : "1px solid rgba(141,147,163,0.22)",
                          background: isCompleted
                            ? "linear-gradient(135deg, rgba(22,101,52,0.32), rgba(6,78,59,0.22))"
                            : isCurrent
                              ? `${stepMeta.tone}22`
                              : "rgba(8,9,12,0.55)",
                          color: isCompleted ? "#86efac" : isCurrent ? stepMeta.tone : "#707684"
                        }}>
                          {isCompleted
                            ? <CheckIcon size={14} color="#86efac" />
                            : <StepIcon size={14} color={isCurrent ? stepMeta.tone : "#707684"} />
                          }
                        </div>
                      </div>
                      {idx < WARMUP_STEP_ORDER.length - 1 && (
                        <div style={{
                          flex: "0 0 4px",
                          height: "2px",
                          borderRadius: "2px",
                          background: isCompleted ? "rgba(134,239,172,0.5)" : "rgba(141,147,163,0.2)"
                        }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* "Liking" indicator when on a view-ad phase to make likes visible */}
            {isPhaseViewAd && !warmupProgressModal.done && !warmupProgressModal.error && (
              <div style={{
                marginTop: "10px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 10px",
                borderRadius: "999px",
                background: "rgba(244,63,94,0.12)",
                border: "1px solid rgba(244,63,94,0.3)",
                color: "#fda4af",
                fontSize: "11px",
                fontWeight: 700
              }}>
                <HeartIcon size={11} color="#fda4af" />
                {t("Просматриваем объявление")}
              </div>
            )}

            {warmupProgressModal.error && (
              <div style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "16px",
                border: "1px solid rgba(248,113,113,0.32)",
                background: "rgba(127,29,29,0.18)",
                color: "#fecaca",
                fontSize: "13px",
                fontWeight: 600
              }}>
                {warmupProgressModal.error}
              </div>
            )}

            {warmupProgressModal.done && !warmupProgressModal.error && warmupProgressModal.result && (
              <div style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "16px",
                border: "1px solid rgba(74,222,128,0.32)",
                background: "rgba(6,78,59,0.18)",
                color: "#bbf7d0",
                fontSize: "12px",
                fontWeight: 600,
                display: "grid",
                gap: "4px"
              }}>
                <div>
                  {t("Шагов выполнено:")} <b>{Number(warmupProgressModal.result.stepsOk) || 0}</b> {t("из")} {Number(warmupProgressModal.result.stepsTotal) || 0}
                </div>
                <div style={{ color: "#86efac" }}>
                  {t("Сетевая работа:")} {((Number(warmupProgressModal.result.totalMs) || 0) / 1000).toFixed(1)}{t("с, сессия:")} {((Date.now() - Number(warmupProgressModal.startedAt || 0)) / 1000).toFixed(1)}{t("с")}
                </div>
              </div>
            )}

            <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                flex: 1,
                height: "12px",
                borderRadius: "999px",
                overflow: "hidden",
                border: "1px solid rgba(141,147,163,0.35)",
                background: `${sa(0.72)}`
              }}>
                <div
                  className={`kl-progress-bar-fill${warmupProgressModal.done ? " is-done" : ""}${warmupProgressModal.error ? " is-error" : ""}`}
                  style={{
                    width: `${Math.max(0, Math.min(100, Number(warmupProgressModal.percent || 0)))}%`,
                    height: "100%",
                    background: warmupProgressModal.error
                      ? "linear-gradient(90deg, #ef4444, #b91c1c)"
                      : warmupProgressModal.done
                        ? "linear-gradient(90deg, #22c55e, #15803d)"
                        : `linear-gradient(90deg, ${accentTone}, ${accentTone}aa)`,
                    transition: "width 280ms ease, background 280ms ease"
                  }}
                />
              </div>
              <div style={{
                minWidth: "48px",
                textAlign: "right",
                color: accentTone,
                fontWeight: 800,
                fontSize: "14px"
              }}>
                {Math.max(0, Math.min(100, Math.round(Number(warmupProgressModal.percent || 0))))}%
              </div>
            </div>

            {warmupProgressModal.done && (
              <div style={{ marginTop: "18px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={handleCloseWarmupModal}
                  style={{
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "transparent",
                    color: "#d5d8e0",
                    fontWeight: 600,
                    padding: "10px 18px",
                    cursor: "pointer"
                  }}
                >
                  {warmupProgressModal.error ? t("Закрыть") : t("Готово")}
                </button>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {addAccountPresence.mounted && (
        <AddAccountModal
          isOpen
          dialogState={addAccountPresence.state}
          isPhoneView={isPhoneView}
          onClose={() => setShowAddAccountModal(false)}
          onSuccess={handleAddAccount}
          onBulkSuccess={handleAddAccountsBulk}
          proxies={proxies}
        />
      )}

      {adModalPresence.mounted && (
        <AdModal
          isOpen
          dialogState={adModalPresence.state}
          isPhoneView={isPhoneView}
          onClose={() => setShowAdModal(false)}
          onSubmit={handleCreateAd}
          onSaveDraft={handleSaveAdDraft}
          publishing={publishingAd}
          accounts={accounts}
          categories={categories}
          categoriesUpdatedAt={categoriesUpdatedAt}
          onRefreshCategories={loadCategories}
          loadingCategories={loadingCategories}
          extraFields={extraFields}
          extraFieldValues={extraFieldValues}
          setExtraFieldValues={setExtraFieldValues}
          loadingExtraFields={loadingExtraFields}
          extraFieldsError={extraFieldsError}
          newAd={newAd}
          setNewAd={setNewAd}
          adImages={adImages}
          setAdImages={setAdImages}
          draftImageUrls={draftImageUrls}
          setDraftImageUrls={setDraftImageUrls}
          photoOrder={adPhotoOrder}
          setPhotoOrder={setAdPhotoOrder}
        />
      )}
    </div>
  );
}

export default App;
